import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { Shield, Users, Building2, Package, Trash2, AlertTriangle, X, LogOut, RefreshCw, Database, CheckCircle, XCircle, Search, Filter, Menu, UserPlus, Store, Plus, Edit, Trash, MapPin, AlertCircle, Info, Check, Glasses, Table, ArrowUpDown, Tag, ChevronDown, ChevronRight, Star, Bell, Sparkles, Moon, Sun, Command as CommandIcon, PanelLeftClose, PanelLeftOpen, Home, Clock, Warehouse, Truck, Palette, BarChart3, TrendingUp, ShoppingBag, FlaskConical, LayoutGrid, CreditCard } from 'lucide-react';
import BadgeStudio from '../../components/BadgeStudio';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import ClientList from '../../components/ClientList';
import LensStockPage from '../LensStock/LensStockPage';
import FrameStockPage from '../FrameStock/FrameStockPage';
import { Toaster, toast } from './components/Toaster';
import { CommandPalette } from './components/CommandPalette';
import { Sparkline } from './components/Sparkline';
import StoreBrandingSettings from './StoreBrandingSettings';
import CourierRouteSettings from './CourierRouteSettings';
import { useTranslation } from 'react-i18next';
import TaskSwitches from '../../components/TaskSwitches/TaskSwitches';
import { taskOverrides, getStoredUser } from '../../utils/tasks';
import { advisorAccess, advisorAccessFor, sectionOfTab, ADVISOR_SECTIONS } from '../../utils/advisor';
import AdvisorAccessEditor from '../../components/AdvisorAccess/AdvisorAccessEditor';
import { downscaleImage } from '../../utils/imageResize';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ──────────────────────────────────────────────────────────────────────────
// Design tokens — every value points at a CSS custom property defined on the
// `.admin-shell` wrapper below. Swapping `data-theme` from "light" → "dark"
// re-paints the whole dashboard in one cheap repaint, no React refactor needed.
// ──────────────────────────────────────────────────────────────────────────
const tokens = {
  // Brand
  brand:                'var(--c-brand)',
  brandDark:            'var(--c-brand-dark)',
  brandSoft:            'var(--c-brand-soft)',
  brandSoftTranslucent: 'var(--c-brand-soft-trans)',
  brand33:              'var(--c-brand-33)',  // ~20% alpha brand
  brand55:              'var(--c-brand-55)',  // ~33% alpha brand

  // Text
  ink:    'var(--c-ink)',
  body:   'var(--c-body)',
  muted:  'var(--c-muted)',

  // Surfaces
  bg:           'var(--c-bg)',
  surface:      'var(--c-surface)',
  surfaceSoft:  'var(--c-surface-soft)',  // alt rows / panel insets
  sidebarBg:    'var(--c-sidebar-bg)',
  topbarBg:     'var(--c-topbar-bg)',
  inputBg:      'var(--c-input-bg)',
  topbarGlass:  'var(--c-topbar-glass)',
  tableHeadBg:  'var(--c-table-head-bg)',
  hover:        'var(--c-hover)',
  activeNavBg:  'var(--c-active-nav-bg)',

  // Borders
  hairline:     'var(--c-hairline)',
  hairlineSoft: 'var(--c-hairline-soft)',

  // Accent ramp (KPI stripes, status chips)
  accentBlue:  'var(--c-accent-blue)',
  accentGreen: 'var(--c-accent-green)',
  accentAmber: 'var(--c-accent-amber)',
  accentRose:  'var(--c-accent-rose)',

  // Shadows
  shadowSm: 'var(--shadow-sm)',
  shadow:   'var(--shadow)',
  shadowMd: 'var(--shadow-md)',
  shadowLg: 'var(--shadow-lg)',

  // Easing
  ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

// Theme variable definitions. Scoped to `.admin-shell` so the rest of the app
// (login, sales screens) is unaffected by the dashboard's dark mode.
const ShellThemeStyles = createGlobalStyle`
  .admin-shell {
    /* ── Light defaults — Clean Slate palette ── */
    --c-brand: #6366f1;
    --c-brand-dark: #4f46e5;
    --c-brand-soft: #eef2ff;
    --c-brand-soft-trans: rgba(99, 102, 241, 0.08);
    --c-brand-33: rgba(99, 102, 241, 0.2);
    --c-brand-55: rgba(99, 102, 241, 0.33);

    /* Text hierarchy — slate scale, generous contrast on each step. */
    --c-ink:  #0f172a;   /* headings, primary text     */
    --c-body: #475569;   /* body copy, table cells     */
    --c-muted:#94a3b8;   /* captions, helper text      */

    /* Surfaces — cool whites, flat fills (no gradients on bg/sidebar/topbar). */
    --c-bg:           #f8fafc;   /* page background      */
    --c-surface:      #ffffff;   /* cards, panels        */
    --c-surface-soft: #f8fafc;   /* nested soft fills    */
    --c-sidebar-bg:   #ffffff;   /* flat white sidebar   */
    --c-topbar-bg:    #ffffff;   /* crisp solid topbar   */
    --c-topbar-glass: rgba(255, 255, 255, 0.82);  /* translucent sticky topbar */
    --c-input-bg:     #ffffff;   /* inputs are white + border, not filled */
    --c-table-head-bg:#f8fafc;   /* subtle thead tint    */
    --c-hover:        #f1f5f9;   /* universal hover tint */
    --c-active-nav-bg:#f1f5f9;   /* flat active nav fill (3px left accent added in sidebar) */

    /* Dividers — primary hairline + a softer one for internal separators. */
    --c-hairline:      #e2e8f0;
    --c-hairline-soft: #f1f5f9;

    /* Accent ramps for KPI top-borders and status chips (added in later steps). */
    --c-accent-blue:   #3b82f6;
    --c-accent-green:  #10b981;
    --c-accent-amber:  #f59e0b;
    --c-accent-rose:   #f43f5e;

    /* Shadows — premium layered soft shadows with a faint indigo tint.
       Two stacked layers (ambient + key) give depth without heaviness. */
    --shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(30, 41, 99, 0.03);
    --shadow:    0 2px 6px -1px rgba(15, 23, 42, 0.06), 0 4px 14px -4px rgba(30, 41, 99, 0.05);
    --shadow-md: 0 8px 24px -8px rgba(15, 23, 42, 0.10), 0 4px 8px -4px rgba(30, 41, 99, 0.06);
    --shadow-lg: 0 24px 48px -12px rgba(15, 23, 42, 0.18), 0 8px 18px -8px rgba(30, 41, 99, 0.10);

    /* ── Typography tokens (Clean Slate) ──
       Font stack: Plus Jakarta Sans → Inter (already preloaded) → system.
       Sizes follow a 4-step modular scale; tracking tightens on display sizes. */
    --font-sans: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont,
                 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

    --fs-display: 1.875rem;   /* 30px — page titles                */
    --fs-h1:      1.5rem;     /* 24px — section titles             */
    --fs-h2:      1.125rem;   /* 18px — card titles                */
    --fs-body:    0.9375rem;  /* 15px — default body text          */
    --fs-sm:      0.8125rem;  /* 13px — secondary, captions        */
    --fs-xs:      0.75rem;    /* 12px — labels, helper text        */

    --fw-regular: 400;
    --fw-medium:  500;
    --fw-semibold:600;
    --fw-bold:    700;

    --tracking-display: -0.022em;
    --tracking-h1:      -0.018em;
    --tracking-h2:      -0.012em;
    --tracking-label:    0.06em;

    --leading-tight:  1.2;
    --leading-snug:   1.35;
    --leading-normal: 1.55;

    /* Apply the font + smoothing to the whole admin shell. Every component
       below inherits since they use font-family: inherit. */
    font-family: var(--font-sans);
    font-feature-settings: 'cv11', 'ss01', 'ss03';
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  .admin-shell[data-theme="dark"] {
    --c-brand: #818cf8;
    --c-brand-dark: #a5b4fc;
    --c-brand-soft: #1e1b4b;
    --c-brand-soft-trans: rgba(129, 140, 248, 0.12);
    --c-brand-33: rgba(129, 140, 248, 0.25);
    --c-brand-55: rgba(129, 140, 248, 0.4);

    --c-ink:  #f1f5f9;
    --c-body: #cbd5e1;
    --c-muted:#94a3b8;

    /* Flat dark surfaces — no gradients. Slate-900/800 base. */
    --c-bg:           #0b1220;
    --c-surface:      #111827;
    --c-surface-soft: #1a2233;
    --c-sidebar-bg:   #0b1220;
    --c-topbar-bg:    #0b1220;
    --c-topbar-glass: rgba(11, 18, 32, 0.82);
    --c-input-bg:     #1a2233;
    --c-table-head-bg:#1a2233;
    --c-hover:        #1e293b;
    --c-active-nav-bg:#1e293b;

    --c-hairline:      #1e293b;
    --c-hairline-soft: #1a2233;

    --c-accent-blue:   #60a5fa;
    --c-accent-green:  #34d399;
    --c-accent-amber:  #fbbf24;
    --c-accent-rose:   #fb7185;

    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.35);
    --shadow:    0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.35);
    --shadow-md: 0 4px 12px -2px rgba(0, 0, 0, 0.5), 0 2px 4px -2px rgba(0, 0, 0, 0.4);
    --shadow-lg: 0 12px 24px -8px rgba(0, 0, 0, 0.55), 0 4px 8px -4px rgba(0, 0, 0, 0.45);

    /* Dark-mode legibility tweaks for the deep parts of the dashboard
       that were authored with hard-coded light colours. Using fairly
       specific selectors so we don't bleed into anything outside .admin-shell. */
    color: var(--c-ink);

    /* Lift hard-coded white surfaces. */
    [style*="background: white"],
    [style*="background:white"],
    [style*="background: #fff"],
    [style*="background:#fff"],
    [style*="background: #ffffff"],
    [style*="background:#ffffff"] {
      background: var(--c-surface) !important;
    }
    [style*="background: #fafafa"], [style*="background:#fafafa"],
    [style*="background: #f9fafb"], [style*="background:#f9fafb"],
    [style*="background: #f8fafc"], [style*="background:#f8fafc"] {
      background: var(--c-surface-soft) !important;
    }
    [style*="background: #f1f5f9"], [style*="background:#f1f5f9"] {
      background: var(--c-input-bg) !important;
    }

    /* Text colors that were hard-coded slate/zinc. */
    [style*="color: #0f172a"], [style*="color:#0f172a"],
    [style*="color: #1e293b"], [style*="color:#1e293b"] {
      color: var(--c-ink) !important;
    }
    [style*="color: #334155"], [style*="color:#334155"],
    [style*="color: #475569"], [style*="color:#475569"] {
      color: var(--c-body) !important;
    }
    [style*="color: #64748b"], [style*="color:#64748b"],
    [style*="color: #94a3b8"], [style*="color:#94a3b8"] {
      color: var(--c-muted) !important;
    }

    /* Borders that were hard-coded #e2e8f0 / #cbd5e1. */
    [style*="border: 1px solid #e2e8f0"], [style*="border:1px solid #e2e8f0"],
    [style*="border: 1px solid #cbd5e1"], [style*="border:1px solid #cbd5e1"] {
      border-color: var(--c-hairline) !important;
    }
  }
`;

// ──────────────────────────────────────────────────────────────────────────
// Tiny utilities — animated count-up, skeleton, time-of-day greeting.
// ──────────────────────────────────────────────────────────────────────────

/**
 * AnimatedNumber — eases a number from its previous value to `value` in
 * `duration` ms via requestAnimationFrame + easeOutCubic.
 */
const AnimatedNumber = ({ value, duration = 700, format = (n) => n.toLocaleString() }) => {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    const target = Number(value) || 0;
    fromRef.current = display;
    startRef.current = null;
    const step = (ts) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(fromRef.current + (target - fromRef.current) * eased);
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return <>{format(display)}</>;
};

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Skeleton = styled.div`
  display: inline-block;
  height: ${p => p.h || '1em'};
  width: ${p => p.w || '100%'};
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 0px, #e8eef7 200px, #f1f5f9 400px);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`;

const greetByTime = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

// ──────────────────────────────────────────────────────────────────────────
// KpiFooter — sparkline + δ% chip that lives at the bottom of each StatCard.
// Shows a 14-day mini-chart and the % change between the most recent 7 days
// and the previous 7 days. Renders a thin shimmer while data is loading.
// ──────────────────────────────────────────────────────────────────────────

const KpiFooterRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding-top: 0.6rem;
  border-top: 1px dashed ${tokens.hairline};
`;

const SparkSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const KpiDelta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  flex-shrink: 0;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${tokens.muted};
`;

const DeltaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: ${p => p.$value >= 0 ? '#dcfce7' : '#fee2e2'};
  color: ${p => p.$value >= 0 ? '#166534' : '#991b1b'};
  border: 1px solid ${p => p.$value >= 0 ? '#bbf7d0' : '#fecaca'};
  text-transform: none;
  letter-spacing: 0;
`;

// Tiny "Soon" pill used on the Profit KPI card while we wait for cost data.
const ComingSoonBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.45rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${tokens.brandSoft};
  color: ${tokens.brand};
  border: 1px solid ${tokens.brand33};
  vertical-align: middle;
`;

const KpiFooter = ({ data, color, loading }) => {
  const { t } = useTranslation();
  // δ between sum of last 7 days vs previous 7 days. Falls back to a neutral
  // chip when there isn't enough history (e.g. on a brand-new install).
  let delta = 0;
  let hasDelta = false;
  if (Array.isArray(data) && data.length >= 4) {
    const half = Math.floor(data.length / 2);
    const recent = data.slice(-half).reduce((s, p) => s + (p?.value ?? p ?? 0), 0);
    const prior  = data.slice(0, data.length - half).reduce((s, p) => s + (p?.value ?? p ?? 0), 0);
    if (prior > 0) {
      delta = ((recent - prior) / prior) * 100;
      hasDelta = true;
    } else if (recent > 0) {
      delta = 100;
      hasDelta = true;
    }
  }

  if (loading) {
    return (
      <KpiFooterRow>
        <SparkSlot><Skeleton w="100%" h="32px" /></SparkSlot>
        <KpiDelta><Skeleton w="44px" h="18px" /></KpiDelta>
      </KpiFooterRow>
    );
  }

  return (
    <KpiFooterRow>
      <SparkSlot>
        <Sparkline data={data} color={color} height={36} ariaLabel="Last 14 days" />
      </SparkSlot>
      <KpiDelta>
        {hasDelta ? (
          <DeltaPill $value={delta}>
            {delta >= 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(0)}%
          </DeltaPill>
        ) : (
          <DeltaPill $value={0}>—</DeltaPill>
        )}
        <span>{t('admin.x14d')}</span>
      </KpiDelta>
    </KpiFooterRow>
  );
};

// Page-header copy per active tab — keeps the H1 in sync with the sidebar.
const TAB_META = {
  overview: { title: 'Overview', subtitle: 'Performance snapshot, sales by employee, and key metrics for your store.' },
  orders: { title: 'Orders', subtitle: 'Group every visit into Confirmed, Completed, or Unsuccessful — from prescription to delivery.' },
  employees: { title: 'Employees', subtitle: 'Manage your team, roles, and store assignments.' },
  'employee-statistics': { title: 'Employee Statistics', subtitle: 'Review employee sales, completion, progressive, and recommendation rates.' },
  clients: { title: 'Clients', subtitle: 'Search the customer database and review purchase history.' },
  stores: { title: 'Retail Stores', subtitle: 'Configure your customer-facing storefronts \u2014 branding, contacts, and locations.' },
  'central-store': { title: 'Central Store', subtitle: 'The company warehouse \u2014 assign inventory and operations managers and review staff.' },
  'courier-routes': { title: 'Courier Routes', subtitle: 'Set daily courier pickup times and paths between stores and central.' },
  'peer-ratings': { title: 'Peer ratings', subtitle: 'Anonymous teammate ratings to help spot strengths and gaps.' },
  labs: { title: 'Labs', subtitle: 'Manage external lens labs and turnaround partners.' },
  administration: { title: 'Administration', subtitle: 'Wilayas, communes, and regional settings.' },
  groups: { title: 'Group', subtitle: 'Visual SPH/CYL correction grouping map used by recommendation rules.' },
  recommendations: { title: 'Recommendations', subtitle: 'Matrix, alternatives, and catalog pricing.' },
  'other-lenses': { title: 'Other Lenses', subtitle: 'Pre-configured products available under the Other order option.' },
  'lens-stock': { title: 'Lens Stock', subtitle: 'In-stock lens inventory across all locations.' },
  'frame-stock': { title: 'Frame Stock', subtitle: 'Frame inventory by store, brand, and model.' },
};

const Container = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at 0% 0%, ${tokens.brand33} 0, transparent 50%),
    radial-gradient(circle at 100% 0%, rgba(168, 85, 247, 0.04) 0, transparent 40%),
    ${tokens.bg};
  display: flex;
  position: relative;
  color: ${tokens.body};
`;

// Sidebar — supports two boolean modes:
//   isOpen      → mobile slide-in/out
//   $collapsed  → desktop icon-only mode (hides labels, shrinks width)
const Sidebar = styled.aside`
  width: ${p => p.$collapsed ? '76px' : '264px'};
  background: ${tokens.sidebarBg};
  border-right: 1px solid ${tokens.hairline};
  min-height: 100vh;
  position: sticky;
  top: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  z-index: 100;
  transition: width 0.28s ${tokens.ease};
  flex-shrink: 0;

  /* slim, branded scrollbar */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: ${tokens.hairline}; border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover { background: ${tokens.muted}; }

  /* Collapsed (icon-only) tweaks across all descendants. */
  &[data-collapsed="true"] {
    .section-label,
    .sidebar-title-text,
    .stock-chevron {
      display: none !important;
    }
    /* Nav labels fade + collapse their width so the transition feels smooth
       (instead of popping out instantly) while the panel narrows. */
    .nav-label {
      opacity: 0;
      max-width: 0;
      flex: 0 0 0;
      margin: 0;
      overflow: hidden;
    }
    .stock-row { justify-content: center !important; }
    .nav-item { justify-content: center; gap: 0; padding-left: 0.4rem; padding-right: 0.4rem; }
    .nav-item .icon { margin: 0; }
    /* Show a tooltip on hover for clarity. */
    .nav-item .nav-tooltip { display: none; }
    .nav-item:hover .nav-tooltip { display: inline-flex; }
    .sidebar-header { padding: 1rem 0.5rem; }
    .sidebar-header img { width: 44px !important; height: 44px !important; }
    .sidebar-header .sidebar-title-block { gap: 0; }
  }

  @media (max-width: 768px) {
    position: fixed;
    left: ${props => props.isOpen ? '0' : '-300px'};
    top: 0;
    width: 280px;
    height: 100vh;
    transition: left 0.32s ${tokens.ease};
    box-shadow: ${props => props.isOpen ? '12px 0 32px rgba(15, 23, 42, 0.18)' : 'none'};
    z-index: 1000;

    /* On mobile, force expanded state — icon-only mode is desktop-only. */
    &[data-collapsed="true"] {
      width: 280px;
      .nav-label, .section-label, .sidebar-title-text, .stock-chevron { display: revert !important; }
      .nav-item { justify-content: flex-start; padding-left: 0.85rem; padding-right: 0.85rem; }
    }
  }
`;

const SidebarHeader = styled.div.attrs({ className: 'sidebar-header' })`
  padding: 1.5rem 1.25rem 1.25rem;
  border-bottom: 1px solid ${tokens.hairline};
`;

const SidebarTitle = styled.h2`
  font-size: 1.0625rem;
  font-weight: 700;
  color: ${tokens.ink};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  letter-spacing: -0.012em;
`;

const SidebarSectionLabel = styled.div.attrs({ className: 'section-label' })`
  padding: 1.1rem 0.875rem 0.4rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.muted};

  &:first-of-type {
    padding-top: 0.5rem;
  }
`;

const SidebarNav = styled.nav`
  padding: 0.375rem 0.5rem;
  flex: 1;
`;

const NavItem = styled.div.attrs(p => ({ className: `nav-item${p.className ? ' ' + p.className : ''}` }))`
  position: relative;
  padding: 0.62rem 0.8rem 0.62rem 0.95rem;
  margin: 0.15rem 0;
  cursor: pointer;
  transition: background 0.18s ${tokens.ease}, color 0.18s ${tokens.ease}, box-shadow 0.18s ${tokens.ease};
  display: flex;
  align-items: center;
  gap: 0.72rem;
  color: ${props => props.active ? tokens.brandDark : tokens.body};
  background: ${props => props.active
    ? `linear-gradient(90deg, ${tokens.brandSoft} 0%, ${tokens.brandSoftTranslucent} 70%, transparent 110%)`
    : 'transparent'};
  border-radius: 10px;
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: 0.875rem;
  line-height: 1.2;
  box-shadow: ${props => props.active ? `inset 0 0 0 1px ${tokens.brand33}` : 'none'};

  /* Flush-left brand accent pill on the active row. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: ${props => props.active ? '55%' : '0'};
    background: ${tokens.brand};
    border-radius: 0 3px 3px 0;
    box-shadow: 0 0 8px -1px ${tokens.brand55};
    transition: height 0.2s ${tokens.ease};
  }

  &:hover {
    background: ${props => props.active
      ? `linear-gradient(90deg, ${tokens.brandSoft} 0%, ${tokens.brandSoftTranslucent} 70%, transparent 110%)`
      : tokens.hover};
    color: ${props => props.active ? tokens.brandDark : tokens.ink};
  }

  .icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: ${props => props.active ? tokens.brand : 'currentColor'};
    transition: color 0.18s ${tokens.ease}, transform 0.18s ${tokens.ease};
  }

  &:hover .icon {
    color: ${props => props.active ? tokens.brand : tokens.ink};
    transform: ${props => props.active ? 'none' : 'translateX(1px)'};
  }
`;

// Tooltip shown next to a NavItem when the sidebar is collapsed.
// Hidden by default — the Sidebar's [data-collapsed="true"] block re-enables
// it on hover. Without `display: none` here it would render permanently as a
// floating dark "fin" sticking out of every nav row.
const NavTooltip = styled.span.attrs({ className: 'nav-tooltip' })`
  display: none;
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: ${tokens.ink};
  color: white;
  padding: 0.3rem 0.55rem;
  border-radius: 6px;
  font-size: 0.78rem;
  white-space: nowrap;
  z-index: 1500;
  pointer-events: none;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.25);

  &::before {
    content: '';
    position: absolute;
    left: -4px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
    width: 8px; height: 8px;
    background: ${tokens.ink};
  }
`;

const NavLabel = styled.span.attrs({ className: 'nav-label' })`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.16s ${tokens.ease}, max-width 0.22s ${tokens.ease};
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: 0.75rem 0.5rem 1rem;
  border-top: 1px solid ${tokens.hairline};
`;

// Desktop-only collapse/expand toggle. Hidden on mobile (where the sidebar
// uses the slide-in pattern instead).
const SidebarCollapseToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${p => p.$collapsed ? 'center' : 'space-between'};
  width: calc(100% - 1rem);
  margin: 0.25rem 0.5rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px solid ${tokens.hairline};
  border-radius: 8px;
  color: ${tokens.muted};
  cursor: pointer;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  transition: background 0.18s ${tokens.ease}, color 0.18s ${tokens.ease}, border-color 0.18s ${tokens.ease};

  &:hover {
    background: ${tokens.hover};
    color: ${tokens.ink};
    border-color: ${tokens.hairline};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoutButton = styled.div`
  padding: 0.55rem 0.875rem;
  margin: 0.125rem 0;
  cursor: pointer;
  transition: background 0.18s ${tokens.ease}, color 0.18s ${tokens.ease};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: #dc2626;
  font-weight: 500;
  font-size: 0.875rem;
  border-radius: 8px;

  &:hover {
    background: #fef2f2;
    color: #b91c1c;
  }

  .icon {
    width: 18px;
    height: 18px;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 0;
  overflow-y: auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const ContentScroll = styled.div`
  flex: 1;
  padding: 1.5rem 2.25rem 2.5rem;
  min-width: 0;

  /* Subtle, branded scrollbar across the dashboard. */
  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
    border: 2px solid ${tokens.bg};
  }
  &::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

  @media (max-width: 768px) {
    padding: 1rem;
    width: 100%;
  }
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 2.25rem;
  background: ${tokens.topbarGlass};
  backdrop-filter: saturate(180%) blur(14px);
  -webkit-backdrop-filter: saturate(180%) blur(14px);
  border-bottom: 1px solid ${tokens.hairline};

  @media (max-width: 768px) {
    padding: 0.75rem 1rem 0.75rem 4rem; /* leave room for the menu button */
    gap: 0.5rem;
  }
`;

const TopSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: ${tokens.inputBg};
  border: 1px solid ${tokens.hairline};
  border-radius: 11px;
  padding: 0.55rem 0.9rem;
  flex: 1;
  max-width: 440px;
  transition: all 0.18s ${tokens.ease};
  color: ${tokens.muted};

  &:hover { border-color: #cbd5e1; }
  &:focus-within {
    background: ${tokens.surface};
    border-color: ${tokens.brand};
    box-shadow: 0 0 0 4px ${tokens.brand33};
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 0.9rem;
    color: ${tokens.ink};
    min-width: 0;

    &::placeholder { color: ${tokens.muted}; }
  }

  kbd {
    display: inline-flex;
    align-items: center;
    padding: 0.1rem 0.45rem;
    background: ${tokens.surface};
    border: 1px solid ${tokens.hairline};
    border-radius: 5px;
    font-size: 0.7rem;
    color: ${tokens.muted};
    font-family: inherit;

    @media (max-width: 768px) { display: none; }
  }
`;

const TopAction = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: 10px;
  cursor: pointer;
  color: ${tokens.muted};
  transition: all 0.15s ${tokens.ease};

  &:hover {
    border-color: ${tokens.brand55};
    color: ${tokens.brandDark};
    background: ${tokens.brandSoft};
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0); }
  &[data-toggled="true"] {
    background: ${tokens.brandSoft};
    border-color: ${tokens.brand55};
    color: ${tokens.brandDark};
  }
`;

const NotifDot = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 0 2px ${tokens.surface};
`;


const UserChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0.85rem 0.35rem 0.4rem;
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: 999px;
  cursor: default;
  transition: all 0.18s ${tokens.ease};

  &:hover {
    border-color: ${tokens.brand55};
    box-shadow: ${tokens.shadowSm};
  }

  @media (max-width: 640px) {
    padding: 0.35rem;

    .user-text { display: none; }
  }
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${tokens.brand} 0%, #a855f7 100%);
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.35);
`;

const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.15;
`;

const UserName = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${tokens.ink};
`;

const UserRole = styled.span`
  font-size: 0.68rem;
  font-weight: 500;
  color: ${tokens.brandDark};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const TabPanel = styled.div`
  animation: ${fadeUp} 0.32s ${tokens.ease};
`;

const Header = styled.div`
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
`;

const TitleAccent = styled.div`
  width: 4px;
  height: 38px;
  background: linear-gradient(180deg, ${tokens.brand} 0%, #a855f7 100%);
  border-radius: 999px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px -1px ${tokens.brand55};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${tokens.ink};
  margin: 0;
  letter-spacing: -0.03em;
  line-height: 1.12;

  @media (max-width: 768px) {
    font-size: 1.45rem;
  }
`;

const Subtitle = styled.p`
  color: ${tokens.muted};
  margin: 0 0 0 1.15rem;
  font-size: 0.92rem;

  @media (max-width: 768px) {
    margin-left: 1.15rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  /* Opening a KPI focuses it: the siblings are hidden and the grid drops to a
     single track so the survivor gets the full width to draw its breakdown. */
  grid-template-columns: ${p => (p.$focused ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))')};
  /* Cards expand inline. align-items: start keeps the others compact when one
     opens its breakdown panel. */
  align-items: start;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

// Expanding panel inside a KPI StatCard. Smooth height transition so the
// breakdown opens like an accordion.
const KpiExpand = styled.div`
  overflow: hidden;
  /* Generous ceiling: a focused card is full-width and can carry a long
     store -> seller -> product tree. Too low a value silently clips it. */
  max-height: ${p => (p.$open ? '6000px' : '0')};
  opacity: ${p => (p.$open ? 1 : 0)};
  margin-top: ${p => (p.$open ? '0.85rem' : '0')};
  padding-top: ${p => (p.$open ? '0.85rem' : '0')};
  border-top: 1px solid ${p => (p.$open ? tokens.hairline : 'transparent')};
  transition:
    max-height 0.32s ${tokens.ease},
    opacity 0.22s ${tokens.ease},
    margin-top 0.22s ${tokens.ease},
    padding-top 0.22s ${tokens.ease},
    border-color 0.22s ${tokens.ease};
`;

// Chevron that flips when the card is open, mirroring the tree-row chevrons.
const ExpandCaret = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 0.4rem;
  border-radius: 6px;
  color: ${tokens.muted};
  background: ${tokens.brandSoft};
  transition: transform 0.22s ${tokens.ease}, color 0.18s ${tokens.ease};
  transform: rotate(${p => (p.$open ? '90deg' : '0deg')});

  ${p => p.$open && `color: ${tokens.brand};`}
`;

const StatCard = styled.div`
  background: ${tokens.surface};
  border-radius: 18px;
  padding: 1.5rem 1.6rem;
  box-shadow: ${tokens.shadowSm};
  border: 1px solid ${tokens.hairline};
  position: relative;
  overflow: hidden;
  transition: transform 0.22s ${tokens.ease}, box-shadow 0.24s ${tokens.ease}, border-color 0.24s ${tokens.ease};
  /* Cards behave as buttons when an onClick is wired (true for the overview KPIs). */
  cursor: ${p => (p.$interactive ? 'pointer' : 'default')};
  user-select: ${p => (p.$interactive ? 'none' : 'auto')};
  ${p => p.$hidden && 'display: none;'}

  /* Top accent stripe — colour follows the StatIcon's iconColor when present. */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${props => props.accent || `linear-gradient(90deg, ${tokens.brand} 0%, #a855f7 100%)`};
    opacity: 0.9;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${tokens.shadowMd};
    border-color: ${p => (p.$interactive ? tokens.brand55 : '#d8dcf0')};
  }

  &:active {
    transform: ${p => (p.$interactive ? 'translateY(-1px)' : 'translateY(-3px)')};
    box-shadow: ${tokens.shadow};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${tokens.brand33}, ${tokens.shadowMd};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.85rem;
`;

const StatTitle = styled.h3`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${tokens.muted};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const StatIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: ${props => props.color || tokens.brandSoft};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor || tokens.brand};
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55),
              inset 0 1px 1px rgba(255, 255, 255, 0.7),
              0 2px 6px -1px rgba(15, 23, 42, 0.10);
  transition: transform 0.22s ${tokens.ease};

  ${StatCard}:hover & {
    transform: scale(1.06);
  }
`;

const StatValue = styled.div`
  font-size: 2.05rem;
  font-weight: 700;
  color: ${tokens.ink};
  margin: 0;
  letter-spacing: -0.025em;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
`;

const SellByPanel = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #fee2e2;
  margin-bottom: 2rem;
`;

const SellByHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const SellByTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #dc2626;
  margin: 0;
`;

const SellByList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const SellByItem = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid #fee2e2;
  background: #fef2f2;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SellByMeta = styled.div`
  color: #64748b;
  font-size: 0.85rem;
`;

const DateRangeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  padding: 0.85rem 1rem;
  background: white;
  border: 1px solid ${tokens.hairline};
  border-radius: 12px;
  box-shadow: ${tokens.shadowSm};
`;

const DateInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${tokens.hairline};
  border-radius: 9px;
  font-size: 0.875rem;
  color: ${tokens.ink};
  background: white;
  outline: none;
  font-family: inherit;
  transition: all 0.15s ${tokens.ease};

  &:hover { border-color: #cbd5e1; }
  &:focus {
    border-color: ${tokens.brand};
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
  }
`;

const DateLabel = styled.label`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${tokens.muted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const TurnoverSub = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
`;

const ProfitPlaceholder = styled.div`
  text-align: center;
  padding: 1.5rem 0.5rem;
  color: #94a3b8;
  font-size: 0.875rem;
`;

// ─── Quantity card breakdown ─────────────────────────────────────────────
// Two-column grid inside the Quantity KPI card: Lenses (left) and Frames
// (right). Each column shows its total upfront; clicking expands into a
// nested breakdown with percentages.
const QtyColumns = styled.div`
  display: grid;
  /* auto-fit + minmax does container-aware stacking: if the KPI card is wide
     enough for two ≥260px tracks side-by-side, we use two columns; otherwise
     it falls back to a single full-width column automatically. This avoids
     the @media-query trap (which checks viewport width, not the card's
     own width) that was forcing labels like "Simple Vision" to truncate to
     "SI…" inside a narrow card. */
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.65rem;
  align-items: start;
`;

const QtyColumn = styled.div`
  background: ${tokens.surfaceSoft};
  border: 1px solid ${tokens.hairline};
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  /* Allow this flex child to shrink below its content's intrinsic width
     when placed inside a narrow grid track. Without this, sub-rows can
     bleed past the column's right edge. */
  min-width: 0;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &[data-open='true'] {
    border-color: ${tokens.brand33};
    box-shadow: 0 1px 0 ${tokens.brand33} inset;
  }
`;

// Clickable header that always shows: dot, title, caret, and the column total.
const QtyColumnHead = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  text-align: left;
  width: 100%;
  cursor: pointer;
  display: grid;
  grid-template-rows: auto auto;
  gap: 0.35rem;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: 2px solid ${tokens.brand};
    outline-offset: 3px;
    border-radius: 6px;
  }
`;

const QtyHeadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.muted};
  min-width: 0;
`;

/* Title slot inside head rows — allows the label to shrink/truncate so the
   trailing count + caret never get pushed off-screen. */
const QtyHeadTitle = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QtyColumnDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${p => p.$color || tokens.brand};
  display: inline-block;
  flex: 0 0 auto;
`;

const QtyHeadCaret = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  color: ${tokens.muted};
  transform: rotate(${p => (p.$open ? '90deg' : '0deg')});
  transition: transform 0.2s ease;
`;

const QtyHeadTotal = styled.div`
  font-size: 1.55rem;
  font-weight: 800;
  color: ${tokens.ink};
  font-variant-numeric: tabular-nums;
  line-height: 1.05;
  letter-spacing: -0.01em;
`;

// Collapsible body — uses grid-template-rows 0fr → 1fr trick so the
// transition animates from real height to zero without measuring.
const QtyBody = styled.div`
  display: grid;
  grid-template-rows: ${p => (p.$open ? '1fr' : '0fr')};
  transition:
    grid-template-rows 0.25s ease,
    margin-top 0.25s ease,
    padding-top 0.25s ease,
    opacity 0.2s ease;
  margin-top: ${p => (p.$open ? '0.25rem' : '0')};
  padding-top: ${p => (p.$open ? '0.5rem' : '0')};
  border-top: 1px dashed ${p => (p.$open ? tokens.hairline : 'transparent')};
  opacity: ${p => (p.$open ? 1 : 0)};

  > .qty-body-inner {
    overflow: hidden;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
`;

// Segmented control at the top of the Quantity card: drill into one branch,
// or lay every store / every seller side by side.
const QtyModeBar = styled.div`
  display: inline-flex;
  gap: 0.15rem;
  padding: 0.2rem;
  margin-bottom: 0.85rem;
  border: 1px solid ${tokens.hairline};
  border-radius: 10px;
  background: ${tokens.surfaceSoft};
`;

const QtyModeButton = styled.button`
  appearance: none;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.3rem 0.7rem;
  border-radius: 7px;
  color: ${p => (p.$active ? tokens.brand : tokens.muted)};
  background: ${p => (p.$active ? tokens.surface : 'transparent')};
  box-shadow: ${p => (p.$active ? tokens.shadowSm : 'none')};
  transition: color 0.18s ${tokens.ease}, background 0.18s ${tokens.ease};

  &:hover { color: ${p => (p.$active ? tokens.brand : tokens.ink)}; }

  &:focus-visible {
    outline: 2px solid ${tokens.brand};
    outline-offset: 2px;
  }
`;

// Head-to-head panel: the picked stores/sellers become columns and the
// metrics become rows, which is the layout that actually makes two numbers
// comparable. The full table below stays put so the selection can be changed.
const QtyVersus = styled.div`
  border: 1px solid ${tokens.brand33};
  border-radius: 12px;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  background: ${tokens.surfaceSoft};
  overflow-x: auto;
`;

const QtyVersusTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
  font-variant-numeric: tabular-nums;

  th, td {
    padding: 0.4rem 0.65rem;
    border-bottom: 1px solid ${tokens.hairline};
    text-align: right;
    white-space: nowrap;
  }

  th:first-child, td:first-child {
    text-align: left;
    color: ${tokens.muted};
    font-weight: 600;
    white-space: normal;
  }

  thead th {
    font-size: 0.78rem;
    font-weight: 800;
    color: ${tokens.ink};
    vertical-align: bottom;
  }

  tbody tr:last-child td { border-bottom: 0; }

  /* Winner of each metric row. Ties are not marked - nothing to call. */
  td[data-best='true'] {
    color: ${tokens.brand};
    font-weight: 800;
  }
`;

const QtyCompareButton = styled.button`
  appearance: none;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.35rem 0.85rem;
  border-radius: 8px;
  color: #fff;
  background: ${tokens.brand};
  transition: opacity 0.18s ${tokens.ease}, background 0.18s ${tokens.ease};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:not(:disabled):hover { opacity: 0.88; }

  &:focus-visible {
    outline: 2px solid ${tokens.brand};
    outline-offset: 2px;
  }
`;

const FilterSelect = styled.select`
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${tokens.ink};
  padding: 0.3rem 0.5rem;
  border: 1px solid ${tokens.hairline};
  border-radius: 8px;
  background: ${tokens.surface};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${tokens.brand};
    outline-offset: 2px;
  }
`;

const QtySelectBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 0.6rem;
  font-size: 0.76rem;
  color: ${tokens.muted};
`;

const QtyCompareTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;

  th, td {
    padding: 0.45rem 0.6rem;
    border-bottom: 1px solid ${tokens.hairline};
    white-space: nowrap;
  }

  th {
    text-align: right;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${tokens.muted};
  }

  th:first-child, td:first-child { text-align: left; white-space: normal; }
  td { text-align: right; color: ${tokens.body}; }
  tbody tr:hover { background: ${tokens.surfaceSoft}; }
`;

// Focusing a store/seller hides its siblings, so there has to be an obvious
// way back up — the rotated caret on the header alone is too quiet a cue.
const QtyBackButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0.15rem 0;
  margin: 0;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  font: inherit;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${tokens.muted};
  transition: color 0.18s ${tokens.ease};

  &:hover { color: ${tokens.brand}; }

  &:focus-visible {
    outline: 2px solid ${tokens.brand};
    outline-offset: 3px;
    border-radius: 6px;
  }

  > svg { transform: rotate(180deg); }
`;

const QtyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  font-size: 0.85rem;
  padding: 0.18rem 0;
  min-width: 0;
`;

const QtyLabel = styled.span`
  color: ${tokens.body};
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  /* Let the label flex/shrink so the value cell on the right never gets
     pushed past the row's right edge. Short tier names (Intern, Extern,
     Rx, Premium, Fair, Modest) display in full; only abnormally long
     names will ellipsize. */
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  /* Slight nudge so the chevron + label baseline aligns visually. */
  > svg { flex: 0 0 auto; }
`;

const QtyValue = styled.span`
  color: ${tokens.ink};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  ${p => p.$muted && `color: ${tokens.muted}; font-weight: 600;`}
`;

// Sub-section header inside the Frames column (Brands / Forms / Sex).
const QtySubLabel = styled.div`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.muted};
  margin: 0.45rem 0 0.1rem;

  &:first-child {
    margin-top: 0;
  }
`;

const QtyEmpty = styled.div`
  font-size: 0.78rem;
  color: ${tokens.muted};
  font-style: italic;
  padding: 0.2rem 0;
`;

// Right-side cell for each row: shows the count + a percentage chip.
// flex-shrink: 0 keeps the count + chip from collapsing when the label
// next to it is long.
const QtyValueCell = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  font-variant-numeric: tabular-nums;
  flex: 0 0 auto;
  white-space: nowrap;
`;

const QtyPct = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  color: ${tokens.muted};
  background: ${tokens.surfaceSoft};
  padding: 0.05rem 0.3rem;
  border-radius: 6px;
  min-width: 30px;
  text-align: right;
  flex: 0 0 auto;
`;

// Thin progress bar that lives at the bottom of a QtyRow so percentages
// also become visual. Width animates as the value updates.
const QtyBarTrack = styled.div`
  height: 3px;
  background: ${tokens.hairline};
  border-radius: 999px;
  overflow: hidden;
  margin-top: 0.18rem;
`;

const QtyBarFill = styled.div`
  height: 100%;
  width: ${p => Math.max(0, Math.min(100, p.$pct || 0))}%;
  background: ${p => p.$color || tokens.brand};
  border-radius: 999px;
  transition: width 0.45s cubic-bezier(.4, 0, .2, 1);
`;

const QtyRowWithBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0.18rem 0;
`;

// Nested sub-section inside the Lenses column — Simple Vision and Progressive
// each get their own card-in-card so it's easy to tell where one ends.
const QtyNested = styled.div`
  background: ${tokens.surface || 'transparent'};
  border: 1px solid ${tokens.hairline};
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  /* Stop drill rows / panels from bleeding outside the nested card on
     narrow widths. */
  min-width: 0;

  & + & { margin-top: 0.55rem; }
`;

const QtyNestedHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.muted};
  min-width: 0;

  /* Title slot — typed as a span wrapped around the section name (Simple
     Vision / Progressive). Truncates with ellipsis only as a safety net
     against unexpectedly long labels. */
  > .qty-nested-title {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > span:last-child {
    margin-left: auto;
    color: ${tokens.ink};
    font-size: 1rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0;
    text-transform: none;
    flex: 0 0 auto;
    white-space: nowrap;
    display: inline-flex;
    align-items: baseline;
  }
`;

// Color swatch used in the Frames > Color breakdown.
const ColorSwatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${p => p.$bg || tokens.muted};
  border: 1px solid ${tokens.hairline};
  flex: 0 0 auto;
`;

// Drill-down row: a QtyRowWithBar that's clickable. On hover the background
// gets a subtle tint and a small chevron rotates to indicate it's expandable.
// The negative margin / extended width is a smaller bleed than before so the
// hover background still extends slightly past the parent's content box
// without pushing the inner content past the right edge of narrow columns.
const QtyDrillRow = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0.22rem 0.35rem;
  margin: 0 -0.35rem;
  border-radius: 7px;
  width: calc(100% + 0.7rem);
  max-width: calc(100% + 0.7rem);
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0;
  color: inherit;
  font: inherit;
  min-width: 0;
  transition: background 0.15s ease;

  &:hover { background: ${tokens.surfaceSoft}; }
  &:focus-visible {
    outline: 2px solid ${tokens.brand};
    outline-offset: 1px;
  }

  &[data-open='true'] {
    background: ${tokens.brandSoftTranslucent || tokens.surfaceSoft};
  }
`;

const QtyDrillCaret = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${tokens.muted};
  margin-right: 0.2rem;
  transform: rotate(${p => (p.$open ? '90deg' : '0deg')});
  transition: transform 0.18s ease;
`;

// Indented panel that appears below an expanded drill row. Holds the deeper
// list (lens names, frame names, etc.). Uses the same grid 0fr → 1fr trick.
// Indentation is tight so deep nesting (qty.lenses.prog.premium → leaf list)
// still has room for content.
const QtyDrillPanel = styled.div`
  display: grid;
  grid-template-rows: ${p => (p.$open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.22s ease, margin-top 0.22s ease, opacity 0.18s ease;
  margin-top: ${p => (p.$open ? '0.3rem' : '0')};
  margin-left: 0.55rem;
  padding-left: 0.55rem;
  border-left: 2px solid ${tokens.brand33};
  opacity: ${p => (p.$open ? 1 : 0)};
  min-width: 0;

  > .qty-drill-inner {
    overflow: hidden;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
`;

// Compact row (no progress bar) for the deepest list level — frame names,
// lens names, turnover items. Just label + count + percentage chip.
const QtyLeafRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  font-size: 0.78rem;
  padding: 0.14rem 0;
  color: ${tokens.body};
  min-width: 0;
`;

const QtyLeafEmpty = styled.div`
  font-size: 0.75rem;
  font-style: italic;
  color: ${tokens.muted};
  padding: 0.25rem 0;
`;

// Type badge used on turnover items so you can tell a lens row from a frame
// row at a glance.
const ItemTypeBadge = styled.span`
  display: inline-block;
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.06rem 0.32rem;
  border-radius: 4px;
  margin-right: 0.4rem;
  background: ${p => {
    switch (p.$type) {
      case 'lens':    return '#e0e7ff';
      case 'frame':   return '#d1fae5';
      case 'coating': return '#fef3c7';
      case 'service': return '#fce7f3';
      default:        return tokens.surfaceSoft;
    }
  }};
  color: ${p => {
    switch (p.$type) {
      case 'lens':    return '#4338ca';
      case 'frame':   return '#047857';
      case 'coating': return '#b45309';
      case 'service': return '#be185d';
      default:        return tokens.muted;
    }
  }};
`;

// Currency formatter for turnover rows.
const fmtDzd = (v) => `${Math.round(Number(v) || 0).toLocaleString()} DZD`;

const DangerZone = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 2px solid #fee2e2;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-top: 1rem;
  }
`;

const DangerZoneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const DangerZoneTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #dc2626;
  margin: 0;
`;

const DangerZoneDescription = styled.p`
  color: #64748b;
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
`;

const DeleteButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    font-size: 0.9375rem;
    padding: 0.875rem 1.25rem;
  }
  
  &:hover {
    background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 1rem;
  animation: fadeIn 0.22s ${tokens.ease};

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 18px;
  width: 100%;
  max-width: ${props => props.large ? '900px' : '500px'};
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 0;
    max-height: 100vh;
    margin: 0;
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  background: ${props => props.variant === 'info'
    ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
    : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.variant === 'info' ? '#bfdbfe' : '#fecaca'};
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.variant === 'info' ? '#1e40af' : '#991b1b'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalMessage = styled.p`
  color: #1e293b;
  line-height: 1.6;
  margin: 0 0 1rem 0;
`;

const WarningBox = styled.div`
  background: #fef3c7;
  border: 2px solid #fbbf24;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
`;

const WarningText = styled.p`
  color: #92400e;
  margin: 0;
  font-weight: 500;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  margin-top: 1rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #dc2626;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    padding: 1rem;
    
    button {
      width: 100%;
    }
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const ConfirmDeleteButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SpinningIcon = styled(RefreshCw)`
  animation: ${spin} 1s linear infinite;
`;

// Orders View Styles
const OrdersContainer = styled.div`
  background: ${tokens.surface};
  border-radius: 18px;
  box-shadow: ${tokens.shadowSm};
  border: 1px solid ${tokens.hairline};
  overflow: hidden;
  transition: box-shadow 0.24s ${tokens.ease};

  &:hover {
    box-shadow: ${tokens.shadow};
  }
`;

const OrdersHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${tokens.inputBg};
  border: 1px solid ${tokens.hairline};
  border-radius: 11px;
  padding: 0.6rem 1rem;
  flex: 1;
  min-width: 250px;
  transition: border-color 0.18s ${tokens.ease}, box-shadow 0.18s ${tokens.ease}, background 0.18s ${tokens.ease};

  svg { color: ${tokens.muted}; flex-shrink: 0; }

  &:focus-within {
    border-color: ${tokens.brand};
    background: ${tokens.surface};
    box-shadow: 0 0 0 3px ${tokens.brand33};
  }

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 0.9375rem;
    color: ${tokens.ink};

    @media (max-width: 768px) {
      font-size: 0.875rem;
    }

    &::placeholder {
      color: ${tokens.muted};
      font-size: 0.875rem;

      @media (max-width: 768px) {
        font-size: 0.8125rem;
      }
    }
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
    
    button {
      flex: 1;
      min-width: calc(33.333% - 0.5rem);
      font-size: 0.8125rem;
      padding: 0.5rem 0.75rem;
    }
  }
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.active ? 'transparent' : tokens.hairline};
  border-radius: 9px;
  background: ${props => props.active
    ? `linear-gradient(135deg, ${tokens.brand} 0%, ${tokens.brandDark} 100%)`
    : 'white'};
  color: ${props => props.active ? 'white' : tokens.muted};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ${tokens.ease};
  box-shadow: ${props => props.active ? '0 4px 10px -2px rgba(99, 102, 241, 0.35)' : 'none'};

  &:hover {
    border-color: ${props => props.active ? 'transparent' : '#cbd5e1'};
    background: ${props => props.active
      ? `linear-gradient(135deg, ${tokens.brandDark} 0%, #4338ca 100%)`
      : '#f1f5f9'};
    color: ${props => props.active ? 'white' : tokens.ink};
    transform: translateY(-1px);
  }

  &:active { transform: translateY(0); }
`;

const OrdersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeader = styled.thead`
  background: ${tokens.tableHeadBg};
  border-bottom: 1px solid ${tokens.hairline};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 1rem 1.5rem;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${tokens.muted};
  text-transform: uppercase;
  letter-spacing: 0.08em;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${tokens.hairlineSoft};
  transition: background 0.18s ${tokens.ease}, box-shadow 0.18s ${tokens.ease};
  cursor: pointer;

  &:hover {
    background: linear-gradient(90deg, ${tokens.brandSoftTranslucent} 0%, transparent 100%);
    box-shadow: inset 3px 0 0 ${tokens.brand};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const MetricsCard = styled.div`
  background:
    radial-gradient(circle at top left, ${tokens.brandSoftTranslucent} 0, transparent 34%),
    ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: 20px;
  padding: 1.15rem;
  margin: 1rem 0;
  box-shadow: ${tokens.shadowMd};
  overflow: hidden;
`;

const MetricsTitle = styled.div`
  font-weight: 800;
  color: ${tokens.ink};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 1.08rem;
  }
`;

const MetricsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
`;

const MetricsTh = styled.th`
  text-align: left;
  font-size: 11px;
  color: ${tokens.muted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.85rem 0.7rem;
  border-bottom: 1px solid ${tokens.hairline};
  background: ${tokens.surface2};
`;

const MetricsTd = styled.td`
  padding: 0.9rem 0.7rem;
  border-bottom: 1px solid ${tokens.hairline};
  font-size: 14px;
  color: ${tokens.ink};
  vertical-align: top;
`;

const RatePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.42rem 0.72rem;
  border-radius: 999px;
  font-weight: 800;
  font-size: 12px;
  background: ${p => p.variant === 'good' ? '#dcfce7' : p.variant === 'warn' ? '#fef3c7' : '#fee2e2'};
  color: ${p => p.variant === 'good' ? '#166534' : p.variant === 'warn' ? '#92400e' : '#991b1b'};
  border: 1px solid ${p => p.variant === 'good' ? '#bbf7d0' : p.variant === 'warn' ? '#fde68a' : '#fecaca'};
  cursor: pointer;
  transition: transform 0.16s ${tokens.ease}, box-shadow 0.16s ${tokens.ease};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${tokens.shadowSm};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${tokens.brand33};
  }
`;

const EmployeeStatsToolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 0 1rem;
  border-bottom: 1px solid ${tokens.hairline};
  margin-bottom: 0.85rem;
`;

const RangeButton = styled.button`
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  border: 1px solid ${p => p.$active ? tokens.brand55 : tokens.hairline};
  background: ${p => p.$active ? tokens.brandSoft : tokens.surface};
  color: ${p => p.$active ? tokens.brandDark : tokens.muted};
  font-weight: 800;
  cursor: pointer;
  transition: all 0.16s ${tokens.ease};

  &:hover {
    border-color: ${tokens.brand55};
    color: ${tokens.brandDark};
  }
`;

const EmployeeStatsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.8rem;
  margin-bottom: 1rem;
`;

const EmployeeStatsMiniCard = styled.div`
  border: 1px solid ${tokens.hairline};
  background: ${tokens.surface};
  border-radius: 16px;
  padding: 0.85rem;

  span {
    display: block;
    color: ${tokens.muted};
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  strong {
    display: block;
    margin-top: 0.25rem;
    color: ${tokens.ink};
    font-size: 1.2rem;
  }
`;

const RawDataOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 2400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const RawDataCard = styled.div`
  width: min(520px, 100%);
  background: ${tokens.surface};
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.3);
`;

const RawDataHeader = styled.div`
  padding: 1.2rem 1.35rem;
  background: linear-gradient(135deg, ${tokens.brandDark} 0%, #312e81 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  gap: 1rem;

  h3 {
    margin: 0;
    font-size: 1.1rem;
  }

  p {
    margin: 0.25rem 0 0;
    color: #cbd5e1;
    font-size: 0.86rem;
  }
`;

const RawDataBody = styled.div`
  padding: 1.25rem 1.35rem;
  display: grid;
  gap: 1rem;
`;

const RawDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const RawDataBox = styled.div`
  border: 1px solid ${tokens.hairline};
  background: ${tokens.surface2};
  border-radius: 14px;
  padding: 0.85rem;

  span {
    display: block;
    color: ${tokens.muted};
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  strong {
    display: block;
    margin-top: 0.25rem;
    color: ${tokens.ink};
    font-size: 1.35rem;
  }
`;

const RawFormula = styled.div`
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 14px;
  padding: 0.85rem;
  line-height: 1.5;
  font-size: 0.9rem;
`;

const StoreStatsButton = styled.button`
  border: 0;
  background: transparent;
  color: ${tokens.ink};
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;

  .chevron {
    width: 24px;
    height: 24px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${tokens.brandDark};
    background: ${tokens.brandSoft};
    transform: rotate(${p => (p.$open ? '90deg' : '0deg')});
    transition: transform 0.18s ${tokens.ease};
    flex-shrink: 0;
  }
`;

const StoreStatsRow = styled.tr`
  background: ${tokens.surface2};

  td {
    border-bottom-color: ${tokens.hairline};
  }
`;

const EmployeeStatsChildRow = styled.tr`
  background: ${tokens.surface};

  td:first-child {
    padding-left: 2.35rem;
  }
`;

const SalaryCalculatorPanel = styled.div`
  border: 1px solid ${tokens.hairline};
  background: ${tokens.surface};
  border-radius: 18px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: ${tokens.shadowSm};
`;

const SalaryCalculatorHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.9rem;

  h3 {
    margin: 0;
    color: ${tokens.ink};
    font-size: 1rem;
  }

  p {
    margin: 0.25rem 0 0;
    color: ${tokens.muted};
    font-size: 0.84rem;
  }
`;

const SalaryInputGroup = styled.label`
  display: grid;
  gap: 0.3rem;
  color: ${tokens.muted};
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  input {
    width: 170px;
    border: 1px solid ${tokens.hairline};
    border-radius: 12px;
    padding: 0.65rem 0.75rem;
    color: ${tokens.ink};
    font-size: 0.95rem;
    font-weight: 800;
    background: ${tokens.surface2};
  }
`;

const SalaryMatrixWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${tokens.hairline};
  border-radius: 14px;
`;

const SalaryMatrix = styled.table`
  width: 100%;
  min-width: 820px;
  border-collapse: collapse;
  background: ${tokens.surface};

  th,
  td {
    border-bottom: 1px solid ${tokens.hairline};
    border-right: 1px solid ${tokens.hairline};
    padding: 0.55rem;
    text-align: center;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  th {
    background: ${tokens.surface2};
    color: ${tokens.muted};
    font-size: 0.76rem;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  td:first-child,
  th:first-child {
    text-align: left;
    position: sticky;
    left: 0;
    z-index: 1;
    background: ${tokens.surface2};
    min-width: 120px;
  }

  input {
    width: 82px;
    border: 1px solid transparent;
    border-radius: 10px;
    padding: 0.5rem;
    text-align: center;
    color: ${tokens.ink};
    font-weight: 800;
    background: ${tokens.surface2};
  }

  input:focus {
    outline: none;
    border-color: ${tokens.brand55};
    box-shadow: 0 0 0 3px ${tokens.brand33};
    background: ${tokens.surface};
  }
`;

const TableCell = styled.td`
  padding: 1rem 1.5rem;
  font-size: 0.9375rem;
  color: #1e293b;
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
  }
`;

// Mobile card view for orders
const OrdersCardList = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    padding: 1rem;
  }
`;

const OrderCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #cbd5e1;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const OrderCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
`;

const OrderCardTitle = styled.div`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
`;

const OrderCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OrderCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
`;

const OrderCardLabel = styled.span`
  color: #64748b;
  font-weight: 500;
`;

const OrderCardValue = styled.span`
  color: #1e293b;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
`;

// Status badge — colour-coded against the three admin buckets:
//   • Green  → Completed (delivered / fully completed)
//   • Amber  → Confirmed in-flight (confirmed, in_production, manufacturing,
//              quality_check, ready_for_pickup, out_for_delivery)
//   • Red    → Unsuccessful (pending / No Order Created / Prescription Only
//              / Order Not Completed / cancelled)
const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => {
    if (props.completed) return '#d1fae5';
    // Confirmed in-flight pipeline → amber.
    if (
      props.status === 'confirmed' ||
      props.status === 'in_production' ||
      props.status === 'manufacturing' ||
      props.status === 'quality_check' ||
      props.status === 'ready_for_pickup' ||
      props.status === 'out_for_delivery'
    ) return '#fef3c7';
    // Unsuccessful (stalled / prescription-only / cancelled) → red.
    if (
      props.status === 'No Order Created' ||
      props.status === 'Order Not Completed' ||
      props.status === 'Prescription Only' ||
      props.status === 'pending' ||
      props.status === 'cancelled'
    ) return '#fee2e2';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.completed) return '#065f46';
    if (
      props.status === 'confirmed' ||
      props.status === 'in_production' ||
      props.status === 'manufacturing' ||
      props.status === 'quality_check' ||
      props.status === 'ready_for_pickup' ||
      props.status === 'out_for_delivery'
    ) return '#92400e';
    if (
      props.status === 'No Order Created' ||
      props.status === 'Order Not Completed' ||
      props.status === 'Prescription Only' ||
      props.status === 'pending' ||
      props.status === 'cancelled'
    ) return '#991b1b';
    return '#374151';
  }};
`;

const EmptyState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  color: ${tokens.muted};

  svg {
    color: #c7d2fe;
  }
`;

const LoadingState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  color: ${tokens.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  font-weight: 500;
`;

const MobileMenuButton = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 1001;
  background: white;
  border: 1px solid ${tokens.hairline};
  border-radius: 10px;
  padding: 0.7rem;
  cursor: pointer;
  box-shadow: ${tokens.shadowMd};
  transition: transform 0.15s ${tokens.ease};

  &:hover { transform: scale(1.05); }
  &:active { transform: scale(0.97); }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailSectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e2e8f0;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DetailValue = styled.span`
  font-size: 0.9375rem;
  color: #1e293b;
  font-weight: ${props => props.bold ? '600' : '400'};
`;

const EyeCorrectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const EyeCorrectionBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
`;

const EyeTitle = styled.h5`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CorrectionRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CorrectionLabel = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
`;

const CorrectionValue = styled.span`
  font-size: 0.875rem;
  color: #1e293b;
  font-weight: 600;
`;

// Add Employee Form Component
const SuccessMessage = styled.div`
  padding: 1rem;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 2px solid #10b981;
  border-radius: 12px;
  color: #065f46;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9375rem;
  font-weight: 500;
  margin-bottom: 1rem;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ErrorMessageStyled = styled.div`
  padding: 1rem;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 2px solid #dc2626;
  border-radius: 12px;
  color: #991b1b;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9375rem;
  font-weight: 500;
  margin-bottom: 1rem;
  animation: slideDown 0.3s ease-out;
`;

const InfoMessage = styled.div`
  padding: 1rem;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border: 2px solid #3b82f6;
  border-radius: 12px;
  color: #1e40af;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  
  strong {
    font-weight: 600;
  }
`;

// ──────────────────────────────────────────────────────────────────────────
// Overview "insights" panels — premium SaaS dashboard sections that sit below
// the hero KPI strip: Today's Activity, Recent Orders, Store Performance, and
// a compact Low-Stock / Sell-By alerts card. All read already-loaded data.
// ──────────────────────────────────────────────────────────────────────────

// Today's Activity — a 4-up strip of compact metric tiles.
const ActivityStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px)  { grid-template-columns: 1fr; }
`;

const ActivityTile = styled.div`
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: 16px;
  padding: 1.1rem 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.9rem;
  box-shadow: ${tokens.shadowSm};
  transition: transform 0.2s ${tokens.ease}, box-shadow 0.2s ${tokens.ease}, border-color 0.2s ${tokens.ease};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${tokens.shadow};
    border-color: #dbe2ee;
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${p => p.$grad || `linear-gradient(135deg, ${tokens.brand} 0%, #a855f7 100%)`};
  box-shadow: inset 0 1px 1px rgba(255,255,255,0.4), 0 3px 8px -2px rgba(15,23,42,0.18);
`;

const ActivityMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ActivityLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: ${tokens.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ActivityValue = styled.span`
  font-size: 1.35rem;
  font-weight: 700;
  color: ${tokens.ink};
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Two-column responsive grid for the larger insight panels.
const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  align-items: start;

  @media (max-width: 1200px) { grid-template-columns: 1fr; }
`;

const Panel = styled.section`
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: 18px;
  box-shadow: ${tokens.shadowSm};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1.1rem 1.35rem;
  border-bottom: 1px solid ${tokens.hairlineSoft};
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: ${tokens.ink};
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 0.55rem;

  svg { color: ${tokens.brand}; }
`;

const PanelAction = styled.button`
  border: none;
  background: transparent;
  color: ${tokens.brand};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3rem 0.5rem;
  border-radius: 8px;
  transition: background 0.15s ${tokens.ease};

  &:hover { background: ${tokens.brandSoft}; }
`;

const PanelBody = styled.div`
  padding: 0.4rem 0.5rem;
  flex: 1;
`;

const PanelEmpty = styled.div`
  padding: 2rem 1.25rem;
  text-align: center;
  color: ${tokens.muted};
  font-size: 0.85rem;
`;

// Generic clickable row used by Recent Orders.
const InsightRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.7rem 0.85rem;
  border-radius: 12px;
  transition: background 0.15s ${tokens.ease};
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};

  &:hover { background: ${p => (p.$clickable ? tokens.hover : 'transparent')}; }
`;

const RowAvatar = styled.div`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${tokens.brand};
  background: ${tokens.brandSoft};
`;

const RowMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const RowTitle = styled.span`
  font-size: 0.86rem;
  font-weight: 600;
  color: ${tokens.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowSub = styled.span`
  font-size: 0.76rem;
  color: ${tokens.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  flex-shrink: 0;
`;

const RowAmount = styled.span`
  font-size: 0.86rem;
  font-weight: 700;
  color: ${tokens.ink};
  font-variant-numeric: tabular-nums;
`;

const MiniStatus = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  color: ${p => p.$color || tokens.muted};
  background: ${p => p.$bg || tokens.hover};
`;

// Store performance — labelled progress bars.
const PerfRow = styled.div`
  padding: 0.6rem 0.85rem;
`;

const PerfTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.4rem;
`;

const PerfName = styled.span`
  font-size: 0.84rem;
  font-weight: 600;
  color: ${tokens.body};
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PerfVal = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${tokens.ink};
  font-variant-numeric: tabular-nums;
`;

const PerfTrack = styled.div`
  height: 7px;
  border-radius: 999px;
  background: ${tokens.hover};
  overflow: hidden;
`;

const PerfFill = styled.div`
  height: 100%;
  border-radius: 999px;
  width: ${p => Math.max(2, Math.min(100, p.$pct || 0))}%;
  background: ${p => p.$grad || `linear-gradient(90deg, ${tokens.brand} 0%, #a855f7 100%)`};
  transition: width 0.6s ${tokens.ease};
`;

// Compact alert row for the slim Low-Stock / Sell-By panel.
const AlertRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.85rem;
  border-radius: 12px;
  transition: background 0.15s ${tokens.ease};

  &:hover { background: ${tokens.hover}; }
`;

const AlertDot = styled.span`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b45309;
  background: #fef3c7;
`;

// Roles that live under an AdministrationLocation rather than a Store/Lab.
// Keep in sync with server/models/User.js + server/routes/users.js role
// families. The labels here are what appears in the admin dashboard's
// "Add Employee" dropdown — plain English, no role code.
const ADMIN_LOCATION_ROLES = ['assistant', 'customer_support', 'accountant', 'other'];
// Subset of STORE_ROLES_FRONT that may only be assigned to the central
// warehouse store. Couriers are dispatched from the central warehouse.
const CENTRAL_STORE_ROLES = ['inventory_manager', 'operations_manager', 'courier'];
const STORE_ROLES_FRONT = ['store_manager', 'sales_person', ...CENTRAL_STORE_ROLES];
const LAB_ROLES_FRONT = ['lab_manager', 'technician'];

// Pretty labels for the role enum used in tables, badges, and modals.
// Falls back to a Title-Cased version of the raw value if we add more
// roles later and forget to add a label here.
const ROLE_LABELS = {
  admin: 'Admin',
  store_manager: 'Store Manager',
  lab_manager: 'Lab Manager',
  sales_person: 'Sales Person',
  technician: 'Technician',
  inventory_manager: 'Inventory & Operations Manager',
  operations_manager: 'Inventory & Operations Manager',
  assistant: 'Assistant',
  customer_support: 'Customer Support',
  accountant: 'Accountant',
  courier: 'Courier',
  other: 'Other (Admin)',
};
const humanizeRole = (role) => {
  if (!role) return 'N/A';
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  return String(role)
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
};

// Quick lookup for the company's central warehouse — used by the form to
// pre-select the store dropdown when an inventory / operations manager is
// being added.
const findCentralWarehouse = (stores) => stores.find((s) => s.isCentralWarehouse);

const CORRECTION_GROUP_ROWS = [
  { sub: '12', minus: '-12.00 to -10.25', plus: '+10.25 to +12.00', color: '#d9e7bd' },
  { sub: '10', minus: '-10.00 to -8.25', plus: '+8.25 to +10.00', color: '#e4b0b0' },
  { sub: '8',  minus: '-8.00 to -6.25',  plus: '+6.25 to +8.00',  color: '#4b5563' },
  { sub: '6',  minus: '-6.00 to -4.25',  plus: '+4.25 to +6.00',  color: '#e7b4b4' },
  { sub: '4',  minus: '-4.00 to -2.25',  plus: '+2.25 to +4.00',  color: '#a9d9e2' },
  { sub: '2',  minus: '-2.00 to -0.25',  plus: '0.00 to +2.00',   color: '#f6d2ae' },
];

const CORRECTION_GROUP_COLUMNS = [
  { group: 'G0', title: 'G0', subtitle: 'sphérique', cylRange: 'CYL = 0.00', tint: '#f8fafc' },
  { group: 'G2', title: 'G2', subtitle: 'torique', cylRange: '0.25 to 2.00 CYL', tint: '#eef6d8' },
  { group: 'G4', title: 'G4', subtitle: 'torique fort', cylRange: '2.25 to 4.00 CYL', tint: '#f8c18d' },
];
const CORRECTION_SUFFIXES = ['2', '4', '6', '8', '10', '12'];
const displayCorrectionGroup = (group) => String(group || '').replace(/\/12$/, '/12+');
const displayCorrectionGroupPair = (groups = []) => (
  Array.isArray(groups) && groups.length
    ? groups.map(displayCorrectionGroup).join(' & ')
    : ''
);
const RECOMMENDATION_SHEET_SIGNS = [
  { id: 'plus', label: '(+)', color: '#059669' },
  { id: 'minus', label: '(−)', color: '#dc2626' },
];
const RECOMMENDATION_GROUP_ROWS = CORRECTION_SUFFIXES.map(suffix => ({
  suffix,
  groups: [`G0/${suffix}`, `G2/${suffix}`, `G4/${suffix}`],
  label: [`G0/${suffix}`, `G2/${suffix}`, `G4/${suffix}`].map(displayCorrectionGroup).join(' & '),
}));
const normalizeLensBrandName = (value = '') => String(value).replace(/digiife/gi, 'Digilife');
// Age at which the recommendation matrix switches regime: brackets starting
// below it hold single-vision lenses, brackets from it up hold progressives.
// Same threshold the sales analytics use for `progressiveSales`.
const PRESBYOPIA_AGE = 43;

// Age zones the Up/Down alternatives are configured against. Served by
// GET /api/lens-alternatives; this is the fallback if an older server omits
// them. Mirrors ALTERNATIVE_ZONES in server/routes/supabaseCommerce.js.
const FALLBACK_ALT_ZONES = [
  { id: 'young',   label: '< 43',   minAge: 0,  maxAge: 42 },
  { id: 'age4350', label: '43-50',  minAge: 43, maxAge: 50 },
  { id: 'age5055', label: '50-55',  minAge: 50, maxAge: 55 },
  { id: 'age55up', label: '55 & +', minAge: 55, maxAge: 999 },
];

// Collapsible card used on the Recommendations tab so each big section
// (matrix / alternatives / catalog) can be folded away. `order` lets the
// visual order differ from source order without moving the JSX.
const CollapsibleCard = ({ title, icon, iconBg = '#eef2ff', iconColor = '#4338ca', chips = [], info, actions, isOpen, onToggle, order = 0, children }) => (
  <div style={{
    background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px',
    overflow: 'hidden', marginBottom: '1.25rem', order,
  }}>
    <div
      onClick={onToggle}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '0.75rem', padding: '1rem 1.25rem', cursor: 'pointer',
        background: isOpen ? '#f8fafc' : 'white',
        borderBottom: isOpen ? '1px solid #e2e8f0' : 'none',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', minWidth: 0 }}>
        <ChevronRight
          size={18}
          style={{ color: '#94a3b8', flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
        />
        {icon ? (
          <span style={{ width: 32, height: 32, borderRadius: 9, background: iconBg, color: iconColor, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
        ) : null}
        <h3 style={{ margin: 0, fontSize: '1.02rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>{title}</h3>
        {chips.map((c, i) => (
          <span key={i} style={{
            fontSize: '0.72rem', fontWeight: 600, borderRadius: 9999, padding: '2px 9px', whiteSpace: 'nowrap',
            background: c.primary ? '#eef2ff' : '#f8fafc', color: c.primary ? '#4338ca' : '#64748b',
            border: c.primary ? 'none' : '1px solid #e2e8f0',
          }}>{c.label}</span>
        ))}
        {info ? (
          <span title={info} style={{ display: 'inline-flex', color: '#94a3b8', cursor: 'help', flexShrink: 0 }}><Info size={15} /></span>
        ) : null}
      </div>
      {actions ? (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
          {actions}
        </div>
      ) : null}
    </div>
    {isOpen ? <div style={{ padding: '1.25rem' }}>{children}</div> : null}
  </div>
);

const getCorrectionRange = (row, sphereSign) => {
  if (sphereSign === 'plus') return row.plus;
  if (sphereSign === 'none') return row.sub === '2' ? 'SPH = 0' : '';
  return row.minus;
};

// Self-contained catalog typeahead used to add a lens to an Alternatives cell.
// Manages its own open/query state; calls onPick with the chosen catalog id.
const AltLensPicker = ({ catalogOptions, catalogRealNames, onPick }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rect, setRect] = useState(null);
  const [idx, setIdx] = useState(0);
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  const matches = catalogOptions.filter(c => {
    if (!tokens.length) return true;
    const hay = `${c.indexValue || ''} ${c.commercialName || ''} ${c.name || ''} ${catalogRealNames(c).join(' ')}`.toLowerCase();
    return tokens.every(t => hay.includes(t));
  }).slice(0, 25);
  const activeIdx = Math.min(idx, Math.max(0, matches.length - 1));
  const pick = (c) => { if (!c) return; onPick(c.id || c._id || c.name); setOpen(false); setQuery(''); setIdx(0); };
  if (!open) {
    return (
      <button onClick={(e) => { setRect(e.currentTarget.getBoundingClientRect()); setOpen(true); setQuery(''); setIdx(0); }}
        style={{ border: '1px dashed #cbd5e1', background: 'transparent', color: '#6366f1', cursor: 'pointer', borderRadius: 9999, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <Plus size={11} /> add
      </button>
    );
  }
  const ddWidth = 280;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const ddLeft = rect ? Math.max(8, Math.min(rect.left, vw - ddWidth - 8)) : 8;
  const ddTop = rect ? rect.bottom + 2 : 0;
  return (
    <span style={{ position: 'relative' }}>
      <input
        autoFocus
        type="text"
        value={query}
        placeholder={t('admin.indexName')}
        onFocus={(e) => setRect(e.currentTarget.getBoundingClientRect())}
        onChange={(e) => { setQuery(e.target.value); setRect(e.currentTarget.getBoundingClientRect()); setIdx(0); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, matches.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); pick(matches[activeIdx]); }
          else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ width: 130, border: '1px dashed #cbd5e1', borderRadius: 9999, padding: '2px 8px', fontSize: '0.72rem', outline: 'none' }}
      />
      <div style={{ position: 'fixed', top: ddTop, left: ddLeft, width: ddWidth, border: '1px solid #cbd5e1', borderRadius: 8, background: 'white', maxHeight: 280, overflowY: 'auto', boxShadow: '0 10px 28px rgba(15,23,42,0.22)', zIndex: 9999 }}>
        {matches.length ? matches.map((c, i) => {
          const reals = catalogRealNames(c).join(' / ');
          const hi = i === activeIdx;
          return (
            <div key={c.id || c.name} ref={el => { if (hi && el) el.scrollIntoView({ block: 'nearest' }); }}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onMouseEnter={() => setIdx(i)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', borderBottom: '1px solid #f1f5f9', background: hi ? '#e0e7ff' : 'white' }}>
              <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: '#3730a3', minWidth: 32 }}>{c.indexValue || '—'}</span>
              <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                <div style={{ fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>{c.commercialName || reals}</div>
                {reals ? <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{reals}</div> : null}
              </div>
              {c.group ? <span style={{ fontSize: '0.64rem', fontWeight: 800, color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 4, padding: '0 0.3rem' }}>{c.group}</span> : null}
            </div>
          );
        }) : <div style={{ padding: '0.5rem 0.6rem', fontSize: '0.76rem', color: '#94a3b8' }}>{t('admin.noCatalogMatch')}</div>}
      </div>
    </span>
  );
};

const CorrectionGroupMap = ({ sphereSign, selectedGroups, onToggle, readOnly = false }) => {
  const { t } = useTranslation();
  const visibleRows = sphereSign === 'none'
    ? CORRECTION_GROUP_ROWS.filter(row => row.sub === '2')
    : CORRECTION_GROUP_ROWS;

  return (
    <div style={{
      border: '1px solid #dbe3ef',
      borderRadius: 14,
      overflow: 'hidden',
      background: '#ffffff',
      boxShadow: '0 10px 30px rgba(15,23,42,0.06)'
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '120px repeat(3, minmax(120px, 1fr))',
        background: '#f8fafc',
        borderBottom: '1px solid #dbe3ef'
      }}>
        <div style={{ padding: '0.7rem 0.8rem', fontSize: 12, fontWeight: 800, color: '#64748b' }}>
          {t('admin.sphRange')}
        </div>
        {CORRECTION_GROUP_COLUMNS.map(col => (
          <div key={col.group} style={{
            padding: '0.65rem 0.8rem',
            borderLeft: '1px solid #dbe3ef',
            fontSize: 12,
            color: '#334155'
          }}>
            <div style={{ fontWeight: 900 }}>{col.title}</div>
            <div style={{ color: '#64748b', fontSize: 11 }}>{col.cylRange}</div>
            <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 2 }}>{col.subtitle}</div>
          </div>
        ))}
      </div>

      {visibleRows.map(row => (
        <div key={row.sub} style={{
          display: 'grid',
          gridTemplateColumns: '120px repeat(3, minmax(120px, 1fr))',
          minHeight: sphereSign === 'none' ? 88 : 74,
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            padding: '0.7rem 0.8rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            background: sphereSign === 'plus' ? '#bbf7d0' : sphereSign === 'minus' ? '#fecaca' : '#f1f5f9',
            color: '#0f172a',
            fontSize: 12,
            fontWeight: 800
          }}>
            <span>{t('admin.g0')}{row.sub}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginTop: 4 }}>
              {getCorrectionRange(row, sphereSign)}
            </span>
          </div>

          {CORRECTION_GROUP_COLUMNS.map((col, colIndex) => {
            const label = `${col.group}/${row.sub}`;
            const checked = selectedGroups.includes(label);
            return (
              <button
                key={label}
                type="button"
                onClick={() => !readOnly && onToggle(label)}
                aria-pressed={checked}
                disabled={readOnly}
                title={`${label} · ${col.cylRange} · SPH ${getCorrectionRange(row, sphereSign)}`}
                style={{
                  border: 'none',
                  borderLeft: '1px solid rgba(15,23,42,0.14)',
                  background: checked
                    ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                    : colIndex === 0
                      ? col.tint
                      : row.color,
                  color: checked ? '#ffffff' : '#111827',
                  cursor: readOnly ? 'default' : 'pointer',
                  padding: '0.75rem',
                  textAlign: 'center',
                  fontWeight: 900,
                  fontSize: 14,
                  boxShadow: checked ? 'inset 0 0 0 3px rgba(255,255,255,0.55)' : 'none',
                  transition: 'transform 140ms ease, box-shadow 140ms ease, background 140ms ease'
                }}
              >
                <div>{label}</div>
                <div style={{
                  fontSize: 10,
                  fontWeight: 700,
                  opacity: checked ? 0.9 : 0.65,
                  marginTop: 4,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em'
                }}>
                  {checked ? 'selected' : col.subtitle}
                  {!checked && (
                    <span style={{ display: 'block', marginTop: 2, fontSize: 9 }}>
                      {col.cylRange}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const FullCorrectionGroupSheet = () => { const { t } = useTranslation(); return  (
  <div style={{
    display: 'grid',
    gap: '1.25rem',
  }}>
    <div style={{
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 18,
      padding: '1rem',
      boxShadow: '0 18px 40px rgba(15,23,42,0.06)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>{t('admin.minusCorrections')}</h3>
          <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.86rem' }}>
            {t('admin.myopiaSideNegativeSphereRanges')}
          </p>
        </div>
        <div style={{ color: '#dc2626', fontWeight: 900 }}>{t('admin.sph')}</div>
      </div>
      <CorrectionGroupMap sphereSign="minus" selectedGroups={[]} onToggle={() => {}} readOnly />
    </div>

    <div style={{
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 18,
      padding: '1rem',
      boxShadow: '0 18px 40px rgba(15,23,42,0.06)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>{t('admin.plusCorrections')}</h3>
          <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.86rem' }}>
            {t('admin.hyperopiaSidePositiveSphereRanges')}
          </p>
        </div>
        <div style={{ color: '#059669', fontWeight: 900 }}>{t('admin.sph2')}</div>
      </div>
      <CorrectionGroupMap sphereSign="plus" selectedGroups={[]} onToggle={() => {}} readOnly />
    </div>

    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '0.75rem'
    }}>
      {[
        { title: 'G0', body: 'Cylinder equals 0. Spherical lenses.' },
        { title: 'G2', body: 'Cylinder above 0 and up to 2.00. Toric lenses.' },
        { title: 'G4', body: 'Cylinder above 2.00 and up to 4.00. Strong toric lenses.' },
        { title: '/2 /4 /6 /8 /10 /12+', body: 'The suffix is selected from absolute sphere power range.' },
      ].map(card => (
        <div key={card.title} style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 14,
          padding: '0.9rem'
        }}>
          <div style={{ fontWeight: 900, color: '#0f172a', marginBottom: '0.35rem' }}>{card.title}</div>
          <div style={{ color: '#64748b', fontSize: '0.85rem', lineHeight: 1.5 }}>{card.body}</div>
        </div>
      ))}
    </div>
  </div>
); };

const AddEmployeeForm = ({
  stores,
  labs,
  adminLocations = [],
  onClose,
  onSuccess,
  // Optional: open the form with a specific role pre-selected (e.g. the
  // Central Store tab opens this with role='inventory_manager').
  defaultRole = null,
  // Optional: prevent the user from changing the role away from
  // defaultRole. Useful from contextual entry points like "Add Inventory
  // Manager" buttons.
  lockRole = false,
}) => {
  const { t } = useTranslation();
  const initialRole = defaultRole || 'sales_person';
  const central = findCentralWarehouse(stores);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: initialRole,
    // Pre-select the central warehouse for inventory/operations managers
    // so the admin doesn't have to dig for it. The dropdown will also be
    // restricted to that store below.
    storeId: CENTRAL_STORE_ROLES.includes(initialRole) && central
      ? (central._id || central.id)
      : '',
    labId: '',
    adminLocationId: '',
    phone: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [successEmployeeId, setSuccessEmployeeId] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      // Add profile image if selected
      if (profileImage) {
        formDataToSend.append('profileImage', profileImage);
      }
      // Don't send employeeId - it will be auto-generated

      const response = await axios.post('/api/users', formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const employeeId = response.data.user?.employeeId || 'Generated';
      setSuccess(`Employee added successfully!`);
      setSuccessEmployeeId(employeeId);
      setError('');

      // Reset form back to the same role the modal opened with, so a quick
      // "add another inventory manager" workflow doesn't slide back to
      // sales_person between submissions.
      setFormData({
        firstName: '',
        lastName: '',
        role: initialRole,
        storeId: CENTRAL_STORE_ROLES.includes(initialRole) && central
          ? (central._id || central.id)
          : '',
        labId: '',
        adminLocationId: '',
        phone: ''
      });
      setProfileImage(null);
      setImagePreview(null);

      // Close modal and refresh after 2 seconds
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      console.error('Error adding employee:', error);
      // A 413 is returned by nginx, not the API, so it has an HTML body with
      // no `message` field — without this it surfaced as the generic failure
      // and gave no hint that the photo was the problem.
      const tooLarge = error.response?.status === 413;
      setError(tooLarge
        ? 'The profile photo is too large to upload. Please choose a smaller image.'
        : (error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Error adding employee. Please try again.'));
      setSuccess('');
    } finally {
      setSubmitting(false);
    }
  };

  const eInputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', color: '#1e293b', background: '#fafafa', outline: 'none', fontFamily: 'inherit' };
  const eLabelStyle = { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '0.28rem', display: 'block' };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.85rem', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.85rem', fontWeight: 500 }}>
          <CheckCircle size={16} style={{ flexShrink: 0 }} />
          <div>{success}{successEmployeeId && <span style={{ marginLeft: '0.4rem', opacity: 0.8 }}>{t('admin.u2014Id')} <strong>{successEmployeeId}</strong></span>}</div>
        </div>
      )}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', fontWeight: 500 }}>
          <XCircle size={15} style={{ flexShrink: 0 }} /> {error}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.7rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '7px', color: '#0369a1', fontSize: '0.78rem' }}>
        <AlertCircle size={13} style={{ flexShrink: 0 }} /> {t('admin.employeeIdIsAutoGenerated')}
      </div>

      {/* Name row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
        <div>
          <label style={eLabelStyle}>{t('admin.firstName')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={eInputStyle} type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} required placeholder={t('admin.habib')} />
        </div>
        <div>
          <label style={eLabelStyle}>{t('admin.lastName')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={eInputStyle} type="text" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} required placeholder={t('admin.khaoua')} />
        </div>
      </div>

      {/* Role + Phone */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
        <div>
          <label style={eLabelStyle}>{t('admin.role')} <span style={{ color: '#6366f1' }}>*</span></label>
          <select value={formData.role} disabled={lockRole}
            onChange={(e) => {
              const role = e.target.value;
              const nextStoreId = STORE_ROLES_FRONT.includes(role)
                ? (CENTRAL_STORE_ROLES.includes(role) ? (central ? (central._id || central.id) : '') : (CENTRAL_STORE_ROLES.includes(formData.role) ? '' : formData.storeId)) : '';
              setFormData({ ...formData, role, storeId: nextStoreId, labId: LAB_ROLES_FRONT.includes(role) ? formData.labId : '', adminLocationId: ADMIN_LOCATION_ROLES.includes(role) ? formData.adminLocationId : '' });
            }}
            style={{ ...eInputStyle, cursor: lockRole ? 'not-allowed' : 'pointer', background: lockRole ? '#f1f5f9' : '#fafafa' }} required>
            <optgroup label="Retail">
              <option value="sales_person">{t('admin.salesPerson')}</option>
              <option value="store_manager">{t('admin.storeManager')}</option>
            </optgroup>
            <optgroup label="Central Store">
              <option value="inventory_manager">{t('admin.inventoryOperationsManager')}</option>
              <option value="courier">{t('admin.courier')}</option>
            </optgroup>
            <optgroup label="Lab">
              <option value="technician">{t('admin.technician')}</option>
              <option value="lab_manager">{t('admin.labManager')}</option>
            </optgroup>
            <optgroup label="Administration">
              <option value="assistant">{t('admin.assistant')}</option>
              <option value="customer_support">{t('admin.customerSupportAgent')}</option>
              <option value="accountant">{t('admin.accountant')}</option>
              <option value="advisor">{t('advisor.roleLabel')}</option>
              <option value="other">{t('admin.otherAdministration')}</option>
            </optgroup>
          </select>
          {lockRole && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.2rem' }}>{t('admin.lockedTo')} <strong>{ROLE_LABELS[formData.role] || formData.role}</strong></div>}
        </div>
        <div>
          <label style={eLabelStyle}>{t('admin.phone')}</label>
          <input style={eInputStyle} type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="0612345678" />
        </div>
      </div>

      {/* Assignment dropdown */}
      {STORE_ROLES_FRONT.includes(formData.role) && (() => {
        const isCentralRole = CENTRAL_STORE_ROLES.includes(formData.role);
        const storeOptions = isCentralRole ? stores.filter(s => s.isCentralWarehouse) : stores.filter(s => !s.isCentralWarehouse);
        return (
          <div>
            <label style={eLabelStyle}>{isCentralRole ? t('admin.centralWarehouse') : t('admin.store')} <span style={{ color: '#6366f1' }}>*</span></label>
            <select value={formData.storeId} onChange={(e) => setFormData({ ...formData, storeId: e.target.value })} style={{ ...eInputStyle, cursor: 'pointer' }} required>
              <option value="">{isCentralRole ? t('admin.selectTheCentralWarehouse') : t('admin.selectAStore')}</option>
              {storeOptions.map(s => <option key={s._id || s.id} value={s._id || s.id}>{s.name} ({s.storeCode}){s.isCentralWarehouse ? t('admin.central') : ''}</option>)}
            </select>
            {isCentralRole && storeOptions.length === 0 && <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.2rem' }}>{t('admin.noCentralWarehouseSetYet')}</div>}
          </div>
        );
      })()}
      {LAB_ROLES_FRONT.includes(formData.role) && (
        <div>
          <label style={eLabelStyle}>{t('admin.lab')} <span style={{ color: '#6366f1' }}>*</span></label>
          <select value={formData.labId} onChange={(e) => setFormData({ ...formData, labId: e.target.value })} style={{ ...eInputStyle, cursor: 'pointer' }} required>
            <option value="">{t('admin.selectALab')}</option>
            {labs.map(l => <option key={l._id || l.id} value={l._id || l.id}>{l.name}</option>)}
          </select>
        </div>
      )}
      {ADMIN_LOCATION_ROLES.includes(formData.role) && (
        <div>
          <label style={eLabelStyle}>{t('admin.administration')} <span style={{ color: '#6366f1' }}>*</span></label>
          <select value={formData.adminLocationId} onChange={(e) => setFormData({ ...formData, adminLocationId: e.target.value })} style={{ ...eInputStyle, cursor: 'pointer' }} required>
            <option value="">{t('admin.selectAnAdministrationLocation')}</option>
            {adminLocations.map(l => <option key={l._id || l.id} value={l._id || l.id}>{l.name}{l.adminCode ? ` (${l.adminCode})` : ''}</option>)}
          </select>
          {adminLocations.length === 0 && <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.2rem' }}>{t('admin.noAdministrationLocationsYetU2014')}</div>}
        </div>
      )}

      {/* Profile photo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '50%', border: '1.5px dashed #cbd5e1', cursor: 'pointer', background: '#f8fafc', overflow: 'hidden', flexShrink: 0 }}>
          {imagePreview ? <img src={imagePreview} alt={t('admin.preview')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <><UserPlus size={16} color="#94a3b8" /><span style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: '0.15rem' }}>{t('admin.photo')}</span></>}
          {/* Phone/tablet photos are multi-megabyte; downscale before we hold
              on to the file so the POST stays small enough to get through. */}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => { const file = e.target.files[0]; if (file) { const shrunk = await downscaleImage(file); setProfileImage(shrunk); const r = new FileReader(); r.onloadend = () => setImagePreview(r.result); r.readAsDataURL(shrunk); } }} />
        </label>
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>{t('admin.profilePhoto')}</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('admin.optionalU2014ClickTheCircle')}</div>
        </div>
      </div>

      <ModalFooter>
        <CancelButton type="button" onClick={onClose} disabled={submitting}>{t('admin.cancel')}</CancelButton>
        <ConfirmDeleteButton type="submit" disabled={submitting} style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}>
          {submitting ? <><SpinningIcon size={18} /> {t('admin.adding')}</> : <><UserPlus size={18} /> {t('admin.addEmployee')}</>}
        </ConfirmDeleteButton>
      </ModalFooter>
    </form>
  );
};

// Edit an existing employee.
//
// Before this, the only way to change anything about an employee — including
// giving them a profile photo they were created without — was to delete the
// account and recreate it, which also burns a fresh employee ID and loses the
// link from all their past orders.
//
// The server's PUT /api/users/:id only writes the fields it actually receives,
// so this form deliberately never sends `email`, `password` or `permissions`:
// email is the login identity derived from the employee ID at creation, and
// sending `permissions` through FormData would flatten it to an empty array.
// Task switches go as `taskGrants` / `taskRemovals` JSON strings instead; the
// server folds them into `permissions` relative to the role.
const EditEmployeeForm = ({
  employee,
  stores,
  labs,
  adminLocations = [],
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const central = findCentralWarehouse(stores);
  const empId = employee._id || employee.id;
  const [formData, setFormData] = useState({
    firstName: employee.firstName || '',
    lastName: employee.lastName || '',
    role: employee.role || 'sales_person',
    storeId: employee.store?._id || employee.store?.id || '',
    labId: employee.lab?._id || employee.lab?.id || '',
    adminLocationId: employee.adminLocation?._id || employee.adminLocation?.id || '',
    phone: employee.phone || '',
    isActive: employee.isActive !== false,
  });
  // Null until the admin picks a new file — an unchanged photo must not be
  // re-uploaded, so the preview and the pending upload are tracked separately.
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(employee.profileImage || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [taskOverrideState, setTaskOverrideState] = useState(() => taskOverrides(employee.permissions));
  const [advisorAccessState, setAdvisorAccessState] = useState(() => advisorAccess(employee.permissions));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const body = new FormData();
      body.append('firstName', formData.firstName);
      body.append('lastName', formData.lastName);
      body.append('role', formData.role);
      body.append('phone', formData.phone || '');
      body.append('isActive', formData.isActive ? 'true' : 'false');
      body.append('taskGrants', JSON.stringify(taskOverrideState.granted));
      body.append('taskRemovals', JSON.stringify(taskOverrideState.removed));
      if (formData.role === 'advisor') body.append('advisorAccess', JSON.stringify(advisorAccessState));
      // These three are always sent, even when empty. Moving someone to a new
      // role has to clear the assignment they no longer belong to, and the
      // server only touches columns that are present in the request.
      body.append('storeId', STORE_ROLES_FRONT.includes(formData.role) ? formData.storeId : '');
      body.append('labId', LAB_ROLES_FRONT.includes(formData.role) ? formData.labId : '');
      body.append('adminLocationId', ADMIN_LOCATION_ROLES.includes(formData.role) ? formData.adminLocationId : '');
      if (profileImage) {
        body.append('profileImage', profileImage);
      }

      await axios.put(`/api/users/${empId}`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(t('admin.employeeUpdatedSuccessfully'));
      onSuccess();
    } catch (err) {
      console.error('Error updating employee:', err);
      // nginx answers an oversized upload itself, with an HTML body that has
      // no `message` field — without this it reads as a generic failure.
      setError(err.response?.status === 413
        ? t('admin.profilePhotoTooLarge')
        : (err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || t('admin.errorUpdatingEmployee')));
    } finally {
      setSubmitting(false);
    }
  };

  const eInputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', color: '#1e293b', background: '#fafafa', outline: 'none', fontFamily: 'inherit' };
  const eLabelStyle = { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '0.28rem', display: 'block' };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', fontWeight: 500 }}>
          <XCircle size={15} style={{ flexShrink: 0 }} /> {error}
        </div>
      )}

      {/* Identity is fixed: the employee ID is what past orders point at, and
          the login email is derived from it. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.7rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '7px', color: '#0369a1', fontSize: '0.78rem' }}>
        <AlertCircle size={13} style={{ flexShrink: 0 }} />
        <span>{t('admin.employeeId')}: <strong>{employee.employeeId || '—'}</strong>{employee.email ? ` · ${employee.email}` : ''}</span>
      </div>

      {/* Name row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
        <div>
          <label style={eLabelStyle}>{t('admin.firstName')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={eInputStyle} type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} required />
        </div>
        <div>
          <label style={eLabelStyle}>{t('admin.lastName')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={eInputStyle} type="text" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} required />
        </div>
      </div>

      {/* Role + Phone */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
        <div>
          <label style={eLabelStyle}>{t('admin.role')} <span style={{ color: '#6366f1' }}>*</span></label>
          <select value={formData.role}
            onChange={(e) => {
              const role = e.target.value;
              const nextStoreId = STORE_ROLES_FRONT.includes(role)
                ? (CENTRAL_STORE_ROLES.includes(role) ? (central ? (central._id || central.id) : '') : (CENTRAL_STORE_ROLES.includes(formData.role) ? '' : formData.storeId)) : '';
              setFormData({ ...formData, role, storeId: nextStoreId, labId: LAB_ROLES_FRONT.includes(role) ? formData.labId : '', adminLocationId: ADMIN_LOCATION_ROLES.includes(role) ? formData.adminLocationId : '' });
            }}
            style={{ ...eInputStyle, cursor: 'pointer' }} required>
            <optgroup label="Retail">
              <option value="sales_person">{t('admin.salesPerson')}</option>
              <option value="store_manager">{t('admin.storeManager')}</option>
            </optgroup>
            <optgroup label="Central Store">
              <option value="inventory_manager">{t('admin.inventoryOperationsManager')}</option>
              <option value="courier">{t('admin.courier')}</option>
            </optgroup>
            <optgroup label="Lab">
              <option value="technician">{t('admin.technician')}</option>
              <option value="lab_manager">{t('admin.labManager')}</option>
            </optgroup>
            <optgroup label="Administration">
              <option value="assistant">{t('admin.assistant')}</option>
              <option value="customer_support">{t('admin.customerSupportAgent')}</option>
              <option value="accountant">{t('admin.accountant')}</option>
              <option value="advisor">{t('advisor.roleLabel')}</option>
              <option value="other">{t('admin.otherAdministration')}</option>
            </optgroup>
          </select>
        </div>
        <div>
          <label style={eLabelStyle}>{t('admin.phone')}</label>
          <input style={eInputStyle} type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="0612345678" />
        </div>
      </div>

      {/* Assignment dropdown */}
      {STORE_ROLES_FRONT.includes(formData.role) && (() => {
        const isCentralRole = CENTRAL_STORE_ROLES.includes(formData.role);
        const storeOptions = isCentralRole ? stores.filter(s => s.isCentralWarehouse) : stores.filter(s => !s.isCentralWarehouse);
        return (
          <div>
            <label style={eLabelStyle}>{isCentralRole ? t('admin.centralWarehouse') : t('admin.store')} <span style={{ color: '#6366f1' }}>*</span></label>
            <select value={formData.storeId} onChange={(e) => setFormData({ ...formData, storeId: e.target.value })} style={{ ...eInputStyle, cursor: 'pointer' }} required>
              <option value="">{isCentralRole ? t('admin.selectTheCentralWarehouse') : t('admin.selectAStore')}</option>
              {storeOptions.map(s => <option key={s._id || s.id} value={s._id || s.id}>{s.name} ({s.storeCode}){s.isCentralWarehouse ? t('admin.central') : ''}</option>)}
            </select>
          </div>
        );
      })()}
      {LAB_ROLES_FRONT.includes(formData.role) && (
        <div>
          <label style={eLabelStyle}>{t('admin.lab')} <span style={{ color: '#6366f1' }}>*</span></label>
          <select value={formData.labId} onChange={(e) => setFormData({ ...formData, labId: e.target.value })} style={{ ...eInputStyle, cursor: 'pointer' }} required>
            <option value="">{t('admin.selectALab')}</option>
            {labs.map(l => <option key={l._id || l.id} value={l._id || l.id}>{l.name}</option>)}
          </select>
        </div>
      )}
      {ADMIN_LOCATION_ROLES.includes(formData.role) && (
        <div>
          <label style={eLabelStyle}>{t('admin.administration')} <span style={{ color: '#6366f1' }}>*</span></label>
          <select value={formData.adminLocationId} onChange={(e) => setFormData({ ...formData, adminLocationId: e.target.value })} style={{ ...eInputStyle, cursor: 'pointer' }} required>
            <option value="">{t('admin.selectAnAdministrationLocation')}</option>
            {adminLocations.map(l => <option key={l._id || l.id} value={l._id || l.id}>{l.name}{l.adminCode ? ` (${l.adminCode})` : ''}</option>)}
          </select>
        </div>
      )}

      {/* Status */}
      <div>
        <label style={eLabelStyle}>{t('admin.status')}</label>
        <select value={formData.isActive ? 'true' : 'false'} onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })} style={{ ...eInputStyle, cursor: 'pointer' }}>
          <option value="true">{t('admin.active')}</option>
          <option value="false">{t('admin.inactive')}</option>
        </select>
      </div>

      {/* Profile photo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '50%', border: '1.5px dashed #cbd5e1', cursor: 'pointer', background: '#f8fafc', overflow: 'hidden', flexShrink: 0 }}>
          {imagePreview ? <img src={imagePreview} alt={t('admin.preview')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <><UserPlus size={16} color="#94a3b8" /><span style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: '0.15rem' }}>{t('admin.photo')}</span></>}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => { const file = e.target.files[0]; if (file) { const shrunk = await downscaleImage(file); setProfileImage(shrunk); const r = new FileReader(); r.onloadend = () => setImagePreview(r.result); r.readAsDataURL(shrunk); } }} />
        </label>
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>{t('admin.profilePhoto')}</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('admin.clickThePhotoToReplace')}</div>
        </div>
      </div>

      {/* Task switches — admins always have every task, so none to show.
          Advisors get the admin-dashboard sections instead. */}
      {formData.role === 'advisor' ? (
        <AdvisorAccessEditor value={advisorAccessState} onChange={setAdvisorAccessState} />
      ) : !['admin', 'super_admin'].includes(formData.role) && (
        <TaskSwitches role={formData.role} overrides={taskOverrideState} onChange={setTaskOverrideState} />
      )}

      <ModalFooter>
        <CancelButton type="button" onClick={onClose} disabled={submitting}>{t('admin.cancel')}</CancelButton>
        <ConfirmDeleteButton type="submit" disabled={submitting} style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}>
          {submitting ? <><SpinningIcon size={18} /> {t('admin.savingEllipsis')}</> : <><Check size={18} /> {t('admin.saveChanges')}</>}
        </ConfirmDeleteButton>
      </ModalFooter>
    </form>
  );
};

// Add Store Form Component
const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 0.5rem;
  position: relative;
`;

const MapIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const MapFallback = styled.div`
  width: 100%;
  height: 100%;
  background: #f8fafc;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

const LocationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;
  
  &:hover {
    background: #4f46e5;
  }
`;

const ALGERIAN_PHONE_RE = /^(0)(5|6|7)\d{8}$/;
const parsePhones = (raw) => {
  if (!raw) return [''];
  const parts = String(raw).split(/[/,;]+/).map(s => s.trim()).filter(Boolean);
  return parts.length ? parts : [''];
};

const AddStoreForm = ({ onClose, onSuccess, editingStore = null, isCentralMode = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    storeCode: '',
    cityCode: '',
    name: '',
    address: '',
    email: '',
    plusCode: '',
    locationAddress: '',
    isCentralWarehouse: false
  });
  const [phones, setPhones] = useState(['']);
  const [phoneErrors, setPhoneErrors] = useState([]);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showMap, setShowMap] = useState(false);
  // Operating model: own lens stock, own lab, and where work is sent.
  const [capabilities, setCapabilities] = useState({
    hasLensStock: false,
    hasLab: false,
    primaryLabId: '',
    fallbackLabId: '',
  });
  const [labs, setLabs] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/labs', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setLabs(data?.labs || (Array.isArray(data) ? data : [])))
      .catch(() => setLabs([]));
  }, []);

  const addPhone = () => setPhones(prev => [...prev, '']);
  const removePhone = (i) => setPhones(prev => prev.filter((_, idx) => idx !== i));
  const updatePhone = (i, val) => {
    setPhones(prev => prev.map((p, idx) => idx === i ? val : p));
    setPhoneErrors(prev => { const next = [...prev]; next[i] = ''; return next; });
  };
  const validatePhones = () => {
    const errs = phones.map(p => {
      if (!p.trim()) return 'Required';
      if (!ALGERIAN_PHONE_RE.test(p.trim())) return 'Invalid Algerian number (e.g. 0612345678)';
      return '';
    });
    setPhoneErrors(errs);
    return errs.every(e => !e);
  };

  // Load store data when editing
  useEffect(() => {
    if (editingStore) {
      setFormData({
        storeCode: editingStore.storeCode || '',
        cityCode: editingStore.branding?.cityCode || editingStore.cityCode || '',
        name: editingStore.name || '',
        address: typeof editingStore.address === 'string'
          ? editingStore.address
          : editingStore.address?.street || '',
        email: editingStore.contact?.email || editingStore.email || '',
        plusCode: editingStore.location?.plusCode || '',
        locationAddress: editingStore.location?.address || '',
        isCentralWarehouse: !!editingStore.isCentralWarehouse
      });
      const caps = editingStore.capabilities || {};
      setCapabilities({
        hasLensStock: Boolean(caps.hasLensStock),
        hasLab: Boolean(caps.hasLab),
        primaryLabId: caps.primaryLabId || '',
        fallbackLabId: caps.fallbackLabId || '',
      });
      setPhones(parsePhones(editingStore.contact?.phone || editingStore.phone));
      if (editingStore.logo) {
        setLogoPreview(editingStore.logo.startsWith('http')
          ? editingStore.logo
          : `http://localhost:5000${editingStore.logo}`);
      }
      if (editingStore.location?.plusCode) {
        setShowMap(true);
      }
    }
  }, [editingStore]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validatePhones()) return;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      const validPhones = phones.map(p => p.trim()).filter(Boolean);
      const phoneString = validPhones.join('/');

      if (formData.address) formDataToSend.append('address', formData.address);
      if (formData.plusCode) {
        formDataToSend.append('plusCode', formData.plusCode.trim().toUpperCase());
        if (formData.locationAddress) formDataToSend.append('locationAddress', formData.locationAddress);
      }
      if (formData.storeCode) formDataToSend.append('storeCode', formData.storeCode);
      if (formData.cityCode) formDataToSend.append('cityCode', formData.cityCode);
      if (formData.name) formDataToSend.append('name', formData.name);
      if (phoneString) formDataToSend.append('phone', phoneString);
      if (formData.email) formDataToSend.append('email', formData.email);
      if (logo) formDataToSend.append('logo', logo);
      if (isCentralMode) formDataToSend.append('isCentralWarehouse', 'true');
      // Multipart, so the capability object travels as JSON text.
      formDataToSend.append('capabilities', JSON.stringify({
        hasLensStock: capabilities.hasLensStock,
        hasLab: capabilities.hasLab,
        primaryLabId: capabilities.primaryLabId || null,
        fallbackLabId: capabilities.fallbackLabId || null,
      }));

      if (editingStore) {
        await axios.put(`/api/stores/${editingStore._id || editingStore.id}`, formDataToSend, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Store updated');
      } else {
        await axios.post('/api/stores', formDataToSend, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success(isCentralMode ? 'Central store added' : 'Store added');
      }
      onSuccess();
    } catch (error) {
      console.error('Error adding store:', error);
      setError(error.response?.data?.message || 'Error adding store. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const PLUS_RE = /^[23456789CFGHJMPQRVWX]{8}\+[23456789CFGHJMPQRVWX]{2,3}$/;
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', color: '#1e293b', background: '#fafafa', outline: 'none', fontFamily: 'inherit', transition: 'border-color 150ms ease' };
  const labelStyle = { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '0.28rem', display: 'block' };
  const divider = { border: 'none', borderTop: '1px solid #f1f5f9', margin: '0.1rem 0' };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', fontWeight: 500 }}>
          <span>⚠</span> {error}
        </div>
      )}

      {/* Store # · City # · Name */}
      <div style={{ display: 'grid', gridTemplateColumns: '68px 68px 1fr', gap: '0.6rem' }}>
        <div>
          <label style={labelStyle}>{t('admin.store2')}</label>
          <input style={inputStyle} type="text" value={formData.storeCode} onChange={(e) => setFormData({ ...formData, storeCode: e.target.value.replace(/\D/g, '').slice(0, 2) })} required inputMode="numeric" maxLength={2} placeholder="01" />
        </div>
        <div>
          <label style={labelStyle}>{t('admin.city')}</label>
          <input style={inputStyle} type="text" value={formData.cityCode} onChange={(e) => setFormData({ ...formData, cityCode: e.target.value.replace(/\D/g, '').slice(0, 2) })} inputMode="numeric" maxLength={2} placeholder="12" />
        </div>
        <div>
          <label style={labelStyle}>{t('admin.storeName')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={inputStyle} type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder={t('admin.eGAlRaeed')} />
        </div>
      </div>

      {/* Address · Email */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
        <div>
          <label style={labelStyle}>{t('admin.address')}</label>
          <input style={inputStyle} type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder={t('admin.cityStreet')} />
        </div>
        <div>
          <label style={labelStyle}>{t('admin.email')}</label>
          <input style={inputStyle} type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="store@example.com" />
        </div>
      </div>

      <hr style={divider} />

      {/* ── Operating model ───────────────────────────────────────────────
          What this branch can do for itself, and where its work goes when it
          can't. A plain store keeps nothing and sends everything to the
          principal lab; a store with its own lab and stock handles its own. */}
      <div>
        <label style={labelStyle}>{t('admin.operatingModel')}</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 0.65rem', background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '8px' }}>
            <input type="checkbox" checked={capabilities.hasLensStock} style={{ marginTop: '0.15rem' }}
              onChange={(e) => setCapabilities(c => ({ ...c, hasLensStock: e.target.checked }))} />
            <span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{t('admin.keepsItsOwnLensStock')}</span>
              <span style={{ display: 'block', fontSize: '0.73rem', color: '#94a3b8', lineHeight: 1.4 }}>
                {t('admin.offNoLensInventoryOf')}
              </span>
            </span>
          </label>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 0.65rem', background: '#fafafa', border: '1.5px solid #e2e8f0', borderRadius: '8px' }}>
            <input type="checkbox" checked={capabilities.hasLab} style={{ marginTop: '0.15rem' }}
              onChange={(e) => setCapabilities(c => ({ ...c, hasLab: e.target.checked, fallbackLabId: e.target.checked ? c.fallbackLabId : '' }))} />
            <span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{t('admin.hasALabOnSite')}</span>
              <span style={{ display: 'block', fontSize: '0.73rem', color: '#94a3b8', lineHeight: 1.4 }}>
                {t('admin.anEdgingMountingLabAt')}
              </span>
            </span>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: capabilities.hasLab ? '1fr 1fr' : '1fr', gap: '0.6rem' }}>
            <div>
              <label style={labelStyle}>{t('admin.sendsWorkTo')}</label>
              <select style={inputStyle} value={capabilities.primaryLabId}
                onChange={(e) => setCapabilities(c => ({ ...c, primaryLabId: e.target.value }))}>
                <option value="">{t('admin.automaticLabInThisTown')}</option>
                {labs.map(l => (
                  <option key={l._id || l.id} value={l._id || l.id}>{l.name}{l.labCode ? ` (${l.labCode})` : ''}</option>
                ))}
              </select>
            </div>
            {/* Only a store with its own lab has anything to overflow FROM. */}
            {capabilities.hasLab && (
              <div>
                <label style={labelStyle}>{t('admin.overflowLab')}</label>
                <select style={inputStyle} value={capabilities.fallbackLabId}
                  onChange={(e) => setCapabilities(c => ({ ...c, fallbackLabId: e.target.value }))}>
                  <option value="">{t('admin.none')}</option>
                  {labs.map(l => (
                    <option key={l._id || l.id} value={l._id || l.id}>{l.name}{l.labCode ? ` (${l.labCode})` : ''}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <span style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.4 }}>
            {capabilities.primaryLabId
              ? t('admin.pinnedToOneLabClear')
              : `Automatic: work goes to a working lab in town #${formData.cityCode || '—'} (a standalone lab, or a branch there that has one) and never leaves town. If there is none, or it is down, it goes central → principal lab → central.`}
          </span>
        </div>
      </div>

      <hr style={divider} />

      {/* Phone Numbers */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>{t('admin.phoneNumbers')} <span style={{ color: '#6366f1' }}>*</span></label>
          <button type="button" onClick={addPhone}
            style={{ padding: '0.2rem 0.55rem', background: '#eef2ff', color: '#4f46e5', border: '1.5px solid #c7d2fe', borderRadius: '6px', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {t('admin.add')}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: phones.length > 1 ? '1fr 1fr' : '1fr', gap: '0.35rem' }}>
          {phones.map((phone, i) => (
            <div key={i}>
              <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                <input style={{ ...inputStyle, flex: 1, padding: '0.45rem 0.65rem', borderColor: phoneErrors[i] ? '#fca5a5' : '#e2e8f0' }} type="tel" value={phone} onChange={(e) => updatePhone(i, e.target.value)} placeholder="0612345678" />
                {phones.length > 1 && (
                  <button type="button" onClick={() => removePhone(i)}
                    style={{ padding: '0.4rem', background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Trash size={12} />
                  </button>
                )}
              </div>
              {phoneErrors[i] && <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.1rem', display: 'block' }}>{phoneErrors[i]}</span>}
            </div>
          ))}
        </div>
      </div>

      <hr style={divider} />

      {/* Plus Code */}
      <div>
        <label style={labelStyle}>{t('admin.googleMapsPlusCode')}</label>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <input style={{ ...inputStyle, flex: 1 }} type="text" value={formData.plusCode}
            onChange={(e) => { const v = e.target.value.toUpperCase().trim(); setFormData({ ...formData, plusCode: v }); if (PLUS_RE.test(v)) setShowMap(true); }}
            placeholder={t('admin.x8fvc9g8f5w')} />
          <button type="button" onClick={() => setShowMap(!showMap)}
            style={{ padding: '0.5rem 0.85rem', background: showMap ? '#eef2ff' : '#f8fafc', color: '#4f46e5', border: '1.5px solid #c7d2fe', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
            <MapPin size={13} /> {showMap ? t('admin.hide') : t('admin.map')}
          </button>
        </div>
        {showMap && (
          <MapContainer style={{ marginTop: '0.5rem', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
            {formData.plusCode && PLUS_RE.test(formData.plusCode) ? (
              <MapIframe key={formData.plusCode} src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyC-TRKLOtgiv7CSG9E4u-kn3yXdII11TKA&q=${encodeURIComponent('+' + formData.plusCode)}&zoom=15`} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={t('admin.locationMap')} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#94a3b8', fontSize: '0.8rem', gap: '0.35rem' }}>
                <MapPin size={22} style={{ opacity: 0.35 }} />
                <span>{t('admin.enterAValidPlusCode')}</span>
              </div>
            )}
          </MapContainer>
        )}
      </div>

      <hr style={divider} />

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '64px', height: '52px', border: '1.5px dashed #cbd5e1', borderRadius: '10px', cursor: 'pointer', background: '#f8fafc', overflow: 'hidden', flexShrink: 0 }}>
          {logoPreview
            ? <img src={logoPreview} alt={t('admin.logo')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : <><Store size={15} color="#94a3b8" /><span style={{ fontSize: '0.58rem', color: '#94a3b8', marginTop: '0.15rem' }}>{t('admin.logo')}</span></>
          }
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const file = e.target.files[0]; if (file) { setLogo(file); const reader = new FileReader(); reader.onloadend = () => setLogoPreview(reader.result); reader.readAsDataURL(file); } }} />
        </label>
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>{t('admin.storeLogo')}</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('admin.optionalClickToUpload')}</div>
        </div>
      </div>
      <ModalFooter>
        <CancelButton type="button" onClick={onClose} disabled={submitting}>
          {t('admin.cancel')}
        </CancelButton>
        <ConfirmDeleteButton
          type="submit"
          disabled={submitting}
          style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
        >
          {submitting ? (
            <>
              <SpinningIcon size={18} />
              {t('admin.adding')}
            </>
          ) : (
            <>
              <Store size={18} />
              {editingStore ? t('admin.updateStore') : t('admin.addStore')}
            </>
          )}
        </ConfirmDeleteButton>
      </ModalFooter>
    </form>
  );
};

const AddLabForm = ({ onClose, onSuccess, editingLab = null, employees = [] }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    labCode: '',
    name: '',
    address: '',
    phone: '',
    email: '',
    plusCode: '',
    locationAddress: '',
    // Town code + operational state drive which stores route here.
    cityCode: '',
    isAvailable: true
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    if (editingLab) {
      setFormData({
        labCode: editingLab.labCode || '',
        name: editingLab.name || '',
        address: typeof editingLab.address === 'string'
          ? editingLab.address
          : editingLab.address?.street || '',
        phone: editingLab.contact?.phone || '',
        email: editingLab.contact?.email || '',
        plusCode: editingLab.location?.plusCode || '',
        locationAddress: editingLab.location?.address || '',
        cityCode: editingLab.cityCode || '',
        isAvailable: editingLab.isAvailable !== false
      });
      if (editingLab.logo) {
        setLogoPreview(editingLab.logo.startsWith('http')
          ? editingLab.logo
          : `http://localhost:5000${editingLab.logo}`);
      }
      if (editingLab.location?.plusCode) {
        setShowMap(true);
      }
    }
  }, [editingLab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();

      if (formData.address) {
        formDataToSend.append('address', formData.address);
      }
      if (formData.plusCode) {
        formDataToSend.append('plusCode', formData.plusCode.trim().toUpperCase());
        if (formData.locationAddress) {
          formDataToSend.append('locationAddress', formData.locationAddress);
        }
      }
      if (formData.labCode) formDataToSend.append('labCode', formData.labCode);
      formDataToSend.append('cityCode', formData.cityCode || '');
      formDataToSend.append('isAvailable', formData.isAvailable ? 'true' : 'false');
      if (formData.name) formDataToSend.append('name', formData.name);
      if (formData.phone) formDataToSend.append('phone', formData.phone);
      if (formData.email) formDataToSend.append('email', formData.email);
      if (logo) {
        formDataToSend.append('logo', logo);
      }

      if (editingLab) {
        await axios.patch(`/api/labs/${editingLab._id || editingLab.id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Lab updated');
      } else {
        await axios.post('/api/labs', formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Lab added');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving lab:', error);
      setError(error.response?.data?.message || 'Error saving lab. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const lInputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', color: '#1e293b', background: '#fafafa', outline: 'none', fontFamily: 'inherit' };
  const lLabelStyle = { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '0.28rem', display: 'block' };
  const lDivider = { border: 'none', borderTop: '1px solid #f1f5f9', margin: '0.1rem 0' };
  const LAB_PLUS_RE = /^[23456789CFGHJMPQRVWX]{8}\+[23456789CFGHJMPQRVWX]{2,3}$/;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', fontWeight: 500 }}>
          <span>⚠</span> {error}
        </div>
      )}

      {/* Code + Name */}
      <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '0.6rem' }}>
        <div>
          <label style={lLabelStyle}>{t('admin.labCode')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={{ ...lInputStyle, background: editingLab ? '#f1f5f9' : '#fafafa', cursor: editingLab ? 'not-allowed' : 'text' }} type="text" value={formData.labCode} onChange={(e) => setFormData({ ...formData, labCode: e.target.value.toUpperCase() })} required disabled={!!editingLab} placeholder={t('admin.lab01')} />
          {editingLab && <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.15rem', display: 'block' }}>{t('admin.cannotChange')}</span>}
        </div>
        <div>
          <label style={lLabelStyle}>{t('admin.labName')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={lInputStyle} type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder={t('admin.eGMainLab')} />
        </div>
      </div>

      {/* Town + operational state: the two things that decide which stores
          route their work here. Down is temporary (machines out) as opposed to
          deleting the lab. */}
      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '0.6rem', alignItems: 'start' }}>
        <div>
          <label style={lLabelStyle}>{t('admin.town')}</label>
          <input style={lInputStyle} type="text" inputMode="numeric" maxLength={2}
            value={formData.cityCode}
            onChange={(e) => setFormData({ ...formData, cityCode: e.target.value.replace(/\D/g, '').slice(0, 2) })}
            placeholder="12" />
        </div>
        <div>
          <label style={lLabelStyle}>{t('admin.status')}</label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 0.65rem', background: formData.isAvailable ? '#f0fdf4' : '#fef2f2', border: `1.5px solid ${formData.isAvailable ? '#bbf7d0' : '#fecaca'}`, borderRadius: '8px' }}>
            <input type="checkbox" checked={formData.isAvailable} style={{ marginTop: '0.15rem' }}
              onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })} />
            <span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: formData.isAvailable ? '#166534' : '#991b1b' }}>
                {formData.isAvailable ? t('admin.openTakingWork') : t('admin.downNotTakingWork')}
              </span>
              <span style={{ display: 'block', fontSize: '0.73rem', color: '#94a3b8', lineHeight: 1.4 }}>
                {t('admin.untickWhenMachinesAreDown')}
              </span>
            </span>
          </label>
        </div>
      </div>

      {/* Address + Phone + Email */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
        <div>
          <label style={lLabelStyle}>{t('admin.address')}</label>
          <input style={lInputStyle} type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder={t('admin.cityStreet')} />
        </div>
        <div>
          <label style={lLabelStyle}>{t('admin.phone')}</label>
          <input style={lInputStyle} type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="0612345678" />
        </div>
      </div>
      <div>
        <label style={lLabelStyle}>{t('admin.email')}</label>
        <input style={lInputStyle} type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="lab@example.com" />
      </div>

      <hr style={lDivider} />

      {/* Plus Code */}
      <div>
        <label style={lLabelStyle}>{t('admin.googleMapsPlusCode')}</label>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <input style={{ ...lInputStyle, flex: 1 }} type="text" value={formData.plusCode}
            onChange={(e) => { const v = e.target.value.toUpperCase().trim(); setFormData({ ...formData, plusCode: v }); if (LAB_PLUS_RE.test(v)) setShowMap(true); }}
            placeholder={t('admin.x8fvc9g8f5w')} />
          <button type="button" onClick={() => setShowMap(!showMap)}
            style={{ padding: '0.5rem 0.85rem', background: showMap ? '#eef2ff' : '#f8fafc', color: '#4f46e5', border: '1.5px solid #c7d2fe', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
            <MapPin size={13} /> {showMap ? t('admin.hide') : t('admin.map')}
          </button>
        </div>
        {showMap && (
          <MapContainer style={{ marginTop: '0.5rem', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
            {formData.plusCode && LAB_PLUS_RE.test(formData.plusCode) ? (
              <MapIframe src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyC-TRKLOtgiv7CSG9E4u-kn3yXdII11TKA&q=${encodeURIComponent('+' + formData.plusCode)}&zoom=15`} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={t('admin.locationMap')} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#94a3b8', fontSize: '0.8rem', gap: '0.35rem' }}>
                <MapPin size={22} style={{ opacity: 0.35 }} /><span>{t('admin.enterAValidPlusCode')}</span>
              </div>
            )}
          </MapContainer>
        )}
      </div>

      <hr style={lDivider} />

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '10px', border: '1.5px dashed #cbd5e1', cursor: 'pointer', background: '#f8fafc', overflow: 'hidden', flexShrink: 0 }}>
          {logoPreview ? <img src={logoPreview} alt={t('admin.logo')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <><Building2 size={16} color="#94a3b8" /><span style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: '0.15rem' }}>{t('admin.logo')}</span></>}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const file = e.target.files[0]; if (file) { setLogo(file); const r = new FileReader(); r.onloadend = () => setLogoPreview(r.result); r.readAsDataURL(file); } }} />
        </label>
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>{t('admin.labLogo')}</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('admin.optionalClickToUpload')}</div>
        </div>
      </div>

      <ModalFooter>
        <CancelButton type="button" onClick={onClose} disabled={submitting}>{t('admin.cancel')}</CancelButton>
        <ConfirmDeleteButton type="submit" disabled={submitting} style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}>
          {submitting ? <><SpinningIcon size={18} /> {t('admin.saving')}</> : <><Building2 size={18} /> {editingLab ? t('admin.updateLab') : t('admin.addLab')}</>}
        </ConfirmDeleteButton>
      </ModalFooter>
    </form>
  );
};

const AddAdministrationLocationForm = ({ onClose, onSuccess, editingLocation = null, employees = [] }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    adminCode: '',
    name: '',
    address: '',
    phone: '',
    email: '',
    plusCode: '',
    locationAddress: ''
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    if (editingLocation) {
      setFormData({
        adminCode: editingLocation.adminCode || '',
        name: editingLocation.name || '',
        address: typeof editingLocation.address === 'string'
          ? editingLocation.address
          : editingLocation.address?.street || '',
        phone: editingLocation.contact?.phone || '',
        email: editingLocation.contact?.email || '',
        plusCode: editingLocation.location?.plusCode || '',
        locationAddress: editingLocation.location?.address || ''
      });
      if (editingLocation.logo) {
        setLogoPreview(editingLocation.logo.startsWith('http')
          ? editingLocation.logo
          : `http://localhost:5000${editingLocation.logo}`);
      }
      if (editingLocation.location?.plusCode) {
        setShowMap(true);
      }
      if (editingLocation.employees?.length) {
        setSelectedEmployees(editingLocation.employees.map((emp) => emp._id || emp.id));
      }
    }
  }, [editingLocation]);

  const toggleEmployee = (id) => {
    setSelectedEmployees((prev) => prev.includes(id)
      ? prev.filter((empId) => empId !== id)
      : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();

      if (formData.address) {
        formDataToSend.append('address', formData.address);
      }
      if (formData.plusCode) {
        formDataToSend.append('plusCode', formData.plusCode.trim().toUpperCase());
        if (formData.locationAddress) {
          formDataToSend.append('locationAddress', formData.locationAddress);
        }
      }
      if (formData.adminCode) formDataToSend.append('adminCode', formData.adminCode);
      if (formData.name) formDataToSend.append('name', formData.name);
      if (formData.phone) formDataToSend.append('phone', formData.phone);
      if (formData.email) formDataToSend.append('email', formData.email);
      formDataToSend.append('employees', selectedEmployees.join(','));
      if (logo) {
        formDataToSend.append('logo', logo);
      }

      if (editingLocation) {
        await axios.patch(`/api/admin-locations/${editingLocation._id || editingLocation.id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Administration location updated');
      } else {
        await axios.post('/api/admin-locations', formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Administration location added');
      }

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save administration location');
    } finally {
      setSubmitting(false);
    }
  };

  const aInputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', color: '#1e293b', background: '#fafafa', outline: 'none', fontFamily: 'inherit' };
  const aLabelStyle = { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '0.28rem', display: 'block' };
  const aDivider = { border: 'none', borderTop: '1px solid #f1f5f9', margin: '0.1rem 0' };
  const ADM_PLUS_RE = /^[23456789CFGHJMPQRVWX]{8}\+[23456789CFGHJMPQRVWX]{2,3}$/;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', fontWeight: 500 }}>
          <AlertCircle size={13} /> {error}
        </div>
      )}

      {/* Code + Name */}
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '0.6rem' }}>
        <div>
          <label style={aLabelStyle}>{t('admin.adminCode')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={{ ...aInputStyle, background: editingLocation ? '#f1f5f9' : '#fafafa', cursor: editingLocation ? 'not-allowed' : 'text' }} type="text" value={formData.adminCode} onChange={(e) => setFormData({ ...formData, adminCode: e.target.value.toUpperCase() })} required disabled={!!editingLocation} placeholder={t('admin.adm01')} />
          {editingLocation && <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.15rem', display: 'block' }}>{t('admin.cannotChange')}</span>}
        </div>
        <div>
          <label style={aLabelStyle}>{t('admin.name')} <span style={{ color: '#6366f1' }}>*</span></label>
          <input style={aInputStyle} type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder={t('admin.eGHeadOffice')} />
        </div>
      </div>

      {/* Address + Phone + Email */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
        <div>
          <label style={aLabelStyle}>{t('admin.address')}</label>
          <input style={aInputStyle} type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder={t('admin.cityStreet')} />
        </div>
        <div>
          <label style={aLabelStyle}>{t('admin.phone')}</label>
          <input style={aInputStyle} type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="0612345678" />
        </div>
      </div>
      <div>
        <label style={aLabelStyle}>{t('admin.email')}</label>
        <input style={aInputStyle} type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="admin@example.com" />
      </div>

      <hr style={aDivider} />

      {/* Plus Code */}
      <div>
        <label style={aLabelStyle}>{t('admin.googleMapsPlusCode')}</label>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <input style={{ ...aInputStyle, flex: 1 }} type="text" value={formData.plusCode}
            onChange={(e) => { const v = e.target.value.toUpperCase().trim(); setFormData({ ...formData, plusCode: v }); if (ADM_PLUS_RE.test(v)) setShowMap(true); }}
            placeholder={t('admin.x8fvc9g8f5w')} />
          <button type="button" onClick={() => setShowMap(!showMap)}
            style={{ padding: '0.5rem 0.85rem', background: showMap ? '#eef2ff' : '#f8fafc', color: '#4f46e5', border: '1.5px solid #c7d2fe', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
            <MapPin size={13} /> {showMap ? t('admin.hide') : t('admin.map')}
          </button>
        </div>
        {showMap && (
          <MapContainer style={{ marginTop: '0.5rem', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
            {formData.plusCode && ADM_PLUS_RE.test(formData.plusCode) ? (
              <MapIframe src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyC-TRKLOtgiv7CSG9E4u-kn3yXdII11TKA&q=${encodeURIComponent('+' + formData.plusCode)}&zoom=15`} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={t('admin.locationMap')} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#94a3b8', fontSize: '0.8rem', gap: '0.35rem' }}>
                <MapPin size={22} style={{ opacity: 0.35 }} /><span>{t('admin.enterAValidPlusCode')}</span>
              </div>
            )}
          </MapContainer>
        )}
      </div>

      <hr style={aDivider} />

      {/* Employees */}
      {employees.length > 0 && (
        <div>
          <label style={aLabelStyle}>{t('admin.assignEmployees')}</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '140px', overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: '8px', padding: '0.6rem 0.75rem', background: '#fafafa' }}>
            {employees.map((emp) => {
              const id = emp._id || emp.id;
              const label = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
              return (
                <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.83rem', cursor: 'pointer', color: '#1e293b' }}>
                  <input type="checkbox" checked={selectedEmployees.includes(id)} onChange={() => toggleEmployee(id)} style={{ accentColor: '#6366f1', cursor: 'pointer' }} />
                  {label || emp.email || 'Employee'}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '10px', border: '1.5px dashed #cbd5e1', cursor: 'pointer', background: '#f8fafc', overflow: 'hidden', flexShrink: 0 }}>
          {logoPreview ? <img src={logoPreview} alt={t('admin.logo')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : <><MapPin size={16} color="#94a3b8" /><span style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: '0.15rem' }}>{t('admin.logo')}</span></>}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const file = e.target.files[0]; if (file) { setLogo(file); const r = new FileReader(); r.onloadend = () => setLogoPreview(r.result); r.readAsDataURL(file); } }} />
        </label>
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>{t('admin.locationLogo')}</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('admin.optionalClickToUpload')}</div>
        </div>
      </div>

      <ModalFooter>
        <CancelButton type="button" onClick={onClose} disabled={submitting}>{t('admin.cancel')}</CancelButton>
        <ConfirmDeleteButton type="submit" disabled={submitting} style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}>
          {submitting ? <><SpinningIcon size={18} /> {t('admin.saving')}</> : <><MapPin size={18} /> {editingLocation ? t('admin.updateLocation') : t('admin.addLocation')}</>}
        </ConfirmDeleteButton>
      </ModalFooter>
    </form>
  );
};

// eslint-disable-next-line no-unused-vars
const AddRecommendationForm = ({ onClose, onSuccess, editingRecommendation = null }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    prescriptionType: 'single_vision',
    name: '',
    description: '',
    price: '',
    estimatedDays: '',
    order: '',
    minSph: '',
    maxSph: '',
    minCyl: '',
    maxCyl: '',
    isActive: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editingRecommendation) {
      setFormData({
        prescriptionType: editingRecommendation.prescriptionType || 'single_vision',
        name: editingRecommendation.name || '',
        description: editingRecommendation.description || '',
        price: editingRecommendation.price !== undefined && editingRecommendation.price !== null
          ? String(editingRecommendation.price)
          : '',
        estimatedDays: editingRecommendation.estimatedDays !== undefined && editingRecommendation.estimatedDays !== null
          ? String(editingRecommendation.estimatedDays)
          : '',
        order: editingRecommendation.order !== undefined && editingRecommendation.order !== null
          ? String(editingRecommendation.order)
          : '',
        minSph: editingRecommendation.minSph !== undefined && editingRecommendation.minSph !== null
          ? String(editingRecommendation.minSph)
          : '',
        maxSph: editingRecommendation.maxSph !== undefined && editingRecommendation.maxSph !== null
          ? String(editingRecommendation.maxSph)
          : '',
        minCyl: editingRecommendation.minCyl !== undefined && editingRecommendation.minCyl !== null
          ? String(editingRecommendation.minCyl)
          : '',
        maxCyl: editingRecommendation.maxCyl !== undefined && editingRecommendation.maxCyl !== null
          ? String(editingRecommendation.maxCyl)
          : '',
        isActive: editingRecommendation.isActive !== false
      });
    }
  }, [editingRecommendation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        prescriptionType: formData.prescriptionType,
        name: formData.name.trim(),
        description: formData.description ? formData.description.trim() : '',
        price: Number(formData.price),
        estimatedDays: Number(formData.estimatedDays),
        order: formData.order === '' ? 0 : Number(formData.order),
        minSph: formData.minSph === '' ? null : Number(formData.minSph),
        maxSph: formData.maxSph === '' ? null : Number(formData.maxSph),
        minCyl: formData.minCyl === '' ? null : Number(formData.minCyl),
        maxCyl: formData.maxCyl === '' ? null : Number(formData.maxCyl)
      };

      if (editingRecommendation) {
        payload.isActive = formData.isActive;
        await axios.put(`/api/lens-recommendation-pricing/${editingRecommendation._id || editingRecommendation.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recommendation updated');
      } else {
        await axios.post('/api/lens-recommendation-pricing', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recommendation added');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving recommendation:', error);
      setError(error.response?.data?.message || 'Error saving recommendation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {error && (
        <div style={{ padding: '0.75rem', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b' }}>
          {error}
        </div>
      )}
      <DetailGrid>
        <DetailItem>
          <DetailLabel>{t('admin.prescriptionType')}</DetailLabel>
          <ConfirmInput
            as="select"
            value={formData.prescriptionType}
            onChange={(e) => setFormData({ ...formData, prescriptionType: e.target.value })}
            required
          >
            <option value="single_vision">{t('admin.singleVision')}</option>
            <option value="progressive">{t('admin.progressive')}</option>
          </ConfirmInput>
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('admin.name2')}</DetailLabel>
          <ConfirmInput
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </DetailItem>
        <DetailItem style={{ gridColumn: '1 / -1' }}>
          <DetailLabel>{t('admin.description')}</DetailLabel>
          <ConfirmInput
            as="textarea"
            rows="3"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('admin.optionalDescription')}
          />
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('admin.priceDzd')}</DetailLabel>
          <ConfirmInput
            type="number"
            min="0"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            required
          />
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('admin.estimatedTimeDays')}</DetailLabel>
          <ConfirmInput
            type="number"
            min="0"
            step="1"
            value={formData.estimatedDays}
            onChange={(e) => setFormData({ ...formData, estimatedDays: e.target.value })}
            required
          />
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('admin.displayOrder')}</DetailLabel>
          <ConfirmInput
            type="number"
            step="1"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: e.target.value })}
            placeholder="0"
          />
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('admin.minSph')}</DetailLabel>
          <ConfirmInput
            type="number"
            step="0.25"
            value={formData.minSph}
            onChange={(e) => setFormData({ ...formData, minSph: e.target.value })}
            placeholder={t('admin.noMinimum')}
          />
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('admin.maxSph')}</DetailLabel>
          <ConfirmInput
            type="number"
            step="0.25"
            value={formData.maxSph}
            onChange={(e) => setFormData({ ...formData, maxSph: e.target.value })}
            placeholder={t('admin.noMaximum')}
          />
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('admin.minCyl')}</DetailLabel>
          <ConfirmInput
            type="number"
            step="0.25"
            value={formData.minCyl}
            onChange={(e) => setFormData({ ...formData, minCyl: e.target.value })}
            placeholder={t('admin.noMinimum')}
          />
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('admin.maxCyl')}</DetailLabel>
          <ConfirmInput
            type="number"
            step="0.25"
            value={formData.maxCyl}
            onChange={(e) => setFormData({ ...formData, maxCyl: e.target.value })}
            placeholder={t('admin.noMaximum')}
          />
        </DetailItem>
        {editingRecommendation && (
          <DetailItem>
            <DetailLabel>{t('admin.status')}</DetailLabel>
            <ConfirmInput
              as="select"
              value={formData.isActive ? 'active' : 'inactive'}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
            >
              <option value="active">{t('admin.active')}</option>
              <option value="inactive">{t('admin.inactive')}</option>
            </ConfirmInput>
          </DetailItem>
        )}
      </DetailGrid>
      <ModalFooter>
        <CancelButton type="button" onClick={onClose} disabled={submitting}>
          {t('admin.cancel')}
        </CancelButton>
        <ConfirmDeleteButton
          type="submit"
          disabled={submitting}
          style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
        >
          {submitting ? (
            <>
              <SpinningIcon size={18} />
              {t('admin.saving')}
            </>
          ) : (
            <>
              <Plus size={18} />
              {editingRecommendation ? t('admin.updateRecommendation') : t('admin.addRecommendation')}
            </>
          )}
        </ConfirmDeleteButton>
      </ModalFooter>
    </form>
  );
};

const PEER_RATING_NOTE_SECTIONS_DEFAULT = 5;
const PEER_RATING_NOTE_SECTIONS_MAX = 30;

const PEER_RATING_WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const DEFAULT_PEER_RATING_CATEGORIES = () => {
  const line = (text) => {
    const slots = Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill('');
    slots[0] = text;
    return slots;
  };
  return [
    { name: 'Teamwork & collaboration', noteSections: line('How well they work with colleagues and support the team.'), coefficients: Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill(1) },
    { name: 'Customer service', noteSections: line('Attitude and care toward customers in the store.'), coefficients: Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill(1) },
    { name: 'Reliability & punctuality', noteSections: line('Shows up on time and follows through on commitments.'), coefficients: Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill(1) },
    { name: 'Communication', noteSections: line('Clear, respectful communication with peers and management.'), coefficients: Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill(1) },
    { name: 'Professional knowledge', noteSections: line('Product and optical knowledge applied on the floor.'), coefficients: Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill(1) }
  ];
};

function normalizeCategoryNoteSections(c) {
  if (Array.isArray(c.noteSections) && c.noteSections.length > 0) {
    return c.noteSections.map((s) => String(s ?? ''));
  }
  if (c.notes) {
    const slots = Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill('');
    slots[0] = String(c.notes);
    return slots;
  }
  return Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill('');
}

function normalizeCategoryCoefficients(category, fallbackCoefficients = []) {
  const noteCount = Math.max(
    normalizeCategoryNoteSections(category).length,
    Array.isArray(category?.coefficients) ? category.coefficients.length : 0,
    fallbackCoefficients.length,
    PEER_RATING_NOTE_SECTIONS_DEFAULT
  );
  const rawCoeffs = Array.isArray(category?.coefficients) ? category.coefficients : fallbackCoefficients;
  return Array.from({ length: noteCount }, (_, i) => Number(rawCoeffs[i]) || 1);
}

function getCategoryCoefficients(category, fallbackCoefficients = []) {
  if (Array.isArray(category?.coefficients) && category.coefficients.length) return category.coefficients;
  return fallbackCoefficients;
}

const PEER_RATING_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')}:00`
}));

function buildPeerRatingFormFromStore(store) {
  const pr = store?.peerRating;
  const rawCoeffs = Array.isArray(pr?.coefficients) ? pr.coefficients : [];
  const cats =
    pr?.categories?.length > 0
      ? pr.categories.map((c) => ({
          name: c.name || '',
          noteSections: normalizeCategoryNoteSections(c),
          coefficients: normalizeCategoryCoefficients(c, rawCoeffs)
        }))
      : DEFAULT_PEER_RATING_CATEGORIES().map((c) => ({
          name: c.name,
          noteSections: [...c.noteSections],
          coefficients: [...c.coefficients]
        }));
  const noteColCount = cats.length > 0 ? Math.max(...cats.map((c) => (c.noteSections || []).length)) : PEER_RATING_NOTE_SECTIONS_DEFAULT;
  const coefficients = Array.from({ length: noteColCount }, (_, i) => typeof rawCoeffs[i] === 'number' ? rawCoeffs[i] : 1);
  return {
    setupNote: pr?.setupNote || '',
    visibleWeekday: typeof pr?.visibleWeekday === 'number' ? pr.visibleWeekday : 1,
    visibleHourStart: typeof pr?.visibleHourStart === 'number' ? pr.visibleHourStart : 8,
    visibleHourEnd: typeof pr?.visibleHourEnd === 'number' ? pr.visibleHourEnd : 18,
    categories: cats,
    coefficients
  };
}

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = (() => {
    const sub = location.pathname.replace(/^\/admin\/dashboard\/?/, '');
    if (!sub) return 'overview';
    return sub;
  })();
  // Advisor accounts see only the sections the admin opened to them; a
  // section on "View" shows a view-only strip (the server refuses changes).
  const advisorSections = useMemo(() => advisorAccessFor(getStoredUser()), []);
  const canSeeTab = useCallback((tab) => !advisorSections || advisorSections[sectionOfTab(tab)] !== 'h', [advisorSections]);
  const hideUnlessVisible = (tab) => (canSeeTab(tab) ? undefined : { display: 'none' });
  const firstVisibleTab = ADVISOR_SECTIONS.find((tab) => canSeeTab(tab)) || 'overview';
  const advisorViewOnly = !!advisorSections && advisorSections[sectionOfTab(activeTab)] === 'v';
  useEffect(() => {
    if (!canSeeTab(activeTab)) navigate('/admin/dashboard/' + firstVisibleTab, { replace: true });
  }, [activeTab, canSeeTab, firstVisibleTab, navigate]);
  // (A change the server refuses as view-only is announced app-wide by
  // components/ViewOnlyNotice.)
  const [isStockOpen, setIsStockOpen] = useState(false);
  // Facilities sidebar group — expanded by default so the freshly-grouped
  // tabs (Retail Stores, Central Store, Labs, Administration) are
  // immediately visible after this refactor.
  const [isFacilitiesOpen, setIsFacilitiesOpen] = useState(true);
  const [storeLogo, setStoreLogo] = useState(null);
  const [storeName, setStoreName] = useState(null);
  // Logged-in user (for the topbar greeting + avatar). Populated from /api/auth/me.
  const [currentUser, setCurrentUser] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [orders, setOrders] = useState([]);
  // Orders arrive already bucketed by status from the server; store and seller
  // are narrowed client-side, since the whole bucket is in memory anyway.
  const [orderStoreFilter, setOrderStoreFilter] = useState('all');
  const [orderSellerFilter, setOrderSellerFilter] = useState('all');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop icon-only sidebar mode. Persisted so it sticks across reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('admin-sidebar-collapsed') === 'true';
  });
  // Light/dark theme — persisted to localStorage. Scoped to the admin shell.
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem('admin-theme');
    if (saved === 'dark' || saved === 'light') return saved;
    return 'light';
  });
  // Cmd+K command palette open state.
  const [paletteOpen, setPaletteOpen] = useState(false);
  // 14-day sparkline data for the KPI cards on the overview tab.
  const [sparklines, setSparklines] = useState({
    turnover: [], orders: [], lensesSold: [], framesSold: [], contactLensesSold: [],
  });

  // KPI cards behave like the old tree cards: clicking the header expands the
  // body to reveal a breakdown. One slot per card so each can open / close
  // independently.
  // Only one KPI is ever open: opening a card focuses it and hides its
  // siblings, so the breakdown gets the whole row. Clicking it again restores
  // the full strip.
  const [kpiOpen, setKpiOpen] = useState({ quantity: false, turnover: false, profit: false, visitors: false });
  const toggleKpi = useCallback((key) => {
    setKpiOpen(p => ({ quantity: false, turnover: false, profit: false, visitors: false, [key]: !p[key] }));
  }, []);
  const focusedKpi = Object.keys(kpiOpen).find(k => kpiOpen[k]) || null;

  // Path-based drill-down state inside the Quantity and Turnover KPI cards.
  // We keep one Set of "open" paths (slash-delimited) so any number of nodes
  // can be expanded simultaneously across an arbitrarily deep tree.
  // Examples of valid paths:
  //   "qty.lenses"
  //   "qty.lenses.sv"
  //   "qty.lenses.sv.intern"
  //   "qty.frames"
  //   "qty.frames.brand=Ray-Ban"
  //   "turnover.items"
  // Quantity card view: drill one branch at a time, or compare every store /
  // every seller in one table.
  const [qtyView, setQtyView] = useState('drill');
  // Rows ticked for head-to-head comparison. Keys are store ids in "stores"
  // mode and "<storeId>:<employeeId>" in "sellers" mode, so two people with
  // the same name at different stores stay distinct — which is the whole
  // point of being able to compare across stores.
  const [qtyCompareSel, setQtyCompareSel] = useState(() => new Set());
  // Ticking a row only stages it. The head-to-head panel renders from the
  // committed list, which is written when Compare is pressed — so the
  // selection can be built up (including across stores) without the panel
  // flickering between every click.
  const [qtyComparison, setQtyComparison] = useState([]);
  // Sellers mode narrows the picker to one store at a time. Picks survive a
  // change of filter, which is what makes a cross-store comparison possible.
  const [qtySellerStore, setQtySellerStore] = useState('all');
  const toggleCompareSel = useCallback((key) => {
    setQtyCompareSel(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const resetCompare = useCallback(() => {
    setQtyCompareSel(new Set());
    setQtyComparison([]);
  }, []);
  const [drillOpen, setDrillOpen] = useState(() => new Set());
  const isDrillOpen = useCallback((path) => drillOpen.has(path), [drillOpen]);
  // Focus one node among its siblings: opening it closes every sibling and
  // discards their sub-tree state, so re-entering a sibling later starts
  // collapsed instead of restoring a stale set of open descendants. Used by
  // the Quantity store/seller levels, which show one branch at a time.
  const focusDrill = useCallback((path, siblingPaths) => {
    setDrillOpen(prev => {
      const next = new Set(prev);
      const wasOpen = next.has(path);
      for (const open of Array.from(next)) {
        if (siblingPaths.some(sib => open === sib || open.startsWith(`${sib}.`))) next.delete(open);
      }
      if (!wasOpen) next.add(path);
      return next;
    });
  }, []);
  const toggleDrill = useCallback((path) => {
    setDrillOpen(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // Per-day total items sold = lenses + frames + contact lenses, matching the
  // headline number on the Quantity card. /sparklines returns plain numbers
  // per day (same as `turnover`), so sum them positionally — reading `.value`
  // off a number yields NaN, which is what flattened this line to zero.
  const quantitySeries = useMemo(() => {
    const series = [
      sparklines.lensesSold || [],
      sparklines.framesSold || [],
      sparklines.contactLensesSold || [],
    ];
    const len = Math.max(...series.map(a => a.length), 0);
    const out = [];
    for (let i = 0; i < len; i++) {
      out.push(series.reduce((sum, arr) => sum + (Number(arr[i]) || 0), 0));
    }
    return out;
  }, [sparklines.lensesSold, sparklines.framesSold, sparklines.contactLensesSold]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [allOrdersForCounts, setAllOrdersForCounts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeStats, setEmployeeStats] = useState([]);
  const [employeeStatsLoading, setEmployeeStatsLoading] = useState(false);
  const [employeeStatsRange, setEmployeeStatsRange] = useState('last7');
  const [employeeStatsStartDate, setEmployeeStatsStartDate] = useState('');
  const [employeeStatsEndDate, setEmployeeStatsEndDate] = useState('');
  const [employeeRateDetails, setEmployeeRateDetails] = useState(null);
  const [employeeStatsOpenStores, setEmployeeStatsOpenStores] = useState(() => new Set());
  // Salary calculator config (minimum wage + the bonus paid at each rate
  // threshold). This is chain-wide payroll policy, so it lives on the server
  // under `tenants.settings.salary`. localStorage is a cache, not the store of
  // record: it paints the table instantly on load and keeps the page usable if
  // the save fails, but the server copy always wins once it arrives — otherwise
  // two admins on two machines would compute different salaries.
  const SALARY_CACHE_KEY = 'admin-salary-config';
  const readSalaryCache = () => {
    try {
      const raw = localStorage.getItem(SALARY_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const [salaryMinimumWage, setSalaryMinimumWage] = useState(() => Number(readSalaryCache()?.minimumWage) || 0);
  const [salaryBonusMatrix, setSalaryBonusMatrix] = useState(() => ({
    completionRate: {},
    progressiveRate: {},
    recommendationRate: {},
    ...(readSalaryCache()?.bonusMatrix || {}),
  }));
  const [salarySaveState, setSalarySaveState] = useState('idle'); // idle | saving | saved | error
  const [salarySaveMessage, setSalarySaveMessage] = useState('');
  const [stores, setStores] = useState([]);
  const [labs, setLabs] = useState([]);
  const [adminLocations, setAdminLocations] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeStoreFilter, setEmployeeStoreFilter] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [storesLoading, setStoresLoading] = useState(false);
  const [labsLoading, setLabsLoading] = useState(false);
  const [adminLocationsLoading, setAdminLocationsLoading] = useState(false);
  // Central Store tab — the company's central warehouse + all employees
  // assigned to it (typically inventory + operations managers).
  const [centralStore, setCentralStore] = useState(null);
  const [centralStoreEmployees, setCentralStoreEmployees] = useState([]);
  const [centralStoreLoading, setCentralStoreLoading] = useState(false);
  const [showCentralContact, setShowCentralContact] = useState(false);
  const [sellByAlerts, setSellByAlerts] = useState([]);
  const [sellByLoading, setSellByLoading] = useState(false);
  const [overviewStartDate, setOverviewStartDate] = useState('');
  const [overviewEndDate, setOverviewEndDate] = useState('');
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [storeInsights, setStoreInsights] = useState(null);
  const [storeInsightsLoading, setStoreInsightsLoading] = useState(false);
  const [storeInsightsStart, setStoreInsightsStart] = useState('');
  const [storeInsightsEnd, setStoreInsightsEnd] = useState('');
  const [siExpandedStore, setSiExpandedStore] = useState(null);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  // Optional context passed to AddEmployeeForm so contextual entry points
  // (e.g. "Add Inventory Manager" on the Central Store tab) can lock the
  // role and pre-select the central warehouse store.
  const [addEmployeeContext, setAddEmployeeContext] = useState({});
  const [showEditEmployeeModal, setShowEditEmployeeModal] = useState(false);
  const [showAddStoreModal, setShowAddStoreModal] = useState(false);
  const [showAddCentralStoreModal, setShowAddCentralStoreModal] = useState(false);
  const [showEditStoreModal, setShowEditStoreModal] = useState(false);
  const [showAddLabModal, setShowAddLabModal] = useState(false);
  const [showEditLabModal, setShowEditLabModal] = useState(false);
  const [showAddAdminLocationModal, setShowAddAdminLocationModal] = useState(false);
  const [showEditAdminLocationModal, setShowEditAdminLocationModal] = useState(false);
  const [showDeleteEmployeeModal, setShowDeleteEmployeeModal] = useState(false);
  const [showDeleteStoreModal, setShowDeleteStoreModal] = useState(false);
  const [showDeleteLabModal, setShowDeleteLabModal] = useState(false);
  const [showDeleteAdminLocationModal, setShowDeleteAdminLocationModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);
  const [selectedAdminLocation, setSelectedAdminLocation] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [showBulkDeleteEmployeeModal, setShowBulkDeleteEmployeeModal] = useState(false);
  const [showBulkDeleteStoreModal, setShowBulkDeleteStoreModal] = useState(false);
  const [deletingEmployees, setDeletingEmployees] = useState(false);
  const [deletingStores, setDeletingStores] = useState(false);
  const [peerRatingStoreId, setPeerRatingStoreId] = useState('');
  const [peerRatingForm, setPeerRatingForm] = useState(() => ({
    setupNote: '',
    visibleWeekday: 1,
    visibleHourStart: 8,
    visibleHourEnd: 18,
    categories: DEFAULT_PEER_RATING_CATEGORIES().map((c) => ({
      name: c.name,
      noteSections: [...c.noteSections],
      coefficients: [...c.coefficients]
    })),
    coefficients: Array(PEER_RATING_NOTE_SECTIONS_DEFAULT).fill(1)
  }));
  const [peerRatingLoading, setPeerRatingLoading] = useState(false);
  const [peerRatingSaving, setPeerRatingSaving] = useState(false);
  const [peerRatingStatus, setPeerRatingStatus] = useState('');
  const [prResultsPeriods, setPrResultsPeriods] = useState([]);
  const [prResultsPeriod, setPrResultsPeriod] = useState('');
  const [prResultsSubmissions, setPrResultsSubmissions] = useState([]);
  const [prResultsLoading, setPrResultsLoading] = useState(false);
  const [prResultsEmployeeId, setPrResultsEmployeeId] = useState('');

  // Other Lens Products state
  const [otherLensProducts, setOtherLensProducts] = useState([]);
  const [otherLensLoading, setOtherLensLoading] = useState(false);
  const [showAddOtherLensModal, setShowAddOtherLensModal] = useState(false);
  const [showEditOtherLensModal, setShowEditOtherLensModal] = useState(false);
  const [selectedOtherLens, setSelectedOtherLens] = useState(null);
  const [otherLensForm, setOtherLensForm] = useState({
    name: '', frameType: '', frameColor: '', frameMaterial: '', lensType: '', lensCoating: '', price: ''
  });

  // ---------------------------------------------------------------
  // Lens recommendation MATRIX (group × sphere-sign × age bracket)
  // ---------------------------------------------------------------
  const [ageBrackets, setAgeBrackets] = useState([]);
  const [matrixRows, setMatrixRows] = useState([]);
  const [matrixLoading, setMatrixLoading] = useState(false);

  // Lens Catalog (single source of truth for price + delivery time per lens).
  const [lensCatalog, setLensCatalog] = useState([]);
  const [lensCatalogLoading, setLensCatalogLoading] = useState(false);
  // Catalog toolbar: search / dispo filter / sort (1,200-row table).
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogDispo, setCatalogDispo] = useState('all');
  const [catalogSort, setCatalogSort] = useState({ col: 'number', dir: 'asc' });
  const [newCatalogLens, setNewCatalogLens] = useState({ group: '', indexValue: '', commercialName: '', name: '', supplier: '', availability: '', price: '', number: '', estimatedDays: '' });

  // Lens Alternatives (drives the "Other" card on the prescription form).
  const [lensAlternativesRows, setLensAlternativesRows] = useState([]);
  const [alternativeZones, setAlternativeZones] = useState(FALLBACK_ALT_ZONES);
  const [lensAlternativesLoading, setLensAlternativesLoading] = useState(false);
  // Alternatives keep the Simple Vision / Progressive split (recommendations don't).
  // Collapse state for the three Recommendations-tab sections.
  const [openRecSections, setOpenRecSections] = useState({ matrix: true, alternatives: true, catalog: true });
  const toggleRecSection = (key) => setOpenRecSections(prev => ({ ...prev, [key]: !prev[key] }));
  // Which matrix cell's lens type-ahead picker is open, the typed query, and the
  // input's screen rect so the dropdown can float wide (outside the narrow cell).
  const [cellPicker, setCellPicker] = useState({ key: null, query: '', rect: null, index: 0 });
  const [showAltRowModal, setShowAltRowModal] = useState(false);
  const [altRowEditing, setAltRowEditing] = useState(null); // null = create
  const [altRowForm, setAltRowForm] = useState({
    prescriptionType: 'single_vision',
    groupPair: [],
    sphereSign: 'minus',
    // Two newline-separated lists, one per direction:
    //   up   → premium upgrades shown under "Other (Up)"
    //   down → cheaper alternatives shown under "Other (Down)"
    // `lensNamesText` (legacy single list) is kept as a fallback for old rows.
    lensNamesUpText: '',
    lensNamesDownText: '',
    lensNamesText: '',
    order: '',
  });

  // Bracket modals
  const [showBracketModal, setShowBracketModal] = useState(false);
  const [bracketEditing, setBracketEditing] = useState(null); // null = create
  const [bracketForm, setBracketForm] = useState({ label: '', minAge: '', maxAge: '', order: '' });

  // Matrix-row modals
  const [showMatrixRowModal, setShowMatrixRowModal] = useState(false);
  const [matrixRowEditing, setMatrixRowEditing] = useState(null); // null = create
  const [matrixRowForm, setMatrixRowForm] = useState({
    prescriptionType: 'single_vision',
    groupPair: [],
    sphereSign: 'minus',
    cells: {},
    order: '',
  });
  const formatPct = (v) => `${Math.round((v || 0) * 100)}%`;
  const rateVariant = (v) => {
    if (v >= 0.7) return 'good';
    if (v >= 0.4) return 'warn';
    return 'bad';
  };
  const salaryThresholds = useMemo(() => [30, 40, 50, 60, 70, 75, 80, 85, 90, 95, 100], []);
  const salaryRateRows = useMemo(() => ([
    { key: 'completionRate', label: 'Rate 1' },
    { key: 'progressiveRate', label: 'Rate 2' },
    { key: 'recommendationRate', label: 'Rate 3' },
  ]), []);
  // Server is the source of truth. Runs once when the tab opens.
  useEffect(() => {
    if (activeTab !== 'employee-statistics') return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get('/api/admin/settings', {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: ctrl.signal,
        });
        const salary = data?.settings?.salary;
        if (salary) {
          setSalaryMinimumWage(Number(salary.minimumWage) || 0);
          setSalaryBonusMatrix({
            completionRate: {},
            progressiveRate: {},
            recommendationRate: {},
            ...(salary.bonusMatrix || {}),
          });
          localStorage.setItem(SALARY_CACHE_KEY, JSON.stringify(salary));
        }
        // Migration 017 not run yet — the local copy is all there is.
        if (data && data.persisted === false) {
          setSalarySaveState('error');
          setSalarySaveMessage(data.message || '');
        }
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          console.warn('Salary settings fetch failed:', e.message);
        }
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Persist on change, debounced — typing in a cell fires per keystroke and
  // each save is a read-modify-write on the tenant row.
  const salarySaveTimer = useRef(null);
  const salaryLoadedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'employee-statistics') return;
    // Skip the very first pass so simply opening the tab does not write back
    // the values it just read.
    if (!salaryLoadedRef.current) { salaryLoadedRef.current = true; return; }

    const salary = { minimumWage: Number(salaryMinimumWage) || 0, bonusMatrix: salaryBonusMatrix };
    localStorage.setItem(SALARY_CACHE_KEY, JSON.stringify(salary));

    clearTimeout(salarySaveTimer.current);
    salarySaveTimer.current = setTimeout(async () => {
      setSalarySaveState('saving');
      try {
        const token = localStorage.getItem('token');
        await axios.put('/api/admin/settings', { salary }, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        setSalarySaveState('saved');
        setSalarySaveMessage('');
      } catch (e) {
        setSalarySaveState('error');
        setSalarySaveMessage(e.response?.data?.message || e.message);
      }
    }, 700);
    return () => clearTimeout(salarySaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaryMinimumWage, salaryBonusMatrix, activeTab]);

  const updateSalaryBonus = (rateKey, threshold, value) => {
    setSalaryBonusMatrix((prev) => ({
      ...prev,
      [rateKey]: {
        ...(prev[rateKey] || {}),
        [threshold]: value,
      },
    }));
  };
  const getBonusForRate = (rateValue, rateKey) => {
    const pct = Math.round((Number(rateValue) || 0) * 100);
    const matched = salaryThresholds
      .filter((threshold) => pct >= threshold)
      .sort((a, b) => b - a)[0];
    if (!matched) return 0;
    return Number(salaryBonusMatrix[rateKey]?.[matched]) || 0;
  };
  const calculateSalary = (rates = {}) => {
    const base = Number(salaryMinimumWage) || 0;
    const bonuses = {
      completionRate: getBonusForRate(rates.completionRate, 'completionRate'),
      progressiveRate: getBonusForRate(rates.progressiveRate, 'progressiveRate'),
      recommendationRate: getBonusForRate(rates.recommendationRate, 'recommendationRate'),
    };
    const bonusTotal = Object.values(bonuses).reduce((sum, value) => sum + value, 0);
    return { base, bonuses, bonusTotal, total: base + bonusTotal };
  };
  const employeeStatsRatesFromCounts = (counts = {}) => ({
    completionRate: (counts.nonSeniorVisitors || 0) > 0
      ? (counts.paidNonSeniorClients || 0) / counts.nonSeniorVisitors
      : 0,
    progressiveRate: (counts.people43Plus || 0) > 0
      ? (counts.progressiveSales || 0) / counts.people43Plus
      : 0,
    recommendationRate: (counts.paidClients || 0) > 0
      ? (counts.recommendationSales || 0) / counts.paidClients
      : 0,
  });
  const makeStatsEntity = ({ employee, counts, rates }) => ({ employee, counts, rates });
  const buildRateDetails = (row, type) => {
    const emp = row.employee || {};
    const c = row.counts || {};
    const r = row.rates || {};
    const employeeName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.employeeId || 'Employee';
    const configs = {
      completion: {
        title: 'Realization rate',
        value: r.completionRate,
        numeratorLabel: 'Paid non-senior clients',
        denominatorLabel: 'Non-senior visits',
        numerator: c.paidNonSeniorClients || 0,
        denominator: c.nonSeniorVisitors || c.visitors || 0,
        formula: 'Paid non-senior clients ÷ non-senior visits',
      },
      progressive: {
        title: 'Progressive 43+ rate',
        value: r.progressiveRate,
        numeratorLabel: 'Progressive sales',
        denominatorLabel: '43+ people',
        numerator: c.progressiveSales || 0,
        denominator: c.people43Plus || 0,
        formula: 'Progressive sales ÷ people aged 43+',
      },
      recommendation: {
        title: 'Advice rate',
        value: r.recommendationRate,
        numeratorLabel: 'Advice sales',
        denominatorLabel: 'Paid clients',
        numerator: c.recommendationSales || 0,
        denominator: c.paidClients || c.sales || 0,
        formula: 'Advice sales ÷ paid clients',
      },
    };
    return { employeeName, ...configs[type] };
  };
  const employeeStatsTotals = useMemo(() => {
    return employeeStats.reduce((acc, row) => {
      const c = row.counts || {};
      acc.paidClients += c.paidClients || c.sales || 0;
      acc.visits += c.nonSeniorVisitors || c.visitors || 0;
      acc.progressiveSales += c.progressiveSales || 0;
      acc.recommendationSales += c.recommendationSales || 0;
      return acc;
    }, { paidClients: 0, visits: 0, progressiveSales: 0, recommendationSales: 0 });
  }, [employeeStats]);
  const employeeStatsByStore = useMemo(() => {
    const map = new Map();
    employeeStats.forEach((row) => {
      const emp = row.employee || {};
      const storeKey = emp.store?.id || emp.store?._id || emp.store?.code || 'unassigned';
      const storeName = emp.store?.name || emp.store?.code || 'Unassigned store';
      if (!map.has(storeKey)) {
        map.set(storeKey, {
          key: storeKey,
          name: storeName,
          code: emp.store?.code || '',
          employees: [],
          counts: {
            paidClients: 0,
            sales: 0,
            paidNonSeniorClients: 0,
            nonSeniorVisitors: 0,
            visitors: 0,
            progressiveSales: 0,
            people43Plus: 0,
            recommendationSales: 0,
          },
        });
      }
      const group = map.get(storeKey);
      const c = row.counts || {};
      group.employees.push(row);
      group.counts.paidClients += c.paidClients || c.sales || 0;
      group.counts.sales = group.counts.paidClients;
      group.counts.paidNonSeniorClients += c.paidNonSeniorClients || 0;
      group.counts.nonSeniorVisitors += c.nonSeniorVisitors || c.visitors || 0;
      group.counts.visitors = group.counts.nonSeniorVisitors;
      group.counts.progressiveSales += c.progressiveSales || 0;
      group.counts.people43Plus += c.people43Plus || 0;
      group.counts.recommendationSales += c.recommendationSales || 0;
    });
    return Array.from(map.values())
      .map((group) => ({
        ...group,
        employee: {
          firstName: group.name,
          lastName: '',
          employeeId: `${group.employees.length} employee${group.employees.length === 1 ? '' : 's'}`,
          role: 'store total',
          store: { name: group.name, code: group.code },
        },
        rates: employeeStatsRatesFromCounts(group.counts),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employeeStats]);
  const toggleEmployeeStatsStore = (storeKey) => {
    setEmployeeStatsOpenStores((prev) => {
      const next = new Set(prev);
      if (next.has(storeKey)) next.delete(storeKey);
      else next.add(storeKey);
      return next;
    });
  };

  // ── Theme persistence: save toggle changes back to localStorage. ──
  useEffect(() => {
    try { localStorage.setItem('admin-theme', theme); } catch (e) { /* ignore quota errors */ }
  }, [theme]);

  // ── Sidebar collapse persistence. ──
  useEffect(() => {
    try { localStorage.setItem('admin-sidebar-collapsed', String(sidebarCollapsed)); } catch (e) { /* ignore */ }
  }, [sidebarCollapsed]);

  // ── Sparkline data for the KPI cards (last 14 days, refreshed when the
  //    overview tab becomes active). ──
  useEffect(() => {
    if (activeTab !== 'overview') return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/analytics/sparklines?days=14', {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: ctrl.signal,
        });
        setSparklines({
          turnover: res.data?.turnover || [],
          orders: res.data?.orders || [],
          lensesSold: res.data?.lensesSold || [],
          framesSold: res.data?.framesSold || [],
          contactLensesSold: res.data?.contactLensesSold || [],
        });
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          console.warn('Sparkline fetch failed:', e.message);
        }
      }
    })();
    return () => ctrl.abort();
  }, [activeTab]);

  // ── Global keyboard shortcuts:
  //    Cmd/Ctrl+K → toggle command palette
  //    /          → open palette (when not typing in a field)
  //    [          → toggle sidebar collapse (desktop)
  //    Cmd+J      → toggle dark mode
  // ──
  useEffect(() => {
    const isTyping = () => {
      const a = document.activeElement;
      if (!a) return false;
      const tag = a.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || a.isContentEditable;
    };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        setTheme((t) => {
          const next = t === 'dark' ? 'light' : 'dark';
          toast.info(`${next === 'dark' ? 'Dark' : 'Light'} mode enabled`, { duration: 1500 });
          return next;
        });
        return;
      }
      if (e.key === '/' && !isTyping()) {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (e.key === '[' && !isTyping() && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setSidebarCollapsed((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Helper to flip the theme — also toasts so the change feels acknowledged.
  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      toast.info(`${next === 'dark' ? 'Dark' : 'Light'} mode enabled`, { duration: 1800 });
      return next;
    });
  }, []);

  useEffect(() => {
    fetchOverviewData('', '');
    // Get store logo and name from user data
    const fetchUserStoreInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.user) {
          setCurrentUser(response.data.user);
        }
        if (response.data.user?.store) {
          if (response.data.user.store.logo) {
            setStoreLogo(response.data.user.store.logo);
          }
          if (response.data.user.store.name) {
            setStoreName(response.data.user.store.name);
          }
        }
      } catch (error) {
        console.error('Error fetching user store info:', error);
      }
    };
    fetchUserStoreInfo();
  }, []);

  useEffect(() => {
    if (activeTab === 'orders') {
      // One all-orders download feeds both the tab counts and (on "All") the
      // list — it is several MB, and fetching it twice doubled the load time.
      const allOrdersRequest = loadAllOrders();
      fetchOrders(allOrdersRequest);
      fetchAllOrdersForCounts(allOrdersRequest);
      // The store/seller filters offer the full rosters, so a store with no
      // orders in the current bucket is still selectable.
      if (stores.length === 0) fetchStores();
      if (employees.length === 0) fetchEmployees();
    } else if (activeTab === 'overview') {
      fetchSellByAlerts();
      fetchOverviewData(overviewStartDate, overviewEndDate);
    } else if (activeTab === 'employees') {
      fetchEmployees();
      // Also fetch stores / labs / admin locations for the employee form so
      // role-conditional dropdowns are populated when the modal opens.
      if (stores.length === 0) {
        fetchStores();
      }
      if (labs.length === 0) {
        fetchLabs();
      }
      if (adminLocations.length === 0) {
        fetchAdminLocations();
      }
      // Clear selections when switching tabs
      setSelectedEmployees([]);
    } else if (activeTab === 'store-analytics') {
      fetchStoreInsights(storeInsightsStart, storeInsightsEnd);
    } else if (activeTab === 'employee-statistics') {
      fetchEmployeeStats();
    } else if (activeTab === 'stores') {
      fetchStores();
      // Clear selections when switching tabs
      setSelectedStores([]);
    } else if (activeTab === 'central-store') {
      // Always pull a fresh snapshot — the central warehouse can change
      // (promotion/demotion happens on the Stores tab) and its staff
      // roster is the whole point of this view.
      fetchStores();
      fetchCentralStore();
    } else if (activeTab === 'recommendations') {
      fetchMatrixData();
      fetchLensAlternatives();
    } else if (activeTab === 'labs') {
      fetchLabs();
      if (employees.length === 0) {
        fetchEmployees();
      }
    } else if (activeTab === 'administration') {
      fetchAdminLocations();
      if (employees.length === 0) {
        fetchEmployees();
      }
    } else if (activeTab === 'peer-ratings') {
      if (stores.length === 0) {
        fetchStores();
      }
      if (employees.length === 0) {
        fetchEmployees();
      }
    } else if (activeTab === 'other-lenses') {
      fetchOtherLensProducts();
    }
  }, [activeTab, orderFilter]);

  const loadAllOrders = () => axios.get('/api/admin/all-orders', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchAllOrdersForCounts = async (allOrdersRequest = loadAllOrders()) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Fetch both real orders and the incomplete-orders feed (which includes
      // prescription-only entries) so the Unsuccessful tab count is correct.
      const [ordersRes, incompleteRes] = await Promise.all([
        allOrdersRequest,
        axios.get('/api/admin/incomplete-orders', { headers }).catch(() => ({ data: { incompleteOrders: [] } })),
      ]);
      const orders = ordersRes.data.orders || [];
      const incomplete = incompleteRes.data.incompleteOrders || [];
      // Dedupe by id — real orders already in `orders` may also appear in incomplete
      const knownIds = new Set(orders.map(o => o._id || o.id));
      const extras = incomplete.filter(i => !knownIds.has(i._id || i.id));
      setAllOrdersForCounts([...orders, ...extras]);
    } catch (error) {
      console.error('Error fetching all orders for counts:', error);
    }
  };

  useEffect(() => {
    if (activeTab !== 'employee-statistics') return;
    fetchEmployeeStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeStatsRange, employeeStatsStartDate, employeeStatsEndDate]);

  const fetchEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out admin users from the list
      const nonAdminUsers = (response.data.users || []).filter(user => user.role !== 'admin');
      setEmployees(nonAdminUsers);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(`Error fetching employees: ${error.response?.data?.message || error.message}`);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchEmployeeStats = async () => {
    setEmployeeStatsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ range: employeeStatsRange });
      if (employeeStatsRange === 'custom') {
        if (employeeStatsStartDate) params.set('startDate', employeeStatsStartDate);
        if (employeeStatsEndDate) params.set('endDate', employeeStatsEndDate);
      }
      const response = await axios.get(`/api/admin/employee-stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeStats(response.data.rows || []);
    } catch (error) {
      console.error('Error fetching employee stats:', error);
      setEmployeeStats([]);
    } finally {
      setEmployeeStatsLoading(false);
    }
  };

  const fetchStores = async () => {
    setStoresLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data.stores || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error(`Error fetching stores: ${error.response?.data?.message || error.message}`);
    } finally {
      setStoresLoading(false);
    }
  };

  // Fetch the central warehouse and the staff assigned to it. Backed by
  // GET /api/stores/central — returns `centralStore: null` when no store
  // has been promoted yet, which the UI uses to render an empty state.
  const fetchCentralStore = async () => {
    setCentralStoreLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/stores/central', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCentralStore(response.data?.centralStore || null);
      setCentralStoreEmployees(response.data?.employees || []);
    } catch (error) {
      console.error('Error fetching central store:', error);
      toast.error(`Error loading central store: ${error.response?.data?.message || error.message}`);
    } finally {
      setCentralStoreLoading(false);
    }
  };

  // Mark / unmark a store as the company's central warehouse. Only one
  // store can be central at a time — the server enforces this with a
  // partial-unique index, but we also do an optimistic local update so
  // the badges flip immediately without waiting for a refetch.
  const toggleCentralWarehouse = async (store) => {
    if (!store) return;
    const id = store._id || store.id;
    const wantsCentral = !store.isCentralWarehouse;

    if (wantsCentral) {
      const existing = stores.find(
        (s) => s.isCentralWarehouse && (s._id || s.id) !== id
      );
      const ok = window.confirm(
        existing
          ? `"${existing.name}" is currently the central warehouse. Switch this title to "${store.name}"?`
          : `Mark "${store.name}" as the central warehouse?`
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(`Remove "${store.name}" as the central warehouse?`);
      if (!ok) return;
    }

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      formDataToSend.append('isCentralWarehouse', wantsCentral ? 'true' : 'false');
      const response = await axios.put(`/api/stores/${id}`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const updated = response.data?.store;
      setStores((prev) => prev.map((s) => {
        const sid = s._id || s.id;
        if (sid === id) return updated || { ...s, isCentralWarehouse: wantsCentral };
        // If we just promoted a different store to central, demote any
        // others client-side so only one badge shows until the next
        // fetch confirms the canonical state.
        if (wantsCentral && s.isCentralWarehouse) return { ...s, isCentralWarehouse: false };
        return s;
      }));
      toast.success(wantsCentral
        ? `"${store.name}" is now the central warehouse`
        : `"${store.name}" is no longer the central warehouse`);
      // Refresh the Central Store tab if the user happens to have it
      // loaded — the promotion flips which store appears there.
      fetchCentralStore();
    } catch (error) {
      console.error('Error toggling central warehouse:', error);
      toast.error(error.response?.data?.message || 'Could not update the central warehouse setting');
    }
  };

  const fetchPeerRatingForStore = async (storeId) => {
    if (!storeId) return;
    setPeerRatingLoading(true);
    setPeerRatingStatus('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPeerRatingForm(buildPeerRatingFormFromStore(response.data.store));
    } catch (error) {
      console.error('Error loading peer rating settings:', error);
      toast.error(`Error loading peer rating settings: ${error.response?.data?.message || error.message}`);
    } finally {
      setPeerRatingLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'peer-ratings' || stores.length === 0) return;
    if (!peerRatingStoreId) {
      setPeerRatingStoreId(stores[0]._id || stores[0].id);
    }
  }, [activeTab, stores, peerRatingStoreId]);

  useEffect(() => {
    if (activeTab !== 'peer-ratings' || !peerRatingStoreId) return;
    fetchPeerRatingForStore(peerRatingStoreId);
  }, [activeTab, peerRatingStoreId]);

  useEffect(() => {
    setPrResultsEmployeeId('');
  }, [peerRatingStoreId]);

  // Fetch available periods when store changes in peer-ratings tab
  useEffect(() => {
    if (activeTab !== 'peer-ratings' || !peerRatingStoreId) return;
    const fetchPeriods = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`/api/peer-ratings/admin/periods?storeId=${peerRatingStoreId}&_=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const periods = resp.data.periods || [];
        setPrResultsPeriods(periods);
        if (periods.length > 0 && !periods.includes(prResultsPeriod)) {
          setPrResultsPeriod(periods[0]);
        }
      } catch (err) {
        console.error('Error fetching peer rating periods:', err);
      }
    };
    fetchPeriods();
  }, [activeTab, peerRatingStoreId]);

  // Fetch submissions for the selected period
  useEffect(() => {
    if (!prResultsPeriod || !peerRatingStoreId) return;
    const fetchResults = async () => {
      setPrResultsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          storeId: peerRatingStoreId,
          period: prResultsPeriod,
          _: String(Date.now())
        });
        if (prResultsEmployeeId) params.set('employeeId', prResultsEmployeeId);
        const resp = await axios.get(`/api/peer-ratings/admin/results?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPrResultsSubmissions(resp.data.submissions || []);
      } catch (err) {
        console.error('Error fetching peer rating results:', err);
      } finally {
        setPrResultsLoading(false);
      }
    };
    fetchResults();
  }, [prResultsPeriod, peerRatingStoreId, prResultsEmployeeId]);

  const savePeerRatingSettings = async () => {
    const id = peerRatingStoreId || (stores[0]?._id || stores[0]?.id);
    if (!id) return;
    const emptyName = peerRatingForm.categories.findIndex((c) => !String(c.name || '').trim());
    if (emptyName !== -1) {
      toast.warning(`Category ${emptyName + 1}: please enter a name.`);
      return;
    }
    const emptyNotesIdx = peerRatingForm.categories.findIndex(
      (c) => !(c.noteSections || []).some((s) => String(s || '').trim())
    );
    if (emptyNotesIdx !== -1) {
      toast.warning(`Category ${emptyNotesIdx + 1}: add at least one note section with text.`);
      return;
    }
    setPeerRatingSaving(true);
    setPeerRatingStatus('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        setupNote: peerRatingForm.setupNote,
        visibleWeekday: Number(peerRatingForm.visibleWeekday),
        visibleHourStart: Number(peerRatingForm.visibleHourStart),
        visibleHourEnd: Number(peerRatingForm.visibleHourEnd),
        categories: peerRatingForm.categories.map((c) => ({
          name: String(c.name || '').trim(),
          noteSections: (c.noteSections || []).map((s) => String(s ?? '')),
          coefficients: getCategoryCoefficients(c, peerRatingForm.coefficients).map((v) => Number(v) || 1)
        })),
        coefficients: (peerRatingForm.coefficients || []).map((v) => Number(v) || 1)
      };
      const response = await axios.put(`/api/stores/${id}/peer-rating`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const saved = response.data.store;
      if (saved) {
        if (!peerRatingStoreId) setPeerRatingStoreId(id);
        setStores((prev) =>
          prev.map((s) => {
            const sid = s._id || s.id;
            if (String(sid) === String(id) || String(sid) === String(saved._id)) {
              return { ...s, peerRating: saved.peerRating };
            }
            return s;
          })
        );
        setPeerRatingForm(buildPeerRatingFormFromStore(saved));
      }
      setPeerRatingStatus('Settings saved. Employees use scores from 0 to 5 in steps of 0.25 for each category.');
    } catch (error) {
      console.error('Error saving peer rating settings:', error);
      toast.error(`Error saving: ${error.response?.data?.message || error.message}`);
    } finally {
      setPeerRatingSaving(false);
    }
  };

  const fetchLabs = async () => {
    setLabsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/labs', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabs(response.data.labs || []);
    } catch (error) {
      console.error('Error fetching labs:', error);
      toast.error(`Error fetching labs: ${error.response?.data?.message || error.message}`);
    } finally {
      setLabsLoading(false);
    }
  };

  const fetchAdminLocations = async () => {
    setAdminLocationsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin-locations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminLocations(response.data.adminLocations || []);
    } catch (error) {
      console.error('Error fetching administration locations:', error);
      toast.error(`Error fetching administration locations: ${error.response?.data?.message || error.message}`);
    } finally {
      setAdminLocationsLoading(false);
    }
  };

  const fetchOtherLensProducts = async () => {
    setOtherLensLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/other-lens-options', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // catalogRouter only wraps the payload for lens_products/frame_products;
      // every other catalogue — this one included — answers with a bare array.
      // Reading `.options` off an array is undefined, so the list stayed empty
      // however many options were saved.
      setOtherLensProducts(Array.isArray(response.data) ? response.data : (response.data?.items || []));
    } catch (error) {
      console.error('Error fetching other lens products:', error);
    } finally {
      setOtherLensLoading(false);
    }
  };

  const handleSaveOtherLens = async () => {
    if (!otherLensForm.name.trim()) {
      toast.warning('Product name is required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (showEditOtherLensModal && selectedOtherLens) {
        await axios.patch(`/api/other-lens-options/${selectedOtherLens._id}`, otherLensForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/other-lens-options', otherLensForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowAddOtherLensModal(false);
      setShowEditOtherLensModal(false);
      setSelectedOtherLens(null);
      setOtherLensForm({ name: '', frameType: '', frameColor: '', frameMaterial: '', lensType: '', lensCoating: '', price: '' });
      fetchOtherLensProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving product');
    }
  };

  const handleDeleteOtherLens = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/other-lens-options/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOtherLensProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting product');
    }
  };

  // ---------------------------------------------------------------
  // Recommendation matrix — fetch / save / delete
  // ---------------------------------------------------------------

  const fetchMatrixData = async () => {
    setMatrixLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/lens-recommendation-matrix', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setMatrixRows(response.data.rows || []);
      setAgeBrackets(response.data.brackets || []);
      // Pull catalog right after the matrix so newly typed lens names get
      // synced into the catalog table on the same render.
      fetchLensCatalog();
    } catch (error) {
      console.error('Error fetching recommendation matrix:', error);
      toast.error(`Error fetching matrix: ${error.response?.data?.message || error.message}`);
    } finally {
      setMatrixLoading(false);
    }
  };

  // ---- Lens Catalog (price + delivery time per unique lens) -------

  const fetchLensCatalog = async () => {
    setLensCatalogLoading(true);
    try {
      const token = localStorage.getItem('token');
      // sync=1 (the default) auto-creates catalog rows for any lens names
      // referenced in the matrix that don't have a catalog entry yet.
      const response = await axios.get('/api/lens-catalog?sync=1', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setLensCatalog(response.data.items || []);
    } catch (error) {
      console.error('Error fetching lens catalog:', error);
    } finally {
      setLensCatalogLoading(false);
    }
  };

  // ---- Lens Alternatives (Other-card source) ----------------------

  const fetchLensAlternatives = async () => {
    setLensAlternativesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/lens-alternatives', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setLensAlternativesRows(response.data.rows || []);
      if (Array.isArray(response.data.zones) && response.data.zones.length) {
        setAlternativeZones(response.data.zones);
      }
    } catch (error) {
      console.error('Error fetching lens alternatives:', error);
    } finally {
      setLensAlternativesLoading(false);
    }
  };

  const openCreateAltRow = () => {
    setAltRowEditing(null);
    setAltRowForm({
      // The sheet shows both types at once now, so a new row starts on
      // single vision and the modal's own selector moves it.
      prescriptionType: 'single_vision',
      groupPair: [],
      sphereSign: 'minus',
      lensNamesUpText: '',
      lensNamesDownText: '',
      lensNamesText: '',
      order: String(lensAlternativesRows.length),
    });
    setShowAltRowModal(true);
  };

  const openEditAltRow = (row) => {
    setAltRowEditing(row);
    setAltRowForm({
      prescriptionType: row.prescriptionType || 'single_vision',
      groupPair: Array.isArray(row.groupPair) ? [...row.groupPair] : [],
      sphereSign: row.sphereSign || 'minus',
      lensNamesUpText:   (row.lensNamesUp   || []).join('\n'),
      lensNamesDownText: (row.lensNamesDown || []).join('\n'),
      lensNamesText:     (row.lensNames     || []).join('\n'),
      order: row.order ?? '',
    });
    setShowAltRowModal(true);
  };

  const toggleGroupInAltForm = (label) => {
    setAltRowForm(prev => {
      const has = prev.groupPair.includes(label);
      return {
        ...prev,
        groupPair: has ? prev.groupPair.filter(g => g !== label) : [...prev.groupPair, label],
      };
    });
  };

  const saveAltRow = async () => {
    const { prescriptionType, groupPair, sphereSign, lensNamesUpText, lensNamesDownText, lensNamesText, order } = altRowForm;
    if (!groupPair || groupPair.length === 0) {
      toast.warning('Pick at least one lens group for this row.');
      return;
    }
    const splitLines = (txt) => (txt || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
    const lensNamesUp   = splitLines(lensNamesUpText);
    const lensNamesDown = splitLines(lensNamesDownText);
    const lensNames     = splitLines(lensNamesText);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        prescriptionType: prescriptionType || 'single_vision',
        groupPair, sphereSign,
        lensNamesUp, lensNamesDown, lensNames,
        order: order === '' ? 0 : Number(order),
      };
      if (altRowEditing) {
        await axios.patch(`/api/lens-alternatives/${altRowEditing._id}`, payload, { headers });
      } else {
        await axios.post('/api/lens-alternatives', payload, { headers });
      }
      setShowAltRowModal(false);
      setAltRowEditing(null);
      fetchLensAlternatives();
      // Catalog auto-creates entries for any new lens names typed here.
      fetchLensCatalog();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving alternatives row');
    }
  };

  const deleteAltRow = async (row) => {
    const label = `${displayCorrectionGroupPair(row.groupPair) || 'row'} (${row.sphereSign})`;
    if (!window.confirm(`Delete alternatives row "${label}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/lens-alternatives/${row._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchLensAlternatives();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting row');
    }
  };

  // Inline add/remove of catalog-linked lenses on an alternatives row's
  // Up/Down list (stores catalog ids, like the recommendation matrix).
  const persistAltLists = async (row, patch) => {
    const next = { ...row, ...patch };
    setLensAlternativesRows(prev => prev.map(r => (r._id === row._id ? next : r)));
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/lens-alternatives/${row._id}`, {
        prescriptionType: next.prescriptionType || 'single_vision',
        groupPair: next.groupPair,
        sphereSign: next.sphereSign,
        zoneId: next.zoneId ?? null,
        lensNamesUp: next.lensNamesUp || [],
        lensNamesDown: next.lensNamesDown || [],
        lensNames: next.lensNames || [],
        order: next.order === '' || next.order == null ? 0 : Number(next.order),
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving alternatives');
      fetchLensAlternatives();
    }
  };

  // ── Zone-aware editing (copy-on-write) ──────────────────────────────
  // A cell shows either its own zone row or, when that zone has never been
  // customised, the un-zoned row that covers every age. Editing an inherited
  // cell must not rewrite the shared row and silently change the other three
  // zones — so the first edit forks a zone row seeded from what was inherited,
  // and later edits patch that fork.
  const writeAltZoneList = async (cell, side, nextList) => {
    const key = side === 'up' ? 'lensNamesUp' : 'lensNamesDown';
    if (cell.row && cell.row.zoneId === cell.zoneId) {
      persistAltLists(cell.row, { [key]: nextList });
      return;
    }
    const base = cell.row || {};
    const forked = {
      prescriptionType: base.prescriptionType || cell.prescriptionType || 'single_vision',
      groupPair: cell.groups,
      sphereSign: cell.sign,
      zoneId: cell.zoneId,
      lensNamesUp: base.lensNamesUp || [],
      lensNamesDown: base.lensNamesDown || [],
      lensNames: [],
      order: Number(base.order || 0),
      [key]: nextList,
    };
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post('/api/lens-alternatives', forked, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLensAlternativesRows(prev => [...prev, data]);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating zone row');
      fetchLensAlternatives();
    }
  };

  const addLensToAltCell = (cell, side, id) => {
    if (!id) return;
    const key = side === 'up' ? 'lensNamesUp' : 'lensNamesDown';
    const cur = cell.row?.[key] || [];
    if (cur.some(x => String(x).toLowerCase() === String(id).toLowerCase())) return;
    writeAltZoneList(cell, side, [...cur, id]);
  };

  const removeLensFromAltCell = (cell, side, id) => {
    const key = side === 'up' ? 'lensNamesUp' : 'lensNamesDown';
    const cur = cell.row?.[key] || [];
    writeAltZoneList(cell, side, cur.filter(x => String(x) !== String(id)));
  };

  // Drop a zone's override so the cell goes back to inheriting the un-zoned row.
  const resetAltZone = async (cell) => {
    if (!cell.row || cell.row.zoneId !== cell.zoneId) return;
    const label = `${displayCorrectionGroupPair(cell.groups)} · ${cell.zoneLabel}`;
    if (!window.confirm(`Reset "${label}" to the all-ages list?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/lens-alternatives/${cell.row._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLensAlternativesRows(prev => prev.filter(r => r._id !== cell.row._id));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error resetting zone');
      fetchLensAlternatives();
    }
  };

  // Optimistic local update — controlled inputs in the catalog table.
  const setCatalogLocal = (id, patch) => {
    setLensCatalog(prev => prev.map(item => (item._id === id ? { ...item, ...patch } : item)));
  };

  const persistCatalogEntry = async (id, patch) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/lens-catalog/${id}`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error('Catalog save failed:', error);
      toast.error(error.response?.data?.message || 'Error saving catalog entry — refresh to see latest data.');
      fetchLensCatalog();
    }
  };

  const addCatalogEntry = async () => {
    const realName = (newCatalogLens.name || '').trim();
    const supplier = (newCatalogLens.supplier || '').trim();
    // Seed the merged Nom réel & Fournisseur list with the first pair (if any);
    // more can be added per-row in the table afterwards.
    const suppliers = (realName || supplier) ? [{ realName, supplier }] : [];
    if (!realName && !(newCatalogLens.commercialName || '').trim()) {
      toast.warning('Enter a commercial name or a real name.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/lens-catalog', {
        group: newCatalogLens.group,
        indexValue: newCatalogLens.indexValue,
        commercialName: (newCatalogLens.commercialName || '').trim(),
        name: realName,
        suppliers,
        availability: newCatalogLens.availability,
        price: newCatalogLens.price === '' ? null : Number(newCatalogLens.price),
        number: newCatalogLens.number === '' ? null : Number(newCatalogLens.number),
        estimatedDays: newCatalogLens.estimatedDays === '' ? null : Number(newCatalogLens.estimatedDays),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewCatalogLens({ group: '', indexValue: '', commercialName: '', name: '', supplier: '', availability: '', price: '', number: '', estimatedDays: '' });
      // Clearing the form was the only feedback, which read as "nothing
      // happened" — the row lands in a long list, so confirm it explicitly.
      toast.success(`Added "${(newCatalogLens.commercialName || realName).trim()}" to the catalog.`);
      fetchLensCatalog();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error adding lens to catalog');
    }
  };

  const deleteCatalogEntry = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from the catalog?\n\nThis only removes the price/time entry — matrix cells that use this lens name keep working.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/lens-catalog/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchLensCatalog();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting catalog entry');
    }
  };

  // ---- Age brackets ----------------------------------------------

  const openCreateBracket = () => {
    setBracketEditing(null);
    setBracketForm({
      label: '',
      minAge: '',
      maxAge: '',
      order: String(ageBrackets.length),
    });
    setShowBracketModal(true);
  };

  const openEditBracket = (bracket) => {
    setBracketEditing(bracket);
    setBracketForm({
      label: bracket.label || '',
      minAge: bracket.minAge ?? '',
      maxAge: bracket.maxAge ?? '',
      order: bracket.order ?? '',
    });
    setShowBracketModal(true);
  };

  const saveBracket = async () => {
    const { label, minAge, maxAge, order } = bracketForm;
    if (!label || minAge === '' || maxAge === '') {
      toast.warning('Label, min age and max age are required.');
      return;
    }
    if (Number(maxAge) < Number(minAge)) {
      toast.warning('Max age must be ≥ min age.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        label: label.trim(),
        minAge: Number(minAge),
        maxAge: Number(maxAge),
        order: order === '' ? 0 : Number(order),
      };
      if (bracketEditing) {
        await axios.patch(`/api/age-brackets/${bracketEditing._id}`, payload, { headers });
      } else {
        await axios.post('/api/age-brackets', payload, { headers });
      }
      setShowBracketModal(false);
      setBracketEditing(null);
      fetchMatrixData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving age bracket');
    }
  };

  const deleteBracket = async (bracket) => {
    if (!window.confirm(`Delete age bracket "${bracket.label}"? Cells in this column will be cleared.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/age-brackets/${bracket._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchMatrixData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting bracket');
    }
  };

  // ---- Matrix rows -----------------------------------------------

  const openCreateMatrixRow = () => {
    setMatrixRowEditing(null);
    setMatrixRowForm({
      // Unified table — single canonical prescription type.
      prescriptionType: 'single_vision',
      groupPair: [],
      sphereSign: 'minus',
      cells: {},
      order: String(matrixRows.length),
    });
    setShowMatrixRowModal(true);
  };

  const openEditMatrixRow = (row) => {
    setMatrixRowEditing(row);
    // `cells` from the API is a plain object keyed by bracket id.
    const cells = {};
    if (row.cells) {
      Object.entries(row.cells).forEach(([k, v]) => {
        cells[k] = {
          lensName: v?.lensName || '',
          price: v?.price ?? '',
          estimatedDays: v?.estimatedDays ?? '',
        };
      });
    }
    setMatrixRowForm({
      prescriptionType: row.prescriptionType || 'single_vision',
      groupPair: Array.isArray(row.groupPair) ? [...row.groupPair] : [],
      sphereSign: row.sphereSign || 'minus',
      cells,
      order: row.order ?? '',
    });
    setShowMatrixRowModal(true);
  };

  const toggleGroupInForm = (label) => {
    setMatrixRowForm(prev => {
      const has = prev.groupPair.includes(label);
      return {
        ...prev,
        groupPair: has ? prev.groupPair.filter(g => g !== label) : [...prev.groupPair, label],
      };
    });
  };

  const updateCellInForm = (bracketId, field, value) => {
    setMatrixRowForm(prev => ({
      ...prev,
      cells: {
        ...prev.cells,
        [bracketId]: {
          ...(prev.cells[bracketId] || { lensName: '', price: '', estimatedDays: '' }),
          [field]: value,
        },
      },
    }));
  };

  const saveMatrixRow = async () => {
    const { prescriptionType, groupPair, sphereSign, cells, order } = matrixRowForm;
    if (!groupPair || groupPair.length === 0) {
      toast.warning('Pick at least one lens group for this row.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        prescriptionType: prescriptionType || 'single_vision',
        groupPair,
        sphereSign,
        cells,
        order: order === '' ? 0 : Number(order),
      };
      if (matrixRowEditing) {
        await axios.patch(`/api/lens-recommendation-matrix/${matrixRowEditing._id}`, payload, { headers });
      } else {
        await axios.post('/api/lens-recommendation-matrix', payload, { headers });
      }
      setShowMatrixRowModal(false);
      setMatrixRowEditing(null);
      fetchMatrixData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving recommendation row');
    }
  };

  const deleteMatrixRow = async (row) => {
    const label = `${displayCorrectionGroupPair(row.groupPair) || 'row'} (${row.sphereSign})`;
    if (!window.confirm(`Delete recommendation row "${label}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/lens-recommendation-matrix/${row._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchMatrixData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting row');
    }
  };

  // ---- Inline cell editing ---------------------------------------

  // Persist a single cell change (lensName | price | estimatedDays).
  // Optimistic: local state is already updated; the network call just
  // syncs the backend.
  const persistCell = async (rowId, bracketId, cell) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `/api/lens-recommendation-matrix/${rowId}/cell/${bracketId}`,
        {
          lensName: cell.lensName ?? '',
          price: cell.price === '' || cell.price === undefined ? null : cell.price,
          estimatedDays: cell.estimatedDays === '' || cell.estimatedDays === undefined ? null : cell.estimatedDays,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Cell save failed:', error);
      toast.error(error.response?.data?.message || 'Error saving cell — refresh to see the latest data.');
      fetchMatrixData();
    }
  };

  const sameGroupSet = (a = [], b = []) => (
    a.length === b.length && a.every(item => b.includes(item))
  );

  const getMatrixRowFor = (groupPair, sphereSign) => {
    const groups = Array.isArray(groupPair) ? groupPair : [groupPair];
    // Unified table — match by group + sign only (prescription type dropped).
    return matrixRows.find(row =>
      row.sphereSign === sphereSign &&
      Array.isArray(row.groupPair) &&
      sameGroupSet(row.groupPair, groups)
    );
  };

  // A cell can list several lenses ("Optimax" or "Ylla"). New cells store a
  // `lensNames` array; legacy cells store a single `lensName`.
  const cellLensNames = (cell) => {
    if (Array.isArray(cell?.lensNames) && cell.lensNames.length) return cell.lensNames.filter(Boolean);
    return cell?.lensName ? [cell.lensName] : [];
  };

  const getSpreadsheetCellLenses = (groupPair, sphereSign, bracketId) => {
    const row = getMatrixRowFor(groupPair, sphereSign);
    return cellLensNames(row?.cells?.[bracketId]).map(normalizeLensBrandName);
  };

  // Which alternatives zone an age bracket belongs to. Matched on the bracket's
  // start age, taking the LAST zone that contains it: the brackets overlap at
  // their edges (both "43-50" and "50-55" contain 50), and a bracket starting
  // exactly on a boundary belongs to the zone it opens, not the one it closes.
  //
  // A client's *age* resolves the other way — first match, mirroring
  // findAgeBracket's lowest-sort_order rule — so age 50 lands on the 43-50
  // bracket and the 43-50 zone alike. The two rules agree at every boundary.
  const zoneForBracket = (bracket, zones) => {
    const min = Number(bracket?.minAge);
    if (!Number.isFinite(min)) return null;
    const hits = zones.filter(z => min >= z.minAge && min <= z.maxAge);
    return hits.length ? hits[hits.length - 1] : null;
  };

  // Which regime a zone belongs to. Independent of the Simple Foyer /
  // Progressifs tab: age says how old the client is, the tab says what kind of
  // lens they are buying, and a 60-year-old buying single-vision distance
  // glasses is both. The matrix only stores one regime per bracket though, so a
  // zone whose regime differs from the active tab has no recommendation to give.
  const zoneRegime = (zone) =>
    (Number(zone?.minAge) >= PRESBYOPIA_AGE ? 'progressive' : 'single_vision');

  // The matrix recommendation for one (group set × sign × zone) — the union of
  // the cells whose bracket falls in that zone. No lens-type filter needed: the
  // zone already selects one side of the age-43 line, and the matrix stores
  // single-vision lenses in its young brackets and progressives in the rest.
  const getRecommendedLensesForZone = (groupPair, sphereSign, zone) => {
    const groups = Array.isArray(groupPair) ? groupPair : [groupPair].filter(Boolean);
    if (!groups.length) return [];
    const row =
      getMatrixRowFor(groups, sphereSign) ||
      matrixRows.find(r =>
        r.sphereSign === sphereSign &&
        Array.isArray(r.groupPair) &&
        groups.every(g => r.groupPair.includes(g))
      );
    if (!row) return [];
    const byName = new Map();
    ageBrackets.forEach(bracket => {
      if (zoneForBracket(bracket, alternativeZones)?.id !== zone.id) return;
      cellLensNames(row.cells?.[bracket._id]).forEach(raw => {
        const name = normalizeLensBrandName(raw);
        if (!name.trim()) return;
        // Key on the resolved catalog entry, not on the stored string. The same
        // lens is written both ways across cells — one bracket holds the
        // catalog id, another the free-text supplier name — and keying on the
        // raw value listed one lens twice under a single label.
        const cat = findCatalog(name);
        const key = cat ? (cat.id || cat._id) : name.trim().toLowerCase();
        if (!byName.has(key)) byName.set(key, { name, brackets: [], ages: [] });
        byName.get(key).brackets.push(bracket.label);
        // Kept so a cell holding several lenses can show which ages each covers
        // — a zone spans up to five brackets and they do not always agree.
        byName.get(key).ages.push({ min: Number(bracket.minAge), max: Number(bracket.maxAge) });
      });
    });
    return [...byName.values()];
  };

  const persistSpreadsheetCellLenses = async (groupPair, sphereSign, bracketId, names) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const groups = Array.isArray(groupPair) ? groupPair : [groupPair];
      const lensNames = (names || []).map(n => normalizeLensBrandName(n)).filter(Boolean);
      let row = getMatrixRowFor(groups, sphereSign);
      const cell = { lensNames, lensName: lensNames[0] || '', price: null, estimatedDays: null };

      if (!row) {
        const response = await axios.post('/api/lens-recommendation-matrix', {
          prescriptionType: 'single_vision', // unified table — single canonical type
          groupPair: groups,
          sphereSign,
          cells: { [bracketId]: cell },
          order: matrixRows.length
        }, { headers });
        setMatrixRows(prev => [...prev, response.data]);
        return;
      }

      const nextCells = {
        ...(row.cells || {}),
        [bracketId]: { ...(row.cells?.[bracketId] || {}), lensNames, lensName: lensNames[0] || '' }
      };
      setMatrixRows(prev => prev.map(r => r._id === row._id ? { ...r, cells: nextCells } : r));
      await axios.patch(`/api/lens-recommendation-matrix/${row._id}/cell/${bracketId}`, { lensNames }, { headers });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving recommendation cell');
      fetchMatrixData();
    }
  };

  const addLensToCell = (groupPair, sphereSign, bracketId, identifier) => {
    if (!identifier) return;
    const current = getSpreadsheetCellLenses(groupPair, sphereSign, bracketId);
    if (current.some(n => String(n).toLowerCase() === String(identifier).toLowerCase())) return; // no duplicates
    persistSpreadsheetCellLenses(groupPair, sphereSign, bracketId, [...current, identifier]);
  };

  const removeLensFromCell = (groupPair, sphereSign, bracketId, name) => {
    const current = getSpreadsheetCellLenses(groupPair, sphereSign, bracketId);
    persistSpreadsheetCellLenses(groupPair, sphereSign, bracketId, current.filter(n => n.toLowerCase() !== String(name).toLowerCase()));
  };

  // Catalog lookups for the cell picker. Index by the primary real name, the
  // commercial name, AND every supplier real name, so a cell's stored name
  // always resolves to its catalog entry (commercial name + suppliers + index).
  const catalogLookup = {};
  const catalogById = {};
  lensCatalog.forEach(c => {
    const cid = c?.id || c?._id;
    if (cid) catalogById[cid] = c;
    const keys = [c?.name, c?.commercialName, ...(Array.isArray(c?.suppliers) ? c.suppliers.map(s => s?.realName) : [])];
    keys.forEach(k => { if (k) catalogLookup[String(k).toLowerCase()] = c; });
  });
  // A cell stores the catalog entry id (unambiguous — the same lens name can
  // exist at several indexes/groups). Resolve by id first, then by name (legacy).
  const findCatalog = (idOrName) => catalogById[idOrName] || catalogLookup[String(idOrName || '').toLowerCase()] || null;
  // Real (supplier) names for a catalog entry, de-duped: "Optimax", "Ylla", …
  const catalogRealNames = (c) => {
    const list = Array.isArray(c?.suppliers) ? c.suppliers.map(s => (s?.realName || '').trim()).filter(Boolean) : [];
    if (!list.length && c?.name) list.push(c.name);
    return [...new Set(list)];
  };
  const catalogOptions = [...lensCatalog]
    .filter(c => c?.name || c?.commercialName)
    .sort((a, b) => {
      // Group same-named lenses together, then order them by group (G2/6, G2/8…).
      const byName = String(a.commercialName || a.name).localeCompare(String(b.commercialName || b.name));
      if (byName !== 0) return byName;
      return String(a.group || '').localeCompare(String(b.group || ''), undefined, { numeric: true });
    });
  // Catalog table view: filter by search + dispo, then sort by the chosen
  // column. Every row is rendered — the list is the record of what's priced,
  // so a lens added past some limit must still be visible without searching
  // for it. (The old 100-row cap hid exactly the rows you'd just added.)
  const sortCatalogBy = (col) => setCatalogSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
  const catalogView = (() => {
    const tokens = catalogSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const dispoMatch = (c) => {
      if (catalogDispo === 'all') return true;
      const a = String(c.availability || c.raw_data?.availability || '').toLowerCase();
      return catalogDispo === 'int' ? a.includes('int') : catalogDispo === 'ext' ? a.includes('ext') : catalogDispo === 'rx' ? a.includes('rx') : true;
    };
    const rows = lensCatalog.filter(c => {
      if (!dispoMatch(c)) return false;
      if (!tokens.length) return true;
      const hay = `${c.group || ''} ${c.indexValue || ''} ${c.commercialName || ''} ${c.name || ''} ${catalogRealNames(c).join(' ')} ${c.number ?? ''}`.toLowerCase();
      return tokens.every(t => hay.includes(t));
    });
    const dir = catalogSort.dir === 'desc' ? -1 : 1;
    const val = (c) => {
      switch (catalogSort.col) {
        case 'commercial': return String(c.commercialName || c.name || '').toLowerCase();
        case 'index': return parseFloat(c.indexValue) || 0;
        case 'price': return c.price == null || c.price === '' ? -Infinity : Number(c.price);
        default: return c.number == null || c.number === '' ? Infinity : Number(c.number);
      }
    };
    rows.sort((a, b) => { const av = val(a), bv = val(b); return av < bv ? -dir : av > bv ? dir : 0; });
    const filtered = tokens.length > 0 || catalogDispo !== 'all';
    return { rows, total: rows.length, filtered };
  })();

  // Does a catalog entry's group cover this matrix row's group family? Returns
  // true (✓ fits), false (⚠ likely wrong group), or null (unknown → no badge).
  const groupFits = (cat, rowGroups) => {
    const g = String(cat?.group || '').toLowerCase();
    if (!g) return null;
    if (g.includes('ts g')) return true; // Rx / all-groups lens fits anywhere
    const catGroups = g.split('&').map(x => x.trim()).filter(Boolean);
    const rows = (rowGroups || []).map(r => String(r).toLowerCase());
    return catGroups.some(cg => rows.includes(cg));
  };

  // Local-only update of a cell (for controlled inputs).
  const setCellLocal = (rowId, bracketId, patch) => {
    setMatrixRows(prev => prev.map(r => {
      if (r._id !== rowId) return r;
      const cells = { ...(r.cells || {}) };
      const existing = cells[bracketId] || { lensName: '', price: null, estimatedDays: null };
      cells[bracketId] = { ...existing, ...patch };
      return { ...r, cells };
    }));
  };

  // Cycle the sphere sign through plus → minus → none and persist.
  const cycleRowSign = async (row) => {
    const order = ['plus', 'minus', 'none'];
    const next = order[(order.indexOf(row.sphereSign) + 1) % order.length];
    setMatrixRows(prev => prev.map(r => (r._id === row._id ? { ...r, sphereSign: next } : r)));
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/lens-recommendation-matrix/${row._id}`, { sphereSign: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating sign');
      fetchMatrixData();
    }
  };


  const fetchSellByAlerts = async () => {
    setSellByLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/frame-stock/sellby-alerts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The endpoint answers { alerts }, not { items } — reading the wrong key
      // meant this panel was empty no matter how many frames were expiring.
      setSellByAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching sell-by alerts:', error);
    } finally {
      setSellByLoading(false);
    }
  };

  const fetchOverviewData = async (start, end) => {
    setOverviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (start) params.append('startDate', start);
      if (end) params.append('endDate', end);
      const response = await axios.get(`/api/analytics/overview?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverviewData(response.data);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setOverviewLoading(false);
    }
  };

  const fetchStoreInsights = async (start, end) => {
    setStoreInsightsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (start) params.append('startDate', start);
      if (end)   params.append('endDate', end);
      const { data } = await axios.get(`/api/analytics/store-insights?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStoreInsights(data);
    } catch (err) {
      console.error('Error fetching store insights:', err);
    } finally {
      setStoreInsightsLoading(false);
    }
  };

  // `allOrdersRequest` is the shared unfiltered download (see loadAllOrders);
  // the "All" bucket reuses it instead of fetching the same data again.
  const fetchOrders = async (allOrdersRequest = null) => {
    setOrdersLoading(true);
    try {
      const token = localStorage.getItem('token');

      // Three buckets are served by two backend endpoints:
      //   • Unsuccessful  → /api/admin/incomplete-orders (orders that never
      //                     passed payment/confirmation, plus prescriptions
      //                     without an order document)
      //   • Confirmed     → /api/admin/all-orders?status=confirmed
      //                     (in-flight pipeline: confirmed → out_for_delivery)
      //   • Completed     → /api/admin/all-orders?status=completed
      //                     (delivered or fully completed)
      //   • All           → /api/admin/all-orders (everything, including
      //                     prescription-only)
      if (orderFilter === 'unsuccessful') {
        const response = await axios.get('/api/admin/incomplete-orders', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const unsuccessfulOrders = response.data.incompleteOrders || [];
        setOrders(unsuccessfulOrders);
      } else {
        let url = '/api/admin/all-orders';
        if (orderFilter === 'completed') url += '?status=completed';
        else if (orderFilter === 'confirmed') url += '?status=confirmed';

        const response = (orderFilter === 'all' && allOrdersRequest)
          ? await allOrdersRequest
          : await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        setOrders(response.data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(`Error fetching orders: ${error.response?.data?.message || error.message}`);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Admin-only: permanently delete an order (cascades its items, workflow and
  // package links on the server).
  const handleDeleteOrder = async (orderId, e) => {
    if (e) e.stopPropagation();
    if (!orderId) return;
    if (!window.confirm('Delete this order permanently? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders((prev) => prev.filter((o) => (o._id || o.id) !== orderId && (o.order?._id) !== orderId));
      toast.success('Order deleted');
    } catch (error) {
      toast.error(`Failed to delete order: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  const handleDeleteAllData = async () => {
    if (deleteConfirmText !== 'DELETE ALL DATA') return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/admin/delete-all-data', {
        headers: { Authorization: `Bearer ${token}` },
        data: { confirm: 'YES_I_AM_SURE' }
      });
      toast.success('All data deleted. Refreshing…', { duration: 1200 });
      localStorage.clear();
      sessionStorage.clear();
      // Give the toast a beat to render before the hard reload.
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      console.error('Error deleting data:', error);
      toast.error(error.response?.data?.message || 'Error deleting data. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }
  };

  // Status families used by the bucket filters. Keep these in sync with
  // the corresponding backend lists in server/routes/admin.js so a row's
  // status badge always agrees with the bucket it lives in.
  const COMPLETED_STATUSES = ['completed', 'delivered'];
  const CONFIRMED_STATUSES = [
    'confirmed',
    'in_production',
    'manufacturing',
    'quality_check',
    'ready_for_pickup',
    'out_for_delivery',
    'back_to_central',
  ];

  const getStatusLabel = (status, isPrescriptionOnly = false) => {
    if (isPrescriptionOnly) return 'Prescription Only';
    if (!status) return 'N/A';
    if (status === 'No Order Created') return 'Prescription Only';
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const isOrderCompleted = (order) => {
    if (orderFilter === 'unsuccessful') {
      return false; // All items in the unsuccessful view are by definition not completed
    }
    return COMPLETED_STATUSES.includes(order.status);
  };

  // Did the order pass the payment/confirmation checkpoint? Used by the
  // Confirmed bucket and to colour the status badge amber for in-flight orders.
  const isOrderConfirmed = (order) => {
    if (!order || order.isPrescriptionOnly) return false;
    return CONFIRMED_STATUSES.includes(order.status);
  };

  const hasEyeData = (eyeData) => {
    if (!eyeData) return false;
    const has = (v) => v !== undefined && v !== null && String(v).trim() !== '';
    return (
      has(eyeData.sphere) ||
      has(eyeData.cylinder) ||
      has(eyeData.axis) ||
      has(eyeData.add) ||
      has(eyeData.prism) ||
      has(eyeData.base) ||
      has(eyeData.pd)
    );
  };

  const hasStandardCorrection = (prescription) => {
    return hasEyeData(prescription.rightEye) || hasEyeData(prescription.leftEye);
  };

  const hasSingleVisionCorrection = (prescription) => {
    return (
      hasEyeData(prescription.rightEyeFar) ||
      hasEyeData(prescription.leftEyeFar) ||
      hasEyeData(prescription.rightEyeCloseUp) ||
      hasEyeData(prescription.leftEyeCloseUp)
    );
  };

  const hasProgressiveCorrection = (prescription) => {
    return (
      hasEyeData(prescription.progressiveRightEyeFar) ||
      hasEyeData(prescription.progressiveLeftEyeFar) ||
      hasEyeData(prescription.progressiveRightEyeCloseUp) ||
      hasEyeData(prescription.progressiveLeftEyeCloseUp)
    );
  };

  const renderPrescriptionDetails = (prescription) => {
    if (!prescription) return null;

    return (
      <>
        <DetailSection>
          <DetailSectionTitle>{t('admin.patientInformation')}</DetailSectionTitle>
          <DetailGrid>
            <DetailItem>
              <DetailLabel>{t('admin.name')}</DetailLabel>
              <DetailValue bold>{prescription.patientName || 'N/A'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('admin.age')}</DetailLabel>
              <DetailValue>{prescription.patientAge || 'N/A'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('admin.address')}</DetailLabel>
              <DetailValue>{prescription.address || 'N/A'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('admin.phone')}</DetailLabel>
              <DetailValue>{prescription.phoneNumber || 'N/A'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('admin.prescriptionDate')}</DetailLabel>
              <DetailValue>
                {prescription.prescriptionDate
                  ? new Date(prescription.prescriptionDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                  : t('admin.nA')}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('admin.prescribedBy')}</DetailLabel>
              <DetailValue>{prescription.prescribedBy || 'N/A'}</DetailValue>
            </DetailItem>
          </DetailGrid>
        </DetailSection>

        {/* Prescription Photo */}
        {prescription.prescriptionPhoto && (
          <DetailSection>
            <DetailSectionTitle>{t('admin.prescriptionPhoto')}</DetailSectionTitle>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1rem',
              padding: '1rem',
              background: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <img
                src={prescription.prescriptionPhoto.startsWith('http')
                  ? prescription.prescriptionPhoto
                  : `${window.location.origin}${prescription.prescriptionPhoto}`}
                alt={t('admin.prescription')}
                style={{
                  maxWidth: '100%',
                  maxHeight: '600px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  // Open image in new tab for full view
                  const imageUrl = prescription.prescriptionPhoto.startsWith('http')
                    ? prescription.prescriptionPhoto
                    : `${window.location.origin}${prescription.prescriptionPhoto}`;
                  window.open(imageUrl, '_blank');
                }}
              />
            </div>
          </DetailSection>
        )}

        {hasStandardCorrection(prescription) && (
          <DetailSection>
            <DetailSectionTitle>{t('admin.eyeCorrectionStandard')}</DetailSectionTitle>
            <EyeCorrectionGrid>
              {hasEyeData(prescription.rightEye) && (
                <EyeCorrectionBox>
                  <EyeTitle>{t('admin.rightEyeOd')}</EyeTitle>
                  {prescription.rightEye.sphere !== undefined && prescription.rightEye.sphere !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                      <CorrectionValue>{prescription.rightEye.sphere}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.rightEye.cylinder !== undefined && prescription.rightEye.cylinder !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                      <CorrectionValue>{prescription.rightEye.cylinder}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.rightEye.axis !== undefined && prescription.rightEye.axis !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                      <CorrectionValue>{prescription.rightEye.axis}°</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.rightEye.add !== undefined && prescription.rightEye.add !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                      <CorrectionValue>{prescription.rightEye.add}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.rightEye.prism !== undefined && prescription.rightEye.prism !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.prism')}</CorrectionLabel>
                      <CorrectionValue>{prescription.rightEye.prism}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.rightEye.base && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.base')}</CorrectionLabel>
                      <CorrectionValue>{prescription.rightEye.base}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.rightEye.pd !== undefined && prescription.rightEye.pd !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.pd')}</CorrectionLabel>
                      <CorrectionValue>{prescription.rightEye.pd}mm</CorrectionValue>
                    </CorrectionRow>
                  )}
                </EyeCorrectionBox>
              )}
              {hasEyeData(prescription.leftEye) && (
                <EyeCorrectionBox>
                  <EyeTitle>{t('admin.leftEyeOs')}</EyeTitle>
                  {prescription.leftEye.sphere !== undefined && prescription.leftEye.sphere !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                      <CorrectionValue>{prescription.leftEye.sphere}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.leftEye.cylinder !== undefined && prescription.leftEye.cylinder !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                      <CorrectionValue>{prescription.leftEye.cylinder}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.leftEye.axis !== undefined && prescription.leftEye.axis !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                      <CorrectionValue>{prescription.leftEye.axis}°</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.leftEye.add !== undefined && prescription.leftEye.add !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                      <CorrectionValue>{prescription.leftEye.add}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.leftEye.prism !== undefined && prescription.leftEye.prism !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.prism')}</CorrectionLabel>
                      <CorrectionValue>{prescription.leftEye.prism}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.leftEye.base && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.base')}</CorrectionLabel>
                      <CorrectionValue>{prescription.leftEye.base}</CorrectionValue>
                    </CorrectionRow>
                  )}
                  {prescription.leftEye.pd !== undefined && prescription.leftEye.pd !== null && (
                    <CorrectionRow>
                      <CorrectionLabel>{t('admin.pd')}</CorrectionLabel>
                      <CorrectionValue>{prescription.leftEye.pd}mm</CorrectionValue>
                    </CorrectionRow>
                  )}
                </EyeCorrectionBox>
              )}
            </EyeCorrectionGrid>
          </DetailSection>
        )}

        {hasSingleVisionCorrection(prescription) && (
          <DetailSection>
            <DetailSectionTitle>{t('admin.singleVisionCorrection')}</DetailSectionTitle>
            {prescription.singleVisionCorrectionType && (
              <DetailItem style={{ marginBottom: '1rem' }}>
                <DetailLabel>{t('admin.correctionType')}</DetailLabel>
                <DetailValue>{prescription.singleVisionCorrectionType}</DetailValue>
              </DetailItem>
            )}
            <EyeCorrectionGrid>
              {(hasEyeData(prescription.rightEyeFar) || hasEyeData(prescription.leftEyeFar)) && (
                <>
                  {hasEyeData(prescription.rightEyeFar) && (
                    <EyeCorrectionBox>
                      <EyeTitle>{t('admin.rightEyeFar')}</EyeTitle>
                      {prescription.rightEyeFar.sphere !== undefined && prescription.rightEyeFar.sphere !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                          <CorrectionValue>{prescription.rightEyeFar.sphere}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.rightEyeFar.cylinder !== undefined && prescription.rightEyeFar.cylinder !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                          <CorrectionValue>{prescription.rightEyeFar.cylinder}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.rightEyeFar.axis !== undefined && prescription.rightEyeFar.axis !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                          <CorrectionValue>{prescription.rightEyeFar.axis}°</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.rightEyeFar.add !== undefined && prescription.rightEyeFar.add !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                          <CorrectionValue>{prescription.rightEyeFar.add}</CorrectionValue>
                        </CorrectionRow>
                      )}
                    </EyeCorrectionBox>
                  )}
                  {hasEyeData(prescription.leftEyeFar) && (
                    <EyeCorrectionBox>
                      <EyeTitle>{t('admin.leftEyeFar')}</EyeTitle>
                      {prescription.leftEyeFar.sphere !== undefined && prescription.leftEyeFar.sphere !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                          <CorrectionValue>{prescription.leftEyeFar.sphere}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.leftEyeFar.cylinder !== undefined && prescription.leftEyeFar.cylinder !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                          <CorrectionValue>{prescription.leftEyeFar.cylinder}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.leftEyeFar.axis !== undefined && prescription.leftEyeFar.axis !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                          <CorrectionValue>{prescription.leftEyeFar.axis}°</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.leftEyeFar.add !== undefined && prescription.leftEyeFar.add !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                          <CorrectionValue>{prescription.leftEyeFar.add}</CorrectionValue>
                        </CorrectionRow>
                      )}
                    </EyeCorrectionBox>
                  )}
                </>
              )}
              {(hasEyeData(prescription.rightEyeCloseUp) || hasEyeData(prescription.leftEyeCloseUp)) && (
                <>
                  {hasEyeData(prescription.rightEyeCloseUp) && (
                    <EyeCorrectionBox>
                      <EyeTitle>{t('admin.rightEyeCloseUp')}</EyeTitle>
                      {prescription.rightEyeCloseUp.sphere !== undefined && prescription.rightEyeCloseUp.sphere !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                          <CorrectionValue>{prescription.rightEyeCloseUp.sphere}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.rightEyeCloseUp.cylinder !== undefined && prescription.rightEyeCloseUp.cylinder !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                          <CorrectionValue>{prescription.rightEyeCloseUp.cylinder}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.rightEyeCloseUp.axis !== undefined && prescription.rightEyeCloseUp.axis !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                          <CorrectionValue>{prescription.rightEyeCloseUp.axis}°</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.rightEyeCloseUp.add !== undefined && prescription.rightEyeCloseUp.add !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                          <CorrectionValue>{prescription.rightEyeCloseUp.add}</CorrectionValue>
                        </CorrectionRow>
                      )}
                    </EyeCorrectionBox>
                  )}
                  {hasEyeData(prescription.leftEyeCloseUp) && (
                    <EyeCorrectionBox>
                      <EyeTitle>{t('admin.leftEyeCloseUp')}</EyeTitle>
                      {prescription.leftEyeCloseUp.sphere !== undefined && prescription.leftEyeCloseUp.sphere !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                          <CorrectionValue>{prescription.leftEyeCloseUp.sphere}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.leftEyeCloseUp.cylinder !== undefined && prescription.leftEyeCloseUp.cylinder !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                          <CorrectionValue>{prescription.leftEyeCloseUp.cylinder}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.leftEyeCloseUp.axis !== undefined && prescription.leftEyeCloseUp.axis !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                          <CorrectionValue>{prescription.leftEyeCloseUp.axis}°</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.leftEyeCloseUp.add !== undefined && prescription.leftEyeCloseUp.add !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                          <CorrectionValue>{prescription.leftEyeCloseUp.add}</CorrectionValue>
                        </CorrectionRow>
                      )}
                    </EyeCorrectionBox>
                  )}
                </>
              )}
            </EyeCorrectionGrid>
          </DetailSection>
        )}

        {hasProgressiveCorrection(prescription) && (
          <DetailSection>
            <DetailSectionTitle>{t('admin.progressiveCorrection')}</DetailSectionTitle>
            <EyeCorrectionGrid>
              {(hasEyeData(prescription.progressiveRightEyeFar) || hasEyeData(prescription.progressiveLeftEyeFar)) && (
                <>
                  {hasEyeData(prescription.progressiveRightEyeFar) && (
                    <EyeCorrectionBox>
                      <EyeTitle>{t('admin.rightEyeFar')}</EyeTitle>
                      {prescription.progressiveRightEyeFar.sphere !== undefined && prescription.progressiveRightEyeFar.sphere !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveRightEyeFar.sphere}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveRightEyeFar.cylinder !== undefined && prescription.progressiveRightEyeFar.cylinder !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveRightEyeFar.cylinder}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveRightEyeFar.axis !== undefined && prescription.progressiveRightEyeFar.axis !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveRightEyeFar.axis}°</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveRightEyeFar.add !== undefined && prescription.progressiveRightEyeFar.add !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveRightEyeFar.add}</CorrectionValue>
                        </CorrectionRow>
                      )}
                    </EyeCorrectionBox>
                  )}
                  {hasEyeData(prescription.progressiveLeftEyeFar) && (
                    <EyeCorrectionBox>
                      <EyeTitle>{t('admin.leftEyeFar')}</EyeTitle>
                      {prescription.progressiveLeftEyeFar.sphere !== undefined && prescription.progressiveLeftEyeFar.sphere !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveLeftEyeFar.sphere}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveLeftEyeFar.cylinder !== undefined && prescription.progressiveLeftEyeFar.cylinder !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveLeftEyeFar.cylinder}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveLeftEyeFar.axis !== undefined && prescription.progressiveLeftEyeFar.axis !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveLeftEyeFar.axis}°</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveLeftEyeFar.add !== undefined && prescription.progressiveLeftEyeFar.add !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveLeftEyeFar.add}</CorrectionValue>
                        </CorrectionRow>
                      )}
                    </EyeCorrectionBox>
                  )}
                </>
              )}
              {(hasEyeData(prescription.progressiveRightEyeCloseUp) || hasEyeData(prescription.progressiveLeftEyeCloseUp)) && (
                <>
                  {hasEyeData(prescription.progressiveRightEyeCloseUp) && (
                    <EyeCorrectionBox>
                      <EyeTitle>{t('admin.rightEyeCloseUp')}</EyeTitle>
                      {prescription.progressiveRightEyeCloseUp.sphere !== undefined && prescription.progressiveRightEyeCloseUp.sphere !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveRightEyeCloseUp.sphere}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveRightEyeCloseUp.cylinder !== undefined && prescription.progressiveRightEyeCloseUp.cylinder !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveRightEyeCloseUp.cylinder}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveRightEyeCloseUp.axis !== undefined && prescription.progressiveRightEyeCloseUp.axis !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveRightEyeCloseUp.axis}°</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveRightEyeCloseUp.add !== undefined && prescription.progressiveRightEyeCloseUp.add !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveRightEyeCloseUp.add}</CorrectionValue>
                        </CorrectionRow>
                      )}
                    </EyeCorrectionBox>
                  )}
                  {hasEyeData(prescription.progressiveLeftEyeCloseUp) && (
                    <EyeCorrectionBox>
                      <EyeTitle>{t('admin.leftEyeCloseUp')}</EyeTitle>
                      {prescription.progressiveLeftEyeCloseUp.sphere !== undefined && prescription.progressiveLeftEyeCloseUp.sphere !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.sphere')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveLeftEyeCloseUp.sphere}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveLeftEyeCloseUp.cylinder !== undefined && prescription.progressiveLeftEyeCloseUp.cylinder !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.cylinder')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveLeftEyeCloseUp.cylinder}</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveLeftEyeCloseUp.axis !== undefined && prescription.progressiveLeftEyeCloseUp.axis !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.axis')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveLeftEyeCloseUp.axis}°</CorrectionValue>
                        </CorrectionRow>
                      )}
                      {prescription.progressiveLeftEyeCloseUp.add !== undefined && prescription.progressiveLeftEyeCloseUp.add !== null && (
                        <CorrectionRow>
                          <CorrectionLabel>{t('admin.add2')}</CorrectionLabel>
                          <CorrectionValue>{prescription.progressiveLeftEyeCloseUp.add}</CorrectionValue>
                        </CorrectionRow>
                      )}
                    </EyeCorrectionBox>
                  )}
                </>
              )}
            </EyeCorrectionGrid>
          </DetailSection>
        )}

        <DetailSection>
          <DetailSectionTitle>{t('admin.additionalInformation')}</DetailSectionTitle>
          <DetailGrid>
            <DetailItem>
              <DetailLabel>{t('admin.prescriptionType2')}</DetailLabel>
              <DetailValue>{prescription.prescriptionType || 'N/A'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>{t('admin.orderType')}</DetailLabel>
              <DetailValue>{prescription.orderType || 'N/A'}</DetailValue>
            </DetailItem>
            {prescription.selectedRecommendation && (
              <DetailItem>
                <DetailLabel>{t('admin.selectedRecommendation')}</DetailLabel>
                <DetailValue>
                  {(() => {
                    const r = prescription.selectedRecommendation;
                    if (typeof r !== 'object' || r === null) return String(r);
                    const parts = [];
                    if (r.name) parts.push(r.name);
                    if (r.price != null) parts.push(`${Number(r.price).toLocaleString()} DZD`);
                    if (r.estimatedDays != null) parts.push(`~${r.estimatedDays} days`);
                    return parts.length ? parts.join(' · ') : 'N/A';
                  })()}
                </DetailValue>
              </DetailItem>
            )}
            {prescription.buyerChoice && (
              <DetailItem>
                <DetailLabel>{t('admin.otherSelection')}</DetailLabel>
                <DetailValue>{typeof prescription.buyerChoice === 'object' ? `${prescription.buyerChoice.frameType || ''} / ${prescription.buyerChoice.lensType || ''}`.replace(/^ \/ | \/ $/g, '') || 'N/A' : prescription.buyerChoice}</DetailValue>
              </DetailItem>
            )}
            {prescription.notes && (
              <DetailItem style={{ gridColumn: '1 / -1' }}>
                <DetailLabel>{t('admin.notes')}</DetailLabel>
                <DetailValue>{prescription.notes}</DetailValue>
              </DetailItem>
            )}
          </DetailGrid>
        </DetailSection>
      </>
    );
  };

  // A row's store lives in one of three places depending on which shape the
  // row arrived in — prescription-only rows have none at all. Mirrors the
  // resolution the table itself does when rendering the Store column.
  const ORDER_UNASSIGNED = '__unassigned__';
  const orderStoreOf = (item) => {
    if (item.isPrescriptionOnly) return null;
    return item.missingStep ? (item.order?.store || item.store) : item.store;
  };
  const entityKey = (entity) => entity?._id || entity?.id || null;
  const orderStoreKey = (item) => entityKey(orderStoreOf(item)) || ORDER_UNASSIGNED;
  const orderSellerKey = (item) => entityKey(item.salesPerson) || ORDER_UNASSIGNED;
  const sellerLabel = (person) =>
    [person?.firstName, person?.lastName].filter(Boolean).join(' ') || person?.employeeId || '';

  // Options are the full rosters, so every store and every employee stays
  // selectable even when the current bucket holds none of their orders —
  // picking one and getting an empty table is a real answer. Anything present
  // on an order but missing from the roster (a deleted store, a deactivated
  // seller) is unioned in so no row becomes unreachable, and "Not assigned"
  // is always offered so the option never disappears between buckets.
  const collectOptions = (entries) => {
    const byKey = new Map();
    for (const [key, label] of entries) {
      if (!key || byKey.has(key)) continue;
      byKey.set(key, label || t('admin.notAssigned'));
    }
    return [...byKey.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const orderStoreOptions = [
    ...collectOptions([
      ...stores.map(store => [entityKey(store), store.name]),
      ...orders.map(item => [entityKey(orderStoreOf(item)), orderStoreOf(item)?.name]),
    ]),
    { value: ORDER_UNASSIGNED, label: t('admin.notAssigned') },
  ];

  // Picking a store narrows the seller list to that store's people.
  const inSelectedStore = (person) =>
    orderStoreFilter === 'all' || entityKey(person?.store) === orderStoreFilter;
  const orderSellerOptions = [
    ...collectOptions([
      ...employees.filter(inSelectedStore).map(emp => [entityKey(emp), sellerLabel(emp)]),
      ...orders
        .filter(item => orderStoreFilter === 'all' || orderStoreKey(item) === orderStoreFilter)
        .map(item => [entityKey(item.salesPerson), sellerLabel(item.salesPerson)]),
    ]),
    { value: ORDER_UNASSIGNED, label: t('admin.notAssigned') },
  ];

  const matchesStoreAndSeller = (item) => (
    (orderStoreFilter === 'all' || orderStoreKey(item) === orderStoreFilter) &&
    (orderSellerFilter === 'all' || orderSellerKey(item) === orderSellerFilter)
  );

  const filteredOrders = orders.filter(order => {
    if (!matchesStoreAndSeller(order)) return false;
    if (orderSearch === '') return true;

    const searchTerm = orderSearch.toLowerCase();

    // Get prescription data from various possible locations
    const prescription = order.prescription || (order.isPrescriptionOnly ? order : null);
    const patientName = prescription?.patientName || '';
    const prescriptionDate = prescription?.prescriptionDate;
    const patientAge = prescription?.patientAge;

    // Calculate approximate birth year from prescription date and age
    let birthYear = null;
    let birthDateStr = '';
    if (prescriptionDate && patientAge) {
      const date = new Date(prescriptionDate);
      birthYear = date.getFullYear() - patientAge;
      // Create a date string for the calculated birth date
      birthDateStr = new Date(birthYear, 0, 1).toLocaleDateString();
    }

    // Format prescription date for search (multiple formats)
    const dateStr = prescriptionDate ? new Date(prescriptionDate).toLocaleDateString() : '';
    const dateStrShort = prescriptionDate ? new Date(prescriptionDate).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '';
    const dateStrLong = prescriptionDate ? new Date(prescriptionDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

    return (
      order.order?.orderNumber?.toLowerCase().includes(searchTerm) ||
      order.orderNumber?.toLowerCase().includes(searchTerm) ||
      patientName.toLowerCase().includes(searchTerm) ||
      order.visitor?.gender?.toLowerCase().includes(searchTerm) ||
      order.salesPerson?.firstName?.toLowerCase().includes(searchTerm) ||
      order.salesPerson?.lastName?.toLowerCase().includes(searchTerm) ||
      order.order?.store?.name?.toLowerCase().includes(searchTerm) ||
      order.store?.name?.toLowerCase().includes(searchTerm) ||
      order.reason?.toLowerCase().includes(searchTerm) ||
      dateStr.toLowerCase().includes(searchTerm) ||
      dateStrShort.toLowerCase().includes(searchTerm) ||
      dateStrLong.toLowerCase().includes(searchTerm) ||
      birthDateStr.toLowerCase().includes(searchTerm) ||
      (birthYear && birthYear.toString().includes(searchTerm)) ||
      (patientAge && patientAge.toString().includes(searchTerm))
    );
  });

  // Bucket counts (computed from the full unfiltered order list so each tab
  // shows its real total even while a different bucket is selected).
  //   • completed    — delivered or fully completed
  //   • confirmed    — confirmed/in-flight pipeline (not yet delivered)
  //   • unsuccessful — never reached payment/confirmation, plus prescriptions
  //                    that never produced an order. Cancelled is excluded
  //                    here (it's a deliberate close-out, not a stalled sale)
  //                    and only appears in "All".
  const countableOrders = allOrdersForCounts.filter(matchesStoreAndSeller);
  const totalCount = countableOrders.length;
  const completedCount = countableOrders.filter(o => {
    if (o.isPrescriptionOnly) return false;
    return COMPLETED_STATUSES.includes(o.status);
  }).length;
  const confirmedCount = countableOrders.filter(o => {
    if (o.isPrescriptionOnly) return false;
    return CONFIRMED_STATUSES.includes(o.status);
  }).length;
  const unsuccessfulCount = countableOrders.filter(o => {
    if (o.isPrescriptionOnly) return true;
    const s = o.status || '';
    if (s === 'No Order Created') return true;
    return (
      !COMPLETED_STATUSES.includes(s) &&
      !CONFIRMED_STATUSES.includes(s) &&
      s !== 'cancelled'
    );
  }).length;

  // ── Command palette: jump-to-tab + quick actions, all keyboard-driven. ──
  const goToTab = useCallback((id) => {
    navigate('/admin/dashboard/' + id);
    setSidebarOpen(false);
    if (id === 'lens-stock' || id === 'frame-stock') setIsStockOpen(true);
    if (['stores', 'store-branding', 'central-store', 'courier-routes', 'labs', 'administration'].includes(id)) {
      setIsFacilitiesOpen(true);
    }
  }, [navigate]);

  const paletteCommands = useMemo(() => {
    const navIcon = (Icon) => <Icon size={16} />;
    const nav = [
      { id: 'go-overview',        label: 'Overview',        section: 'Navigation', icon: navIcon(Home),       hint: 'Performance snapshot',       keywords: 'home dashboard kpi', shortcut: 'O', onRun: () => goToTab('overview') },
      { id: 'go-orders',          label: 'Orders',          section: 'Navigation', icon: navIcon(Package),    hint: 'Track every order',          keywords: 'sales prescription',  shortcut: 'R', onRun: () => goToTab('orders') },
      { id: 'go-employees',       label: 'Employees',       section: 'Navigation', icon: navIcon(Users),      hint: 'Team & roles',               keywords: 'team staff users',                onRun: () => goToTab('employees') },
      { id: 'go-employee-stats',  label: 'Employee Statistics', section: 'Navigation', icon: navIcon(BarChart3), hint: 'Employee sales and rates', keywords: 'employee statistics staff performance rates', onRun: () => goToTab('employee-statistics') },
      { id: 'go-clients',         label: 'Clients',         section: 'Navigation', icon: navIcon(UserPlus),   hint: 'Customer database',          keywords: 'customers patients',              onRun: () => goToTab('clients') },
      { id: 'go-peer-ratings',    label: 'Peer ratings',    section: 'Navigation', icon: navIcon(Star),       hint: 'Anonymous teammate ratings', keywords: 'reviews feedback rating',         onRun: () => goToTab('peer-ratings') },
      { id: 'go-stores',          label: 'Retail Stores',   section: 'Facilities', icon: navIcon(Store),      hint: 'Customer-facing storefronts', keywords: 'shops branches retail storefront', onRun: () => goToTab('stores') },
      { id: 'go-central-store',   label: 'Central Store',   section: 'Facilities', icon: navIcon(Warehouse),  hint: 'Central warehouse & managers', keywords: 'warehouse hq central inventory operations', onRun: () => goToTab('central-store') },
      { id: 'go-labs',            label: 'Labs',            section: 'Facilities', icon: navIcon(Building2),  hint: 'External lens labs',         keywords: 'partners suppliers',              onRun: () => goToTab('labs') },
      { id: 'go-administration',  label: 'Administration',  section: 'Facilities', icon: navIcon(MapPin),     hint: 'Wilayas & regions',          keywords: 'commune location address',        onRun: () => goToTab('administration') },
      { id: 'go-groups',          label: 'Group',           section: 'Catalog',    icon: navIcon(Filter),     hint: 'SPH/CYL correction map',     keywords: 'correction groupe g2 g4 sph cyl', onRun: () => goToTab('groups') },
      { id: 'go-recommendations', label: 'Recommendations', section: 'Navigation', icon: navIcon(Filter),     hint: 'Lens matrix, catalog, alts', keywords: 'lens matrix catalog price',       onRun: () => goToTab('recommendations') },
      { id: 'go-lens-stock',      label: 'Lens Stock',      section: 'Navigation', icon: navIcon(Database),   hint: 'In-stock lens inventory',    keywords: 'inventory stock',                 onRun: () => goToTab('lens-stock') },
      { id: 'go-frame-stock',     label: 'Frame Stock',     section: 'Navigation', icon: navIcon(Glasses),    hint: 'Frame inventory by store',   keywords: 'inventory frames',                onRun: () => goToTab('frame-stock') },
    ];
    const actions = [
      {
        id: 'toggle-theme', section: 'Appearance', icon: theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />,
        label: theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
        hint: 'Re-themes the dashboard', keywords: 'dark light theme appearance color',
        onRun: () => toggleTheme(),
      },
      {
        id: 'toggle-sidebar', section: 'Appearance', icon: sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />,
        label: sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar to icons',
        hint: 'More room for content', keywords: 'sidebar nav collapse expand',
        onRun: () => setSidebarCollapsed((v) => !v),
      },
      {
        id: 'refresh-overview', section: 'Actions', icon: <RefreshCw size={16} />,
        label: 'Refresh overview data', hint: 'Re-pulls KPIs and sparklines',
        keywords: 'reload sync update refetch',
        onRun: () => {
          fetchOverviewData(overviewStartDate, overviewEndDate);
          toast.info('Overview data refreshed', { duration: 1500 });
        },
      },
      {
        id: 'refresh-orders', section: 'Actions', icon: <RefreshCw size={16} />,
        label: 'Refresh orders', hint: 'Re-pulls the latest orders',
        keywords: 'reload sync orders update',
        onRun: () => {
          const allOrdersRequest = loadAllOrders();
          fetchOrders(allOrdersRequest);
          fetchAllOrdersForCounts(allOrdersRequest);
          toast.info('Orders refreshed', { duration: 1500 });
        },
      },
      {
        id: 'logout', section: 'Account', icon: <LogOut size={16} />,
        label: 'Log out', hint: 'End your admin session',
        keywords: 'sign out exit',
        onRun: () => handleLogout(),
      },
    ];
    const paletteTab = (id) => (id === 'go-employee-stats' ? 'employee-statistics' : id.replace(/^go-/, ''));
    return [...nav.filter((item) => canSeeTab(paletteTab(item.id))), ...actions];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, sidebarCollapsed, goToTab, toggleTheme, overviewStartDate, overviewEndDate, canSeeTab]);

  return (
    <Container className="admin-shell" data-theme={theme} data-collapsed={sidebarCollapsed}>
      <MobileMenuButton onClick={() => setSidebarOpen(!sidebarOpen)}>
        <Menu size={24} />
      </MobileMenuButton>
      <SidebarOverlay isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <Sidebar isOpen={sidebarOpen} $collapsed={sidebarCollapsed} data-collapsed={sidebarCollapsed}>
        <SidebarHeader>
          {storeLogo || storeName ? (
            <div className="sidebar-title-block" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
              {storeLogo && (
                <img
                  src={storeLogo}
                  alt={storeName || 'Store Logo'}
                  style={{
                    width: '120px',
                    height: '120px',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              )}
              <SidebarTitle className="sidebar-title-text" style={{ fontSize: '1.25rem', textAlign: 'center' }}>
                {storeName || 'Store Admin'}
              </SidebarTitle>
            </div>
          ) : (
            <SidebarTitle>
              <Shield size={28} />
              <span className="sidebar-title-text">{t('admin.adminPanel')}</span>
            </SidebarTitle>
          )}
        </SidebarHeader>
        <SidebarNav>
          {['overview', 'orders', 'clients', 'store-analytics'].some(canSeeTab) && <SidebarSectionLabel>{t('admin.workspace')}</SidebarSectionLabel>}
          <NavItem style={hideUnlessVisible('overview')} active={activeTab === 'overview'} onClick={() => { navigate('/admin/dashboard/overview'); setSidebarOpen(false); }}>
            <Home className="icon" />
            <NavLabel>{t('admin.overview')}</NavLabel>
            <NavTooltip>{t('admin.overview')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('orders')} active={activeTab === 'orders'} onClick={() => { navigate('/admin/dashboard/orders'); setSidebarOpen(false); }}>
            <Package className="icon" />
            <NavLabel>{t('admin.orders')}</NavLabel>
            <NavTooltip>{t('admin.orders')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('clients')} active={activeTab === 'clients'} onClick={() => { navigate('/admin/dashboard/clients'); setSidebarOpen(false); }}>
            <UserPlus className="icon" />
            <NavLabel>{t('admin.clients')}</NavLabel>
            <NavTooltip>{t('admin.clients')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('store-analytics')} active={activeTab === 'store-analytics'} onClick={() => { navigate('/admin/dashboard/store-analytics'); setSidebarOpen(false); }}>
            <TrendingUp className="icon" />
            <NavLabel>{t('admin.storeAnalytics')}</NavLabel>
            <NavTooltip>{t('admin.storeAnalytics')}</NavTooltip>
          </NavItem>

          {['lens-stock', 'frame-stock'].some(canSeeTab) && <SidebarSectionLabel>{t('admin.inventory')}</SidebarSectionLabel>}
          <NavItem style={hideUnlessVisible('lens-stock')} active={activeTab === 'lens-stock'} onClick={() => { navigate('/admin/dashboard/lens-stock'); setSidebarOpen(false); }}>
            <Database className="icon" />
            <NavLabel>{t('admin.lensStock')}</NavLabel>
            <NavTooltip>{t('admin.lensStock')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('frame-stock')} active={activeTab === 'frame-stock'} onClick={() => { navigate('/admin/dashboard/frame-stock'); setSidebarOpen(false); }}>
            <Glasses className="icon" />
            <NavLabel>{t('admin.frameStock')}</NavLabel>
            <NavTooltip>{t('admin.frameStock')}</NavTooltip>
          </NavItem>

          {['employees', 'employee-statistics', 'employee-badges', 'peer-ratings'].some(canSeeTab) && <SidebarSectionLabel>{t('admin.team')}</SidebarSectionLabel>}
          <NavItem style={hideUnlessVisible('employees')} active={activeTab === 'employees'} onClick={() => { navigate('/admin/dashboard/employees'); setSidebarOpen(false); }}>
            <Users className="icon" />
            <NavLabel>{t('admin.employees')}</NavLabel>
            <NavTooltip>{t('admin.employees')}</NavTooltip>
          </NavItem>
          <NavItem
            active={activeTab === 'employee-statistics'}
            onClick={() => { navigate('/admin/dashboard/employee-statistics'); setSidebarOpen(false); }}
            style={{ paddingLeft: sidebarCollapsed ? '0.4rem' : '2.4rem', fontSize: '0.88rem', ...hideUnlessVisible('employee-statistics') }}
          >
            <BarChart3 className="icon" size={17} />
            <NavLabel>{t('admin.employeeStatistics')}</NavLabel>
            <NavTooltip>{t('admin.employeeStatistics')}</NavTooltip>
          </NavItem>
          <NavItem
            active={activeTab === 'employee-badges'}
            onClick={() => { navigate('/admin/dashboard/employee-badges'); setSidebarOpen(false); }}
            style={{ paddingLeft: sidebarCollapsed ? '0.4rem' : '2.4rem', fontSize: '0.88rem', ...hideUnlessVisible('employee-badges') }}
          >
            <CreditCard className="icon" size={17} />
            <NavLabel>{t('admin.employeeBadges')}</NavLabel>
            <NavTooltip>{t('admin.employeeBadges')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('peer-ratings')} active={activeTab === 'peer-ratings'} onClick={() => { navigate('/admin/dashboard/peer-ratings'); setSidebarOpen(false); }}>
            <Star className="icon" />
            <NavLabel>{t('admin.peerRatings')}</NavLabel>
            <NavTooltip>{t('admin.peerRatings')}</NavTooltip>
          </NavItem>

          {['stores', 'central-store', 'labs', 'courier-routes'].some(canSeeTab) && <SidebarSectionLabel>{t('admin.network')}</SidebarSectionLabel>}
          <NavItem style={hideUnlessVisible('stores')} active={activeTab === 'stores'} onClick={() => { navigate('/admin/dashboard/stores'); setSidebarOpen(false); }}>
            <Store className="icon" />
            <NavLabel>{t('admin.retailStores')}</NavLabel>
            <NavTooltip>{t('admin.retailStores')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('central-store')} active={activeTab === 'central-store'} onClick={() => { navigate('/admin/dashboard/central-store'); setSidebarOpen(false); }}>
            <Warehouse className="icon" />
            <NavLabel>{t('admin.centralStore')}</NavLabel>
            <NavTooltip>{t('admin.centralStore')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('labs')} active={activeTab === 'labs'} onClick={() => { navigate('/admin/dashboard/labs'); setSidebarOpen(false); }}>
            <FlaskConical className="icon" />
            <NavLabel>{t('admin.labs')}</NavLabel>
            <NavTooltip>{t('admin.labs')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('courier-routes')} active={activeTab === 'courier-routes'} onClick={() => { navigate('/admin/dashboard/courier-routes'); setSidebarOpen(false); }}>
            <Truck className="icon" />
            <NavLabel>{t('admin.courierRoutes')}</NavLabel>
            <NavTooltip>{t('admin.courierRoutes')}</NavTooltip>
          </NavItem>

          {['recommendations', 'groups', 'store-branding', 'administration'].some(canSeeTab) && <SidebarSectionLabel>{t('admin.setup')}</SidebarSectionLabel>}
          <NavItem style={hideUnlessVisible('recommendations')} active={activeTab === 'recommendations'} onClick={() => { navigate('/admin/dashboard/recommendations'); setSidebarOpen(false); }}>
            <Table className="icon" />
            <NavLabel>{t('admin.recommendations')}</NavLabel>
            <NavTooltip>{t('admin.recommendations')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('groups')} active={activeTab === 'groups'} onClick={() => { navigate('/admin/dashboard/groups'); setSidebarOpen(false); }}>
            <LayoutGrid className="icon" />
            <NavLabel>{t('admin.correctionGroups')}</NavLabel>
            <NavTooltip>{t('admin.correctionGroups')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('store-branding')} active={activeTab === 'store-branding'} onClick={() => { navigate('/admin/dashboard/store-branding'); setSidebarOpen(false); }}>
            <Palette className="icon" />
            <NavLabel>{t('admin.storeBranding')}</NavLabel>
            <NavTooltip>{t('admin.storeBranding')}</NavTooltip>
          </NavItem>
          <NavItem style={hideUnlessVisible('administration')} active={activeTab === 'administration'} onClick={() => { navigate('/admin/dashboard/administration'); setSidebarOpen(false); }}>
            <MapPin className="icon" />
            <NavLabel>{t('admin.administration')}</NavLabel>
            <NavTooltip>{t('admin.administration')}</NavTooltip>
          </NavItem>
          {/* "Other Lenses" tab retired — alternatives are managed under Recommendations. */}
        </SidebarNav>
        <SidebarFooter>
          {/* Desktop-only collapse / expand toggle. */}
          <SidebarCollapseToggle
            $collapsed={sidebarCollapsed}
            onClick={() => setSidebarCollapsed((v) => !v)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <PanelLeftClose size={16} /> {t('admin.collapse')}
              </span>
              <kbd style={{
                fontSize: '0.65rem', padding: '0.1rem 0.35rem', background: tokens.surface,
                border: `1px solid ${tokens.hairline}`, borderRadius: 5, color: tokens.muted, fontFamily: 'inherit',
              }}>[</kbd>
            </>}
          </SidebarCollapseToggle>
          <LogoutButton onClick={handleLogout}>
            <LogOut className="icon" />
            <NavLabel>{t('admin.logout')}</NavLabel>
            <NavTooltip>{t('admin.logout')}</NavTooltip>
          </LogoutButton>
        </SidebarFooter>
      </Sidebar>

      <MainContent>
        {/* Sticky top bar — global search (opens command palette), quick
            actions, theme + sidebar toggles, and user chip. */}
        <TopBar>
          <TopSearch
            role="button"
            tabIndex={0}
            onClick={() => setPaletteOpen(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPaletteOpen(true); } }}
            style={{ cursor: 'text' }}
            title={t('admin.openCommandPalette')}
          >
            <Search size={16} />
            {/* Visual-only placeholder — clicking anywhere opens the palette,
                which has the real input. Keeps a familiar search-bar look. */}
            <span style={{ flex: 1, color: '#94a3b8', fontSize: '0.9rem' }}>
              {`Search ${TAB_META[activeTab]?.title?.toLowerCase() || 'dashboard'} or jump to…`}
            </span>
            <kbd>{navigator.platform?.toLowerCase().includes('mac') ? t('admin.k') : t('admin.ctrlK')}</kbd>
          </TopSearch>

          <div style={{ flex: 1 }} />

          <TopAction
            onClick={() => setSidebarCollapsed((v) => !v)}
            data-toggled={sidebarCollapsed ? 'true' : 'false'}
            title={sidebarCollapsed ? 'Expand sidebar ([)' : 'Collapse sidebar ([)'}
            aria-label={t('admin.toggleSidebar')}
            style={{ display: window.innerWidth <= 768 ? 'none' : undefined }}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </TopAction>
          <TopAction
            onClick={toggleTheme}
            data-toggled={theme === 'dark' ? 'true' : 'false'}
            title={theme === 'dark' ? 'Switch to light mode (⌘J)' : 'Switch to dark mode (⌘J)'}
            aria-label={t('admin.toggleTheme')}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </TopAction>
          <TopAction
            onClick={() => setPaletteOpen(true)}
            title={t('admin.openCommandPaletteK')}
            aria-label={t('admin.commandPalette')}
          >
            <CommandIcon size={18} />
          </TopAction>
          <TopAction title={t('admin.quickAdd')} aria-label={t('admin.quickAdd')} onClick={() => toast.info('Quick add coming soon', { duration: 2000 })}>
            <Plus size={18} />
          </TopAction>
          <TopAction title={t('admin.notifications')} aria-label={t('admin.notifications')} onClick={() => toast.info('No new notifications', { duration: 2000 })}>
            <Bell size={18} />
            <NotifDot />
          </TopAction>

          <UserChip>
            <Avatar>
              {(() => {
                const f = currentUser?.firstName?.[0] || '';
                const l = currentUser?.lastName?.[0] || '';
                const initials = (f + l).toUpperCase();
                return initials || <Shield size={16} />;
              })()}
            </Avatar>
            <UserMeta className="user-text">
              <UserName>
                {currentUser
                  ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'Admin'
                  : t('admin.admin')}
              </UserName>
              <UserRole>{currentUser?.role || 'admin'}</UserRole>
            </UserMeta>
          </UserChip>
        </TopBar>
        {advisorViewOnly && (
          <div role="status" style={{ margin: '0.75rem 1.5rem 0', padding: '0.55rem 0.9rem', borderRadius: 10, background: '#eef2ff', border: '1px solid #c7d2fe', color: '#3730a3', fontSize: '0.85rem', fontWeight: 600 }}>
            {t('advisor.viewOnlyStrip')}
          </div>
        )}

        <ContentScroll>
          {/* These tabs render their own rich page header (title + action buttons),
              so we suppress the shared TAB_META header to avoid a duplicate title. */}
          {!['frame-stock', 'lens-stock'].includes(activeTab) && (
          <Header>
            <TitleRow>
              <TitleAccent />
              <Title>
                {TAB_META[activeTab]?.title || 'Admin Dashboard'}
                {activeTab === 'overview' && currentUser?.firstName && (
                  <span style={{
                    display: 'block', fontSize: '0.95rem', fontWeight: 500,
                    color: tokens.muted, marginTop: '0.15rem',
                  }}>
                    <Sparkles size={14} style={{ verticalAlign: '-2px', marginRight: '0.3rem', color: '#a855f7' }} />
                    {greetByTime()}, {currentUser.firstName} {t('admin.hereSWhatSHappening')}
                  </span>
                )}
              </Title>
            </TitleRow>
            {!(activeTab === 'overview' && currentUser?.firstName) && (
              <Subtitle>{TAB_META[activeTab]?.subtitle || 'Manage your optical store system'}</Subtitle>
            )}
          </Header>
          )}

          <TabPanel key={activeTab}>

        {activeTab === 'overview' && (
          <>
            {/* Date Range Filter — sits above the KPI strip so changing it
                visibly refreshes the headline numbers right below. */}
            <DateRangeBar>
              <DateLabel>{t('admin.from')}</DateLabel>
              <DateInput
                type="date"
                value={overviewStartDate}
                onChange={(e) => {
                  setOverviewStartDate(e.target.value);
                  fetchOverviewData(e.target.value, overviewEndDate);
                }}
              />
              <DateLabel>{t('admin.till')}</DateLabel>
              <DateInput
                type="date"
                value={overviewEndDate}
                onChange={(e) => {
                  setOverviewEndDate(e.target.value);
                  fetchOverviewData(overviewStartDate, e.target.value);
                }}
              />
              {(overviewStartDate || overviewEndDate) && (
                <button
                  onClick={() => {
                    setOverviewStartDate('');
                    setOverviewEndDate('');
                    fetchOverviewData('', '');
                  }}
                  style={{
                    padding: '0.5rem 0.85rem',
                    border: `1px solid ${tokens.hairline}`,
                    borderRadius: '9px',
                    background: 'white',
                    color: tokens.muted,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    transition: 'all 0.15s ' + tokens.ease,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fee2e2';
                    e.currentTarget.style.color = '#991b1b';
                    e.currentTarget.style.borderColor = '#fecaca';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.color = tokens.muted;
                    e.currentTarget.style.borderColor = tokens.hairline;
                  }}
                >
                  <X size={13} />
                  {t('admin.clear')}
                </button>
              )}
            </DateRangeBar>

            {/* Hero KPI strip — three cards, in business order:
                  Quantity (items sold)  →  Turnover  →  Profit (placeholder).
                Each card expands inline (accordion-style) to reveal its own
                breakdown — no duplicate detail cards below. Clicks on inner
                tree rows stop propagation so they don't collapse the parent. */}
            <StatsGrid $focused={!!focusedKpi}>
              <StatCard
                accent="linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)"
                $interactive
                $hidden={!!focusedKpi && focusedKpi !== 'quantity'}
                role="button"
                tabIndex={0}
                aria-expanded={kpiOpen.quantity}
                onClick={() => toggleKpi('quantity')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleKpi('quantity'); } }}
                aria-label={`${kpiOpen.quantity ? 'Collapse' : 'Expand'} Quantity breakdown`}
              >
                <StatHeader>
                  <StatTitle>
                    {t('admin.quantity')}
                    <ExpandCaret $open={kpiOpen.quantity} aria-hidden="true">
                      <ChevronRight size={14} />
                    </ExpandCaret>
                  </StatTitle>
                  <StatIcon color="#fef3c7" iconColor="#d97706">
                    <Package size={20} />
                  </StatIcon>
                </StatHeader>
                <StatValue>
                  {overviewLoading ? (
                    <Skeleton w="80px" h="1.95rem" />
                  ) : (
                    <>
                      <AnimatedNumber
                        value={(overviewData?.lensQuantities?.total || 0) + (overviewData?.frameQuantities?.total || 0) + (overviewData?.contactLensQuantities?.total || 0)}
                      />
                      <span style={{ fontSize: '0.85rem', color: tokens.muted, fontWeight: 500, marginLeft: '0.45rem' }}>
                        items
                      </span>
                    </>
                  )}
                </StatValue>
                <KpiFooter
                  data={quantitySeries}
                  color="#f59e0b"
                  loading={overviewLoading || quantitySeries.length === 0}
                />

                {/* Inline breakdown — two columns (Lenses, Frames). Each column
                    shows its total upfront. Lenses expands into Simple Vision
                    and Progressive sub-cards (each with %). Frames expands into
                    five categorical breakdowns (brands / shapes / age / size /
                    color), every row tagged with its share of that category's
                    total. Progressive tiers are placeholder rows until lenses
                    can be tagged by tier server-side. */}
                <KpiExpand $open={kpiOpen.quantity} onClick={(e) => e.stopPropagation()}>
                  {overviewLoading ? (
                    <QtyColumns>
                      {[0, 1].map(i => (
                        <QtyColumn key={i}>
                          <Skeleton w="60%" h="12px" />
                          <Skeleton w="40%" h="22px" />
                        </QtyColumn>
                      ))}
                    </QtyColumns>
                  ) : (() => {
                    // Quantity drills store -> seller -> product. Everything below a
                    // seller is the same three-column breakdown that used to sit
                    // directly on the card, so it is rendered by one function taking a
                    // data source plus a drill-path prefix - each seller needs its own
                    // independent open/closed state, which is what the prefix buys.
                    const renderQtySection = (src, pfx, { showOrigin = false } = {}) => {
                      const intern    = src?.lensQuantities?.intern || 0;
                      const extern    = src?.lensQuantities?.extern || 0;
                      const rx        = src?.lensQuantities?.rx     || 0;
                      const progTotal = src?.lensQuantities?.progressive || 0;
                      const svTotal   = intern + extern + rx;
                      const lensTotal = svTotal + progTotal;
                      const clTotal       = src?.contactLensQuantities?.total       || 0;
                      const clHard        = src?.contactLensQuantities?.hard        || 0;
                      const clSoft        = src?.contactLensQuantities?.soft        || 0;
                      const clTherapeutic = src?.contactLensQuantities?.therapeutic || 0;
                      const clNamesByGroup = src?.contactLensNames || {};
                      const clNamesHard        = clNamesByGroup.hard        || [];
                      const clNamesSoft        = clNamesByGroup.soft        || [];
                      const clNamesTherapeutic = clNamesByGroup.therapeutic || [];

                      // Lens names per source group (drives the deepest level of
                      // the Lenses drill-down).
                      const lensNamesByGroup = src?.lensNames || {};
                      const lensNamesIntern = lensNamesByGroup.intern || [];
                      const lensNamesExtern = lensNamesByGroup.extern || [];
                      const lensNamesRx     = lensNamesByGroup.rx     || [];
                      const lensNamesProg   = lensNamesByGroup.progressive || [];
                      // Progressive tiers — which SLOT the salesperson picked in
                      // the recommendation flow: the professional recommendation
                      // → Fair, "Other (Up)" → Premium, "Other (Down)" → Modest.
                      const lensNamesProgPremium = lensNamesByGroup.progressivePremium || [];
                      const lensNamesProgFair    = lensNamesByGroup.progressiveFair    || [];
                      const lensNamesProgModest  = lensNamesByGroup.progressiveModest  || [];
                      const progPremium = src?.lensQuantities?.progressivePremium ?? lensNamesProgPremium.reduce((s, l) => s + (l.count || 0), 0);
                      const progFair    = src?.lensQuantities?.progressiveFair    ?? lensNamesProgFair.reduce((s, l) => s + (l.count || 0), 0);
                      const progModest  = src?.lensQuantities?.progressiveModest  ?? lensNamesProgModest.reduce((s, l) => s + (l.count || 0), 0);
                      // Use the sum of tier counts when available so the bars
                      // and percentages always add up to 100% inside Progressive.
                      const progDenom = (progPremium + progFair + progModest) || progTotal;

                      const frameTotal = src?.frameQuantities?.total || 0;
                      const byBrand    = src?.frameQuantities?.byBrand || [];
                      const byShape    = src?.frameQuantities?.byShape || [];
                      const byGender   = src?.frameQuantities?.byGender || src?.frameQuantities?.byAge || [];
                      const bySize     = src?.frameQuantities?.bySize  || [];
                      const byColor    = src?.frameQuantities?.byColor || [];
                      // Stock-only frame items (with attributes) — used for the
                      // deepest drill-down level (frame names matching a chosen
                      // brand / shape / age / size / color).
                      const frameItems = src?.frameQuantities?.items || [];

                      // Format a percentage chip — never NaN, always 0–100.
                      const pct = (count, denom) => {
                        if (!denom || denom <= 0) return '0%';
                        return `${Math.round((count / denom) * 100)}%`;
                      };
                      const pctNum = (count, denom) => {
                        if (!denom || denom <= 0) return 0;
                        return (count / denom) * 100;
                      };

                      const colorSwatch = (name) => {
                        // Frame colours come out of the catalogue as hex
                        // (#d4af37); older/manual rows use plain names.
                        const value = String(name).trim();
                        if (/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)) return value;
                        switch (value.toLowerCase()) {
                          case 'black':   return '#111827';
                          case 'white':   return '#f8fafc';
                          case 'grey':
                          case 'gray':    return '#94a3b8';
                          case 'brown':   return '#92400e';
                          case 'red':     return '#dc2626';
                          case 'orange':  return '#f97316';
                          case 'yellow':  return '#eab308';
                          case 'green':   return '#16a34a';
                          case 'blue':    return '#2563eb';
                          case 'purple':  return '#9333ea';
                          case 'pink':    return '#ec4899';
                          default:        return '#cbd5e1';
                        }
                      };

                      const prettyShape = (n) => String(n).replace(/_/g, ' ').toLowerCase()
                        .replace(/\b\w/g, c => c.toUpperCase());

                      // Lens → Stores → Employees. The store/employee levels
                      // only earn their place in the tenant-wide view; once
                      // you have already drilled into a seller they can only
                      // repeat the branch you came down.
                      const renderLeafList = (items, opts = {}) => {
                        const { limit = 8, prettyName = null } = opts;
                        if (!items || items.length === 0) return <QtyLeafEmpty>{t('admin.noItemsRecorded')}</QtyLeafEmpty>;
                        const denom = items.reduce((s, it) => s + (it.count || 0), 0);

                        return items.slice(0, limit).map((it, i) => {
                          const hasStores = showOrigin && it.byStore?.length > 0;
                          const lensPath = `${pfx}.ll.${i}.${it.name}`;
                          const lensOpen = isDrillOpen(lensPath);

                          return (
                            <div key={i} style={{ borderBottom: `1px solid ${tokens.hairline}`, paddingBottom: '0.1rem', marginBottom: '0.1rem' }}>
                              {/* Level 1 — Lens name row */}
                              <QtyLeafRow
                                as="button"
                                onClick={() => hasStores && toggleDrill(lensPath)}
                                style={{ all: 'unset', display: 'flex', alignItems: 'center', width: '100%', cursor: hasStores ? 'pointer' : 'default', padding: '0.2rem 0', borderRadius: 4 }}
                              >
                                {hasStores && (
                                  <ChevronRight size={11} style={{ marginRight: 4, color: tokens.muted, flexShrink: 0, transform: lensOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                )}
                                <span title={prettyName ? prettyName(it.name) : it.name}
                                  style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, fontSize: '0.82rem', color: tokens.ink }}>
                                  {prettyName ? prettyName(it.name) : it.name}
                                </span>
                                <QtyValueCell>
                                  <QtyValue>{it.count}</QtyValue>
                                  <QtyPct>{pct(it.count, denom)}</QtyPct>
                                </QtyValueCell>
                              </QtyLeafRow>

                              {/* Level 2 — Stores */}
                              {hasStores && lensOpen && (
                                <div style={{ paddingLeft: '1rem', paddingBottom: '0.3rem' }}>
                                  {it.byStore.map((store, si) => {
                                    const storePath = `${lensPath}.s.${si}`;
                                    const storeOpen = isDrillOpen(storePath);
                                    const hasEmps = store.employees?.length > 0;
                                    return (
                                      <div key={si}>
                                        <div
                                          onClick={() => hasEmps && toggleDrill(storePath)}
                                          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.18rem 0', cursor: hasEmps ? 'pointer' : 'default' }}
                                        >
                                          {hasEmps && (
                                            <ChevronRight size={10} style={{ color: tokens.muted, flexShrink: 0, transform: storeOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                          )}
                                          {!hasEmps && <span style={{ width: 14 }} />}
                                          <Store size={11} style={{ color: '#6366f1', flexShrink: 0 }} />
                                          <span style={{ flex: 1, fontSize: '0.78rem', color: tokens.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{store.name}</span>
                                          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#6366f1', flexShrink: 0 }}>{store.count}</span>
                                          <span style={{ fontSize: '0.7rem', color: tokens.muted, flexShrink: 0, minWidth: 32, textAlign: 'right' }}>{pct(store.count, it.count)}</span>
                                        </div>

                                        {/* Level 3 — Employees at this store */}
                                        {hasEmps && storeOpen && (
                                          <div style={{ paddingLeft: '1.5rem', paddingBottom: '0.25rem' }}>
                                            {store.employees.map((emp, ei) => (
                                              <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.15rem 0' }}>
                                                <Users size={10} style={{ color: '#10b981', flexShrink: 0 }} />
                                                <span style={{ flex: 1, fontSize: '0.76rem', color: tokens.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.name}</span>
                                                <span style={{ fontSize: '0.76rem', fontWeight: 700, color: '#10b981', flexShrink: 0 }}>{emp.count}</span>
                                                <span style={{ fontSize: '0.7rem', color: tokens.muted, flexShrink: 0, minWidth: 32, textAlign: 'right' }}>{pct(emp.count, store.count)}</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        });
                      };

                      // Render a category list (brands / shapes / age / size /
                      // color). Each category row is itself clickable; on click,
                      // a deeper panel reveals the actual frame names matching
                      // that filter (drawn from stock-only frameItems).
                      const renderCategoryRows = (rows, opts = {}) => {
                        const {
                          color = tokens.brand,
                          withSwatch = false,
                          prettyName = null,
                          pathPrefix,
                          filterKey,
                        } = opts;
                        if (!rows || rows.length === 0) {
                          return <QtyEmpty>{t('admin.noData')}</QtyEmpty>;
                        }
                        const denom = rows.reduce((s, r) => s + (r.count || 0), 0);
                        const visible = rows.slice(0, 6);
                        const rowPaths = visible.map(r => `${pathPrefix}.${r.name}`);
                        const focusedRow = rowPaths.findIndex(rp => isDrillOpen(rp));
                        return visible.map((item, i) => {
                          if (focusedRow >= 0 && focusedRow !== i) return null;
                          const path = rowPaths[i];
                          const open = isDrillOpen(path);
                          // Match logic for each filter dimension.
                          const matches = (frame) => {
                            // Colour used to arrive as an array on some rows; a
                            // single value is what the catalogue actually
                            // stores, so handle both.
                            const value = frame[filterKey];
                            if (Array.isArray(value)) return value.includes(item.name);
                            return value === item.name;
                          };
                          const matchedFrames = frameItems.filter(matches);
                          const display = prettyName ? prettyName(item.name) : item.name;
                          return (
                            <div key={i}>
                              <QtyDrillRow
                                type="button"
                                data-open={open}
                                onClick={() => focusDrill(path, rowPaths)}
                                aria-expanded={open}
                              >
                                <QtyRow style={{ padding: 0 }}>
                                  <QtyLabel>
                                    <QtyDrillCaret $open={open} aria-hidden="true">
                                      <ChevronRight size={12} />
                                    </QtyDrillCaret>
                                    {withSwatch && <ColorSwatch $bg={colorSwatch(item.name)} />}
                                    {display}
                                  </QtyLabel>
                                  <QtyValueCell>
                                    <QtyValue>{item.count}</QtyValue>
                                    <QtyPct>{pct(item.count, denom)}</QtyPct>
                                  </QtyValueCell>
                                </QtyRow>
                                <QtyBarTrack>
                                  <QtyBarFill $pct={pctNum(item.count, denom)} $color={color} />
                                </QtyBarTrack>
                              </QtyDrillRow>
                              <QtyDrillPanel $open={open} aria-hidden={!open}>
                                <div className="qty-drill-inner">
                                  {renderLeafList(matchedFrames)}
                                </div>
                              </QtyDrillPanel>
                            </div>
                          );
                        });
                      };

                      // Every level of this card focuses: opening a node
                      // hides its siblings, so what stays on screen is the
                      // path you clicked. Each focused header is still there
                      // and still clickable, which is how you walk back up.
                      const columnPaths = [`${pfx}.lenses`, `${pfx}.frames`, `${pfx}.contact`];
                      const focusedColumn = columnPaths.findIndex(path => isDrillOpen(path));
                      const lensSectionPaths = [`${pfx}.lenses.sv`, `${pfx}.lenses.prog`];
                      const focusedLensSection = lensSectionPaths.findIndex(path => isDrillOpen(path));
                      const svGroups = [
                        { key: 'intern', label: 'Intern', count: intern, items: lensNamesIntern, color: '#6366f1' },
                        { key: 'extern', label: 'Extern', count: extern, items: lensNamesExtern, color: '#0ea5e9' },
                        { key: 'rx',     label: 'Rx',     count: rx,     items: lensNamesRx,     color: '#a855f7' },
                      ];
                      const svPaths = svGroups.map(g => `${pfx}.lenses.sv.${g.key}`);
                      const focusedSv = svPaths.findIndex(path => isDrillOpen(path));
                      const progTiers = [
                        { key: 'premium', label: 'Premium', count: progPremium, items: lensNamesProgPremium, color: '#a855f7' },
                        { key: 'fair',    label: 'Fair',    count: progFair,    items: lensNamesProgFair,    color: '#8b5cf6' },
                        { key: 'modest',  label: 'Modest',  count: progModest,  items: lensNamesProgModest,  color: '#c084fc' },
                      ];
                      const progPaths = progTiers.map(tr => `${pfx}.lenses.prog.${tr.key}`);
                      const focusedProg = progPaths.findIndex(path => isDrillOpen(path));
                      const clGroups = [
                        { key: 'hard',        label: 'Hard',        count: clHard,        items: clNamesHard,        color: '#0ea5e9' },
                        { key: 'soft',        label: 'Soft',        count: clSoft,        items: clNamesSoft,        color: '#10b981' },
                        { key: 'therapeutic', label: 'Therapeutic', count: clTherapeutic, items: clNamesTherapeutic, color: '#8b5cf6' },
                      ];
                      const clPaths = clGroups.map(g => `${pfx}.contact.${g.key}`);
                      const focusedCl = clPaths.findIndex(path => isDrillOpen(path));
                      const frameAxes = [
                        { key: 'brand',  label: t('admin.brands'),      rows: byBrand,  color: '#10b981' },
                        { key: 'shape',  label: t('admin.shapes'),      rows: byShape,  color: '#0ea5e9', prettyName: prettyShape },
                        { key: 'gender', label: t('admin.frameGender'), rows: byGender, color: '#f59e0b', prettyName: prettyShape },
                        { key: 'size',   label: t('admin.size'),        rows: bySize,   color: '#8b5cf6' },
                        { key: 'color',  label: t('admin.color'),       rows: byColor,  color: '#ec4899', withSwatch: true },
                      ];
                      // An axis is focused when one of its own rows is open.
                      // renderCategoryRows caps the list at 6, so match that.
                      const focusedFrameAxis = frameAxes.findIndex(axis =>
                        (axis.rows || []).slice(0, 6).some(row => isDrillOpen(`${pfx}.frames.${axis.key}.${row.name}`)));

                      return (
                        <QtyColumns>
                          {/* ── Lenses column ─────────────────────────── */}
                          {(focusedColumn < 0 || focusedColumn === 0) && (
                          <QtyColumn data-open={isDrillOpen(`${pfx}.lenses`)}>
                            <QtyColumnHead
                              type="button"
                              onClick={() => focusDrill(`${pfx}.lenses`, columnPaths)}
                              aria-expanded={isDrillOpen(`${pfx}.lenses`)}
                              aria-label={`${isDrillOpen(`${pfx}.lenses`) ? 'Collapse' : 'Expand'} Lenses breakdown`}
                            >
                              <QtyHeadRow>
                                <QtyColumnDot $color="#6366f1" />
                                <QtyHeadTitle>{t('admin.lenses')}</QtyHeadTitle>
                                <QtyHeadCaret $open={isDrillOpen(`${pfx}.lenses`)} aria-hidden="true">
                                  <ChevronRight size={14} />
                                </QtyHeadCaret>
                              </QtyHeadRow>
                              <QtyHeadTotal>{lensTotal}</QtyHeadTotal>
                            </QtyColumnHead>
                            <QtyBody $open={isDrillOpen(`${pfx}.lenses`)} aria-hidden={!isDrillOpen(`${pfx}.lenses`)}>
                              <div className="qty-body-inner">
                                {/* ── Simple Vision sub-card ── */}
                                {(focusedLensSection < 0 || focusedLensSection === 0) && (
                                <QtyNested>
                                  <QtyDrillRow
                                    type="button"
                                    data-open={isDrillOpen(`${pfx}.lenses.sv`)}
                                    onClick={() => focusDrill(`${pfx}.lenses.sv`, lensSectionPaths)}
                                    aria-expanded={isDrillOpen(`${pfx}.lenses.sv`)}
                                  >
                                    <QtyNestedHead>
                                      <QtyDrillCaret $open={isDrillOpen(`${pfx}.lenses.sv`)} aria-hidden="true">
                                        <ChevronRight size={12} />
                                      </QtyDrillCaret>
                                      <QtyColumnDot $color="#6366f1" />
                                      <span className="qty-nested-title">{t('admin.simpleVision')}</span>
                                      <span>
                                        {svTotal}
                                        <QtyPct style={{ marginLeft: '0.35rem' }}>
                                          {pct(svTotal, lensTotal)}
                                        </QtyPct>
                                      </span>
                                    </QtyNestedHead>
                                  </QtyDrillRow>
                                  <QtyDrillPanel $open={isDrillOpen(`${pfx}.lenses.sv`)} aria-hidden={!isDrillOpen(`${pfx}.lenses.sv`)}>
                                    <div className="qty-drill-inner">
                                      {svGroups.map((group, gi) => {
                                        const path = svPaths[gi];
                                        const open = isDrillOpen(path);
                                        if (focusedSv >= 0 && focusedSv !== gi) return null;
                                        return (
                                          <div key={group.key}>
                                            <QtyDrillRow
                                              type="button"
                                              data-open={open}
                                              onClick={() => focusDrill(path, svPaths)}
                                              aria-expanded={open}
                                            >
                                              <QtyRow style={{ padding: 0 }}>
                                                <QtyLabel>
                                                  <QtyDrillCaret $open={open} aria-hidden="true">
                                                    <ChevronRight size={12} />
                                                  </QtyDrillCaret>
                                                  {group.label}
                                                </QtyLabel>
                                                <QtyValueCell>
                                                  <QtyValue>{group.count}</QtyValue>
                                                  <QtyPct>{pct(group.count, svTotal)}</QtyPct>
                                                </QtyValueCell>
                                              </QtyRow>
                                              <QtyBarTrack>
                                                <QtyBarFill $pct={pctNum(group.count, svTotal)} $color={group.color} />
                                              </QtyBarTrack>
                                            </QtyDrillRow>
                                            <QtyDrillPanel $open={open} aria-hidden={!open}>
                                              <div className="qty-drill-inner">
                                                {renderLeafList(group.items)}
                                              </div>
                                            </QtyDrillPanel>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </QtyDrillPanel>
                                </QtyNested>
                                )}

                                {/* ── Progressive sub-card ── */}
                                {(focusedLensSection < 0 || focusedLensSection === 1) && (
                                <QtyNested>
                                  <QtyDrillRow
                                    type="button"
                                    data-open={isDrillOpen(`${pfx}.lenses.prog`)}
                                    onClick={() => focusDrill(`${pfx}.lenses.prog`, lensSectionPaths)}
                                    aria-expanded={isDrillOpen(`${pfx}.lenses.prog`)}
                                  >
                                    <QtyNestedHead>
                                      <QtyDrillCaret $open={isDrillOpen(`${pfx}.lenses.prog`)} aria-hidden="true">
                                        <ChevronRight size={12} />
                                      </QtyDrillCaret>
                                      <QtyColumnDot $color="#a855f7" />
                                      <span className="qty-nested-title">{t('admin.progressive')}</span>
                                      <span>
                                        {progTotal}
                                        <QtyPct style={{ marginLeft: '0.35rem' }}>
                                          {pct(progTotal, lensTotal)}
                                        </QtyPct>
                                      </span>
                                    </QtyNestedHead>
                                  </QtyDrillRow>
                                  <QtyDrillPanel $open={isDrillOpen(`${pfx}.lenses.prog`)} aria-hidden={!isDrillOpen(`${pfx}.lenses.prog`)}>
                                    <div className="qty-drill-inner">
                                      {(progDenom === 0 && lensNamesProg.length > 0) ? (
                                        // No tier breakdown available (legacy data) —
                                        // fall back to the flat lens-name list.
                                        renderLeafList(lensNamesProg)
                                      ) : (
                                        progTiers.map((tier, ti) => {
                                          const path = progPaths[ti];
                                          const open = isDrillOpen(path);
                                          if (focusedProg >= 0 && focusedProg !== ti) return null;
                                          return (
                                            <div key={tier.key}>
                                              <QtyDrillRow
                                                type="button"
                                                data-open={open}
                                                onClick={() => focusDrill(path, progPaths)}
                                                aria-expanded={open}
                                              >
                                                <QtyRow style={{ padding: 0 }}>
                                                  <QtyLabel>
                                                    <QtyDrillCaret $open={open} aria-hidden="true">
                                                      <ChevronRight size={12} />
                                                    </QtyDrillCaret>
                                                    {tier.label}
                                                  </QtyLabel>
                                                  <QtyValueCell>
                                                    <QtyValue>{tier.count}</QtyValue>
                                                    <QtyPct>{pct(tier.count, progDenom)}</QtyPct>
                                                  </QtyValueCell>
                                                </QtyRow>
                                                <QtyBarTrack>
                                                  <QtyBarFill $pct={pctNum(tier.count, progDenom)} $color={tier.color} />
                                                </QtyBarTrack>
                                              </QtyDrillRow>
                                              <QtyDrillPanel $open={open} aria-hidden={!open}>
                                                <div className="qty-drill-inner">
                                                  {renderLeafList(tier.items)}
                                                </div>
                                              </QtyDrillPanel>
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  </QtyDrillPanel>
                                </QtyNested>
                                )}
                              </div>
                            </QtyBody>
                          </QtyColumn>
                          )}

                          {/* ── Frames column (stock-only drill-downs) ── */}
                          {(focusedColumn < 0 || focusedColumn === 1) && (
                          <QtyColumn data-open={isDrillOpen(`${pfx}.frames`)}>
                            <QtyColumnHead
                              type="button"
                              onClick={() => focusDrill(`${pfx}.frames`, columnPaths)}
                              aria-expanded={isDrillOpen(`${pfx}.frames`)}
                              aria-label={`${isDrillOpen(`${pfx}.frames`) ? 'Collapse' : 'Expand'} Frames breakdown`}
                            >
                              <QtyHeadRow>
                                <QtyColumnDot $color="#10b981" />
                                <QtyHeadTitle>{t('admin.frames')}</QtyHeadTitle>
                                <QtyHeadCaret $open={isDrillOpen(`${pfx}.frames`)} aria-hidden="true">
                                  <ChevronRight size={14} />
                                </QtyHeadCaret>
                              </QtyHeadRow>
                              <QtyHeadTotal>{frameTotal}</QtyHeadTotal>
                            </QtyColumnHead>
                            <QtyBody $open={isDrillOpen(`${pfx}.frames`)} aria-hidden={!isDrillOpen(`${pfx}.frames`)}>
                              <div className="qty-body-inner">
                                {frameAxes.map((axis, ai) => {
                                  if (focusedFrameAxis >= 0 && focusedFrameAxis !== ai) return null;
                                  return (
                                    <React.Fragment key={axis.key}>
                                      <QtySubLabel>{axis.label}</QtySubLabel>
                                      {renderCategoryRows(axis.rows, {
                                        color: axis.color,
                                        prettyName: axis.prettyName || null,
                                        withSwatch: !!axis.withSwatch,
                                        pathPrefix: `${pfx}.frames.${axis.key}`,
                                        filterKey: axis.key,
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            </QtyBody>
                          </QtyColumn>
                          )}

                          {/* ── Contact Lenses column ── */}
                          {(focusedColumn < 0 || focusedColumn === 2) && (
                          <QtyColumn data-open={isDrillOpen(`${pfx}.contact`)}>
                            <QtyColumnHead
                              type="button"
                              onClick={() => focusDrill(`${pfx}.contact`, columnPaths)}
                              aria-expanded={isDrillOpen(`${pfx}.contact`)}
                              aria-label={`${isDrillOpen(`${pfx}.contact`) ? 'Collapse' : 'Expand'} Contact Lenses breakdown`}
                            >
                              <QtyHeadRow>
                                <QtyColumnDot $color="#06b6d4" />
                                <QtyHeadTitle>{t('admin.contactLenses')}</QtyHeadTitle>
                                <QtyHeadCaret $open={isDrillOpen(`${pfx}.contact`)} aria-hidden="true">
                                  <ChevronRight size={14} />
                                </QtyHeadCaret>
                              </QtyHeadRow>
                              <QtyHeadTotal>{clTotal}</QtyHeadTotal>
                            </QtyColumnHead>
                            <QtyBody $open={isDrillOpen(`${pfx}.contact`)} aria-hidden={!isDrillOpen(`${pfx}.contact`)}>
                              <div className="qty-body-inner">
                                {clGroups.map((group, gi) => {
                                  const path = clPaths[gi];
                                  const open = isDrillOpen(path);
                                  if (focusedCl >= 0 && focusedCl !== gi) return null;
                                  return (
                                    <div key={group.key}>
                                      <QtyDrillRow
                                        type="button"
                                        data-open={open}
                                        onClick={() => focusDrill(path, clPaths)}
                                        aria-expanded={open}
                                      >
                                        <QtyRow style={{ padding: 0 }}>
                                          <QtyLabel>
                                            <QtyDrillCaret $open={open} aria-hidden="true">
                                              <ChevronRight size={12} />
                                            </QtyDrillCaret>
                                            {group.label}
                                          </QtyLabel>
                                          <QtyValueCell>
                                            <QtyValue>{group.count}</QtyValue>
                                            <QtyPct>{pct(group.count, clTotal)}</QtyPct>
                                          </QtyValueCell>
                                        </QtyRow>
                                        <QtyBarTrack>
                                          <QtyBarFill $pct={pctNum(group.count, clTotal)} $color={group.color} />
                                        </QtyBarTrack>
                                      </QtyDrillRow>
                                      <QtyDrillPanel $open={open} aria-hidden={!open}>
                                        <div className="qty-drill-inner">
                                          {renderLeafList(group.items)}
                                        </div>
                                      </QtyDrillPanel>
                                    </div>
                                  );
                                })}
                              </div>
                            </QtyBody>
                          </QtyColumn>
                          )}
                        </QtyColumns>
                      );
                    };

                    const stores = overviewData?.quantityByStore || [];
                    // Older payloads (or a window with no sales) carry no store tree -
                    // fall back to the flat breakdown rather than rendering nothing.
                    if (!stores.length) return renderQtySection(overviewData, 'qty', { showOrigin: true });
                    const storesTotal = stores.reduce((s, st) => s + (st.total || 0), 0);
                    // Drilling a store focuses it — the other stores drop away
                    // so the one you picked owns the card. Clicking its header
                    // again brings the full list back.
                    const storePaths = stores.map((_, i) => `qty.s${i}`);
                    const focusedStore = storePaths.findIndex(path => isDrillOpen(path));

                    const modeSwitch = (
                      <QtyModeBar role="tablist">
                        {[
                          { key: 'drill',   label: t('admin.viewDrill') },
                          { key: 'stores',  label: t('admin.compareStores') },
                          { key: 'sellers', label: t('admin.compareSellers') },
                        ].map(mode => (
                          <QtyModeButton
                            key={mode.key}
                            type="button"
                            role="tab"
                            aria-selected={qtyView === mode.key}
                            $active={qtyView === mode.key}
                            onClick={() => { setQtyView(mode.key); resetCompare(); setQtySellerStore('all'); }}
                          >
                            {mode.label}
                          </QtyModeButton>
                        ))}
                      </QtyModeBar>
                    );

                    if (qtyView !== 'drill') {
                      // One row per store, or one row per seller across every
                      // store — the seller rows carry their store as a subtitle
                      // so two people with the same first name stay apart.
                      const allRows = qtyView === 'stores'
                        ? stores.map(store => ({ key: store.storeId || store.name, label: store.name, sub: null, storeKey: store.storeId || store.name, data: store }))
                        : stores.flatMap(store => store.sellers.map(seller => ({
                          key: `${store.storeId || store.name}:${seller.employeeId || seller.name}`,
                          label: seller.name,
                          sub: store.name,
                          storeKey: store.storeId || store.name,
                          data: seller,
                        })));
                      allRows.sort((a, b) => b.data.total - a.data.total);
                      // The filter only narrows the picker; already-staged
                      // picks from other stores stay staged.
                      const rows = (qtyView === 'sellers' && qtySellerStore !== 'all')
                        ? allRows.filter(row => row.storeKey === qtySellerStore)
                        : allRows;
                      const maxTotal = rows.reduce((m, r) => Math.max(m, r.data.total || 0), 0);
                      const columns = [
                        { label: t('admin.lenses'),        get: d => d.lensQuantities.total },
                        { label: t('admin.frames'),        get: d => d.frameQuantities.total },
                        { label: t('admin.contactLenses'), get: d => d.contactLensQuantities.total },
                        { label: 'Intern',                 get: d => d.lensQuantities.intern },
                        { label: 'Extern',                 get: d => d.lensQuantities.extern },
                        { label: 'Rx',                     get: d => d.lensQuantities.rx },
                        { label: t('admin.progressive'),   get: d => d.lensQuantities.progressive },
                      ];

                      // Resolve against allRows so a committed comparison
                      // survives narrowing the picker to a single store.
                      const picked = qtyComparison
                        .map(key => allRows.find(row => row.key === key))
                        .filter(Boolean);
                      const stagedCount = allRows.filter(row => qtyCompareSel.has(row.key)).length;
                      const metrics = [{ label: t('admin.total'), get: d => d.total }, ...columns];

                      return (
                        <>
                          {modeSwitch}
                          {picked.length >= 2 && (
                            <QtyVersus>
                              <QtyVersusTable>
                                <thead>
                                  <tr>
                                    <th>{qtyView === 'stores' ? t('admin.store') : t('admin.seller')}</th>
                                    {picked.map(row => (
                                      <th key={row.key}>
                                        {row.label}
                                        {row.sub && <div style={{ fontSize: '0.7rem', fontWeight: 500, color: tokens.muted }}>{row.sub}</div>}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {metrics.map(metric => {
                                    const values = picked.map(row => metric.get(row.data) || 0);
                                    const best = Math.max(...values);
                                    const tied = values.filter(v => v === best).length === values.length;
                                    return (
                                      <tr key={metric.label}>
                                        <td>{metric.label}</td>
                                        {values.map((value, vi) => (
                                          <td key={picked[vi].key} data-best={(!tied && value === best && value > 0) ? 'true' : undefined}>
                                            {value}
                                          </td>
                                        ))}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </QtyVersusTable>
                            </QtyVersus>
                          )}
                          {rows.length === 0 ? <QtyEmpty>{t('admin.noData')}</QtyEmpty> : (
                            <div style={{ overflowX: 'auto' }}>
                              <QtySelectBar>
                                {qtyView === 'sellers' && (
                                  <FilterSelect
                                    value={qtySellerStore}
                                    onChange={(e) => setQtySellerStore(e.target.value)}
                                    aria-label={t('admin.store')}
                                  >
                                    <option value="all">{t('admin.allStores')}</option>
                                    {stores.map(store => (
                                      <option key={store.storeId || store.name} value={store.storeId || store.name}>
                                        {store.name}
                                      </option>
                                    ))}
                                  </FilterSelect>
                                )}
                                <span>
                                  {stagedCount === 0
                                    ? t('admin.selectToCompare')
                                    : `${stagedCount} ${t('admin.selectedCount')}`}
                                </span>
                                <QtyCompareButton
                                  type="button"
                                  disabled={stagedCount < 2}
                                  onClick={() => setQtyComparison([...qtyCompareSel])}
                                >
                                  {t('admin.compare')}
                                </QtyCompareButton>
                                {(stagedCount > 0 || picked.length > 0) && (
                                  <QtyModeButton type="button" onClick={resetCompare}>
                                    {t('admin.clearSelection')}
                                  </QtyModeButton>
                                )}
                              </QtySelectBar>
                              <QtyCompareTable>
                                <thead>
                                  <tr>
                                    <th aria-label={t('admin.selectToCompare')} style={{ width: 28 }} />
                                    <th>{qtyView === 'stores' ? t('admin.store') : t('admin.seller')}</th>
                                    <th>{t('admin.total')}</th>
                                    {columns.map(col => <th key={col.label}>{col.label}</th>)}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map(row => (
                                    <tr key={row.key} style={qtyCompareSel.has(row.key) ? { background: tokens.brandSoft } : undefined}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={qtyCompareSel.has(row.key)}
                                          onChange={() => toggleCompareSel(row.key)}
                                          aria-label={`${t('admin.selectToCompare')}: ${row.label}`}
                                          style={{ cursor: 'pointer' }}
                                        />
                                      </td>
                                      <td>
                                        <div style={{ fontWeight: 700, color: tokens.ink }}>{row.label}</div>
                                        {row.sub && <div style={{ fontSize: '0.72rem', color: tokens.muted }}>{row.sub}</div>}
                                        <QtyBarTrack style={{ marginTop: '0.3rem' }}>
                                          <QtyBarFill $pct={maxTotal > 0 ? (row.data.total / maxTotal) * 100 : 0} $color="#f59e0b" />
                                        </QtyBarTrack>
                                      </td>
                                      <td style={{ fontWeight: 800, color: tokens.ink }}>{row.data.total}</td>
                                      {columns.map(col => {
                                        const value = col.get(row.data) || 0;
                                        return <td key={col.label} style={{ color: value ? tokens.body : tokens.muted }}>{value}</td>;
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </QtyCompareTable>
                            </div>
                          )}
                        </>
                      );
                    }

                    return (
                      <>
                        {modeSwitch}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                        {focusedStore >= 0 && (
                          <QtyBackButton type="button" onClick={() => focusDrill(storePaths[focusedStore], storePaths)}>
                            <ChevronRight size={12} />
                            {t('admin.backToAllStores')}
                          </QtyBackButton>
                        )}
                        {stores.map((store, si) => {
                          const storePath = storePaths[si];
                          const storeOpen = isDrillOpen(storePath);
                          if (focusedStore >= 0 && focusedStore !== si) return null;
                          const storeShare = storesTotal > 0 ? Math.round((store.total / storesTotal) * 100) : 0;
                          const sellerPaths = store.sellers.map((_, i) => `${storePath}.e${i}`);
                          const focusedSeller = sellerPaths.findIndex(path => isDrillOpen(path));
                          return (
                            <QtyColumn key={store.storeId || si} data-open={storeOpen}>
                              <QtyColumnHead
                                type="button"
                                onClick={() => focusDrill(storePath, storePaths)}
                                aria-expanded={storeOpen}
                                aria-label={`${storeOpen ? 'Back to all stores from' : 'Focus'} ${store.name}`}
                              >
                                <QtyHeadRow>
                                  <Store size={12} style={{ color: '#6366f1', flexShrink: 0 }} />
                                  <QtyHeadTitle title={store.name}>{store.name}</QtyHeadTitle>
                                  <QtyHeadCaret $open={storeOpen} aria-hidden="true">
                                    <ChevronRight size={14} />
                                  </QtyHeadCaret>
                                </QtyHeadRow>
                                <QtyHeadTotal>
                                  {store.total}
                                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: tokens.muted, marginLeft: '0.4rem' }}>{storeShare}%</span>
                                </QtyHeadTotal>
                              </QtyColumnHead>
                              <QtyBody $open={storeOpen} aria-hidden={!storeOpen}>
                                <div className="qty-body-inner">
                                  {focusedSeller >= 0 && (
                                    <QtyBackButton type="button" onClick={() => focusDrill(sellerPaths[focusedSeller], sellerPaths)}>
                                      <ChevronRight size={12} />
                                      {t('admin.backToAllSellers')}
                                    </QtyBackButton>
                                  )}
                                  {store.sellers.length === 0
                                    ? <QtyEmpty>{t('admin.noData')}</QtyEmpty>
                                    : store.sellers.map((seller, ei) => {
                                      const sellerPath = sellerPaths[ei];
                                      const sellerOpen = isDrillOpen(sellerPath);
                                      if (focusedSeller >= 0 && focusedSeller !== ei) return null;
                                      return (
                                        <div key={seller.employeeId || ei}>
                                          <QtyDrillRow
                                            type="button"
                                            data-open={sellerOpen}
                                            onClick={() => focusDrill(sellerPath, sellerPaths)}
                                            aria-expanded={sellerOpen}
                                          >
                                            <QtyRow style={{ padding: 0 }}>
                                              <QtyLabel>
                                                <QtyDrillCaret $open={sellerOpen} aria-hidden="true">
                                                  <ChevronRight size={12} />
                                                </QtyDrillCaret>
                                                <Users size={11} style={{ color: '#10b981', flexShrink: 0 }} />
                                                {seller.name}
                                              </QtyLabel>
                                              <QtyValueCell>
                                                <QtyValue>{seller.total}</QtyValue>
                                                <QtyPct>{store.total > 0 ? `${Math.round((seller.total / store.total) * 100)}%` : '0%'}</QtyPct>
                                              </QtyValueCell>
                                            </QtyRow>
                                            <QtyBarTrack>
                                              <QtyBarFill $pct={store.total > 0 ? (seller.total / store.total) * 100 : 0} $color="#10b981" />
                                            </QtyBarTrack>
                                          </QtyDrillRow>
                                          <QtyDrillPanel $open={sellerOpen} aria-hidden={!sellerOpen}>
                                            <div className="qty-drill-inner">
                                              {renderQtySection(seller, sellerPath)}
                                            </div>
                                          </QtyDrillPanel>
                                        </div>
                                      );
                                    })}
                                </div>
                              </QtyBody>
                            </QtyColumn>
                          );
                        })}
                      </div>
                      </>
                    );
                  })()}
                </KpiExpand>
              </StatCard>

              <StatCard
                accent="linear-gradient(90deg, #6366f1 0%, #a855f7 100%)"
                $interactive
                $hidden={!!focusedKpi && focusedKpi !== 'turnover'}
                role="button"
                tabIndex={0}
                aria-expanded={kpiOpen.turnover}
                onClick={() => toggleKpi('turnover')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleKpi('turnover'); } }}
                aria-label={`${kpiOpen.turnover ? 'Collapse' : 'Expand'} Turnover breakdown`}
              >
                <StatHeader>
                  <StatTitle>
                    {t('admin.turnover')}
                    <ExpandCaret $open={kpiOpen.turnover} aria-hidden="true">
                      <ChevronRight size={14} />
                    </ExpandCaret>
                  </StatTitle>
                  <StatIcon color="#eef2ff" iconColor="#6366f1">
                    <Sparkles size={20} />
                  </StatIcon>
                </StatHeader>
                <StatValue>
                  {overviewLoading ? (
                    <Skeleton w="120px" h="1.95rem" />
                  ) : (
                    <>
                      <AnimatedNumber value={overviewData?.turnover?.total || 0} />
                      <span style={{ fontSize: '0.95rem', color: tokens.muted, fontWeight: 500, marginLeft: '0.4rem' }}>
                        {t('admin.dzd')}
                      </span>
                    </>
                  )}
                </StatValue>
                <KpiFooter
                  data={sparklines.turnover}
                  color="#6366f1"
                  loading={overviewLoading || sparklines.turnover.length === 0}
                />

                <KpiExpand $open={kpiOpen.turnover} onClick={(e) => e.stopPropagation()}>
                  {overviewLoading ? (
                    <div style={{ padding: '0.5rem 0' }}>
                      <Skeleton w="60%" h="12px" />
                      <div style={{ height: 8 }} />
                      <Skeleton w="100%" h="14px" />
                      <div style={{ height: 6 }} />
                      <Skeleton w="100%" h="14px" />
                      <div style={{ height: 6 }} />
                      <Skeleton w="100%" h="14px" />
                    </div>
                  ) : (() => {
                    const topItems = overviewData?.turnover?.topItems || [];
                    const orderCount = overviewData?.turnover?.orderCount || 0;
                    return (
                      <div className="qty-body-inner">
                        <TurnoverSub style={{ marginBottom: '0.65rem' }}>
                          {orderCount} order{orderCount !== 1 ? 's' : ''}
                          {overviewStartDate || overviewEndDate ? t('admin.inSelectedPeriod') : ' total'}
                          {topItems.length > 0 && ' · top items by revenue'}
                        </TurnoverSub>
                        {topItems.length === 0 ? (
                          <QtyEmpty>{t('admin.noItemsInThisPeriod')}</QtyEmpty>
                        ) : (
                          topItems.slice(0, 8).map((it, i) => (
                            <QtyRowWithBar key={i}>
                              <QtyRow style={{ padding: 0 }}>
                                <QtyLabel
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                    flex: 1,
                                  }}
                                >
                                  <ItemTypeBadge $type={it.type}>{it.type}</ItemTypeBadge>
                                  <span style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {it.name}
                                  </span>
                                </QtyLabel>
                                <QtyValueCell>
                                  <span style={{
                                    fontSize: '0.8rem',
                                    color: tokens.muted,
                                    fontWeight: 500,
                                    fontVariantNumeric: 'tabular-nums',
                                  }}>
                                    ×{it.quantity}
                                  </span>
                                  <QtyValue>{fmtDzd(it.revenue)}</QtyValue>
                                  <QtyPct>{`${(it.pctRevenue || 0).toFixed(1)}%`}</QtyPct>
                                </QtyValueCell>
                              </QtyRow>
                              <QtyBarTrack>
                                <QtyBarFill $pct={it.pctRevenue || 0} $color="#6366f1" />
                              </QtyBarTrack>
                            </QtyRowWithBar>
                          ))
                        )}
                      </div>
                    );
                  })()}
                </KpiExpand>
              </StatCard>

              <StatCard
                accent="linear-gradient(90deg, #10b981 0%, #34d399 100%)"
                $interactive
                $hidden={!!focusedKpi && focusedKpi !== 'profit'}
                role="button"
                tabIndex={0}
                aria-expanded={kpiOpen.profit}
                onClick={() => toggleKpi('profit')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleKpi('profit'); } }}
                aria-label={`${kpiOpen.profit ? 'Collapse' : 'Expand'} Profit breakdown`}
              >
                <StatHeader>
                  <StatTitle>
                    {t('admin.profit')}
                    <ComingSoonBadge title={t('admin.profitCalculationComingSoon')}>{t('admin.soon')}</ComingSoonBadge>
                    <ExpandCaret $open={kpiOpen.profit} aria-hidden="true">
                      <ChevronRight size={14} />
                    </ExpandCaret>
                  </StatTitle>
                  <StatIcon color="#d1fae5" iconColor="#059669">
                    <Sparkles size={20} />
                  </StatIcon>
                </StatHeader>
                <StatValue style={{ color: tokens.muted, display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
                  <span style={{ fontSize: '1.75rem' }}>—</span>
                  <span style={{ fontSize: '0.85rem', color: tokens.muted, fontWeight: 500 }}>
                    {t('admin.awaitingCostData')}
                  </span>
                </StatValue>
                <KpiFooter
                  data={[]}
                  color="#10b981"
                  loading={false}
                />

                <KpiExpand $open={kpiOpen.profit} onClick={(e) => e.stopPropagation()}>
                  <ProfitPlaceholder>
                    {t('admin.profitWillBeCalculatedOnce')}
                  </ProfitPlaceholder>
                </KpiExpand>
              </StatCard>

              {/* ── Visitors card ── */}
              <StatCard
                accent="linear-gradient(90deg, #06b6d4 0%, #0ea5e9 100%)"
                $interactive
                $hidden={!!focusedKpi && focusedKpi !== 'visitors'}
                role="button"
                tabIndex={0}
                aria-expanded={kpiOpen.visitors}
                onClick={() => toggleKpi('visitors')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleKpi('visitors'); } }}
                aria-label={`${kpiOpen.visitors ? 'Collapse' : 'Expand'} Visitors breakdown`}
              >
                <StatHeader>
                  <StatTitle>
                    {t('admin.visitors')}
                    <ExpandCaret $open={kpiOpen.visitors} aria-hidden="true">
                      <ChevronRight size={14} />
                    </ExpandCaret>
                  </StatTitle>
                  <StatIcon color="#e0f2fe" iconColor="#0ea5e9">
                    <Users size={20} />
                  </StatIcon>
                </StatHeader>
                <StatValue>
                  {overviewLoading ? (
                    <Skeleton w="80px" h="1.95rem" />
                  ) : (
                    <>
                      <AnimatedNumber value={overviewData?.visitorStats?.total || 0} />
                      <span style={{ fontSize: '0.85rem', color: tokens.muted, fontWeight: 500, marginLeft: '0.45rem' }}>
                        unique
                      </span>
                    </>
                  )}
                </StatValue>
                <KpiFooter data={[]} color="#06b6d4" loading={overviewLoading} />

                <KpiExpand $open={kpiOpen.visitors} onClick={(e) => e.stopPropagation()}>
                  {overviewLoading ? (
                    <div style={{ padding: '1rem', color: tokens.muted, fontSize: '0.85rem' }}>{t('admin.loading')}</div>
                  ) : (() => {
                    const byStore = overviewData?.visitorStats?.byStore || [];
                    const total   = overviewData?.visitorStats?.total   || 0;
                    const pct = (a, b) => b > 0 ? `${Math.round((a / b) * 100)}%` : '0%';
                    const pctNum = (a, b) => b > 0 ? (a / b) * 100 : 0;

                    if (byStore.length === 0) return <QtyEmpty>{t('admin.noVisitorDataInThis')}</QtyEmpty>;

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                        {byStore.map((store, si) => {
                          const storePath = `vis.store.${si}`;
                          const storeOpen = isDrillOpen(storePath);
                          return (
                            <div key={si}>
                              {/* Store row */}
                              <QtyDrillRow
                                type="button"
                                data-open={storeOpen}
                                onClick={() => toggleDrill(storePath)}
                                aria-expanded={storeOpen}
                              >
                                <QtyRow style={{ padding: 0 }}>
                                  <QtyLabel>
                                    <QtyDrillCaret $open={storeOpen} aria-hidden="true">
                                      <ChevronRight size={12} />
                                    </QtyDrillCaret>
                                    <Store size={12} style={{ color: '#06b6d4', flexShrink: 0 }} />
                                    {store.name}
                                  </QtyLabel>
                                  <QtyValueCell>
                                    <QtyValue>{store.count}</QtyValue>
                                    <QtyPct>{pct(store.count, total)}</QtyPct>
                                  </QtyValueCell>
                                </QtyRow>
                                <QtyBarTrack>
                                  <QtyBarFill $pct={pctNum(store.count, total)} $color="#06b6d4" />
                                </QtyBarTrack>
                              </QtyDrillRow>

                              {/* Age group rows inside store */}
                              <QtyDrillPanel $open={storeOpen} aria-hidden={!storeOpen}>
                                <div className="qty-drill-inner">
                                  {store.byAge.length === 0
                                    ? <QtyLeafEmpty>{t('admin.noAgeData')}</QtyLeafEmpty>
                                    : store.byAge.map((ag, ai) => {
                                      const agePath = `vis.store.${si}.age.${ai}`;
                                      const ageOpen = isDrillOpen(agePath);
                                      const hasMotives = ag.byMotive?.length > 0;
                                      return (
                                        <div key={ai}>
                                          <div
                                            onClick={() => hasMotives && toggleDrill(agePath)}
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.18rem 0', cursor: hasMotives ? 'pointer' : 'default' }}
                                          >
                                            {hasMotives
                                              ? <ChevronRight size={10} style={{ color: tokens.muted, flexShrink: 0, transform: ageOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                              : <span style={{ width: 10 }} />
                                            }
                                            <span style={{ flex: 1, fontSize: '0.78rem', color: tokens.body }}>{ag.ageGroup}</span>
                                            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0ea5e9' }}>{ag.count}</span>
                                            <span style={{ fontSize: '0.7rem', color: tokens.muted, minWidth: 32, textAlign: 'right' }}>{pct(ag.count, store.count)}</span>
                                          </div>
                                          {hasMotives && ageOpen && (
                                            <div style={{ paddingLeft: '1.25rem', paddingBottom: '0.25rem' }}>
                                              {ag.byMotive.map((m, mi) => (
                                                <div key={mi} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.15rem 0' }}>
                                                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#06b6d4', flexShrink: 0 }} />
                                                  <span style={{ flex: 1, fontSize: '0.76rem', color: tokens.body }}>{m.motive}</span>
                                                  <span style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0891b2' }}>{m.count}</span>
                                                  <span style={{ fontSize: '0.7rem', color: tokens.muted, minWidth: 32, textAlign: 'right' }}>{pct(m.count, ag.count)}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  }
                                </div>
                              </QtyDrillPanel>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </KpiExpand>
              </StatCard>
            </StatsGrid>

          </>
        )}

        {activeTab === 'clients' && <ClientList />}

        {/* ── Store Analytics Tab ─────────────────────────────────────────── */}
        {activeTab === 'store-analytics' && (() => {
          const fmtMoney = (n) => Number(n || 0).toLocaleString('fr-DZ') + ' DZD';
          const pct = (a, b) => b > 0 ? `${Math.round((a / b) * 100)}%` : '0%';
          const pctNum = (a, b) => b > 0 ? (a / b) * 100 : 0;

          const stores     = storeInsights?.stores    || [];
          const overall    = storeInsights?.overall   || {};
          const ageGroups  = overall.ageGroups        || [];
          const genders    = overall.genders          || [];
          const ageByProd  = overall.ageByProduct     || [];

          const maxRevenue = Math.max(...stores.map(s => s.revenue), 1);
          const maxVisits  = Math.max(...stores.map(s => s.visitorCount), 1);
          const maxOrders  = Math.max(...stores.map(s => s.orderCount), 1);
          const maxAge     = Math.max(...ageGroups.map(a => a.count), 1);

          const BAR = {
            track: { background: '#f1f5f9', borderRadius: 4, height: 7, flex: '1 1 auto', overflow: 'hidden' },
            fill: (pctV, color) => ({ width: `${Math.min(pctV, 100)}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.5s ease' }),
          };

          const SectionHead = ({ children, color = tokens.brand }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: `2px solid ${color}20` }}>
              <div style={{ width: 4, height: 18, borderRadius: 2, background: color }} />
              <span style={{ fontWeight: 700, fontSize: '1rem', color: tokens.ink }}>{children}</span>
            </div>
          );

          const Card = ({ children, style }) => (
            <div style={{ background: tokens.surface, border: `1px solid ${tokens.hairline}`, borderRadius: 14, padding: '1.25rem 1.5rem', ...style }}>
              {children}
            </div>
          );

          return (
            <div style={{ padding: '1.5rem', maxWidth: 1100, margin: '0 auto' }}>
              {/* Date filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.75rem', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: '1.35rem', color: tokens.ink }}>{t('admin.storeAnalytics2')}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <DateLabel>{t('admin.from')}</DateLabel>
                  <DateInput type="date" value={storeInsightsStart} onChange={e => { setStoreInsightsStart(e.target.value); fetchStoreInsights(e.target.value, storeInsightsEnd); }} />
                  <DateLabel>{t('admin.to')}</DateLabel>
                  <DateInput type="date" value={storeInsightsEnd} onChange={e => { setStoreInsightsEnd(e.target.value); fetchStoreInsights(storeInsightsStart, e.target.value); }} />
                  {(storeInsightsStart || storeInsightsEnd) && (
                    <button onClick={() => { setStoreInsightsStart(''); setStoreInsightsEnd(''); fetchStoreInsights('', ''); }}
                      style={{ padding: '0.4rem 0.75rem', border: `1px solid ${tokens.hairline}`, borderRadius: 8, background: 'white', color: tokens.muted, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                      {t('admin.clear')}
                    </button>
                  )}
                </div>
              </div>

              {storeInsightsLoading ? (
                <div style={{ textAlign: 'center', padding: '4rem', color: tokens.muted, fontSize: '0.95rem' }}>{t('admin.loadingAnalytics')}</div>
              ) : !storeInsights ? (
                <div style={{ textAlign: 'center', padding: '4rem', color: tokens.muted }}>{t('admin.noDataYet')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                  {/* ── 1. Store Sales Ranking ── */}
                  <Card>
                    <SectionHead color="#6366f1">{t('admin.storeSalesRanking')}</SectionHead>
                    {stores.length === 0 ? <div style={{ color: tokens.muted, fontSize: '0.875rem' }}>{t('admin.noSalesData')}</div> : stores.map((s, i) => (
                      <div key={s.storeId} style={{ marginBottom: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.3rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: tokens.muted, width: 18 }}>#{i + 1}</span>
                          <button onClick={() => setSiExpandedStore(siExpandedStore === s.storeId ? null : s.storeId)}
                            style={{ all: 'unset', cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: tokens.ink, flex: 1 }}>{s.name}</span>
                            <span style={{ fontSize: '0.8rem', color: tokens.muted }}>{s.orderCount} {t('admin.orders2')} {s.visitorCount} visitors</span>
                            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#6366f1' }}>{fmtMoney(s.revenue)}</span>
                            <ChevronRight size={14} style={{ transform: siExpandedStore === s.storeId ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: tokens.muted }} />
                          </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: 24 }}>
                          <div style={BAR.track}><div style={BAR.fill(pctNum(s.revenue, maxRevenue), '#6366f1')} /></div>
                          <span style={{ fontSize: '0.75rem', color: tokens.muted, minWidth: 36 }}>{pct(s.revenue, maxRevenue)}</span>
                        </div>
                        {/* Expanded: items sold + frame brands */}
                        {siExpandedStore === s.storeId && (
                          <div style={{ marginTop: '0.75rem', paddingLeft: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div style={{ background: tokens.surfaceSoft, borderRadius: 10, padding: '0.75rem 1rem' }}>
                              <div style={{ fontWeight: 600, fontSize: '0.8rem', color: tokens.muted, marginBottom: '0.5rem' }}>{t('admin.itemsSold')}</div>
                              {[
                                { label: 'Lenses',         count: s.itemsSold.lens,         color: '#6366f1' },
                                { label: 'Frames',         count: s.itemsSold.frame,        color: '#f59e0b' },
                                { label: 'Contact Lenses', count: s.itemsSold.contact_lens, color: '#06b6d4' },
                              ].map(row => (
                                <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: row.color, flexShrink: 0 }} />
                                  <span style={{ fontSize: '0.8rem', flex: 1, color: tokens.body }}>{row.label}</span>
                                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: tokens.ink }}>{row.count}</span>
                                </div>
                              ))}
                            </div>
                            <div style={{ background: tokens.surfaceSoft, borderRadius: 10, padding: '0.75rem 1rem' }}>
                              <div style={{ fontWeight: 600, fontSize: '0.8rem', color: tokens.muted, marginBottom: '0.5rem' }}>{t('admin.topFrameBrands')}</div>
                              {s.frameBrands.length === 0
                                ? <div style={{ fontSize: '0.8rem', color: tokens.muted }}>{t('admin.noFrameSales')}</div>
                                : s.frameBrands.slice(0, 5).map((b, bi) => (
                                  <div key={bi} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
                                    <span style={{ fontSize: '0.8rem', flex: 1, color: tokens.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: tokens.ink }}>{b.count}</span>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </Card>

                  {/* ── 2. Store Visitor Ranking ── */}
                  <Card>
                    <SectionHead color="#10b981">{t('admin.storeVisitorRanking')}</SectionHead>
                    {[...stores].sort((a, b) => b.visitorCount - a.visitorCount).map((s, i) => (
                      <div key={s.storeId} style={{ marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.3rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: tokens.muted, width: 18 }}>#{i + 1}</span>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: tokens.ink, flex: 1 }}>{s.name}</span>
                          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#10b981' }}>{s.visitorCount} visitors</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: 24 }}>
                          <div style={BAR.track}><div style={BAR.fill(pctNum(s.visitorCount, maxVisits), '#10b981')} /></div>
                          <span style={{ fontSize: '0.75rem', color: tokens.muted, minWidth: 36 }}>{pct(s.visitorCount, maxVisits)}</span>
                        </div>
                      </div>
                    ))}
                  </Card>

                  {/* ── 3. Age Group Breakdown ── */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    <Card>
                      <SectionHead color="#f59e0b">{t('admin.visitorsByAgeGroup')}</SectionHead>
                      {ageGroups.length === 0 ? <div style={{ color: tokens.muted, fontSize: '0.875rem' }}>{t('admin.noData2')}</div>
                        : ageGroups.map((ag, i) => (
                          <div key={i} style={{ marginBottom: '0.65rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                              <span style={{ fontSize: '0.85rem', color: tokens.body, fontWeight: 500 }}>{ag.name}</span>
                              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: tokens.ink }}>{ag.count}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={BAR.track}><div style={BAR.fill(pctNum(ag.count, maxAge), '#f59e0b')} /></div>
                              <span style={{ fontSize: '0.75rem', color: tokens.muted, minWidth: 36 }}>{pct(ag.count, maxAge)}</span>
                            </div>
                          </div>
                        ))
                      }
                    </Card>
                    <Card>
                      <SectionHead color="#a855f7">{t('admin.visitorsByGender')}</SectionHead>
                      {genders.length === 0 ? <div style={{ color: tokens.muted, fontSize: '0.875rem' }}>{t('admin.noData2')}</div>
                        : (() => {
                          const total = genders.reduce((s, g) => s + g.count, 0);
                          const colors = ['#a855f7', '#ec4899', '#6366f1', '#94a3b8'];
                          return genders.map((g, i) => (
                            <div key={i} style={{ marginBottom: '0.65rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                <span style={{ fontSize: '0.85rem', color: tokens.body, fontWeight: 500, textTransform: 'capitalize' }}>{g.name}</span>
                                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: tokens.ink }}>{g.count} <span style={{ color: tokens.muted, fontWeight: 400 }}>({pct(g.count, total)})</span></span>
                              </div>
                              <div style={BAR.track}><div style={BAR.fill(pctNum(g.count, total), colors[i % colors.length])} /></div>
                            </div>
                          ));
                        })()
                      }
                    </Card>
                  </div>

                  {/* ── 4. Age Group × Product Type ── */}
                  <Card>
                    <SectionHead color="#06b6d4">{t('admin.whatAgeGroupsBuy')}</SectionHead>
                    {ageByProd.length === 0 ? <div style={{ color: tokens.muted, fontSize: '0.875rem' }}>{t('admin.noData2')}</div> : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${tokens.hairline}` }}>
                              <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: tokens.muted, fontWeight: 600 }}>{t('admin.ageGroup')}</th>
                              <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#6366f1', fontWeight: 600 }}>{t('admin.lenses')}</th>
                              <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#f59e0b', fontWeight: 600 }}>{t('admin.frames')}</th>
                              <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#06b6d4', fontWeight: 600 }}>{t('admin.contactLenses')}</th>
                              <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: tokens.muted, fontWeight: 600 }}>{t('admin.total')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ageByProd.map((row, i) => {
                              const total = row.lens + row.frame + row.contact_lens;
                              return (
                                <tr key={i} style={{ borderBottom: `1px solid ${tokens.hairline}`, background: i % 2 === 0 ? 'transparent' : tokens.surfaceSoft }}>
                                  <td style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: tokens.ink }}>{row.ageGroup}</td>
                                  <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#6366f1', fontWeight: 600 }}>{row.lens}</td>
                                  <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#f59e0b', fontWeight: 600 }}>{row.frame}</td>
                                  <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#06b6d4', fontWeight: 600 }}>{row.contact_lens}</td>
                                  <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: tokens.ink, fontWeight: 700 }}>{total}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>

                  {/* ── 5. Frame Brands by Store ── */}
                  <Card>
                    <SectionHead color="#f59e0b">{t('admin.frameBrandsByStore')}</SectionHead>
                    {stores.filter(s => s.frameBrands.length > 0).length === 0
                      ? <div style={{ color: tokens.muted, fontSize: '0.875rem' }}>{t('admin.noFrameSalesRecorded')}</div>
                      : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
                          {stores.filter(s => s.frameBrands.length > 0).map(s => (
                            <div key={s.storeId} style={{ background: tokens.surfaceSoft, borderRadius: 10, padding: '0.85rem 1rem' }}>
                              <div style={{ fontWeight: 700, fontSize: '0.875rem', color: tokens.ink, marginBottom: '0.6rem', display: 'flex', justifyContent: 'space-between' }}>
                                <span>{s.name}</span>
                                <span style={{ fontWeight: 400, color: tokens.muted, fontSize: '0.8rem' }}>{s.itemsSold.frame} frames</span>
                              </div>
                              {s.frameBrands.slice(0, 6).map((b, bi) => {
                                const maxB = s.frameBrands[0]?.count || 1;
                                return (
                                  <div key={bi} style={{ marginBottom: '0.35rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.15rem' }}>
                                      <span style={{ fontSize: '0.78rem', color: tokens.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{b.name}</span>
                                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: tokens.ink }}>{b.count}</span>
                                    </div>
                                    <div style={BAR.track}><div style={BAR.fill(pctNum(b.count, maxB), '#f59e0b')} /></div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )
                    }
                  </Card>

                  {/* ── 6. Orders ranking ── */}
                  <Card>
                    <SectionHead color="#0ea5e9">{t('admin.storeOrderVolume')}</SectionHead>
                    {[...stores].sort((a, b) => b.orderCount - a.orderCount).map((s, i) => (
                      <div key={s.storeId} style={{ marginBottom: '0.65rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: tokens.muted, width: 18 }}>#{i + 1}</span>
                          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: tokens.ink, flex: 1 }}>{s.name}</span>
                          <span style={{ fontWeight: 700, color: '#0ea5e9' }}>{s.orderCount} orders</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: 24 }}>
                          <div style={BAR.track}><div style={BAR.fill(pctNum(s.orderCount, maxOrders), '#0ea5e9')} /></div>
                          <span style={{ fontSize: '0.75rem', color: tokens.muted, minWidth: 36 }}>{pct(s.orderCount, maxOrders)}</span>
                        </div>
                      </div>
                    ))}
                  </Card>

                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'orders' && (
          <OrdersContainer>
            <OrdersHeader>
              <SearchBar>
                <Search size={20} color="#94a3b8" />
                <input
                  type="text"
                  placeholder={t('admin.searchByOrderNumberCustomer')}
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                />
              </SearchBar>
              <FilterGroup>
                <FilterSelect
                  value={orderStoreFilter}
                  onChange={(e) => { setOrderStoreFilter(e.target.value); setOrderSellerFilter('all'); }}
                  aria-label={t('admin.store')}
                >
                  <option value="all">{t('admin.allStores')}</option>
                  {orderStoreOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </FilterSelect>
                <FilterSelect
                  value={orderSellerFilter}
                  onChange={(e) => setOrderSellerFilter(e.target.value)}
                  aria-label={t('admin.salesPerson')}
                >
                  <option value="all">{t('admin.allSellers')}</option>
                  {orderSellerOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </FilterSelect>
                <FilterButton
                  active={orderFilter === 'all'}
                  onClick={() => setOrderFilter('all')}
                >
                  {t('admin.all')}{totalCount})
                </FilterButton>
                <FilterButton
                  active={orderFilter === 'confirmed'}
                  onClick={() => setOrderFilter('confirmed')}
                  title={t('admin.ordersThatReachedPaymentConfirmation')}
                >
                  <Clock size={16} style={{ marginRight: '0.25rem' }} />
                  {t('admin.confirmed')}{confirmedCount})
                </FilterButton>
                <FilterButton
                  active={orderFilter === 'completed'}
                  onClick={() => setOrderFilter('completed')}
                  title={t('admin.ordersThatHaveBeenConfirmed')}
                >
                  <CheckCircle size={16} style={{ marginRight: '0.25rem' }} />
                  {t('admin.completed')}{completedCount})
                </FilterButton>
                <FilterButton
                  active={orderFilter === 'unsuccessful'}
                  onClick={() => setOrderFilter('unsuccessful')}
                  title={t('admin.visitsThatReachedThePrescription')}
                >
                  <XCircle size={16} style={{ marginRight: '0.25rem' }} />
                  {t('admin.unsuccessful')}{unsuccessfulCount})
                </FilterButton>
              </FilterGroup>
            </OrdersHeader>

            {ordersLoading ? (
              // Skeleton table — matches the real header columns so the layout
              // doesn't jump when data arrives.
              <OrdersTable>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>{t('admin.orderNumber')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.status')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.customer')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.salesPerson')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.store')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.date')}</TableHeaderCell>
                    <TableHeaderCell>{orderFilter === 'unsuccessful' ? t('admin.reason') : t('admin.totalAmount')}</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`sk-${i}`} style={{ cursor: 'default' }}>
                      <TableCell><Skeleton w="120px" h="14px" /></TableCell>
                      <TableCell><Skeleton w="80px" h="22px" /></TableCell>
                      <TableCell><Skeleton w="140px" h="14px" /></TableCell>
                      <TableCell><Skeleton w="110px" h="14px" /></TableCell>
                      <TableCell><Skeleton w="90px" h="14px" /></TableCell>
                      <TableCell><Skeleton w="80px" h="14px" /></TableCell>
                      <TableCell><Skeleton w="90px" h="14px" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </OrdersTable>
            ) : filteredOrders.length === 0 ? (
              <EmptyState>
                <Package size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>
                  {orderFilter === 'unsuccessful' && 'No unsuccessful orders found'}
                  {orderFilter === 'confirmed'    && 'No confirmed orders found'}
                  {orderFilter === 'completed'    && 'No completed orders found'}
                  {orderFilter === 'all'          && 'No orders found'}
                </p>
                {orders.length === 0 ? (
                  <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                    {orderFilter === 'unsuccessful' &&
                      'No visits got stuck at the prescription stage. '}
                    {orderFilter === 'confirmed' &&
                      'No orders are currently confirmed and awaiting delivery. '}
                    {orderFilter === 'completed' &&
                      'No orders have been delivered yet. '}
                    {orderFilter === 'all' &&
                      'There are currently no orders in the system. '}
                  </p>
                ) : orderSearch && (
                  <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                    {t('admin.noOrdersMatchYourSearch')}
                  </p>
                )}
              </EmptyState>
            ) : (
              <>
                <OrdersTable>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell>{t('admin.orderNumber')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.status')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.customer')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.salesPerson')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.store')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.date')}</TableHeaderCell>
                      {orderFilter === 'unsuccessful' && (
                        <TableHeaderCell>{t('admin.reason')}</TableHeaderCell>
                      )}
                      {orderFilter !== 'unsuccessful' && (
                        <TableHeaderCell>{t('admin.totalAmount')}</TableHeaderCell>
                      )}
                    </TableHeaderRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((item) => {
                      // Three possible row shapes:
                      //   A. Unsuccessful (composite from /api/admin/incomplete-orders):
                      //      { prescription, order: {…}|null, visitor, missingStep, reason }
                      //      — order data is nested under item.order.
                      //   B. Real Order document (from /api/admin/all-orders):
                      //      { _id, orderNumber, status, total, … } and we attach
                      //      item.prescription on the server for convenience.
                      //   C. Prescription-only (from /api/admin/all-orders, "All" view):
                      //      { isPrescriptionOnly: true, prescription, …, status: 'No Order Created' }
                      //
                      // We can't detect Shape A by `item.prescription || item.order`
                      // anymore because Shape B also has item.prescription attached —
                      // that mistakenly classified real confirmed orders as Shape A
                      // and rendered them as "No Order Created" / "Prescription Only".
                      // `missingStep` is only set by /incomplete-orders, so it's the
                      // reliable Shape A detector.
                      const isPrescriptionOnly = !!item.isPrescriptionOnly;
                      const isIncompleteStructure = !!item.missingStep;
                      const orderNumber = isPrescriptionOnly
                        ? 'No Order Created'
                        : (isIncompleteStructure
                          ? (item.order?.orderNumber || 'No Order Created')
                          : (item.orderNumber || 'N/A'));
                      const status = isPrescriptionOnly
                        ? 'pending'
                        : (isIncompleteStructure
                          ? (item.order?.status || 'No Order Created')
                          : item.status);
                      const visitor = item.visitor;
                      const salesPerson = item.salesPerson;
                      const store = isPrescriptionOnly
                        ? item.store
                        : (isIncompleteStructure
                          ? (item.order?.store || item.store)
                          : item.store);
                      const date = isPrescriptionOnly
                        ? (item.createdAt || item.orderDate)
                        : (isIncompleteStructure
                          ? (item.order?.orderDate || item.order?.createdAt || item.prescription?.createdAt || item.createdAt)
                          : (item.orderDate || item.createdAt));
                      const reason = item.reason || '';
                      const uniqueId = isPrescriptionOnly
                        ? item._id
                        : (isIncompleteStructure
                          ? (item.order?._id || item.prescription?._id || item._id)
                          : item._id);
                      // Only real orders can be deleted (prescription-only rows have none).
                      const deletableOrderId = isPrescriptionOnly
                        ? null
                        : (isIncompleteStructure ? (item.order?._id || null) : item._id);

                      // Customer name comes from the attached prescription on every
                      // shape that has one (A, B with prescription attached, and C).
                      const prescription = item.prescription || (isPrescriptionOnly ? item : null);
                      const patientName = prescription?.patientName || null;
                      const customerDisplay = patientName || (visitor?.gender && visitor?.ageGroup
                        ? `${visitor.gender} - ${visitor.ageGroup}`
                        : 'N/A');

                      return (
                        <TableRow key={uniqueId} onClick={() => setSelectedOrder(item)}>
                          <TableCell>
                            <strong>{orderNumber}</strong>
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={status}
                              completed={isOrderCompleted(item)}
                            >
                              {isOrderCompleted(item) ? (
                                <CheckCircle size={14} />
                              ) : isOrderConfirmed(item) ? (
                                <Clock size={14} />
                              ) : (
                                <XCircle size={14} />
                              )}
                              {getStatusLabel(status, isPrescriptionOnly)}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>
                            {customerDisplay}
                          </TableCell>
                          <TableCell>
                            {salesPerson
                              ? `${salesPerson.firstName || ''} ${salesPerson.lastName || ''}`.trim() || 'N/A'
                              : t('admin.nA')
                            }
                          </TableCell>
                          <TableCell>
                            {store?.name || store?.storeCode || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {date
                              ? new Date(date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                              : t('admin.nA')
                            }
                          </TableCell>
                          {orderFilter === 'unsuccessful' && (
                            <TableCell>
                              <div style={{
                                maxWidth: '400px',
                                color: '#64748b',
                                fontSize: '0.875rem',
                                lineHeight: '1.5',
                                wordBreak: 'break-word'
                              }} title={reason || 'No reason provided'}>
                                {reason || <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>{t('admin.noReasonProvided')}</span>}
                              </div>
                            </TableCell>
                          )}
                          {orderFilter !== 'unsuccessful' && (
                            <TableCell>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <strong>
                                  {item.total && item.total > 0 ? `${item.total.toLocaleString()} DZD` : t('admin.nA')}
                                </strong>
                                {deletableOrderId && (
                                  <button
                                    type="button"
                                    title={t('admin.deleteOrder')}
                                    onClick={(e) => handleDeleteOrder(deletableOrderId, e)}
                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </OrdersTable>

                <OrdersCardList>
                  {filteredOrders.map((item) => {
                    // Same shape detection as the desktop table above. See
                    // detailed comment there for why we use `missingStep`
                    // instead of `item.prescription || item.order`.
                    const isPrescriptionOnly = !!item.isPrescriptionOnly;
                    const isIncompleteStructure = !!item.missingStep;
                    const orderNumber = isPrescriptionOnly
                      ? 'No Order Created'
                      : (isIncompleteStructure
                        ? (item.order?.orderNumber || 'No Order Created')
                        : (item.orderNumber || 'N/A'));
                    const status = isPrescriptionOnly
                      ? 'pending'
                      : (isIncompleteStructure
                        ? (item.order?.status || 'No Order Created')
                        : item.status);
                    const visitor = item.visitor;
                    const salesPerson = item.salesPerson;
                    const store = isPrescriptionOnly
                      ? null
                      : (isIncompleteStructure
                        ? (item.order?.store || item.store)
                        : item.store);
                    const date = isPrescriptionOnly
                      ? (item.createdAt || item.orderDate)
                      : (isIncompleteStructure
                        ? (item.order?.orderDate || item.order?.createdAt || item.prescription?.createdAt || item.createdAt)
                        : (item.orderDate || item.createdAt));
                    const reason = item.reason || '';
                    const uniqueId = isPrescriptionOnly
                      ? item._id
                      : (isIncompleteStructure
                        ? (item.order?._id || item.prescription?._id || item._id)
                        : item._id);
                    const deletableOrderId = isPrescriptionOnly
                      ? null
                      : (isIncompleteStructure ? (item.order?._id || null) : item._id);

                    const prescription = item.prescription || (isPrescriptionOnly ? item : null);
                    const patientName = prescription?.patientName || null;
                    const customerDisplay = patientName || (visitor?.gender && visitor?.ageGroup
                      ? `${visitor.gender} - ${visitor.ageGroup}`
                      : 'N/A');

                    return (
                      <OrderCard key={uniqueId} onClick={() => setSelectedOrder(item)}>
                        <OrderCardHeader>
                          <OrderCardTitle>{orderNumber}</OrderCardTitle>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <StatusBadge
                              status={status}
                              completed={isOrderCompleted(item)}
                            >
                              {isOrderCompleted(item) ? (
                                <CheckCircle size={14} />
                              ) : isOrderConfirmed(item) ? (
                                <Clock size={14} />
                              ) : (
                                <XCircle size={14} />
                              )}
                              {getStatusLabel(status, isPrescriptionOnly)}
                            </StatusBadge>
                            {deletableOrderId && (
                              <button
                                type="button"
                                title={t('admin.deleteOrder')}
                                onClick={(e) => handleDeleteOrder(deletableOrderId, e)}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </OrderCardHeader>
                        <OrderCardBody>
                          <OrderCardRow>
                            <OrderCardLabel>{t('admin.customer2')}</OrderCardLabel>
                            <OrderCardValue>
                              {customerDisplay}
                            </OrderCardValue>
                          </OrderCardRow>
                          <OrderCardRow>
                            <OrderCardLabel>{t('admin.salesPerson2')}</OrderCardLabel>
                            <OrderCardValue>
                              {salesPerson
                                ? `${salesPerson.firstName || ''} ${salesPerson.lastName || ''}`.trim() || 'N/A'
                                : t('admin.nA')
                              }
                            </OrderCardValue>
                          </OrderCardRow>
                          <OrderCardRow>
                            <OrderCardLabel>{t('admin.store3')}</OrderCardLabel>
                            <OrderCardValue>
                              {store?.name || store?.storeCode || 'N/A'}
                            </OrderCardValue>
                          </OrderCardRow>
                          <OrderCardRow>
                            <OrderCardLabel>{t('admin.date2')}</OrderCardLabel>
                            <OrderCardValue>
                              {date
                                ? new Date(date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })
                                : t('admin.nA')
                              }
                            </OrderCardValue>
                          </OrderCardRow>
                          {orderFilter === 'unsuccessful' && (
                            <OrderCardRow>
                              <OrderCardLabel>{t('admin.reason2')}</OrderCardLabel>
                              <OrderCardValue style={{
                                color: '#64748b',
                                fontSize: '0.875rem',
                                wordBreak: 'break-word'
                              }}>
                                {reason || <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>{t('admin.noReasonProvided')}</span>}
                              </OrderCardValue>
                            </OrderCardRow>
                          )}
                          {orderFilter !== 'unsuccessful' && (
                            <OrderCardRow>
                              <OrderCardLabel>{t('admin.total2')}</OrderCardLabel>
                              <OrderCardValue style={{ fontWeight: '600' }}>
                                {item.total && item.total > 0 ? `${item.total.toLocaleString()} DZD` : t('admin.nA')}
                              </OrderCardValue>
                            </OrderCardRow>
                          )}
                        </OrderCardBody>
                      </OrderCard>
                    );
                  })}
                </OrdersCardList>
              </>
            )}
          </OrdersContainer>
        )}

        {activeTab === 'employee-badges' && (
          <OrdersContainer>
            <OrdersHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  {t('admin.employeeBadges')}
                </h3>
              </div>
            </OrdersHeader>
            <div style={{ padding: '1.25rem' }}>
              <BadgeStudio />
            </div>
          </OrdersContainer>
        )}

        {activeTab === 'employees' && (
          <OrdersContainer>
            <OrdersHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  {t('admin.employees2')}{employees.length})
                </h3>
                {selectedEmployees.length > 0 && (
                  <span style={{ fontSize: '0.875rem', color: '#6366f1', fontWeight: '500' }}>
                    {selectedEmployees.length} selected
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {selectedEmployees.length > 0 && (
                  <button onClick={() => setShowBulkDeleteEmployeeModal(true)}
                    style={{ padding: '0.5rem 1rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                    <Trash size={18} /> {t('admin.deleteSelected')}{selectedEmployees.length})
                  </button>
                )}
                <button onClick={() => setShowAddEmployeeModal(true)}
                  style={{ padding: '0.5rem 1rem', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                  <UserPlus size={18} /> {t('admin.addEmployee')}
                </button>
              </div>
            </OrdersHeader>
            {/* Search + Filter bar */}
            <div style={{ display: 'flex', gap: '0.6rem', padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder={t('admin.searchByNameOrEmployee')}
                value={employeeSearch}
                onChange={e => setEmployeeSearch(e.target.value)}
                style={{ flex: 1, minWidth: '180px', padding: '0.45rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', outline: 'none', background: '#fafafa' }}
              />
              <select
                value={employeeStoreFilter}
                onChange={e => setEmployeeStoreFilter(e.target.value)}
                style={{ padding: '0.45rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', outline: 'none', background: '#fafafa', cursor: 'pointer', minWidth: '150px' }}
              >
                <option value="">{t('admin.allLocations')}</option>
                {[...new Set(employees.map(e => e.store?.name || e.lab?.name || e.adminLocation?.name || 'Unassigned'))].sort().map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {(employeeSearch || employeeStoreFilter) && (
                <button onClick={() => { setEmployeeSearch(''); setEmployeeStoreFilter(''); }}
                  style={{ padding: '0.45rem 0.7rem', background: '#f1f5f9', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}>
                  {t('admin.clear')}
                </button>
              )}
            </div>
            {employeesLoading ? (
              <LoadingState><SpinningIcon size={20} /> {t('admin.loadingEmployees')}</LoadingState>
            ) : employees.length === 0 ? (
              <EmptyState>
                <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>{t('admin.noEmployeesFound')}</p>
              </EmptyState>
            ) : (() => {
              // Filter employees
              const search = employeeSearch.toLowerCase();
              const filtered = employees.filter(emp => {
                const matchSearch = !search || `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(search) || (emp.employeeId || '').toLowerCase().includes(search);
                const locationName = emp.store?.name || emp.lab?.name || emp.adminLocation?.name || 'Unassigned';
                const matchStore = !employeeStoreFilter || locationName === employeeStoreFilter;
                return matchSearch && matchStore;
              });

              // Group employees by store/lab/admin location
              const groups = {};
              filtered.forEach(emp => {
                const key = emp.store?.name || emp.lab?.name || emp.adminLocation?.name || 'Unassigned';
                const isCentral = emp.store?.isCentralWarehouse;
                const groupKey = isCentral ? `${key} (Central)` : key;
                if (!groups[groupKey]) groups[groupKey] = { label: groupKey, isCentral, employees: [] };
                groups[groupKey].employees.push(emp);
              });
              const sortedGroups = Object.values(groups).sort((a, b) => {
                if (a.isCentral) return -1;
                if (b.isCentral) return 1;
                if (a.label === 'Unassigned') return 1;
                if (b.label === 'Unassigned') return -1;
                return a.label.localeCompare(b.label);
              });
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0.5rem 0' }}>
                  {sortedGroups.map(group => (
                    <div key={group.label} style={{ border: '1.5px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                      {/* Group header */}
                      <div onClick={() => setCollapsedGroups(p => ({ ...p, [group.label]: p[group.label] === false ? true : false }))}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 1rem', background: group.isCentral ? 'linear-gradient(135deg,#fffbeb,#fef3c7)' : '#f8fafc', borderBottom: collapsedGroups[group.label] ? 'none' : '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {group.isCentral ? <Warehouse size={15} color="#d97706" /> : <Store size={15} color="#6366f1" />}
                          <span style={{ fontWeight: 700, fontSize: '0.875rem', color: group.isCentral ? '#92400e' : '#1e293b' }}>{group.label}</span>
                          <span style={{ fontSize: '0.72rem', color: '#94a3b8', background: '#f1f5f9', padding: '0.1rem 0.45rem', borderRadius: '99px' }}>{group.employees.length}</span>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{(collapsedGroups[group.label] === false || employeeSearch || employeeStoreFilter) ? '▾' : '▸'}</span>
                      </div>
                      {/* Employee rows — collapsed by default unless search is active */}
                      {(collapsedGroups[group.label] === false || employeeSearch || employeeStoreFilter) && (<div>
                        {group.employees.map((employee, idx) => {
                          const empId = employee._id || employee.id;
                          const isSelected = selectedEmployees.includes(empId);
                          return (
                            <div key={empId} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 1fr 140px 80px 124px', gap: '0.5rem', alignItems: 'center', padding: '0.6rem 1rem', borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none', background: isSelected ? '#eef2ff' : 'white', transition: 'background 150ms' }}>
                              <input type="checkbox" checked={isSelected} onChange={(e) => { if (e.target.checked) setSelectedEmployees(p => [...p, empId]); else setSelectedEmployees(p => p.filter(id => id !== empId)); }} style={{ cursor: 'pointer', accentColor: '#6366f1' }} />
                              {/* Name + ID */}
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{employee.firstName} {employee.lastName}</div>
                                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.1rem' }}>{employee.employeeId || '—'}</div>
                              </div>
                              {/* Email */}
                              <div style={{ fontSize: '0.8rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{employee.email}</div>
                              {/* Role badge */}
                              <div>
                                <StatusBadge status={employee.role} completed={employee.isActive} style={{ fontSize: '0.72rem' }}>
                                  {humanizeRole(employee.role)}
                                </StatusBadge>
                              </div>
                              {/* Status */}
                              <StatusBadge status={employee.isActive ? 'active' : 'inactive'} completed={employee.isActive} style={{ fontSize: '0.72rem' }}>
                                {employee.isActive ? t('admin.active') : t('admin.inactive')}
                              </StatusBadge>
                              {/* Actions */}
                              <div style={{ display: 'flex', gap: '0.3rem' }}>
                                <button title={t('admin.printBadge')} onClick={async () => { try { const token = localStorage.getItem('token'); const w = window.open('', '_blank'); if (w) { const r = await fetch(`/api/users/${empId}/badge`, { headers: { Authorization: `Bearer ${token}` } }); const html = await r.text(); w.document.write(html); w.document.close(); setTimeout(() => w.print(), 500); } } catch { toast.error('Error loading badge'); } }}
                                  style={{ padding: '0.3rem', background: '#eef2ff', color: '#4f46e5', border: '1.5px solid #c7d2fe', borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  <Package size={13} />
                                </button>
                                <button title={t('admin.edit')} onClick={() => { setSelectedEmployee(employee); setShowEditEmployeeModal(true); }}
                                  style={{ padding: '0.3rem', background: '#f0fdf4', color: '#16a34a', border: '1.5px solid #bbf7d0', borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  <Edit size={13} />
                                </button>
                                <button title={t('admin.remove')} onClick={() => { setSelectedEmployee(employee); setShowDeleteEmployeeModal(true); }}
                                  style={{ padding: '0.3rem', background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  <Trash size={13} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>)}
                    </div>
                  ))}
                </div>
              );
            })()}
          </OrdersContainer>
        )}

        {activeTab === 'employee-statistics' && (
          <OrdersContainer>
            <MetricsCard>
              <MetricsTitle>
                <span><BarChart3 size={18} /> {t('admin.employeeStatistics2')}</span>
                <button
                  onClick={fetchEmployeeStats}
                  style={{
                    padding: '0.48rem 0.85rem',
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    fontWeight: 800,
                    color: '#334155'
                  }}
                  disabled={employeeStatsLoading}
                >
                  {employeeStatsLoading ? t('admin.refreshing') : t('admin.refresh')}
                </button>
              </MetricsTitle>

              <EmployeeStatsToolbar>
                {[
                  ['today', 'Today'],
                  ['last7', 'Last 7 days'],
                  ['month', 'This month'],
                  ['custom', 'Custom'],
                ].map(([value, label]) => (
                  <RangeButton
                    key={value}
                    onClick={() => setEmployeeStatsRange(value)}
                    $active={employeeStatsRange === value}
                  >
                    {label}
                  </RangeButton>
                ))}
                {employeeStatsRange === 'custom' && (
                  <>
                    <input
                      type="date"
                      value={employeeStatsStartDate}
                      onChange={(e) => setEmployeeStatsStartDate(e.target.value)}
                      style={{ padding: '0.45rem 0.65rem', border: '1px solid #cbd5e1', borderRadius: 10 }}
                    />
                    <input
                      type="date"
                      value={employeeStatsEndDate}
                      onChange={(e) => setEmployeeStatsEndDate(e.target.value)}
                      style={{ padding: '0.45rem 0.65rem', border: '1px solid #cbd5e1', borderRadius: 10 }}
                    />
                  </>
                )}
              </EmployeeStatsToolbar>

              {employeeStatsLoading ? (
                <div style={{ padding: '1rem', color: '#64748b' }}>{t('admin.loadingEmployeeStatistics')}</div>
              ) : employeeStats.length === 0 ? (
                <div style={{ padding: '1rem', color: '#64748b' }}>{t('admin.noStatsFoundForThis')}</div>
              ) : (
                <>
                  <SalaryCalculatorPanel>
                    <SalaryCalculatorHeader>
                      <div>
                        <h3>{t('admin.salaryCalculator')}</h3>
                        <p>{t('admin.minimumWagePlusBonusesBased')}</p>
                        {/* Saving is automatic, so say so — otherwise there is
                            no way to tell the numbers were kept. */}
                        {salarySaveState !== 'idle' && (
                          <p style={{
                            marginTop: '0.15rem',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: salarySaveState === 'error' ? '#dc2626'
                              : salarySaveState === 'saving' ? tokens.muted : '#16a34a',
                          }}>
                            {salarySaveState === 'saving' && t('admin.savingEllipsis')}
                            {salarySaveState === 'saved' && t('admin.savedAutomatically')}
                            {salarySaveState === 'error' && (salarySaveMessage || t('admin.saveFailedLocalOnly'))}
                          </p>
                        )}
                      </div>
                      <SalaryInputGroup>
                        {t('admin.minimumWage')}
                        <input
                          type="number"
                          min="0"
                          value={salaryMinimumWage}
                          onChange={(event) => setSalaryMinimumWage(event.target.value)}
                          placeholder="0"
                        />
                      </SalaryInputGroup>
                    </SalaryCalculatorHeader>
                    <SalaryMatrixWrap>
                      <SalaryMatrix>
                        <thead>
                          <tr>
                            <th>{t('admin.rate')}</th>
                            {salaryThresholds.map((threshold) => (
                              <th key={threshold}>{threshold}%</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {salaryRateRows.map((rateRow) => (
                            <tr key={rateRow.key}>
                              <td><strong>{rateRow.label}</strong></td>
                              {salaryThresholds.map((threshold) => (
                                <td key={`${rateRow.key}-${threshold}`}>
                                  <input
                                    type="number"
                                    min="0"
                                    value={salaryBonusMatrix[rateRow.key]?.[threshold] || ''}
                                    onChange={(event) => updateSalaryBonus(rateRow.key, threshold, event.target.value)}
                                    placeholder="0"
                                    aria-label={`${rateRow.label} ${threshold}% bonus`}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </SalaryMatrix>
                    </SalaryMatrixWrap>
                  </SalaryCalculatorPanel>
                  <EmployeeStatsSummary>
                    <EmployeeStatsMiniCard>
                      <span>{t('admin.employees')}</span>
                      <strong>{employeeStats.length}</strong>
                    </EmployeeStatsMiniCard>
                    <EmployeeStatsMiniCard>
                      <span>{t('admin.rawPaidClients')}</span>
                      <strong>{employeeStatsTotals.paidClients}</strong>
                    </EmployeeStatsMiniCard>
                    <EmployeeStatsMiniCard>
                      <span>{t('admin.rawVisits')}</span>
                      <strong>{employeeStatsTotals.visits}</strong>
                    </EmployeeStatsMiniCard>
                    <EmployeeStatsMiniCard>
                      <span>{t('admin.recommendedSales')}</span>
                      <strong>{employeeStatsTotals.recommendationSales}</strong>
                    </EmployeeStatsMiniCard>
                  </EmployeeStatsSummary>
                  <div style={{ overflowX: 'auto', border: '1px solid var(--c-hairline)', borderRadius: 16 }}>
                    <MetricsTable>
                      <thead>
                        <tr>
                          <MetricsTh>{t('admin.storeEmployee')}</MetricsTh>
                          <MetricsTh>{t('admin.rate1Conversion')}</MetricsTh>
                          <MetricsTh>{t('admin.rate2Progressive43')}</MetricsTh>
                          <MetricsTh>{t('admin.rate3Recommendation')}</MetricsTh>
                          <MetricsTh>{t('admin.salary')}</MetricsTh>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeStatsByStore.map((storeGroup) => {
                          const storeOpen = employeeStatsOpenStores.has(storeGroup.key);
                          const storeRow = makeStatsEntity(storeGroup);
                          const storePayroll = storeGroup.employees.reduce((sum, employeeRow) => (
                            sum + calculateSalary(employeeRow.rates || {}).total
                          ), 0);
                          return (
                            <React.Fragment key={storeGroup.key}>
                              <StoreStatsRow>
                                <MetricsTd>
                                  <StoreStatsButton type="button" $open={storeOpen} onClick={() => toggleEmployeeStatsStore(storeGroup.key)}>
                                    <span className="chevron"><ChevronRight size={16} /></span>
                                    <span>
                                      <div style={{ fontWeight: 900 }}>{storeGroup.name}</div>
                                      <div style={{ fontSize: 12, color: '#64748b' }}>
                                        {storeGroup.employees.length} employee{storeGroup.employees.length === 1 ? '' : 's'}
                                      </div>
                                    </span>
                                  </StoreStatsButton>
                                </MetricsTd>
                                <MetricsTd>
                                  <RatePill type="button" variant={rateVariant(storeGroup.rates.completionRate)} onClick={() => setEmployeeRateDetails(buildRateDetails(storeRow, 'completion'))}>
                                    {formatPct(storeGroup.rates.completionRate)}
                                  </RatePill>
                                </MetricsTd>
                                <MetricsTd>
                                  <RatePill type="button" variant={rateVariant(storeGroup.rates.progressiveRate)} onClick={() => setEmployeeRateDetails(buildRateDetails(storeRow, 'progressive'))}>
                                    {formatPct(storeGroup.rates.progressiveRate)}
                                  </RatePill>
                                </MetricsTd>
                                <MetricsTd>
                                  <RatePill type="button" variant={rateVariant(storeGroup.rates.recommendationRate)} onClick={() => setEmployeeRateDetails(buildRateDetails(storeRow, 'recommendation'))}>
                                    {formatPct(storeGroup.rates.recommendationRate)}
                                  </RatePill>
                                </MetricsTd>
                                <MetricsTd>
                                  <strong>{fmtDzd(storePayroll)}</strong>
                                  <div style={{ fontSize: 12, color: '#64748b' }}>{t('admin.storePayroll')}</div>
                                </MetricsTd>
                              </StoreStatsRow>
                              {storeOpen && storeGroup.employees.map((row) => {
                                const emp = row.employee || {};
                                const r = row.rates || {};
                                const salary = calculateSalary(r);
                                return (
                                  <EmployeeStatsChildRow key={emp.id || emp.employeeId}>
                                    <MetricsTd>
                                      <div style={{ fontWeight: 800 }}>{emp.firstName} {emp.lastName}</div>
                                      <div style={{ fontSize: 12, color: '#64748b' }}>
                                        {emp.employeeId} · {emp.role}
                                      </div>
                                    </MetricsTd>
                                    <MetricsTd>
                                      <RatePill type="button" variant={rateVariant(r.completionRate)} onClick={() => setEmployeeRateDetails(buildRateDetails(row, 'completion'))}>
                                        {formatPct(r.completionRate)}
                                      </RatePill>
                                    </MetricsTd>
                                    <MetricsTd>
                                      <RatePill type="button" variant={rateVariant(r.progressiveRate)} onClick={() => setEmployeeRateDetails(buildRateDetails(row, 'progressive'))}>
                                        {formatPct(r.progressiveRate)}
                                      </RatePill>
                                    </MetricsTd>
                                    <MetricsTd>
                                      <RatePill type="button" variant={rateVariant(r.recommendationRate)} onClick={() => setEmployeeRateDetails(buildRateDetails(row, 'recommendation'))}>
                                        {formatPct(r.recommendationRate)}
                                      </RatePill>
                                    </MetricsTd>
                                    <MetricsTd>
                                      <strong>{fmtDzd(salary.total)}</strong>
                                      <div style={{ fontSize: 12, color: '#64748b' }}>
                                        base {fmtDzd(salary.base)} {t('admin.bonus')} {fmtDzd(salary.bonusTotal)}
                                      </div>
                                    </MetricsTd>
                                  </EmployeeStatsChildRow>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </MetricsTable>
                  </div>
                </>
              )}
            </MetricsCard>
            {employeeRateDetails && (
              <RawDataOverlay onMouseDown={(event) => {
                if (event.target === event.currentTarget) setEmployeeRateDetails(null);
              }}>
                <RawDataCard role="dialog" aria-modal="true" aria-labelledby="rate-detail-title">
                  <RawDataHeader>
                    <div>
                      <h3 id="rate-detail-title">{employeeRateDetails.title}</h3>
                      <p>{employeeRateDetails.employeeName}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEmployeeRateDetails(null)}
                      aria-label={t('admin.closeRawDataDetails')}
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 12,
                        border: '1px solid rgba(255,255,255,0.18)',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={18} />
                    </button>
                  </RawDataHeader>
                  <RawDataBody>
                    <RawDataGrid>
                      <RawDataBox>
                        <span>{employeeRateDetails.numeratorLabel}</span>
                        <strong>{employeeRateDetails.numerator}</strong>
                      </RawDataBox>
                      <RawDataBox>
                        <span>{employeeRateDetails.denominatorLabel}</span>
                        <strong>{employeeRateDetails.denominator}</strong>
                      </RawDataBox>
                      <RawDataBox>
                        <span>{t('admin.percentage')}</span>
                        <strong>{formatPct(employeeRateDetails.value)}</strong>
                      </RawDataBox>
                      <RawDataBox>
                        <span>{t('admin.rawFraction')}</span>
                        <strong>{employeeRateDetails.numerator}/{employeeRateDetails.denominator}</strong>
                      </RawDataBox>
                    </RawDataGrid>
                    <RawFormula>{employeeRateDetails.formula}</RawFormula>
                  </RawDataBody>
                </RawDataCard>
              </RawDataOverlay>
            )}
          </OrdersContainer>
        )}

        {activeTab === 'stores' && (() => {
          // Retail Stores tab — only customer-facing storefronts. The
          // central warehouse moved to its own tab, so we filter it out
          // here. `selectedStores` is also intersected with this list so
          // bulk-delete can never accidentally target the warehouse.
          const retailStores = stores.filter((s) => !s.isCentralWarehouse);
          const retailIds = new Set(retailStores.map((s) => s._id || s.id));
          const retailSelected = selectedStores.filter((id) => retailIds.has(id));
          return (
          <OrdersContainer>
            <OrdersHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  {t('admin.retailStores2')}{retailStores.length})
                </h3>
                {retailSelected.length > 0 && (
                  <span style={{
                    fontSize: '0.875rem',
                    color: '#6366f1',
                    fontWeight: '500'
                  }}>
                    {retailSelected.length} selected
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {retailSelected.length > 0 && (
                  <button
                    onClick={() => setShowBulkDeleteStoreModal(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontWeight: '500',
                      fontSize: '0.875rem'
                    }}
                  >
                    <Trash size={18} />
                    {t('admin.deleteSelected')}{retailSelected.length})
                  </button>
                )}
                <button
                  onClick={() => setShowAddStoreModal(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  <Plus size={18} />
                  {t('admin.addStore')}
                </button>
              </div>
            </OrdersHeader>
            {storesLoading ? (
              <LoadingState>
                <SpinningIcon size={20} />
                {t('admin.loadingStores')}
              </LoadingState>
            ) : retailStores.length === 0 ? (
              <EmptyState>
                <Building2 size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>{t('admin.noRetailStoresYet')}</p>
                <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                  {t('admin.use')} <strong>{t('admin.addStore')}</strong> {t('admin.aboveToRegisterYourFirst')}
                </p>
              </EmptyState>
            ) : (
              <>
                <OrdersTable>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell style={{ width: '36px' }}>
                        <input
                          type="checkbox"
                          checked={retailSelected.length === retailStores.length && retailStores.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStores(retailStores.map(store => store._id || store.id));
                            } else {
                              setSelectedStores([]);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell style={{ width: '70px' }}>{t('admin.city2')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.name')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.address')}</TableHeaderCell>
                      <TableHeaderCell style={{ width: '120px' }}>{t('admin.phone')}</TableHeaderCell>
                      <TableHeaderCell style={{ width: '80px' }}>{t('admin.status')}</TableHeaderCell>
                      <TableHeaderCell style={{ width: '90px' }}>{t('admin.actions')}</TableHeaderCell>
                    </TableHeaderRow>
                  </TableHeader>
                  <TableBody>
                    {retailStores.map((store) => {
                      const storeId = store._id || store.id;
                      const isSelected = selectedStores.includes(storeId);
                      const addressText = typeof store.address === 'string'
                        ? store.address
                        : store.address?.street || 'N/A';
                      return (
                        <TableRow key={storeId}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedStores([...selectedStores, storeId]);
                                } else {
                                  setSelectedStores(selectedStores.filter(id => id !== storeId));
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                              <strong style={{ fontSize: '0.85rem' }}>{store.storeCode || 'N/A'}</strong>
                              <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{store.branding?.cityCode || store.cityCode || '—'}</span>
                            </div>
                          </TableCell>
                          <TableCell>{store.name}</TableCell>
                          <TableCell style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={addressText}>
                            {addressText}
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                              {(store.contact?.phone || store.phone || 'N/A').split('/').map((p, i) => (
                                <span key={i} style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{p.trim()}</span>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={store.isActive ? 'active' : 'inactive'} completed={store.isActive}>
                              {store.isActive ? t('admin.active') : t('admin.inactive')}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.35rem' }}>
                              <button
                                onClick={() => toggleCentralWarehouse(store)}
                                title={t('admin.promoteToCentral')}
                                style={{ padding: '0.3rem', background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                              >
                                <Warehouse size={14} />
                              </button>
                              <button
                                onClick={() => { setSelectedStore(store); setShowEditStoreModal(true); }}
                                title={t('admin.edit')}
                                style={{ padding: '0.3rem', background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => { setSelectedStore(store); setShowDeleteStoreModal(true); }}
                                title={t('admin.delete')}
                                style={{ padding: '0.3rem', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </OrdersTable>
              </>
            )}
          </OrdersContainer>
          );
        })()}

        {activeTab === 'store-branding' && (
          <StoreBrandingSettings />
        )}

        {activeTab === 'courier-routes' && (
          <CourierRouteSettings />
        )}

        {activeTab === 'central-store' && (() => {
          // ── Central Store tab ────────────────────────────────────────────
          // Renders the company's single central warehouse + role-grouped
          // assignment cards for Inventory and Operations managers, plus a
          // table of every staffer that lives there.
          const inventoryOpsManagers = centralStoreEmployees.filter(
            (e) => e.role === 'inventory_manager' || e.role === 'operations_manager'
          );
          const couriers = centralStoreEmployees.filter((e) => e.role === 'courier');
          const otherStaff = centralStoreEmployees.filter(
            (e) => !['inventory_manager', 'operations_manager', 'courier'].includes(e.role)
          );
          const centralAddress = centralStore?.address;
          const centralAddressLine = typeof centralAddress === 'string'
            ? centralAddress
            : [centralAddress?.street, centralAddress?.city, centralAddress?.state, centralAddress?.country]
                .filter(Boolean)
                .join(', ') || '—';

          // Reusable card for "Inventory Manager" / "Operations Manager"
          // sections. Shows current assignees inline and exposes a single
          // "Add" button that opens the AddEmployeeForm with role locked.
          const ManagerCard = ({ role, label, accent, members, icon: Icon }) => { const { t } = useTranslation(); return  (
            <div style={{
              background: 'white',
              border: `1px solid ${accent}33`,
              borderTop: `4px solid ${accent}`,
              borderRadius: '14px',
              padding: '1.25rem 1.25rem 1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.875rem',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '10px',
                    background: `${accent}1a`, color: accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Icon size={18} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9375rem' }}>{label}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {members.length === 0 ? t('admin.noOneAssignedYet') : `${members.length} assigned`}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!centralStore) {
                      toast.info('Set a central warehouse first.');
                      return;
                    }
                    setAddEmployeeContext({ defaultRole: role, lockRole: true });
                    setShowAddEmployeeModal(true);
                  }}
                  disabled={!centralStore}
                  style={{
                    padding: '0.4rem 0.75rem',
                    background: centralStore ? accent : '#cbd5e1',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: centralStore ? 'pointer' : 'not-allowed',
                    fontWeight: 600, fontSize: '0.8125rem',
                    display: 'inline-flex', alignItems: 'center', gap: '0.375rem'
                  }}
                  title={centralStore ? `Add ${label}` : 'Promote a store to central warehouse first'}
                >
                  <UserPlus size={14} /> {t('admin.add3')} {label.split(' ')[0]}
                </button>
              </div>

              {members.length === 0 ? (
                <div style={{
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '10px',
                  color: '#64748b',
                  fontSize: '0.875rem'
                }}>
                  {t('admin.no')} {label.toLowerCase()} {t('admin.onRecordClick')} <strong>{t('admin.add3')} {label.split(' ')[0]}</strong> {t('admin.toAssignOne')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {members.map((m) => {
                    const id = m._id || m.id;
                    return (
                      <div key={id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.625rem 0.875rem',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px', gap: '0.75rem'
                      }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>
                            {m.firstName} {m.lastName}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {m.employeeId} {m.email ? `\u00b7 ${m.email}` : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedEmployee(m);
                            setShowDeleteEmployeeModal(true);
                          }}
                          style={{
                            padding: '0.375rem 0.625rem',
                            background: '#fee2e2', color: '#991b1b',
                            border: '1px solid #fecaca', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.8125rem',
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem'
                          }}
                          title={t('admin.removeThisEmployee')}
                        >
                          <Trash size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ); };

          return (
            <OrdersContainer>
              <OrdersHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '12px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 6px 16px -6px #d97706'
                  }}>
                    <Warehouse size={22} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1e293b' }}>
                      {t('admin.centralStore2')}
                    </h3>
                    <p style={{ margin: '0.125rem 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                      {t('admin.theSingleDistributionHubServing')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={fetchCentralStore}
                  disabled={centralStoreLoading}
                  style={{
                    padding: '0.5rem 0.875rem',
                    background: 'white', color: '#334155',
                    border: '1px solid #e2e8f0', borderRadius: '8px',
                    cursor: centralStoreLoading ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    fontSize: '0.875rem', fontWeight: 600
                  }}
                >
                  <RefreshCw size={14} />
                  {centralStoreLoading ? t('admin.refreshing2') : t('admin.refresh')}
                </button>
              </OrdersHeader>

              {centralStoreLoading ? (
                <LoadingState>
                  <SpinningIcon size={20} />
                  {t('admin.loadingCentralStore')}
                </LoadingState>
              ) : !centralStore ? (
                <EmptyState>
                  <Warehouse size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>{t('admin.noCentralWarehouseYet')}</p>
                  <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginTop: '0.25rem', maxWidth: '32rem', marginInline: 'auto' }}>
                    {t('admin.createANewCentralStore')}
                  </p>
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.25rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => { setShowAddCentralStoreModal(true); }}
                      style={{ padding: '0.5rem 1rem', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <Warehouse size={16} /> {t('admin.addCentralStore')}
                    </button>
                    <button
                      onClick={() => { navigate('/admin/dashboard/stores'); setIsFacilitiesOpen(true); }}
                      style={{ padding: '0.5rem 1rem', background: '#f8fafc', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <Store size={16} /> {t('admin.promoteExistingStore')}
                    </button>
                  </div>
                </EmptyState>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {/* Header card with the central store's identity. */}
                  <div style={{
                    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                    border: '1px solid #fcd34d',
                    borderRadius: '14px',
                    padding: '1.25rem 1.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.875rem'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.875rem' }}>
                      <div>
                        <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#92400e' }}>{t('admin.storeCode')}</div>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginTop: '0.25rem' }}>{centralStore.storeCode}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#92400e' }}>{t('admin.name')}</div>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginTop: '0.25rem' }}>{centralStore.name}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#92400e' }}>{t('admin.address')}</div>
                        <div style={{ fontSize: '0.9375rem', color: '#0f172a', marginTop: '0.25rem' }}>{centralAddressLine}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#92400e' }}>{t('admin.contact')}</div>
                        <details style={{ marginTop: '0.25rem' }}>
                          <summary style={{ fontSize: '0.8rem', fontWeight: 600, color: '#92400e', cursor: 'pointer', userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {'\u25b8'} {(centralStore.contact?.phone || centralStore.phone || '').split('/').filter(Boolean).length || 0} {t('admin.numberS')}
                          </summary>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', marginTop: '0.3rem' }}>
                            {(centralStore.contact?.phone || centralStore.phone || '').split('/').map(p => p.trim()).filter(Boolean).map((p, i) => (
                              <span key={i} style={{ fontSize: '0.875rem', color: '#0f172a' }}>{p}</span>
                            ))}
                            {centralStore.contact?.email && <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{centralStore.contact.email}</span>}
                          </div>
                        </details>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', borderTop: '1px solid #fde68a', paddingTop: '0.75rem' }}>
                      <button
                        onClick={() => { setSelectedStore(centralStore); setShowEditStoreModal(true); }}
                        style={{ padding: '0.35rem 0.75rem', background: '#fef3c7', color: '#92400e', border: '1.5px solid #fcd34d', borderRadius: '7px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                      >
                        <Edit size={13} /> {t('admin.edit')}
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Delete "${centralStore.name}" as the central store? This cannot be undone.`)) return;
                          try {
                            const token = localStorage.getItem('token');
                            await axios.delete(`/api/stores/${centralStore._id || centralStore.id}`, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Central store deleted');
                            setCentralStore(null);
                            fetchStores();
                          } catch (err) {
                            toast.error(err.response?.data?.message || 'Could not delete central store');
                          }
                        }}
                        style={{ padding: '0.35rem 0.75rem', background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: '7px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                      >
                        <Trash size={13} /> {t('admin.delete')}
                      </button>
                    </div>
                  </div>

                  {/* Manager assignment cards. */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '1rem'
                  }}>
                    <ManagerCard
                      role="inventory_manager"
                      label="Inventory & Operations Manager"
                      accent="#0ea5e9"
                      members={inventoryOpsManagers}
                      icon={Package}
                    />
                    <ManagerCard
                      role="courier"
                      label="Courier"
                      accent="#ea580c"
                      members={couriers}
                      icon={Truck}
                    />
                  </div>

                  {/* Roster of everyone else assigned to the central store
                      (currently empty for most setups but future-proof if
                      you add other store-bound roles). */}
                  {otherStaff.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 0.625rem', fontSize: '0.875rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('admin.otherStaffAtTheCentral')}
                      </h4>
                      <OrdersTable>
                        <TableHeader>
                          <TableHeaderRow>
                            <TableHeaderCell>{t('admin.employeeId')}</TableHeaderCell>
                            <TableHeaderCell>{t('admin.name')}</TableHeaderCell>
                            <TableHeaderCell>{t('admin.role')}</TableHeaderCell>
                            <TableHeaderCell>{t('admin.email')}</TableHeaderCell>
                          </TableHeaderRow>
                        </TableHeader>
                        <TableBody>
                          {otherStaff.map((emp) => {
                            const id = emp._id || emp.id;
                            return (
                              <TableRow key={id}>
                                <TableCell><strong>{emp.employeeId}</strong></TableCell>
                                <TableCell>{emp.firstName} {emp.lastName}</TableCell>
                                <TableCell>{humanizeRole(emp.role)}</TableCell>
                                <TableCell>{emp.email || '\u2014'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </OrdersTable>
                    </div>
                  )}
                </div>
              )}
            </OrdersContainer>
          );
        })()}

        {activeTab === 'peer-ratings' && (
          <OrdersContainer>
            <OrdersHeader>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  {t('admin.peerRatingsPerStore')}
                </h3>
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#64748b', maxWidth: '48rem' }}>
                  {t('admin.chooseWhichWeekdayAndTime')}{' '}
                  <strong>0–5</strong> {t('admin.inStepsOf')} <strong>0.25</strong>.
                </p>
              </div>
            </OrdersHeader>

            {storesLoading || stores.length === 0 ? (
              <LoadingState>
                <SpinningIcon size={20} />
                {stores.length === 0 && !storesLoading ? t('admin.addAStoreFirst') : t('admin.loadingStores')}
              </LoadingState>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.875rem' }}>{t('admin.store')}</span>
                  <select
                    value={peerRatingStoreId || (stores[0]?._id || stores[0]?.id || '')}
                    onChange={(e) => setPeerRatingStoreId(e.target.value)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      fontSize: '0.9375rem',
                      maxWidth: '24rem'
                    }}
                  >
                    {stores.map((s) => {
                      const sid = s._id || s.id;
                      return (
                        <option key={sid} value={sid}>
                          {s.name} ({s.storeCode})
                        </option>
                      );
                    })}
                  </select>
                </label>

                {peerRatingLoading ? (
                  <LoadingState>
                    <SpinningIcon size={20} />
                    {t('admin.loadingSettings')}
                  </LoadingState>
                ) : (
                  <>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.875rem' }}>
                        {t('admin.adminSetupNoteOptional')}
                      </span>
                      <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                        {t('admin.reminderForYouOrOther')}
                      </span>
                      <textarea
                        value={peerRatingForm.setupNote}
                        onChange={(e) => setPeerRatingForm((f) => ({ ...f, setupNote: e.target.value }))}
                        rows={2}
                        placeholder={t('admin.internalNoteAboutWhenHow')}
                        style={{
                          padding: '0.6rem 0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.9375rem',
                          resize: 'vertical'
                        }}
                      />
                    </label>

                    <div style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '1.25rem',
                      maxWidth: '52rem'
                    }}>
                      <h4 style={{ margin: '0 0 1rem', fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b' }}>
                        {t('admin.schedule')}
                      </h4>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#475569' }}>
                        {t('admin.day')}
                      </span>
                      <select
                        value={peerRatingForm.visibleWeekday}
                        onChange={(e) =>
                          setPeerRatingForm((f) => ({ ...f, visibleWeekday: Number(e.target.value) }))
                        }
                        style={{
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.9375rem',
                          minWidth: '10rem',
                          background: 'white'
                        }}
                      >
                        {PEER_RATING_WEEKDAY_OPTIONS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#475569' }}>{t('admin.from')}</span>
                      <select
                        value={peerRatingForm.visibleHourStart}
                        onChange={(e) =>
                          setPeerRatingForm((f) => ({ ...f, visibleHourStart: Number(e.target.value) }))
                        }
                        style={{
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.9375rem',
                          minWidth: '7rem',
                          background: 'white'
                        }}
                      >
                        {PEER_RATING_HOUR_OPTIONS.map((h) => (
                          <option key={h.value} value={h.value}>{h.label}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#475569' }}>{t('admin.to')}</span>
                      <select
                        value={peerRatingForm.visibleHourEnd}
                        onChange={(e) =>
                          setPeerRatingForm((f) => ({ ...f, visibleHourEnd: Number(e.target.value) }))
                        }
                        style={{
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.9375rem',
                          minWidth: '7rem',
                          background: 'white'
                        }}
                      >
                        {PEER_RATING_HOUR_OPTIONS.map((h) => (
                          <option key={h.value} value={h.value}>{h.label}</option>
                        ))}
                      </select>
                    </label>
                    <span style={{
                      fontSize: '0.8125rem',
                      color: '#64748b',
                      paddingBottom: '0.5rem'
                    }}>
                      {t('admin.timezone')} {stores.find((s) => String(s._id || s.id) === String(peerRatingStoreId))?.settings?.timezone || 'Africa/Algiers'}
                    </span>
                      </div>
                    </div>

                    <div
                      style={{
                        padding: '0.75rem 1rem',
                        background: '#f1f5f9',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        color: '#475569',
                        maxWidth: '52rem'
                      }}
                    >
                      <strong>{t('admin.ratingScale')}</strong> {t('admin.x0To5InSteps')}
                    </div>

                    <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>{t('admin.categories')}</h4>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => {
                            const maxCols = Math.max(...peerRatingForm.categories.map(c => (c.noteSections || []).length));
                            if (maxCols >= PEER_RATING_NOTE_SECTIONS_MAX) return;
                            setPeerRatingForm((f) => ({
                              ...f,
                              categories: f.categories.map(c => ({
                                ...c,
                                noteSections: [...(c.noteSections || []), '']
                              })),
                              coefficients: [...(f.coefficients || []), 1]
                            }));
                          }}
                          style={{
                            padding: '0.4rem 0.85rem',
                            background: '#f0fdf4',
                            color: '#166534',
                            border: '1px solid #bbf7d0',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '0.8125rem'
                          }}
                        >
                          {t('admin.addNoteRow')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const minCols = Math.min(...peerRatingForm.categories.map(c => (c.noteSections || []).length));
                            if (minCols <= 1) return;
                            setPeerRatingForm((f) => ({
                              ...f,
                              categories: f.categories.map(c => ({
                                ...c,
                                noteSections: (c.noteSections || []).slice(0, -1)
                              })),
                              coefficients: (f.coefficients || []).slice(0, -1)
                            }));
                          }}
                          style={{
                            padding: '0.4rem 0.85rem',
                            background: '#fef2f2',
                            color: '#991b1b',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '0.8125rem'
                          }}
                        >
                          {t('admin.removeLastNoteRow')}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setPeerRatingForm((f) => {
                              const colCount = f.categories.length > 0 ? (f.categories[0].noteSections || []).length : PEER_RATING_NOTE_SECTIONS_DEFAULT;
                              return {
                                ...f,
                                categories: [
                                  ...f.categories,
                                  { name: '', noteSections: Array(colCount).fill('') }
                                ]
                              };
                            })
                          }
                          style={{
                            padding: '0.4rem 0.85rem',
                            background: '#e0e7ff',
                            color: '#3730a3',
                            border: '1px solid #c7d2fe',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '0.8125rem'
                          }}
                        >
                          {t('admin.addCategoryColumn')}
                        </button>
                      </div>
                    </div>

                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      border: '2px solid #e2e8f0',
                      background: 'white'
                    }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {peerRatingForm.categories.map((cat, index) => (
                            <React.Fragment key={`cat-head-${index}`}>
                              <th colSpan={2} style={{ padding: '0.55rem 0.6rem', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 700, color: '#334155', border: '1px solid #e2e8f0', minWidth: '220px' }}>
                                <input
                                  type="text"
                                  value={cat.name}
                                  onChange={(e) =>
                                    setPeerRatingForm((f) => {
                                      const categories = [...f.categories];
                                      categories[index] = { ...categories[index], name: e.target.value };
                                      return { ...f, categories };
                                    })
                                  }
                                  placeholder={`Category ${index + 1}`}
                                  style={{ padding: '0.35rem 0.5rem', borderRadius: '5px', border: '1px solid #e2e8f0', fontSize: '0.875rem', width: '100%', boxSizing: 'border-box', fontWeight: 600, textAlign: 'center' }}
                                />
                                <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', marginTop: '0.35rem' }}>
                                  <button type="button" disabled={index === 0} onClick={() => setPeerRatingForm((f) => {
                                    if (index === 0) return f;
                                    const next = [...f.categories];
                                    [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                    return { ...f, categories: next };
                                  })} title={t('admin.moveLeft')} style={{ padding: '0.15rem 0.35rem', fontSize: '0.7rem', borderRadius: '4px', border: '1px solid #e2e8f0', background: index === 0 ? '#f8fafc' : '#fff', cursor: index === 0 ? 'not-allowed' : 'pointer' }}>←</button>
                                  <button type="button" disabled={index === peerRatingForm.categories.length - 1} onClick={() => setPeerRatingForm((f) => {
                                    if (index >= f.categories.length - 1) return f;
                                    const next = [...f.categories];
                                    [next[index + 1], next[index]] = [next[index], next[index + 1]];
                                    return { ...f, categories: next };
                                  })} title={t('admin.moveRight')} style={{ padding: '0.15rem 0.35rem', fontSize: '0.7rem', borderRadius: '4px', border: '1px solid #e2e8f0', background: index === peerRatingForm.categories.length - 1 ? '#f8fafc' : '#fff', cursor: index === peerRatingForm.categories.length - 1 ? 'not-allowed' : 'pointer' }}>→</button>
                                  <button type="button" disabled={peerRatingForm.categories.length <= 1} onClick={() => {
                                    if (peerRatingForm.categories.length <= 1) return;
                                    if (!window.confirm('Remove this category?')) return;
                                    setPeerRatingForm((f) => ({ ...f, categories: f.categories.filter((_, i) => i !== index) }));
                                  }} title={t('admin.remove')} style={{ padding: '0.15rem 0.35rem', fontSize: '0.7rem', borderRadius: '4px', border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', cursor: peerRatingForm.categories.length <= 1 ? 'not-allowed' : 'pointer' }}>✕</button>
                                </div>
                              </th>
                            </React.Fragment>
                          ))}
                        </tr>
                        <tr style={{ background: '#f8fafc' }}>
                          {peerRatingForm.categories.map((_, index) => (
                            <React.Fragment key={`cat-subhead-${index}`}>
                              <th style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#475569', border: '1px solid #e2e8f0', minWidth: '170px' }}>{t('admin.note')}</th>
                              <th style={{ padding: '0.45rem 0.35rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#92400e', border: '1px solid #e2e8f0', width: '64px' }}>coef</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const rowCount = peerRatingForm.categories.length > 0 ? Math.max(...peerRatingForm.categories.map(c => (c.noteSections || []).length)) : PEER_RATING_NOTE_SECTIONS_DEFAULT;
                          return Array.from({ length: rowCount }, (_, si) => (
                            <tr key={`note-row-${si}`} style={{ background: si % 2 === 0 ? '#fff' : '#f8fafc' }}>
                              {peerRatingForm.categories.map((cat, index) => {
                                const catCoeffs = getCategoryCoefficients(cat, peerRatingForm.coefficients);
                                return (
                                  <React.Fragment key={`cat-${index}-note-${si}`}>
                                    <td style={{ padding: '0.45rem 0.5rem', border: '1px solid #e2e8f0', verticalAlign: 'middle' }}>
                                      <input
                                        type="text"
                                        value={(cat.noteSections || [])[si] || ''}
                                        onChange={(e) =>
                                          setPeerRatingForm((f) => {
                                            const categories = [...f.categories];
                                            const cur = categories[index];
                                            const ns = [...(cur.noteSections || [])];
                                            while (ns.length <= si) ns.push('');
                                            ns[si] = e.target.value;
                                            categories[index] = { ...cur, noteSections: ns };
                                            return { ...f, categories };
                                          })
                                        }
                                        placeholder={`Note ${si + 1}`}
                                        style={{ padding: '0.35rem 0.5rem', borderRadius: '5px', border: '1px solid #e2e8f0', fontSize: '0.8125rem', width: '100%', boxSizing: 'border-box' }}
                                      />
                                    </td>
                                    <td style={{ padding: '0.45rem 0.35rem', border: '1px solid #e2e8f0', verticalAlign: 'middle', textAlign: 'center', background: '#fffbeb' }}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={catCoeffs[si] ?? 1}
                                        onChange={(e) =>
                                          setPeerRatingForm((f) => {
                                            const categories = [...f.categories];
                                            const cur = categories[index];
                                            const coeffs = [...getCategoryCoefficients(cur, f.coefficients)];
                                            while (coeffs.length <= si) coeffs.push(1);
                                            coeffs[si] = e.target.value === '' ? '' : Number(e.target.value);
                                            categories[index] = { ...cur, coefficients: coeffs };
                                            return { ...f, categories };
                                          })
                                        }
                                        style={{ padding: '0.35rem 0.25rem', borderRadius: '5px', border: '1px solid #fde68a', fontSize: '0.8125rem', width: '3.5rem', textAlign: 'center', background: '#fffbeb', fontWeight: 600, color: '#92400e' }}
                                      />
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                    </div>

                    {peerRatingStatus ? (
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#15803d' }}>{peerRatingStatus}</p>
                    ) : null}

                    <button
                      type="button"
                      onClick={savePeerRatingSettings}
                      disabled={peerRatingSaving}
                      style={{
                        padding: '0.6rem 1.25rem',
                        background: peerRatingSaving ? '#94a3b8' : '#6366f1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: peerRatingSaving ? 'wait' : 'pointer',
                        fontWeight: 600,
                        fontSize: '0.9375rem',
                        alignSelf: 'flex-start'
                      }}
                    >
                      {peerRatingSaving ? t('admin.saving2') : t('admin.savePeerRatingSettings')}
                    </button>
                  </>
                )}

                {/* ── Submitted Ratings Results ── */}
                <div style={{ marginTop: '2.5rem', borderTop: '2px solid #e2e8f0', paddingTop: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem', fontSize: '1.125rem', fontWeight: 700, color: '#1e293b' }}>
                    {t('admin.submittedRatings')}
                  </h4>

                  {prResultsPeriods.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>{t('admin.noRatingsSubmittedYetFor')}</p>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>
                          {t('admin.week')}
                          <select
                            value={prResultsPeriod}
                            onChange={(e) => setPrResultsPeriod(e.target.value)}
                            style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.875rem', background: 'white' }}
                          >
                            {prResultsPeriods.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>
                          {t('admin.store3')}
                          <select
                            value={peerRatingStoreId || (stores[0]?._id || stores[0]?.id || '')}
                            onChange={(e) => setPeerRatingStoreId(e.target.value)}
                            style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.875rem', minWidth: '13rem', background: 'white' }}
                          >
                            {stores.map((s) => {
                              const sid = s._id || s.id;
                              return (
                                <option key={sid} value={sid}>
                                  {s.name} {s.storeCode ? `(${s.storeCode})` : ''}
                                </option>
                              );
                            })}
                          </select>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>
                          {t('admin.rater')}
                          <select
                            value={prResultsEmployeeId}
                            onChange={(e) => setPrResultsEmployeeId(e.target.value)}
                            style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.875rem', minWidth: '13rem', background: 'white' }}
                          >
                            <option value="">{t('admin.allRaters')}</option>
                            {(() => {
                              const optionsById = new Map();
                              const addOption = (id, label) => {
                                if (!id || optionsById.has(String(id))) return;
                                optionsById.set(String(id), label || String(id));
                              };
                              prResultsSubmissions.forEach((sub) => {
                                const raterStoreId = sub.rater?.store?._id || sub.rater?.store?.id || sub.rater?.storeId || sub.rater?.store_id;
                                if (raterStoreId && String(raterStoreId) !== String(peerRatingStoreId)) return;
                                addOption(sub.rater?._id || sub.rater?.id, `${sub.rater?.firstName || ''} ${sub.rater?.lastName || ''}`.trim());
                              });
                              employees.forEach((emp) => {
                                const empStoreId = emp.store?._id || emp.store?.id || emp.store;
                                if (String(empStoreId || '') !== String(peerRatingStoreId)) return;
                                const empId = emp._id || emp.id;
                                addOption(empId, `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || empId);
                              });
                              return [...optionsById.entries()]
                                .sort((a, b) => a[1].localeCompare(b[1]))
                                .map(([id, label]) => <option key={id} value={id}>{label}</option>);
                            })()}
                          </select>
                        </label>
                        {prResultsLoading && <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>{t('admin.loading2')}</span>}
                      </div>

                      {prResultsSubmissions.length === 0 && !prResultsLoading ? (
                        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                          {t('admin.noSubmissionsForThisWeek')}{prResultsEmployeeId ? t('admin.andRater') : ''}.
                        </p>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem', border: '2px solid #e2e8f0' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>{t('admin.rater2')}</th>
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>{t('admin.rated')}</th>
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 600, color: '#475569', minWidth: '140px' }}>{t('admin.category')}</th>
                                {(() => {
                                  const configuredCats = peerRatingForm.categories || [];
                                  const configuredNoteCount = configuredCats.length
                                    ? Math.max(...configuredCats.map((cat) => (cat.noteSections || []).filter((note) => String(note || '').trim()).length), 0)
                                    : 0;
                                  const submittedNoteCount = Math.max(...prResultsSubmissions.flatMap((sub) => (sub.ratings || []).map((rating) => (rating.noteScores || []).length)), 0);
                                  const noteCount = Math.max(configuredNoteCount, submittedNoteCount);
                                  return Array.from({ length: noteCount }, (_, ni) => {
                                    const categoryCoeffs = configuredCats.map((cat) => getCategoryCoefficients(cat, peerRatingForm.coefficients)[ni]).filter((value) => value != null);
                                    const uniqueCoeffs = [...new Set(categoryCoeffs.map((value) => Number(value) || 1))];
                                    const coeffLabel = uniqueCoeffs.length === 1 ? `x${uniqueCoeffs[0]}` : 'varies';
                                    return (
                                      <th key={ni} style={{ padding: '0.6rem 0.75rem', textAlign: 'center', border: '1px solid #e2e8f0', fontWeight: 600, color: '#475569', minWidth: '95px' }}>
                                        <div>{t('admin.note')} {ni + 1}</div>
                                        <div style={{ marginTop: '0.15rem', color: '#92400e', fontSize: '0.6875rem', fontWeight: 700 }}>{t('admin.coeff')} {coeffLabel}</div>
                                      </th>
                                    );
                                  });
                                })()}
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center', border: '1px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>{t('admin.avg')}</th>
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'center', border: '1px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>{t('admin.date')}</th>
                                <th style={{ padding: '0.6rem 0.5rem', border: '1px solid #e2e8f0', width: '50px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {prResultsSubmissions.flatMap((sub, si) => {
                                const rater = sub.rater;
                                const ratee = sub.ratee;
                                const configuredCats = peerRatingForm.categories || [];
                                const submittedCats = (sub.ratings || [])
                                  .map((rating) => rating.categoryName)
                                  .filter(Boolean);
                                const cats = configuredCats.length > 0
                                  ? configuredCats
                                  : [...new Set(submittedCats)].map((name) => ({ name }));
                                const configuredNoteCount = configuredCats.length
                                  ? Math.max(...configuredCats.map((cat) => (cat.noteSections || []).filter((note) => String(note || '').trim()).length), 0)
                                  : 0;
                                const submittedNoteCount = Math.max(...(sub.ratings || []).map((rating) => (rating.noteScores || []).length), 0);
                                const noteCount = Math.max(configuredNoteCount, submittedNoteCount);
                                const normalizeRatingName = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
                                return cats.map((cat, ci) => {
                                  const match = (sub.ratings || []).find((r) => normalizeRatingName(r.categoryName) === normalizeRatingName(cat.name));
                                  const noteScores = match?.noteScores || [];
                                  let totalWeighted = 0;
                                  let totalCoeff = 0;
                                  const coeffs = getCategoryCoefficients(cat, peerRatingForm.coefficients);
                                  noteScores.forEach((score, ni) => {
                                    const c = coeffs[ni] != null ? coeffs[ni] : 1;
                                    totalWeighted += score * c;
                                    totalCoeff += c;
                                  });
                                  const catAvg = totalCoeff > 0 ? totalWeighted / totalCoeff : 0;
                                  const avgColor = catAvg >= 4 ? '#5b21b6' : catAvg >= 2.5 ? '#92400e' : '#991b1b';
                                  const avgBg = catAvg >= 4 ? '#ede9fe' : catAvg >= 2.5 ? '#fef3c7' : '#fecaca';
                                  const rowIndex = si * Math.max(cats.length, 1) + ci;
                                  return (
                                  <tr key={`${sub._id || si}-${cat.name || ci}`} style={{ background: rowIndex % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <td style={{ padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                      {rater ? `${rater.firstName} ${rater.lastName}` : '—'}
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                      {ratee ? `${ratee.firstName} ${ratee.lastName}` : '—'}
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>
                                      {cat.name || `Cat ${ci + 1}`}
                                    </td>
                                    {Array.from({ length: noteCount }, (_, ni) => {
                                      const score = noteScores[ni];
                                      const noteText = (cat.noteSections || [])[ni];
                                      return (
                                        <td key={ni} title={noteText || `Note ${ni + 1}`} style={{ padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                          {score != null ? (
                                            <span style={{ padding: '0.2rem 0.5rem', borderRadius: '6px', background: '#fffbeb', color: '#92400e', fontWeight: 700, fontSize: '0.8125rem' }}>
                                              {Number(score).toFixed(1)}
                                            </span>
                                          ) : (
                                            <span style={{ color: '#cbd5e1' }}>—</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                    <td style={{ padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', textAlign: 'center', fontWeight: 700 }}>
                                      {totalCoeff > 0 ? (
                                        <span style={{
                                          padding: '0.2rem 0.5rem',
                                          borderRadius: '6px',
                                          background: avgBg,
                                          color: avgColor,
                                          fontWeight: 700
                                        }}>
                                          {catAvg.toFixed(2)}
                                        </span>
                                      ) : '—'}
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', textAlign: 'center', color: '#64748b', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                      {sub.createdAt ? new Date(sub.createdAt).toLocaleDateString() : '—'}
                                    </td>
                                    <td style={{ padding: '0.4rem 0.5rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (!window.confirm(`Delete rating from ${rater?.firstName || '?'} → ${ratee?.firstName || '?'}?`)) return;
                                          try {
                                            const token = localStorage.getItem('token');
                                            await axios.delete(`/api/peer-ratings/admin/${sub._id}`, { headers: { Authorization: `Bearer ${token}` } });
                                            setPrResultsSubmissions((prev) => prev.filter((s) => s._id !== sub._id));
                                          } catch (err) {
                                            toast.error(err.response?.data?.message || 'Failed to delete');
                                          }
                                        }}
                                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.6875rem', borderRadius: '5px', border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', cursor: 'pointer', fontWeight: 600 }}
                                      >
                                        ✕
                                      </button>
                                    </td>
                                  </tr>
                                  );
                                });
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>

              </div>
            )}
          </OrdersContainer>
        )}

        {activeTab === 'labs' && (
          <OrdersContainer>
            <OrdersHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  {t('admin.labs2')}{labs.length})
                </h3>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowAddLabModal(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  <Plus size={18} />
                  {t('admin.addLab')}
                </button>
              </div>
            </OrdersHeader>
            {labsLoading ? (
              <LoadingState>
                <SpinningIcon size={20} />
                {t('admin.loadingLabs')}
              </LoadingState>
            ) : labs.length === 0 ? (
              <EmptyState>
                <Building2 size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>{t('admin.noLabsFound')}</p>
              </EmptyState>
            ) : (
              <>
                <OrdersTable>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell>{t('admin.labCode')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.labName')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.address')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.phone')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.manager')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.status')}</TableHeaderCell>
                      <TableHeaderCell>{t('admin.actions')}</TableHeaderCell>
                    </TableHeaderRow>
                  </TableHeader>
                  <TableBody>
                    {labs.map((lab) => {
                      const labId = lab._id || lab.id;
                      const managerName = lab.contact?.manager
                        ? `${lab.contact.manager.firstName || ''} ${lab.contact.manager.lastName || ''}`.trim()
                        : 'Unassigned';
                      return (
                        <TableRow key={labId}>
                          <TableCell><strong>{lab.labCode || 'N/A'}</strong></TableCell>
                          <TableCell><strong>{lab.name}</strong></TableCell>
                          <TableCell>
                            {typeof lab.address === 'string'
                              ? lab.address
                              : lab.address?.street
                                ? lab.address.street
                                : t('admin.nA')}
                          </TableCell>
                          <TableCell>{lab.contact?.phone || 'N/A'}</TableCell>
                          <TableCell>{managerName || 'Unassigned'}</TableCell>
                          <TableCell>
                            <StatusBadge status={lab.isActive ? 'active' : 'inactive'} completed={lab.isActive}>
                              {lab.isActive ? t('admin.active') : t('admin.inactive')}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => {
                                  setSelectedLab(lab);
                                  setShowEditLabModal(true);
                                }}
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  background: '#dbeafe',
                                  color: '#1e40af',
                                  border: '1px solid #93c5fd',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  fontSize: '0.875rem'
                                }}
                              >
                                <Edit size={14} />
                                {t('admin.edit')}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedLab(lab);
                                  setShowDeleteLabModal(true);
                                }}
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  background: '#fee2e2',
                                  color: '#991b1b',
                                  border: '1px solid #fecaca',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  fontSize: '0.875rem'
                                }}
                              >
                                <Trash size={14} />
                                {t('admin.delete')}
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </OrdersTable>
              </>
            )}
          </OrdersContainer>
        )}

        {activeTab === 'administration' && (
          <>
          <OrdersContainer>
            <OrdersHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                  {t('admin.administrationLocations')}{adminLocations.length})
                </h3>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowAddAdminLocationModal(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  <Plus size={18} />
                  {t('admin.addLocation')}
                </button>
              </div>
            </OrdersHeader>
            {adminLocationsLoading ? (
              <LoadingState>
                <SpinningIcon size={20} />
                {t('admin.loadingAdministrationLocations')}
              </LoadingState>
            ) : adminLocations.length === 0 ? (
              <EmptyState>
                <MapPin size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>{t('admin.noAdministrationLocationsFound')}</p>
              </EmptyState>
            ) : (
              <OrdersTable>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>{t('admin.code')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.name')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.employees')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.created')}</TableHeaderCell>
                    <TableHeaderCell>{t('admin.actions')}</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {adminLocations.map((location) => (
                    <TableRow key={location._id || location.id}>
                      <TableCell><strong>{location.adminCode || 'N/A'}</strong></TableCell>
                      <TableCell><strong>{location.name}</strong></TableCell>
                      <TableCell>{location.employees?.length || 0}</TableCell>
                      <TableCell>
                        {location.createdAt ? new Date(location.createdAt).toLocaleDateString() : t('admin.nA')}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => {
                              setSelectedAdminLocation(location);
                              setShowEditAdminLocationModal(true);
                            }}
                            style={{
                              padding: '0.375rem 0.75rem',
                              background: '#dbeafe',
                              color: '#1e40af',
                              border: '1px solid #93c5fd',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontSize: '0.875rem'
                            }}
                          >
                            <Edit size={14} />
                            {t('admin.edit')}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedAdminLocation(location);
                              setShowDeleteAdminLocationModal(true);
                            }}
                            style={{
                              padding: '0.375rem 0.75rem',
                              background: '#fee2e2',
                              color: '#991b1b',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontSize: '0.875rem'
                            }}
                          >
                            <Trash size={14} />
                            {t('admin.delete')}
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </OrdersTable>
            )}
          </OrdersContainer>

          {/* Danger Zone — relocated here from the Overview page. Destructive
              actions live in Administration only, away from daily dashboards. */}
          <DangerZone style={{ marginTop: '1.5rem' }}>
            <DangerZoneHeader>
              <AlertTriangle size={24} color="#dc2626" />
              <DangerZoneTitle>{t('admin.dangerZone')}</DangerZoneTitle>
            </DangerZoneHeader>
            <DangerZoneDescription>
              {t('admin.thisActionWillPermanentlyDelete')}
            </DangerZoneDescription>
            <DeleteButton onClick={() => setShowDeleteModal(true)}>
              <Trash2 size={20} />
              {t('admin.deleteAllDataStartFresh')}
            </DeleteButton>
          </DangerZone>
          </>
        )}

        {activeTab === 'groups' && (
          <OrdersContainer>
            <OrdersHeader>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  {t('admin.correctionGroupMap')}
                </h3>
                <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
                  {t('admin.useThisSheetToUnderstand')}
                </p>
              </div>
            </OrdersHeader>
            <div style={{ padding: '1rem' }}>
              <FullCorrectionGroupSheet />
            </div>
          </OrdersContainer>
        )}

        {activeTab === 'recommendations' && (
          <OrdersContainer>
            {/* Three collapsible sections; visual order via `order` → matrix, alternatives, catalog. */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
            <CollapsibleCard
              order={0}
              isOpen={openRecSections.matrix}
              onToggle={() => toggleRecSection('matrix')}
              title={t('admin.lensRecommendationMatrix')}
              icon={<Table size={18} />}
              iconBg="#eef2ff" iconColor="#4338ca"
              chips={[{ label: `${matrixRows.length} row${matrixRows.length === 1 ? '' : 's'}`, primary: true }, { label: `${ageBrackets.length} bracket${ageBrackets.length === 1 ? '' : 's'}` }]}
              info="One unified table across all ages (no Simple Foyer / Progressifs split). Each cell is an index + lens from the catalog; price & delivery come from the catalog. For add ≥ 2.00 the salesperson view auto-promotes to the next cyl tier."
              actions={(
                <button
                  onClick={openCreateBracket}
                  style={{
                    padding: '0.5rem 1rem', background: '#0ea5e9', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    fontWeight: 500, fontSize: '0.875rem',
                  }}
                >
                  <Plus size={18} /> {t('admin.addAgeBracket')}
                </button>
              )}
            >

            {/* Age brackets summary chips */}
            <div style={{
              border: '1px solid #e2e8f0', borderRadius: '8px',
              padding: '0.75rem 1rem', marginBottom: '1rem', background: '#f8fafc',
            }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' }}>
                {t('admin.ageBrackets')}{ageBrackets.length})
              </div>
              {ageBrackets.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                  {t('admin.noAgeBracketsYetClick')} <strong>{t('admin.addAgeBracket')}</strong> {t('admin.toDefineYourColumnsE')} <em>{t('admin.x08Ans')}</em>, <em>{t('admin.x912Ans')}</em>{t('admin.etc')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {ageBrackets.map(b => (
                    <div key={b._id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.25rem 0.5rem 0.25rem 0.75rem',
                      background: 'white', border: '1px solid #cbd5e1', borderRadius: '9999px',
                      fontSize: '0.8rem', color: '#0f172a',
                    }}>
                      <span style={{ fontWeight: 500 }}>{b.label}</span>
                      <span style={{ color: '#64748b', fontSize: '0.7rem' }}>({b.minAge}–{b.maxAge})</span>
                      <button
                        onClick={() => openEditBracket(b)}
                        title={t('admin.editBracket')}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0, display: 'flex' }}
                      >
                        <Edit size={12} />
                      </button>
                      <button
                        onClick={() => deleteBracket(b)}
                        title={t('admin.deleteBracket')}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0, display: 'flex' }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {matrixLoading ? (
              // Skeleton matrix — same column shape as the real grid so the
              // page doesn't reflow when data arrives.
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                  fontSize: '0.85rem', minWidth: 720,
                }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      <th style={{ padding: '0.6rem', textAlign: 'left', borderBottom: '2px solid #cbd5e1' }}>{t('admin.groupS')}</th>
                      <th style={{ padding: '0.6rem', textAlign: 'center', borderBottom: '2px solid #cbd5e1' }}>{t('admin.sign')}</th>
                      {(ageBrackets.length ? ageBrackets : Array.from({ length: 5 })).map((b, i) => (
                        <th key={b?._id || i} style={{
                          padding: '0.6rem', textAlign: 'center', borderBottom: '2px solid #cbd5e1',
                          color: '#0f172a', whiteSpace: 'nowrap',
                        }}>
                          {b?.label ? b.label : <Skeleton w="50px" h="14px" />}
                        </th>
                      ))}
                      <th style={{ padding: '0.6rem', textAlign: 'right', borderBottom: '2px solid #cbd5e1' }}>{t('admin.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, ri) => (
                      <tr key={`mk-${ri}`} style={{ background: ri % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: '0.6rem', borderBottom: '1px solid #e2e8f0' }}>
                          <Skeleton w="120px" h="14px" />
                        </td>
                        <td style={{ padding: '0.6rem', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
                          <Skeleton w="20px" h="14px" />
                        </td>
                        {Array.from({ length: Math.max(5, ageBrackets.length || 5) }).map((__, ci) => (
                          <td key={ci} style={{ padding: '0.4rem', borderBottom: '1px solid #e2e8f0' }}>
                            <Skeleton w="100%" h="22px" />
                          </td>
                        ))}
                        <td style={{ padding: '0.6rem', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>
                          <Skeleton w="40px" h="14px" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto', border: '1px solid #94a3b8', borderRadius: 2, background: 'white', marginBottom: '1.25rem', width: '100%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: Math.max(980, 210 + ageBrackets.length * 150), fontSize: '0.78rem', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '0.35rem 0.45rem', border: '1px solid #94a3b8', background: '#ffffff', width: 170, textAlign: 'left', position: 'sticky', left: 0, zIndex: 4 }}>{t('admin.age2')}</th>
                        <th style={{ padding: '0.35rem', border: '1px solid #94a3b8', background: '#ffffff', width: 42, position: 'sticky', left: 170, zIndex: 4 }}></th>
                        {ageBrackets.map(bracket => (
                          <th key={bracket._id} style={{ padding: '0.35rem 0.45rem', border: '1px solid #94a3b8', background: '#ffffff', minWidth: 140, fontWeight: 800 }}>
                            {bracket.label}
                          </th>
                        ))}
                        {ageBrackets.length === 0 && (
                          <th style={{ padding: '0.35rem 0.45rem', border: '1px solid #94a3b8', background: '#ffffff', minWidth: 220, fontWeight: 800 }}>
                            {t('admin.lensNamesAddAgeBracket')}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {RECOMMENDATION_GROUP_ROWS.map(rowGroup => (
                        RECOMMENDATION_SHEET_SIGNS.map((sign, signIndex) => {
                            return (
                              <tr key={`${rowGroup.label}-${sign.id}`}>
                                {signIndex === 0 && (
                                  <td rowSpan={2} style={{
                                    padding: '0.42rem 0.55rem',
                                    border: '1px solid #94a3b8',
                                    background: '#ffffff',
                                    fontWeight: 800,
                                    textAlign: 'center',
                                    color: '#0f172a',
                                    lineHeight: 1.25,
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 3
                                  }}>
                                    {rowGroup.label}
                                  </td>
                                )}
                                <td style={{
                                  padding: '0.42rem',
                                  border: '1px solid #94a3b8',
                                  textAlign: 'center',
                                  fontWeight: 800,
                                  color: '#0f172a',
                                  background: '#ffffff',
                                  position: 'sticky',
                                  left: 170,
                                  zIndex: 3
                                }}>
                                  {sign.label}
                                </td>
                                {ageBrackets.length === 0 ? (
                                  <td style={{ padding: '0.42rem 0.55rem', border: '1px solid #94a3b8', color: '#94a3b8', background: '#ffffff' }}>
                                    {t('admin.addAgeBracketsToEdit')}
                                  </td>
                                ) : ageBrackets.map(bracket => {
                                  const lenses = getSpreadsheetCellLenses(rowGroup.groups, sign.id, bracket._id);
                                  return (
                                    <td key={bracket._id} style={{ padding: '0.3rem', border: '1px solid #94a3b8', background: '#ffffff', verticalAlign: 'top' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                        {lenses.map(name => {
                                          const cat = findCatalog(name);
                                          const idx = cat?.indexValue || '';
                                          const reals = cat ? catalogRealNames(cat) : [];
                                          const realDisplay = reals.join(' / ');
                                          // Commercial name leads; fall back to real names / raw stored value.
                                          const primary = cat?.commercialName || realDisplay || name;
                                          const fit = groupFits(cat, rowGroup.groups);
                                          return (
                                            <div key={name} style={{
                                              background: '#ffffff', border: '1px solid #e2e8f0',
                                              borderRadius: 6, padding: '0.25rem 0.4rem', lineHeight: 1.3,
                                            }}>
                                              {/* Primary row: index pill · commercial name · group-fit · remove */}
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                                {idx ? <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.62rem', fontWeight: 700, color: '#3730a3', background: '#eef2ff', borderRadius: 4, padding: '0 0.25rem', flexShrink: 0 }}>{idx}</span> : null}
                                                <span title={primary} style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primary}</span>
                                                {fit === true ? (
                                                  <Check size={12} style={{ color: '#059669', flexShrink: 0 }} />
                                                ) : fit === false ? (
                                                  <span title={`Lens group: ${cat?.group || '—'} — may not fit this row`} style={{ display: 'flex', flexShrink: 0 }}><AlertTriangle size={12} style={{ color: '#d97706' }} /></span>
                                                ) : null}
                                                <button
                                                  onClick={() => removeLensFromCell(rowGroup.groups, sign.id, bracket._id, name)}
                                                  title={t('admin.removeLens')}
                                                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 0, display: 'flex', flexShrink: 0 }}
                                                >
                                                  <X size={11} />
                                                </button>
                                              </div>
                                              {/* Secondary: supplier/real names, muted, wraps */}
                                              {realDisplay ? (
                                                <div title={realDisplay} style={{ fontSize: '0.66rem', color: '#94a3b8', whiteSpace: 'normal', wordBreak: 'break-word', marginTop: 1 }}>{realDisplay}</div>
                                              ) : null}
                                            </div>
                                          );
                                        })}
                                        {(() => {
                                          const cellKey = `${rowGroup.groups.join(',')}|${sign.id}|${bracket._id}`;
                                          const active = cellPicker.key === cellKey;
                                          const openPicker = (el) => setCellPicker({ key: cellKey, query: '', rect: el ? el.getBoundingClientRect() : null, index: 0 });
                                          if (!active) {
                                            return lenses.length ? (
                                              <button onClick={(e) => openPicker(e.currentTarget)} style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', color: '#6366f1', cursor: 'pointer', fontSize: '0.68rem', fontWeight: 600, padding: '0.1rem 0.15rem', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                                <Plus size={11} /> {t('admin.addLens')}
                                              </button>
                                            ) : (
                                              <button onClick={(e) => openPicker(e.currentTarget)} title={t('admin.addALens')} style={{ width: '100%', border: '1px dashed #e2e8f0', background: 'transparent', color: '#cbd5e1', cursor: 'pointer', borderRadius: 6, padding: '0.3rem', display: 'flex', justifyContent: 'center' }}>
                                                <Plus size={14} />
                                              </button>
                                            );
                                          }
                                          const query = active ? cellPicker.query : '';
                                          const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
                                          const matches = catalogOptions.filter(c => {
                                            if (!tokens.length) return true;
                                            const hay = `${c.indexValue || ''} ${c.commercialName || ''} ${c.name || ''} ${catalogRealNames(c).join(' ')}`.toLowerCase();
                                            return tokens.every(t => hay.includes(t));
                                          }).slice(0, 25);
                                          const rect = cellPicker.rect;
                                          const ddWidth = 300;
                                          const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
                                          const ddLeft = rect ? Math.max(8, Math.min(rect.left, viewportW - ddWidth - 8)) : 8;
                                          const ddTop = rect ? rect.bottom + 2 : 0;
                                          const activeIdx = active ? Math.min(cellPicker.index ?? 0, Math.max(0, matches.length - 1)) : 0;
                                          // Add the picked lens and close the list (so it's clear the pick landed).
                                          const pickMatch = (c) => {
                                            if (!c) return;
                                            addLensToCell(rowGroup.groups, sign.id, bracket._id, c.id || c._id || c.name);
                                            setCellPicker({ key: null, query: '', rect: null, index: 0 });
                                          };
                                          return (
                                            <div>
                                              <input
                                                autoFocus
                                                type="text"
                                                value={query}
                                                placeholder={t('admin.indexName2')}
                                                title={t('admin.typeAnIndexOrProduct')}
                                                onFocus={(event) => setCellPicker({ key: cellKey, query, rect: event.currentTarget.getBoundingClientRect(), index: 0 })}
                                                onChange={(event) => setCellPicker({ key: cellKey, query: event.target.value, rect: event.currentTarget.getBoundingClientRect(), index: 0 })}
                                                onKeyDown={(event) => {
                                                  if (event.key === 'ArrowDown') {
                                                    event.preventDefault();
                                                    setCellPicker(p => ({ ...p, key: cellKey, index: Math.min((p.index ?? 0) + 1, matches.length - 1) }));
                                                  } else if (event.key === 'ArrowUp') {
                                                    event.preventDefault();
                                                    setCellPicker(p => ({ ...p, key: cellKey, index: Math.max((p.index ?? 0) - 1, 0) }));
                                                  } else if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    pickMatch(matches[activeIdx]);
                                                  } else if (event.key === 'Escape') {
                                                    event.preventDefault();
                                                    setCellPicker({ key: null, query: '', rect: null, index: 0 });
                                                    event.currentTarget.blur();
                                                  }
                                                }}
                                                onBlur={() => setTimeout(() => setCellPicker(prev => (prev.key === cellKey ? { key: null, query: '', rect: null, index: 0 } : prev)), 150)}
                                                style={{
                                                  width: '100%', border: '1px dashed #cbd5e1', borderRadius: 6,
                                                  padding: '0.25rem 0.4rem', fontSize: '0.72rem', color: '#0f172a',
                                                  background: '#f8fafc', outline: 'none', boxSizing: 'border-box',
                                                }}
                                              />
                                              {active && (
                                                <div style={{
                                                  position: 'fixed', top: ddTop, left: ddLeft, width: ddWidth,
                                                  border: '1px solid #cbd5e1', borderRadius: 8,
                                                  background: 'white', maxHeight: 300, overflowY: 'auto',
                                                  boxShadow: '0 10px 28px rgba(15,23,42,0.22)', zIndex: 9999,
                                                }}>
                                                  {matches.length ? matches.map((c, i) => {
                                                    const reals = catalogRealNames(c).join(' / ');
                                                    const highlighted = i === activeIdx;
                                                    return (
                                                    <div
                                                      key={c.id || c.name}
                                                      ref={el => { if (highlighted && el) el.scrollIntoView({ block: 'nearest' }); }}
                                                      onMouseDown={(event) => {
                                                        event.preventDefault();
                                                        pickMatch(c);
                                                      }}
                                                      onMouseEnter={() => setCellPicker(p => (p.key === cellKey ? { ...p, index: i } : p))}
                                                      style={{
                                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                        padding: '0.45rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        background: highlighted ? '#e0e7ff' : 'white',
                                                      }}
                                                    >
                                                      <span style={{ fontWeight: 800, color: '#7c3aed', flexShrink: 0, minWidth: 34 }}>{c.indexValue || '—'}</span>
                                                      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                                                        {/* real names (red) then commercial (green) — wrap so full names show */}
                                                        <div title={reals} style={{ fontWeight: 700, color: '#dc2626', whiteSpace: 'normal', wordBreak: 'break-word' }}>{reals}</div>
                                                        {c.commercialName ? <div title={c.commercialName} style={{ fontWeight: 600, color: '#059669', fontSize: '0.72rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>{c.commercialName}</div> : null}
                                                      </div>
                                                      {/* Group — so identical-looking lenses (different price per group) aren't confused */}
                                                      {c.group ? <span style={{ fontSize: '0.66rem', fontWeight: 800, color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 4, padding: '0.05rem 0.35rem', flexShrink: 0, alignSelf: 'center' }}>{c.group}</span> : null}
                                                    </div>
                                                    );
                                                  }) : (
                                                    <div style={{ padding: '0.5rem 0.6rem', fontSize: '0.78rem', color: '#94a3b8' }}>
                                                      {t('admin.noCatalogMatchAddIt')}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            </CollapsibleCard>

            {/* ─────────────────────────────────────────────────────────────
                Lens Catalog — single source of truth for price + delivery.
                Each unique lens name referenced in the matrix above appears
                here exactly ONCE, so pricing is set once and reflected in
                every matrix cell that uses that lens.
            ───────────────────────────────────────────────────────────── */}
            <CollapsibleCard
              order={2}
              isOpen={openRecSections.catalog}
              onToggle={() => toggleRecSection('catalog')}
              title={t('admin.lensCatalogPricing')}
              icon={<Tag size={16} />}
              iconBg="#fef3c7" iconColor="#92400e"
              chips={[{ label: `${lensCatalog.length} lens${lensCatalog.length === 1 ? '' : 'es'}` }]}
              info="Set the price & delivery time per lens here — the matrix uses these values automatically."
            >

              {/* Add new lens row. Nine fixed columns overflowed the panel on
                  anything under a very wide screen, pushing the "+ Add lens"
                  button off-screen — the form looked read-only because the one
                  control that submits it was unreachable. Auto-fit wraps the
                  fields instead so every input and the button stay visible. */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '0.5rem', marginBottom: '1rem', alignItems: 'center',
                padding: '0.75rem', background: '#f8fafc', border: '1px dashed #cbd5e1',
                borderRadius: '8px',
              }}>
                <input
                  type="text"
                  placeholder={t('admin.groupe')}
                  value={newCatalogLens.group}
                  onChange={(e) => setNewCatalogLens(prev => ({ ...prev, group: e.target.value }))}
                  style={{ padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                />
                <input
                  type="text"
                  placeholder={t('admin.indice')}
                  value={newCatalogLens.indexValue}
                  onChange={(e) => setNewCatalogLens(prev => ({ ...prev, indexValue: e.target.value }))}
                  style={{ padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                />
                <input
                  type="text"
                  placeholder={t('admin.nomCommercial')}
                  value={newCatalogLens.commercialName}
                  onChange={(e) => setNewCatalogLens(prev => ({ ...prev, commercialName: e.target.value }))}
                  style={{
                    padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1',
                    borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600,
                  }}
                />
                <input
                  type="text"
                  placeholder={t('admin.nomREl')}
                  value={newCatalogLens.name}
                  onChange={(e) => setNewCatalogLens(prev => ({ ...prev, name: e.target.value }))}
                  style={{
                    padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1',
                    borderRadius: '6px', fontSize: '0.85rem',
                  }}
                />
                <input
                  type="text"
                  placeholder={t('admin.fournisseur')}
                  value={newCatalogLens.supplier}
                  onChange={(e) => setNewCatalogLens(prev => ({ ...prev, supplier: e.target.value }))}
                  style={{
                    padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1',
                    borderRadius: '6px', fontSize: '0.85rem', color: '#64748b',
                  }}
                />
                <input
                  type="text"
                  placeholder={t('admin.dispo')}
                  value={newCatalogLens.availability}
                  onChange={(e) => setNewCatalogLens(prev => ({ ...prev, availability: e.target.value }))}
                  style={{ padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                />
                <input
                  type="number"
                  placeholder={t('admin.prix')}
                  value={newCatalogLens.price}
                  onChange={(e) => setNewCatalogLens(prev => ({ ...prev, price: e.target.value }))}
                  style={{
                    padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1',
                    borderRadius: '6px', fontSize: '0.85rem',
                  }}
                />
                <input
                  type="number"
                  placeholder={t('admin.n')}
                  value={newCatalogLens.number}
                  onChange={(e) => setNewCatalogLens(prev => ({ ...prev, number: e.target.value }))}
                  style={{
                    padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1',
                    borderRadius: '6px', fontSize: '0.85rem',
                  }}
                />
                <button
                  onClick={addCatalogEntry}
                  title={t('admin.addThisLensToThe')}
                  style={{
                    padding: '0.5rem 0.9rem', background: '#059669', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap',
                    // Always ends the form on its own full-width line, so it
                    // can't be mistaken for another input as the row wraps.
                    gridColumn: '1 / -1', justifySelf: 'start', minWidth: '140px',
                  }}
                >
                  {t('admin.addLens2')}
                </button>
              </div>

              {/* Search / dispo filter toolbar */}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                  <Search size={15} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input
                    type="text"
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    placeholder={t('admin.searchCommercialRealNameGroup')}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.4rem 0.6rem 0.4rem 30px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.85rem', outline: 'none' }}
                  />
                </div>
                {[
                  { id: 'all', label: 'All' },
                  { id: 'int', label: 'Stock Int' },
                  { id: 'ext', label: 'Stock Ext' },
                  { id: 'rx', label: 'Rx' },
                ].map(opt => {
                  const on = catalogDispo === opt.id;
                  return (
                    <button key={opt.id} onClick={() => setCatalogDispo(opt.id)} style={{
                      fontSize: '0.78rem', padding: '0.35rem 0.75rem', borderRadius: 8, cursor: 'pointer',
                      border: on ? '1px solid #6366f1' : '1px solid #e2e8f0',
                      background: on ? '#6366f1' : 'white', color: on ? 'white' : '#475569', fontWeight: on ? 600 : 500,
                    }}>{opt.label}</button>
                  );
                })}
              </div>

              {lensCatalogLoading ? (
                <div style={{ padding: '1rem', color: '#64748b', textAlign: 'center' }}>
                  {t('admin.loadingCatalog')}
                </div>
              ) : lensCatalog.length === 0 ? (
                <div style={{ padding: '1.5rem', color: '#94a3b8', textAlign: 'center', fontStyle: 'italic' }}>
                  {t('admin.noLensesInTheCatalog')}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <datalist id="catalog-group-options">
                    <option value="Ts G" />
                    {['G0', 'G2', 'G4'].flatMap(g => ['2', '4', '6', '8', '10', '12'].map(s => `${g}/${s}`)).map(gp => <option key={gp} value={gp} />)}
                  </datalist>
                  <table style={{
                    width: '100%', borderCollapse: 'separate', borderSpacing: 0,
                    fontSize: '0.85rem',
                  }}>
                    <thead>
                      <tr>
                        <th colSpan={8} style={{ padding: '0.35rem', textAlign: 'center', border: '1px solid #111827', background: '#ffffff', fontWeight: 800 }}>
                          {t('admin.prixExistantSystMe')}
                        </th>
                      </tr>
                      <tr style={{ background: '#f8fafc' }}>
                        {[
                          { label: 'Groupe' },
                          { label: 'Indice', sort: 'index' },
                          { label: 'Nom commercial', sort: 'commercial' },
                          { label: 'Nom réel & Fournisseur' },
                          { label: 'Dispo' },
                          { label: 'PRIX', sort: 'price', right: true },
                          { label: 'N°', sort: 'number', right: true },
                          { label: '' },
                        ].map((col, idx) => (
                          <th key={col.label || idx}
                            onClick={col.sort ? () => sortCatalogBy(col.sort) : undefined}
                            title={col.sort ? 'Sort' : undefined}
                            style={{
                              padding: '0.35rem 0.45rem',
                              textAlign: col.right ? 'right' : 'left',
                              border: '1px solid #111827',
                              width: idx === 0 ? 170 : idx === 1 ? 60 : idx === 3 ? 240 : idx === 4 ? 84 : idx === 5 ? 90 : idx === 6 ? 50 : idx === 7 ? 50 : undefined,
                              cursor: col.sort ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap',
                            }}>
                            {col.label}{col.sort && catalogSort.col === col.sort ? (catalogSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {catalogView.rows
                        .map((item, idx) => {
                        const rowBg = String(item.availability || '').toLowerCase().includes('rx')
                          ? '#eaf2df'
                          : String(item.availability || '').toLowerCase().includes('int')
                            ? '#fde9d9'
                            : (idx % 2 === 0 ? 'white' : '#fafafa');
                        const priceMissing = item.price === null || item.price === undefined;
                        return (
                          <tr key={item._id} style={{ background: rowBg }}>
                            <td style={{ padding: '0.25rem', border: '1px solid #111827', verticalAlign: 'top' }}>
                              {(() => {
                                // A catalog entry can cover several correction groups
                                // (e.g. "G0/2&G0/4&G2/2&G2/4"). Edit them as add/remove chips.
                                const groups = String(item.group || '').split('&').map(g => g.trim()).filter(Boolean);
                                const commit = (next) => persistCatalogEntry(item._id, { group: next.join('&') });
                                const addGroup = (val) => {
                                  const g = String(val || '').trim();
                                  if (!g || groups.some(x => x.toLowerCase() === g.toLowerCase())) return;
                                  const next = [...groups, g];
                                  setCatalogLocal(item._id, { group: next.join('&') });
                                  commit(next);
                                };
                                const removeGroup = (i) => {
                                  const next = groups.filter((_, idx) => idx !== i);
                                  setCatalogLocal(item._id, { group: next.join('&') });
                                  commit(next);
                                };
                                return (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem', alignItems: 'center' }}>
                                    {groups.map((g, i) => (
                                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.15rem', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 4, padding: '0.05rem 0.3rem', fontSize: '0.72rem', fontWeight: 700, color: '#0369a1' }}>
                                        {g}
                                        <button type="button" title={t('admin.removeGroup')} onClick={() => removeGroup(i)} style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1, padding: 0 }}>×</button>
                                      </span>
                                    ))}
                                    <input
                                      type="text"
                                      list="catalog-group-options"
                                      placeholder={t('admin.group')}
                                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGroup(e.currentTarget.value); e.currentTarget.value = ''; } }}
                                      onBlur={(e) => { if (e.currentTarget.value.trim()) { addGroup(e.currentTarget.value); e.currentTarget.value = ''; } }}
                                      style={{ width: 56, padding: '0.15rem 0.25rem', border: '1px dashed #cbd5e1', borderRadius: 4, fontSize: '0.7rem', outline: 'none', background: '#f8fafc' }}
                                    />
                                  </div>
                                );
                              })()}
                            </td>
                            <td style={{ padding: 0, border: '1px solid #111827' }}>
                              <input
                                type="text"
                                value={item.indexValue || ''}
                                onChange={(e) => setCatalogLocal(item._id, { indexValue: e.target.value })}
                                onBlur={(e) => persistCatalogEntry(item._id, { indexValue: e.target.value.trim() })}
                                style={{ width: '100%', padding: '0.32rem', border: 'none', background: 'transparent', outline: 'none' }}
                              />
                            </td>
                            <td style={{ padding: 0, border: '1px solid #111827', fontWeight: 600, color: '#0f172a' }}>
                              <input
                                type="text"
                                value={item.commercialName || ''}
                                placeholder={t('admin.nomCommercial')}
                                onChange={(e) => setCatalogLocal(item._id, { commercialName: e.target.value })}
                                onBlur={(e) => {
                                  const next = e.target.value.trim();
                                  if (next !== (item.commercialName || '')) {
                                    persistCatalogEntry(item._id, { commercialName: next });
                                  }
                                }}
                                style={{
                                  width: '100%', padding: '0.35rem 0.5rem',
                                  border: '1px solid transparent', borderRadius: '4px',
                                  background: 'transparent', fontSize: '0.85rem', color: '#0f172a',
                                  fontWeight: 600, outline: 'none',
                                }}
                                onFocus={(e) => {
                                  e.currentTarget.style.background = '#eff6ff';
                                  e.currentTarget.style.borderColor = '#60a5fa';
                                }}
                              />
                            </td>
                            <td style={{ padding: '0.25rem', border: '1px solid #111827', verticalAlign: 'top' }}>
                              {(() => {
                                const list = Array.isArray(item.suppliers) ? item.suppliers : [];
                                const commit = (next) => persistCatalogEntry(item._id, {
                                  suppliers: next.map((s) => ({ realName: (s.realName || '').trim(), supplier: (s.supplier || '').trim() })),
                                });
                                return (
                                  <>
                                    {list.length === 0 && (
                                      <div style={{ fontSize: '0.7rem', fontStyle: 'italic', color: '#94a3b8', marginBottom: '0.2rem' }}>
                                        {t('admin.notAvailable')}
                                      </div>
                                    )}
                                    {list.map((entry, idx) => (
                                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.2rem' }}>
                                        <input
                                          type="text"
                                          value={entry.realName || ''}
                                          placeholder={t('admin.nomREl')}
                                          onChange={(e) => setCatalogLocal(item._id, { suppliers: list.map((s, i) => (i === idx ? { ...s, realName: e.target.value } : s)) })}
                                          onBlur={() => commit(list)}
                                          style={{ flex: '1 1 56%', minWidth: 0, padding: '0.2rem 0.3rem', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '0.74rem', color: '#0f172a', fontWeight: 500, outline: 'none' }}
                                        />
                                        <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>·</span>
                                        <input
                                          type="text"
                                          value={entry.supplier || ''}
                                          placeholder={t('admin.fournisseur')}
                                          onChange={(e) => setCatalogLocal(item._id, { suppliers: list.map((s, i) => (i === idx ? { ...s, supplier: e.target.value } : s)) })}
                                          onBlur={() => commit(list)}
                                          style={{ flex: '1 1 44%', minWidth: 0, padding: '0.2rem 0.3rem', border: '1px solid #e2e8f0', borderRadius: '3px', fontSize: '0.7rem', color: '#64748b', outline: 'none' }}
                                        />
                                        <button
                                          type="button"
                                          title={t('admin.deleteThisSupplier')}
                                          onClick={() => { const next = list.filter((_, i) => i !== idx); setCatalogLocal(item._id, { suppliers: next }); commit(next); }}
                                          style={{ flex: '0 0 auto', border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0 0.15rem' }}
                                        >×</button>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      onClick={() => { const next = [...list, { realName: '', supplier: '' }]; setCatalogLocal(item._id, { suppliers: next }); }}
                                      style={{ marginTop: '0.1rem', border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#059669', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600, borderRadius: '4px', padding: '0.15rem 0.5rem' }}
                                    >{t('admin.addSupplier')}</button>
                                  </>
                                );
                              })()}
                            </td>
                            <td style={{ padding: 0, border: '1px solid #111827' }}>
                              <input
                                type="text"
                                value={item.availability || ''}
                                onChange={(e) => setCatalogLocal(item._id, { availability: e.target.value })}
                                onBlur={(e) => persistCatalogEntry(item._id, { availability: e.target.value.trim() })}
                                style={{
                                  width: '100%', padding: '0.32rem', border: 'none', background: 'transparent',
                                  outline: 'none', color: String(item.availability || '').toLowerCase().includes('int') ? '#dc2626' : '#0f172a',
                                  fontWeight: String(item.availability || '').toLowerCase().includes('int') ? 700 : 500,
                                }}
                              />
                            </td>
                            <td style={{ padding: 0, border: '1px solid #111827' }}>
                              <input
                                type="number"
                                value={item.price ?? ''}
                                placeholder={t('admin.toBeSet')}
                                onChange={(e) => setCatalogLocal(item._id, { price: e.target.value === '' ? null : Number(e.target.value) })}
                                onBlur={(e) => {
                                  const v = e.target.value === '' ? null : Number(e.target.value);
                                  persistCatalogEntry(item._id, { price: v });
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                style={{
                                  width: '100%', padding: '0.32rem',
                                  border: 'none',
                                  background: priceMissing ? '#fff7ed' : 'transparent',
                                  fontSize: '0.85rem', color: '#0f172a', outline: 'none', textAlign: 'right',
                                }}
                                onFocus={(e) => {
                                  e.currentTarget.style.background = '#fffbeb';
                                  e.currentTarget.style.borderColor = '#fbbf24';
                                }}
                              />
                            </td>
                            <td style={{ padding: 0, border: '1px solid #111827' }}>
                              <input
                                type="number"
                                value={item.number ?? ''}
                                onChange={(e) => setCatalogLocal(item._id, { number: e.target.value === '' ? null : Number(e.target.value) })}
                                onBlur={(e) => persistCatalogEntry(item._id, { number: e.target.value === '' ? null : Number(e.target.value) })}
                                style={{ width: '100%', padding: '0.32rem', border: 'none', background: 'transparent', outline: 'none', textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '0.25rem', border: '1px solid #111827', textAlign: 'right' }}>
                              <button
                                onClick={() => deleteCatalogEntry(item)}
                                title={t('admin.removeFromCatalog')}
                                style={{
                                  padding: '0.3rem 0.55rem', background: '#fee2e2', color: '#991b1b',
                                  border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer',
                                  fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                }}
                              >
                                <Trash size={12} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.6rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                    <span>
                      {catalogView.filtered
                        ? `${catalogView.total} match${catalogView.total === 1 ? '' : 'es'} of ${lensCatalog.length}`
                        : `${lensCatalog.length} lens${lensCatalog.length === 1 ? '' : 'es'}`}
                    </span>
                  </div>
                </div>
              )}
            </CollapsibleCard>

            {/* ─────────────────────────────────────────────────────────────
                Lens Alternatives — drives the "Other" card on the
                prescription form. One row per (group × sphere sign), each
                row holds a flat list of alternative lens names. No age.
            ───────────────────────────────────────────────────────────── */}
            <CollapsibleCard
              order={1}
              isOpen={openRecSections.alternatives}
              onToggle={() => toggleRecSection('alternatives')}
              title={t('admin.lensAlternatives')}
              icon={<ArrowUpDown size={16} />}
              iconBg="#dcfce7" iconColor="#166534"
              chips={[{ label: `${lensAlternativesRows.length} row${lensAlternativesRows.length === 1 ? '' : 's'}` }]}
              info="Up (premium) and Down (cheaper) lenses shown when the salesperson clicks Other. Pricing & delivery come from the catalog. One row per group and sign; the age column decides the lens type, since under 43 is always single vision and 43+ is progressive. Both types are still stored separately — a client over 43 buying single-vision glasses keeps getting single-vision alternatives; that list is simply edited under the '< 43' column rather than having a column of its own. Cells marked 'all ages' inherit one shared list; editing one gives that age range its own list and leaves the others alone."
              actions={(
                <button
                  onClick={openCreateAltRow}
                  style={{
                    padding: '0.5rem 1rem', background: '#6366f1', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    fontWeight: 500, fontSize: '0.875rem',
                  }}
                >
                  <Plus size={18} /> {t('admin.addAlternativesRow')}
                </button>
              )}
            >

              {lensAlternativesLoading ? (
                <LoadingState>
                  <SpinningIcon size={20} />
                  {t('admin.loadingAlternatives')}
                </LoadingState>
              ) : (() => {
                // One sheet, no tab. Simple Foyer and Progressifs hold genuinely
                // different lenses for every group, so they stay separate data —
                // they are just a row level here rather than a mode switch.
                const visibleAltRows = lensAlternativesRows;
                if (visibleAltRows.length === 0) {
                  return (
                    <EmptyState>
                      <Filter size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                      <p>{t('admin.noAlternativesRowsYet')}</p>
                    </EmptyState>
                  );
                }

                // ── Conseil technique sheet ───────────────────────────────
                // One block per group set, two rows (Positif / Négatif), one
                // column per age zone. Each cell stacks premium options above
                // the matrix recommendation and cheaper ones below, so vertical
                // distance from the red row reads as distance in price.
                //
                // Alternatives are stored per zone. A zone that has never been
                // customised inherits the un-zoned row that covers every age —
                // shown dimmed and labelled, and forked on first edit so the
                // other three zones are left alone.
                const zones = alternativeZones;

                // Both lens types still exist as separate stored rows; they are
                // reached through the age column rather than shown as a level.
                // Only the row-level actions in the rail still name them.
                const LENS_TYPES = [
                  { id: 'single_vision', label: t('admin.simpleFoyer'), short: 'SF' },
                  { id: 'progressive',   label: t('admin.progressifs'), short: 'PG' },
                ];
                const blocks = [];
                const blockByKey = new Map();
                visibleAltRows.forEach(row => {
                  const key = (row.groupPair || []).join('|');
                  if (!blockByKey.has(key)) {
                    const created = {
                      key,
                      groups: row.groupPair || [],
                      label: displayCorrectionGroupPair(row.groupPair) || t('admin.noGroup'),
                      bucket: {},
                    };
                    blockByKey.set(key, created);
                    blocks.push(created);
                  }
                  const block = blockByKey.get(key);
                  const sign = row.sphereSign === 'plus' ? 'plus' : row.sphereSign === 'minus' ? 'minus' : null;
                  if (!sign) return;
                  const bk = `${row.prescriptionType || 'single_vision'}|${sign}`;
                  (block.bucket[bk] = block.bucket[bk] || []).push(row);
                });
                const tierOf = (groups = []) => {
                  const m = /\/(\d+)/.exec(groups[0] || '');
                  return m ? Number(m[1]) : 999;
                };
                blocks.sort((a, b) => tierOf(a.groups) - tierOf(b.groups) || a.label.localeCompare(b.label));

                // ── Shadowing ────────────────────────────────────────────────
                // The sales lookup matches on group *membership* and takes the
                // oldest match, so a narrow set (G0/2 & G2/2) can be completely
                // eclipsed by an older wider one (G0/2 & G2/2 & G4/2) and never
                // fire. Sorting by createdAt here, not by `order`, because that
                // is the rule the server actually applies.
                const byCreated = [...visibleAltRows].sort(
                  (a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || ''))
                );
                const winnerKeyFor = (group, sign, type) => {
                  const hit = byCreated.find(r =>
                    (r.prescriptionType || 'single_vision') === type &&
                    r.sphereSign === sign &&
                    (r.groupPair || []).includes(group)
                  );
                  return hit ? (hit.groupPair || []).join('|') : null;
                };
                // Returns the sets that eclipse this one, or [] when it is live.
                const eclipsedBy = (block) => {
                  const others = new Set();
                  let wins = 0, total = 0;
                  for (const g of block.groups) {
                    for (const type of ['single_vision', 'progressive']) {
                      for (const sign of ['plus', 'minus']) {
                        total += 1;
                        const w = winnerKeyFor(g, sign, type);
                        if (w === block.key) wins += 1;
                        else if (w) others.add(w);
                      }
                    }
                  }
                  return (total && !wins) ? [...others] : [];
                };

                // Resolve one (block, type, sign, zone) to the row that governs it.
                const cellFor = (block, type, sign, zone) => {
                  const candidates = block.bucket[`${type}|${sign}`] || [];
                  const scoped = candidates.find(r => r.zoneId === zone.id);
                  const shared = candidates.find(r => !r.zoneId);
                  const row = scoped || shared || null;
                  return {
                    groups: block.groups, sign, prescriptionType: type,
                    zoneId: zone.id, zoneLabel: zone.label,
                    row, inherited: !scoped && !!shared, missing: !row,
                  };
                };

                // A commercial name is not unique — most are shared by several
                // catalog entries across different lens groups. So two cells in
                // one zone can hold genuinely different lenses that print the
                // same text. Merging them would hide that; instead, when two
                // lines would read identically, name the group that separates
                // them. An unexpected group showing up here is worth seeing.
                // Compact age span for one lens, e.g. "0-8" or "9-42" or "55+".
                const spanOf = (ages) => {
                  const valid = (ages || []).filter(a => Number.isFinite(a.min));
                  if (!valid.length) return '';
                  const min = Math.min(...valid.map(a => a.min));
                  const maxes = valid.map(a => a.max).filter(Number.isFinite);
                  const max = maxes.length ? Math.max(...maxes) : null;
                  return (max === null || max >= 999) ? `${min}+` : `${min}-${max}`;
                };

                const recLines = (list) => {
                  const resolved = list.map(({ name, brackets, ages }) => {
                    const cat = findCatalog(name);
                    return {
                      key: name,
                      idx: cat?.indexValue || '',
                      base: cat?.commercialName || (cat ? catalogRealNames(cat).join(' / ') : name),
                      group: cat?.group || '',
                      span: spanOf(ages),
                      brackets,
                    };
                  });
                  const counts = new Map();
                  resolved.forEach(r => counts.set(r.base, (counts.get(r.base) || 0) + 1));
                  return resolved.map(r => ({
                    ...r,
                    label: (counts.get(r.base) > 1 && r.group) ? `${r.base} (${r.group})` : r.base,
                  }));
                };

                const cellBorder = '1px solid #94a3b8';
                const baseCell = { border: cellBorder, background: '#ffffff', verticalAlign: 'top' };

                const lensBox = (name, direction, cell, key) => {
                  const cat = findCatalog(name);
                  const idxValue = cat?.indexValue || '';
                  const lbl = cat?.commercialName || (cat ? catalogRealNames(cat).join(' / ') : name);
                  const tint = direction === 'up' ? '#059669' : '#b45309';
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}>
                      {idxValue ? (
                        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.6rem', fontWeight: 700, color: tint, flexShrink: 0 }}>
                          {idxValue}
                        </span>
                      ) : null}
                      <span title={cat ? lbl : t('admin.notInCatalog')} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lbl}
                      </span>
                      {!cat && <span title={t('admin.notInCatalog')} style={{ color: '#d97706', fontWeight: 700, fontSize: '0.62rem', flexShrink: 0 }}>?</span>}
                      <button
                        onClick={() => removeLensFromAltCell(cell, direction, name)}
                        title={t('admin.remove')}
                        style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0 }}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  );
                };

                const band = (direction, cell) => {
                  const list = (direction === 'up' ? cell.row?.lensNamesUp : cell.row?.lensNamesDown) || [];
                  const bg = direction === 'up' ? '#f0fdf4' : '#fffbeb';
                  return (
                    <div style={{
                      background: bg, padding: '0.25rem 0.3rem',
                      display: 'flex', flexDirection: 'column', gap: '0.15rem',
                      opacity: cell.inherited ? 0.62 : 1,
                    }}>
                      {direction === 'up' && (
                        <div style={{ fontSize: '0.56rem', fontWeight: 700, letterSpacing: '0.08em', color: '#059669', textAlign: 'center' }}>▲ UP</div>
                      )}
                      {list.map((name, i) => lensBox(name, direction, cell, `${cell.zoneId}-${direction}-${i}-${name}`))}
                      <AltLensPicker
                        catalogOptions={catalogOptions}
                        catalogRealNames={catalogRealNames}
                        onPick={(id) => addLensToAltCell(cell, direction, id)}
                      />
                      {direction === 'down' && (
                        <div style={{ fontSize: '0.56rem', fontWeight: 700, letterSpacing: '0.08em', color: '#b45309', textAlign: 'center' }}>▼ DOWN</div>
                      )}
                    </div>
                  );
                };

                return (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed',
                    fontSize: '0.76rem', minWidth: 180 * zones.length + 170,
                  }}>
                    <colgroup>
                      <col style={{ width: 170 }} />
                      {zones.map(z => <col key={`col-${z.id}`} style={{ minWidth: 180 }} />)}
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ ...baseCell, padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 800 }}>
                          {t('admin.groupS')}
                        </th>
                        {zones.map(z => (
                          <th key={z.id} style={{ ...baseCell, padding: '0.4rem 0.5rem', textAlign: 'center', fontWeight: 800 }}>
                            {z.label}
                            <div style={{ fontWeight: 500, fontSize: '0.6rem', color: '#64748b', marginTop: 1 }}>
                              {t('admin.ageOfClient')}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {blocks.map(block => {
                        const eclipsers = eclipsedBy(block);
                        const dead = eclipsers.length > 0;
                        return ['plus', 'minus'].map((sign, signIdx) => (
                          <tr key={`${block.key}-${sign}`} style={dead ? { opacity: 0.5 } : undefined}>
                            {/* Rail: group set → sign. Lens type is not a level
                                here — the age column determines it (under 43 is
                                always single vision, 43+ is progressive), so
                                showing it again would say the same thing twice.
                                Both types still exist in the data; each column
                                simply edits the one that applies to its ages. */}
                            <td style={{
                              ...baseCell, padding: '0.4rem 0.5rem', verticalAlign: 'middle',
                              borderTop: signIdx === 0 ? '2px solid #cbd5e1' : cellBorder,
                            }}>
                              {signIdx === 0 && (
                                <div style={{ fontWeight: 700, marginBottom: '0.15rem' }}>{block.label}</div>
                              )}
                              {signIdx === 0 && dead && (
                                <div
                                  title={t('admin.groupSetNeverServed', {
                                    other: eclipsers.map(k => displayCorrectionGroupPair(k.split('|'))).join(', '),
                                  })}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                    margin: '0 0 0.25rem', padding: '0.1rem 0.3rem',
                                    background: '#fee2e2', color: '#991b1b',
                                    border: '1px solid #fecaca', borderRadius: 4,
                                    fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.03em',
                                  }}
                                >
                                  <AlertTriangle size={9} />{t('admin.neverUsed')}
                                </div>
                              )}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                                <span style={{ fontStyle: 'italic', fontWeight: 600, fontSize: '0.7rem', color: sign === 'plus' ? '#059669' : '#dc2626' }}>
                                  {sign === 'plus' ? t('admin.positive') : t('admin.negative')}
                                </span>
                              </div>
                              {/* Row-level actions, one cluster per underlying
                                  lens type — a row now spans both, so they can't
                                  be a single unlabelled pair. */}
                              {LENS_TYPES.map(type => {
                                const inBucket = block.bucket[`${type.id}|${sign}`] || [];
                                const shared = inBucket.find(r => !r.zoneId);
                                const extras = inBucket.filter(r => !r.zoneId).length - 1;
                                if (!shared) return null;
                                return (
                                  <div key={type.id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem' }}>
                                    <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', minWidth: '1.6rem' }}>
                                      {type.short}
                                    </span>
                                    <button type="button" onClick={() => openEditAltRow(shared)} title={`${t('admin.editRow')} — ${type.label}`}
                                      style={{ border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', padding: 0, display: 'flex' }}>
                                      <Edit size={11} />
                                    </button>
                                    <button type="button" onClick={() => deleteAltRow(shared)} title={`${t('admin.delete')} — ${type.label}`}
                                      style={{ border: 'none', background: 'transparent', color: '#cbd5e1', cursor: 'pointer', padding: 0, display: 'flex' }}>
                                      <Trash size={11} />
                                    </button>
                                    {extras > 0 && (
                                      <span title={t('admin.duplicateAltRowsIgnored')}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: '#d97706', fontWeight: 700, fontSize: '0.64rem' }}>
                                        <AlertTriangle size={10} />+{extras}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </td>
                            {zones.map(zone => {
                              // The column's ages decide the lens type: under 43
                              // single vision, 43+ progressive. Both still live
                              // as separate rows underneath.
                              const cell = cellFor(block, zoneRegime(zone), sign, zone);
                              const rec = getRecommendedLensesForZone(block.groups, sign, zone);
                              return (
                                <td key={`${block.key}-${sign}-${zone.id}`} style={{
                                  ...baseCell, padding: 0,
                                  borderTop: signIdx === 0 ? '2px solid #cbd5e1' : cellBorder,
                                }}>
                                  <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                                    gap: '0.3rem', padding: '0.1rem 0.35rem', minHeight: '1.1rem',
                                    borderBottom: '1px solid #e2e8f0',
                                  }}>
                                    {cell.inherited ? (
                                      <span title={t('admin.inheritedFromAllAges')} style={{ fontSize: '0.56rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.03em' }}>
                                        {t('admin.allAges')}
                                      </span>
                                    ) : cell.row ? (
                                      <button
                                        type="button"
                                        onClick={() => resetAltZone(cell)}
                                        title={t('admin.resetToAllAges')}
                                        style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex' }}
                                      >
                                        <RefreshCw size={10} />
                                      </button>
                                    ) : null}
                                  </div>
                                  {band('up', cell)}
                                  <div style={{
                                    background: '#fff7f7', borderTop: '1px solid #fecaca', borderBottom: '1px solid #fecaca',
                                    padding: '0.3rem 0.35rem', textAlign: 'center',
                                    color: '#dc2626', fontWeight: 800,
                                  }}>
                                    {rec.length ? recLines(rec).map(({ key, idx, label, group, span, brackets }, _i, all) => (
                                      <div
                                        key={`${block.key}-${sign}-${zone.id}-rec-${key}`}
                                        title={[label, group && `group ${group}`, brackets.join(', ')]
                                          .filter(Boolean).join(' — ')}
                                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      >
                                        {idx ? `${idx} ` : ''}{label}
                                        {/* Only when the zone disagrees with itself: name the
                                            ages each lens covers, so a split is readable
                                            without hovering. */}
                                        {all.length > 1 && span && (
                                          <span style={{ fontWeight: 600, fontSize: '0.6rem', color: '#94a3b8', marginLeft: '0.3rem' }}>
                                            {span}
                                          </span>
                                        )}
                                      </div>
                                    )) : (
                                      <span style={{ color: '#cbd5e1', fontWeight: 500, fontStyle: 'italic', fontSize: '0.68rem' }}>
                                        {t('admin.noMatrixRecommendation')}
                                      </span>
                                    )}
                                  </div>
                                  {band('down', cell)}
                                </td>
                              );
                            })}
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </CollapsibleCard>
            </div>
          </OrdersContainer>
        )}

        {activeTab === 'other-lenses' && (
          <OrdersContainer>
            <OrdersHeader>
              <h2 style={{ margin: 0, color: '#1e293b' }}>{t('admin.otherLensProducts')}</h2>
              <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                {t('admin.createProductsThatSalespeopleCan')}
              </p>
            </OrdersHeader>

            <div style={{ padding: '1.5rem' }}>
              <button
                onClick={() => {
                  setOtherLensForm({ name: '', frameType: '', frameColor: '', frameMaterial: '', lensType: '', lensCoating: '', price: '' });
                  setShowAddOtherLensModal(true);
                }}
                style={{
                  padding: '0.625rem 1.25rem', borderRadius: '0.5rem', border: 'none',
                  background: '#059669', color: 'white', fontWeight: '600', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', marginBottom: '1.5rem'
                }}
              >
                <Plus size={16} />
                {t('admin.addProduct')}
              </button>

              {otherLensLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>{t('admin.loading2')}</div>
              ) : otherLensProducts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                  {t('admin.noProductsAddedYetClick')}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
                  {otherLensProducts.map(product => (
                    <div key={product._id} style={{
                      background: 'white', borderRadius: '0.75rem', border: '1px solid #e2e8f0',
                      padding: '1.25rem', position: 'relative'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1rem' }}>{product.name}</h4>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Edit
                            size={16}
                            style={{ cursor: 'pointer', color: '#64748b' }}
                            onClick={() => {
                              setSelectedOtherLens(product);
                              setOtherLensForm({
                                name: product.name || '',
                                frameType: product.frameType || '',
                                frameColor: product.frameColor || '',
                                frameMaterial: product.frameMaterial || '',
                                lensType: product.lensType || '',
                                lensCoating: product.lensCoating || '',
                                price: product.price || ''
                              });
                              setShowEditOtherLensModal(true);
                            }}
                          />
                          <Trash
                            size={16}
                            style={{ cursor: 'pointer', color: '#ef4444' }}
                            onClick={() => handleDeleteOtherLens(product._id)}
                          />
                        </div>
                      </div>
                      {product.price > 0 && (
                        <p style={{ margin: '0 0 0.75rem', fontWeight: '600', color: '#059669', fontSize: '1.1rem' }}>
                          {product.price.toLocaleString()} {t('admin.dzd')}
                        </p>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem', fontSize: '0.8rem', color: '#64748b' }}>
                        {product.frameType && <span>{t('admin.frame')} {product.frameType}</span>}
                        {product.frameColor && <span>{t('admin.color2')} {product.frameColor}</span>}
                        {product.frameMaterial && <span>{t('admin.material')} {product.frameMaterial}</span>}
                        {product.lensType && <span>{t('admin.lens')} {product.lensType}</span>}
                        {product.lensCoating && <span>{t('admin.coating')} {product.lensCoating}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </OrdersContainer>
        )}

        {/* Add / Edit Other Lens Product Modal */}
        {(showAddOtherLensModal || showEditOtherLensModal) && (
          <ModalOverlay onClick={() => { setShowAddOtherLensModal(false); setShowEditOtherLensModal(false); setSelectedOtherLens(null); }}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  <Glasses size={24} />
                  {showEditOtherLensModal ? t('admin.editProduct') : t('admin.addNewProduct')}
                </ModalTitle>
                <X size={24} style={{ cursor: 'pointer', color: '#991b1b' }} onClick={() => { setShowAddOtherLensModal(false); setShowEditOtherLensModal(false); setSelectedOtherLens(null); }} />
              </ModalHeader>
              <ModalBody>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', color: '#1e293b', fontSize: '0.875rem' }}>{t('admin.productName')}</label>
                    <input type="text" value={otherLensForm.name} onChange={(e) => setOtherLensForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('admin.eGPremiumProgressivePackage')} style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#475569', fontSize: '0.85rem' }}>{t('admin.frameType')}</label>
                      <input type="text" value={otherLensForm.frameType} onChange={(e) => setOtherLensForm(prev => ({ ...prev, frameType: e.target.value }))}
                        placeholder={t('admin.eGFullRim')} style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#475569', fontSize: '0.85rem' }}>{t('admin.frameColor')}</label>
                      <input type="text" value={otherLensForm.frameColor} onChange={(e) => setOtherLensForm(prev => ({ ...prev, frameColor: e.target.value }))}
                        placeholder={t('admin.eGBlack')} style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#475569', fontSize: '0.85rem' }}>{t('admin.frameMaterial')}</label>
                      <input type="text" value={otherLensForm.frameMaterial} onChange={(e) => setOtherLensForm(prev => ({ ...prev, frameMaterial: e.target.value }))}
                        placeholder={t('admin.eGTitanium')} style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#475569', fontSize: '0.85rem' }}>{t('admin.lensType')}</label>
                      <input type="text" value={otherLensForm.lensType} onChange={(e) => setOtherLensForm(prev => ({ ...prev, lensType: e.target.value }))}
                        placeholder={t('admin.eGProgressive')} style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#475569', fontSize: '0.85rem' }}>{t('admin.lensCoating')}</label>
                      <input type="text" value={otherLensForm.lensCoating} onChange={(e) => setOtherLensForm(prev => ({ ...prev, lensCoating: e.target.value }))}
                        placeholder={t('admin.eGAntiReflective')} style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#475569', fontSize: '0.85rem' }}>{t('admin.priceDzd2')}</label>
                      <input type="number" value={otherLensForm.price} onChange={(e) => setOtherLensForm(prev => ({ ...prev, price: e.target.value }))}
                        placeholder={t('admin.eG50000')} style={{ width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <button onClick={handleSaveOtherLens} disabled={!otherLensForm.name.trim()}
                    style={{
                      padding: '0.75rem', borderRadius: '0.5rem', border: 'none', fontSize: '0.9rem',
                      background: otherLensForm.name.trim() ? '#059669' : '#e2e8f0',
                      color: otherLensForm.name.trim() ? 'white' : '#94a3b8',
                      fontWeight: '600', cursor: otherLensForm.name.trim() ? 'pointer' : 'not-allowed', marginTop: '0.5rem'
                    }}
                  >
                    {showEditOtherLensModal ? t('admin.updateProduct') : t('admin.createProduct')}
                  </button>
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {activeTab === 'lens-stock' && (
          <LensStockPage />
        )}

        {activeTab === 'frame-stock' && (
          <FrameStockPage />
        )}
          </TabPanel>
        </ContentScroll>
      </MainContent>

      {selectedOrder && (
        <ModalOverlay onClick={() => setSelectedOrder(null)}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <Package size={24} />
                {t('admin.orderDetails')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#991b1b' }}
                onClick={() => setSelectedOrder(null)}
              />
            </ModalHeader>
            <ModalBody>
              {(() => {
                // Same shape detection as the order list. `missingStep` is
                // only present on items returned by /api/admin/incomplete-orders
                // (Shape A, the composite incomplete-orders response). Real
                // Order documents from /api/admin/all-orders also have
                // `prescription` attached for convenience, so we can't use
                // that to distinguish shapes.
                const isPrescriptionOnly = !!selectedOrder.isPrescriptionOnly;
                const isIncompleteStructure = !!selectedOrder.missingStep;
                const orderNumber = isPrescriptionOnly
                  ? 'No Order Created'
                  : (isIncompleteStructure
                    ? (selectedOrder.order?.orderNumber || 'No Order Created')
                    : (selectedOrder.orderNumber || 'N/A'));
                const status = isPrescriptionOnly
                  ? 'No Order Created'
                  : (isIncompleteStructure
                    ? (selectedOrder.order?.status || 'No Order Created')
                    : selectedOrder.status);
                const visitor = selectedOrder.visitor;
                const salesPerson = selectedOrder.salesPerson;
                const store = isPrescriptionOnly
                  ? null
                  : (isIncompleteStructure
                    ? (selectedOrder.order?.store || selectedOrder.store)
                    : selectedOrder.store);
                const date = isPrescriptionOnly
                  ? (selectedOrder.createdAt || selectedOrder.orderDate)
                  : (isIncompleteStructure
                    ? (selectedOrder.order?.orderDate || selectedOrder.order?.createdAt || selectedOrder.prescription?.createdAt || selectedOrder.createdAt)
                    : (selectedOrder.orderDate || selectedOrder.createdAt));
                const reason = selectedOrder.reason || '';
                const prescription = selectedOrder.prescription || (isPrescriptionOnly ? selectedOrder : null);
                const order = isPrescriptionOnly
                  ? null
                  : (isIncompleteStructure ? (selectedOrder.order || null) : selectedOrder);

                return (
                  <>
                    <DetailSection>
                      <DetailSectionTitle>{t('admin.orderInformation')}</DetailSectionTitle>
                      <DetailGrid>
                        <DetailItem>
                          <DetailLabel>{t('admin.orderNumber')}</DetailLabel>
                          <DetailValue bold>{orderNumber}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>{t('admin.status')}</DetailLabel>
                          <DetailValue>
                            <StatusBadge status={status} completed={isOrderCompleted(selectedOrder)}>
                              {getStatusLabel(status, isPrescriptionOnly)}
                            </StatusBadge>
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>{t('admin.date')}</DetailLabel>
                          <DetailValue>
                            {date
                              ? new Date(date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                              : t('admin.nA')}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>{t('admin.store')}</DetailLabel>
                          <DetailValue>{store?.name || store?.storeCode || 'N/A'}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>{t('admin.salesPerson')}</DetailLabel>
                          <DetailValue>
                            {salesPerson
                              ? `${salesPerson.firstName || ''} ${salesPerson.lastName || ''}`.trim() || 'N/A'
                              : t('admin.nA')}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>{t('admin.customer')}</DetailLabel>
                          <DetailValue>
                            {(() => {
                              const customerName = isPrescriptionOnly
                                ? (prescription?.patientName || selectedOrder.patientName || null)
                                : (prescription?.patientName || null);
                              return customerName || (visitor?.gender && visitor?.ageGroup
                                ? `${visitor.gender} - ${visitor.ageGroup}`
                                : 'N/A');
                            })()}
                          </DetailValue>
                        </DetailItem>
                      </DetailGrid>
                    </DetailSection>

                    {orderFilter === 'unsuccessful' && reason && (
                      <DetailSection>
                        <DetailSectionTitle>{t('admin.whyThisSaleStalled')}</DetailSectionTitle>
                        <DetailValue style={{
                          padding: '1rem',
                          background: '#fef3c7',
                          border: '1px solid #fbbf24',
                          borderRadius: '8px',
                          color: '#92400e'
                        }}>
                          {reason}
                        </DetailValue>
                      </DetailSection>
                    )}

                    {prescription && renderPrescriptionDetails(prescription)}

                    {order && order.items && order.items.length > 0 && (
                      <DetailSection>
                        <DetailSectionTitle>{t('admin.orderItems')}</DetailSectionTitle>
                        <div style={{ marginTop: '0.75rem' }}>
                          {order.items.map((item, index) => (
                            <div key={index} style={{
                              padding: '1rem',
                              marginBottom: '0.5rem',
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px'
                            }}>
                              <DetailValue bold style={{ marginBottom: '0.5rem' }}>
                                {item.name || `${item.type} - ${item.brand || ''} ${item.model || ''}`.trim()}
                              </DetailValue>
                              <DetailGrid>
                                {item.brand && (
                                  <DetailItem>
                                    <DetailLabel>{t('admin.brand')}</DetailLabel>
                                    <DetailValue>{item.brand}</DetailValue>
                                  </DetailItem>
                                )}
                                {item.model && (
                                  <DetailItem>
                                    <DetailLabel>{t('admin.model')}</DetailLabel>
                                    <DetailValue>{item.model}</DetailValue>
                                  </DetailItem>
                                )}
                                {item.quantity && (
                                  <DetailItem>
                                    <DetailLabel>{t('admin.quantity')}</DetailLabel>
                                    <DetailValue>{item.quantity}</DetailValue>
                                  </DetailItem>
                                )}
                                {item.unitPrice && (
                                  <DetailItem>
                                    <DetailLabel>{t('admin.unitPrice')}</DetailLabel>
                                    <DetailValue>{item.unitPrice.toLocaleString()} {t('admin.dzd')}</DetailValue>
                                  </DetailItem>
                                )}
                                {item.totalPrice && (
                                  <DetailItem>
                                    <DetailLabel>{t('admin.totalPrice')}</DetailLabel>
                                    <DetailValue bold>{item.totalPrice.toLocaleString()} {t('admin.dzd')}</DetailValue>
                                  </DetailItem>
                                )}
                              </DetailGrid>
                            </div>
                          ))}
                        </div>
                        {order.total && (
                          <div style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: '#eef2ff',
                            border: '2px solid #6366f1',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <DetailLabel style={{ fontSize: '1rem' }}>{t('admin.totalAmount2')}</DetailLabel>
                            <DetailValue bold style={{ fontSize: '1.25rem', color: '#6366f1' }}>
                              {order.total.toLocaleString()} {t('admin.dzd')}
                            </DetailValue>
                          </div>
                        )}
                      </DetailSection>
                    )}
                  </>
                );
              })()}
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setSelectedOrder(null)}>
                {t('admin.close')}
              </CancelButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Add Employee Modal */}
      {showAddEmployeeModal && (
        <ModalOverlay onClick={() => setShowAddEmployeeModal(false)}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                <UserPlus size={24} />
                {t('admin.addNewEmployee')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#1e40af' }}
                onClick={() => setShowAddEmployeeModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <AddEmployeeForm
                stores={stores}
                labs={labs}
                adminLocations={adminLocations}
                defaultRole={addEmployeeContext.defaultRole || null}
                lockRole={!!addEmployeeContext.lockRole}
                onClose={() => { setShowAddEmployeeModal(false); setAddEmployeeContext({}); }}
                onSuccess={() => {
                  setShowAddEmployeeModal(false);
                  setAddEmployeeContext({});
                  fetchEmployees();
                  fetchAdminLocations();
                  // Refresh the central store view too so newly-added inv/ops
                  // managers show up immediately under the right card.
                  fetchCentralStore();
                }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Edit Employee Modal */}
      {showEditEmployeeModal && selectedEmployee && (
        <ModalOverlay onClick={() => { setShowEditEmployeeModal(false); setSelectedEmployee(null); }}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                <Edit size={24} />
                {t('admin.editEmployee')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#1e40af' }}
                onClick={() => { setShowEditEmployeeModal(false); setSelectedEmployee(null); }}
              />
            </ModalHeader>
            <ModalBody>
              <EditEmployeeForm
                // Remount when a different employee is picked, so the form
                // state re-seeds from the new row instead of keeping the old.
                key={selectedEmployee._id || selectedEmployee.id}
                employee={selectedEmployee}
                stores={stores}
                labs={labs}
                adminLocations={adminLocations}
                onClose={() => { setShowEditEmployeeModal(false); setSelectedEmployee(null); }}
                onSuccess={() => {
                  setShowEditEmployeeModal(false);
                  setSelectedEmployee(null);
                  fetchEmployees();
                  fetchAdminLocations();
                  fetchCentralStore();
                }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Add Store Modal */}
      {showAddStoreModal && (
        <ModalOverlay onClick={() => setShowAddStoreModal(false)}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                <Store size={24} />
                {t('admin.addNewStore')}
              </ModalTitle>
              <X size={24} style={{ cursor: 'pointer', color: '#1e40af' }} onClick={() => setShowAddStoreModal(false)} />
            </ModalHeader>
            <ModalBody>
              <AddStoreForm
                onClose={() => setShowAddStoreModal(false)}
                onSuccess={() => { setShowAddStoreModal(false); fetchStores(); }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {showAddCentralStoreModal && (
        <ModalOverlay onClick={() => setShowAddCentralStoreModal(false)}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderBottom: '1px solid #fcd34d' }}>
              <ModalTitle>
                <Warehouse size={24} color="#d97706" />
                <span style={{ color: '#92400e' }}>{t('admin.addCentralStore')}</span>
              </ModalTitle>
              <X size={24} style={{ cursor: 'pointer', color: '#92400e' }} onClick={() => setShowAddCentralStoreModal(false)} />
            </ModalHeader>
            <ModalBody>
              <AddStoreForm
                isCentralMode
                onClose={() => setShowAddCentralStoreModal(false)}
                onSuccess={() => { setShowAddCentralStoreModal(false); fetchStores(); fetchCentralStore(); }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Add Lab Modal */}
      {showAddLabModal && (
        <ModalOverlay onClick={() => setShowAddLabModal(false)}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                <Building2 size={24} />
                {t('admin.addNewLab')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#1e40af' }}
                onClick={() => setShowAddLabModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <AddLabForm
                employees={employees}
                onClose={() => setShowAddLabModal(false)}
                onSuccess={() => {
                  setShowAddLabModal(false);
                  fetchLabs();
                }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Add Administration Location Modal */}
      {showAddAdminLocationModal && (
        <ModalOverlay onClick={() => setShowAddAdminLocationModal(false)}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                <MapPin size={24} />
                {t('admin.addAdministrationLocation')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#1e40af' }}
                onClick={() => setShowAddAdminLocationModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <AddAdministrationLocationForm
                employees={employees}
                onClose={() => setShowAddAdminLocationModal(false)}
                onSuccess={() => {
                  setShowAddAdminLocationModal(false);
                  fetchAdminLocations();
                }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Edit Administration Location Modal */}
      {showEditAdminLocationModal && selectedAdminLocation && (
        <ModalOverlay onClick={() => {
          setShowEditAdminLocationModal(false);
          setSelectedAdminLocation(null);
        }}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                <Edit size={24} />
                {t('admin.editAdministrationLocation')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#1e40af' }}
                onClick={() => {
                  setShowEditAdminLocationModal(false);
                  setSelectedAdminLocation(null);
                }}
              />
            </ModalHeader>
            <ModalBody>
              <AddAdministrationLocationForm
                editingLocation={selectedAdminLocation}
                employees={employees}
                onClose={() => {
                  setShowEditAdminLocationModal(false);
                  setSelectedAdminLocation(null);
                }}
                onSuccess={() => {
                  setShowEditAdminLocationModal(false);
                  setSelectedAdminLocation(null);
                  fetchAdminLocations();
                }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Edit Store Modal */}
      {showEditStoreModal && selectedStore && (
        <ModalOverlay onClick={() => {
          setShowEditStoreModal(false);
          setSelectedStore(null);
        }}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                <Edit size={24} />
                {t('admin.editStore')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#1e40af' }}
                onClick={() => {
                  setShowEditStoreModal(false);
                  setSelectedStore(null);
                }}
              />
            </ModalHeader>
            <ModalBody>
              <AddStoreForm
                editingStore={selectedStore}
                onClose={() => {
                  setShowEditStoreModal(false);
                  setSelectedStore(null);
                }}
                onSuccess={() => {
                  setShowEditStoreModal(false);
                  setSelectedStore(null);
                  fetchStores();
                }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Edit Lab Modal */}
      {showEditLabModal && selectedLab && (
        <ModalOverlay onClick={() => {
          setShowEditLabModal(false);
          setSelectedLab(null);
        }}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                <Edit size={24} />
                {t('admin.editLab')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#1e40af' }}
                onClick={() => {
                  setShowEditLabModal(false);
                  setSelectedLab(null);
                }}
              />
            </ModalHeader>
            <ModalBody>
              <AddLabForm
                editingLab={selectedLab}
                employees={employees}
                onClose={() => {
                  setShowEditLabModal(false);
                  setSelectedLab(null);
                }}
                onSuccess={() => {
                  setShowEditLabModal(false);
                  setSelectedLab(null);
                  fetchLabs();
                }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Lab Modal */}
      {showDeleteLabModal && selectedLab && (
        <ModalOverlay onClick={() => setShowDeleteLabModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={24} />
                {t('admin.deleteLab')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#991b1b' }}
                onClick={() => setShowDeleteLabModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <ModalMessage>
                {t('admin.areYouSureYouWant')} <strong>{selectedLab.name}</strong>?
              </ModalMessage>
              <WarningBox>
                <WarningText>
                  <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    {t('admin.thisActionCannotBeUndone')}
                  </span>
                </WarningText>
              </WarningBox>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowDeleteLabModal(false)}>
                {t('admin.cancel')}
              </CancelButton>
              <ConfirmDeleteButton
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`/api/labs/${selectedLab._id || selectedLab.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Lab deleted');
                    setShowDeleteLabModal(false);
                    setSelectedLab(null);
                    fetchLabs();
                  } catch (error) {
                    console.error('Error deleting lab:', error);
                    toast.error(error.response?.data?.message || 'Error deleting lab. Please try again.');
                  }
                }}
              >
                <Trash2 size={18} />
                {t('admin.deleteLab')}
              </ConfirmDeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Administration Location Modal */}
      {showDeleteAdminLocationModal && selectedAdminLocation && (
        <ModalOverlay onClick={() => setShowDeleteAdminLocationModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={24} />
                {t('admin.deleteAdministrationLocation')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#991b1b' }}
                onClick={() => setShowDeleteAdminLocationModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <ModalMessage>
                {t('admin.areYouSureYouWant')} <strong>{selectedAdminLocation.name}</strong>?
              </ModalMessage>
              <WarningBox>
                <WarningText>
                  <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    {t('admin.thisActionCannotBeUndone2')}
                  </span>
                </WarningText>
              </WarningBox>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowDeleteAdminLocationModal(false)}>
                {t('admin.cancel')}
              </CancelButton>
              <ConfirmDeleteButton
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`/api/admin-locations/${selectedAdminLocation._id || selectedAdminLocation.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Administration location deleted');
                    setShowDeleteAdminLocationModal(false);
                    setSelectedAdminLocation(null);
                    fetchAdminLocations();
                  } catch (error) {
                    console.error('Error deleting administration location:', error);
                    toast.error(error.response?.data?.message || 'Error deleting administration location. Please try again.');
                  }
                }}
              >
                <Trash2 size={18} />
                {t('admin.deleteLocation')}
              </ConfirmDeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Age Bracket Modal (create + edit) */}
      {showBracketModal && (
        <ModalOverlay onClick={() => setShowBracketModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                {bracketEditing ? <Edit size={24} /> : <Plus size={24} />}
                {bracketEditing ? t('admin.editAgeBracket') : t('admin.addAgeBracket')}
              </ModalTitle>
              <X size={24} style={{ cursor: 'pointer', color: '#1e40af' }} onClick={() => setShowBracketModal(false)} />
            </ModalHeader>
            <ModalBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                  {t('admin.label')}
                  <input
                    type="text"
                    value={bracketForm.label}
                    onChange={(e) => setBracketForm(p => ({ ...p, label: e.target.value }))}
                    placeholder={t('admin.eG08Ans')}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '0.25rem' }}
                  />
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                    {t('admin.minAge')}
                    <input
                      type="number"
                      min="0"
                      value={bracketForm.minAge}
                      onChange={(e) => setBracketForm(p => ({ ...p, minAge: e.target.value }))}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '0.25rem' }}
                    />
                  </label>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                    {t('admin.maxAge')}
                    <input
                      type="number"
                      min="0"
                      value={bracketForm.maxAge}
                      onChange={(e) => setBracketForm(p => ({ ...p, maxAge: e.target.value }))}
                      placeholder={t('admin.x999For')}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '0.25rem' }}
                    />
                  </label>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                    {t('admin.order')}
                    <input
                      type="number"
                      value={bracketForm.order}
                      onChange={(e) => setBracketForm(p => ({ ...p, order: e.target.value }))}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '0.25rem' }}
                    />
                  </label>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  {t('admin.for55AnsSetMin')}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowBracketModal(false)}>{t('admin.cancel')}</CancelButton>
              <button
                onClick={saveBracket}
                style={{
                  padding: '0.5rem 1rem', background: '#0ea5e9', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  fontWeight: 500, fontSize: '0.875rem',
                }}
              >
                {bracketEditing ? t('admin.saveChanges') : t('admin.createBracket')}
              </button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Matrix Row Modal (create + edit) */}
      {showMatrixRowModal && (
        <ModalOverlay onClick={() => setShowMatrixRowModal(false)}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                {matrixRowEditing ? <Edit size={24} /> : <Plus size={24} />}
                {matrixRowEditing ? t('admin.editRecommendationRow') : t('admin.addRecommendationRow')}
              </ModalTitle>
              <X size={24} style={{ cursor: 'pointer', color: '#1e40af' }} onClick={() => setShowMatrixRowModal(false)} />
            </ModalHeader>
            <ModalBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Sphere sign */}
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.4rem' }}>
                    {t('admin.sphereSign')}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[
                      { id: 'plus', label: '(+) Hyperopia', color: '#059669' },
                      { id: 'minus', label: '(−) Myopia', color: '#dc2626' },
                      { id: 'none', label: 'None (plano, SPH = 0)', color: '#475569' },
                    ].map(opt => {
                      const active = matrixRowForm.sphereSign === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setMatrixRowForm(p => ({ ...p, sphereSign: opt.id }))}
                          style={{
                            padding: '0.4rem 0.75rem', borderRadius: '8px',
                            border: `1px solid ${active ? opt.color : '#cbd5e1'}`,
                            background: active ? `${opt.color}1a` : 'white',
                            color: active ? opt.color : '#334155',
                            cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Group selector */}
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.4rem' }}>
                    {t('admin.correctionMapClickOneOr')}
                  </div>
                  <CorrectionGroupMap
                    sphereSign={matrixRowForm.sphereSign}
                    selectedGroups={matrixRowForm.groupPair}
                    onToggle={toggleGroupInForm}
                  />
                  {matrixRowForm.groupPair.length > 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#475569' }}>
                      {t('admin.selected')} <strong>{displayCorrectionGroupPair(matrixRowForm.groupPair)}</strong>
                    </div>
                  )}
                </div>

                {/* Order */}
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', maxWidth: 200 }}>
                  {t('admin.displayOrder2')}
                  <input
                    type="number"
                    value={matrixRowForm.order}
                    onChange={(e) => setMatrixRowForm(p => ({ ...p, order: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '0.25rem' }}
                  />
                </label>

                {/* Cells per age bracket */}
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.4rem' }}>
                    {t('admin.recommendationsPerAgeBracket')}
                  </div>
                  {ageBrackets.length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                      {t('admin.addAgeBracketsFirst')}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {ageBrackets.map(b => {
                        const cell = matrixRowForm.cells[b._id] || { lensName: '', price: '', estimatedDays: '' };
                        return (
                          <div key={b._id} style={{
                            display: 'grid',
                            gridTemplateColumns: '140px 1fr',
                            gap: '0.5rem', alignItems: 'center',
                            padding: '0.4rem 0.5rem', borderRadius: '6px', background: '#f8fafc',
                          }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#334155' }}>{b.label}</div>
                            <input
                              type="text"
                              placeholder={t('admin.lensNameEGOptimax')}
                              value={cell.lensName}
                              onChange={(e) => updateCellInForm(b._id, 'lensName', e.target.value)}
                              style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                    {t('admin.pricingDeliveryTimeAreManaged')} <strong>{t('admin.lensCatalog')}</strong> {t('admin.tableBelowTheMatrix')}
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowMatrixRowModal(false)}>{t('admin.cancel')}</CancelButton>
              <button
                onClick={saveMatrixRow}
                style={{
                  padding: '0.5rem 1rem', background: '#6366f1', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  fontWeight: 500, fontSize: '0.875rem',
                }}
              >
                {matrixRowEditing ? t('admin.saveChanges') : t('admin.createRow')}
              </button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* ─── Lens Alternatives Row Modal ─── */}
      {showAltRowModal && (
        <ModalOverlay onClick={() => setShowAltRowModal(false)}>
          <ModalContent large onClick={(e) => e.stopPropagation()}>
            <ModalHeader variant="info">
              <ModalTitle variant="info">
                {altRowEditing ? <Edit size={24} /> : <Plus size={24} />}
                {altRowEditing ? t('admin.editAlternativesRow') : t('admin.addAlternativesRow')}
              </ModalTitle>
              <X size={24} style={{ cursor: 'pointer', color: '#1e40af' }} onClick={() => setShowAltRowModal(false)} />
            </ModalHeader>
            <ModalBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Prescription type */}
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.4rem' }}>
                    {t('admin.prescriptionType4')}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[
                      { id: 'single_vision', label: 'Simple Foyer' },
                      { id: 'progressive',   label: 'Progressifs' },
                    ].map(opt => {
                      const active = altRowForm.prescriptionType === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setAltRowForm(p => ({ ...p, prescriptionType: opt.id }))}
                          style={{
                            padding: '0.4rem 0.75rem', borderRadius: '8px',
                            border: `1px solid ${active ? '#6366f1' : '#cbd5e1'}`,
                            background: active ? '#eef2ff' : 'white',
                            color: active ? '#4338ca' : '#334155',
                            cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Sphere sign */}
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.4rem' }}>
                    {t('admin.sphereSign')}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {[
                      { id: 'plus', label: '(+) Hyperopia', color: '#059669' },
                      { id: 'minus', label: '(−) Myopia', color: '#dc2626' },
                      { id: 'none', label: 'None (plano, SPH = 0)', color: '#475569' },
                    ].map(opt => {
                      const active = altRowForm.sphereSign === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setAltRowForm(p => ({ ...p, sphereSign: opt.id }))}
                          style={{
                            padding: '0.4rem 0.75rem', borderRadius: '8px',
                            border: `1px solid ${active ? opt.color : '#cbd5e1'}`,
                            background: active ? `${opt.color}1a` : 'white',
                            color: active ? opt.color : '#334155',
                            cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Group selector */}
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.4rem' }}>
                    {t('admin.correctionMapClickOneOr2')}
                  </div>
                  <CorrectionGroupMap
                    sphereSign={altRowForm.sphereSign}
                    selectedGroups={altRowForm.groupPair}
                    onToggle={toggleGroupInAltForm}
                  />
                  {altRowForm.groupPair.length > 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#475569' }}>
                    {t('admin.selected')} <strong>{displayCorrectionGroupPair(altRowForm.groupPair)}</strong>
                    </div>
                  )}
                </div>

                {/* Order */}
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', maxWidth: 200 }}>
                  {t('admin.displayOrder2')}
                  <input
                    type="number"
                    value={altRowForm.order}
                    onChange={(e) => setAltRowForm(p => ({ ...p, order: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '0.25rem' }}
                  />
                </label>

                <div style={{ fontSize: '0.8rem', color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.7rem 0.9rem' }}>
                  {t('admin.thisDialogSetsThe')} <strong>{t('admin.groupS2')}</strong>, <strong>sign</strong> and <strong>order</strong>{t('admin.addThe')} <span style={{ color: '#059669', fontWeight: 600 }}>{t('admin.up')}</span> / <span style={{ color: '#ca8a04', fontWeight: 600 }}>{t('admin.down')}</span> {t('admin.lensesDirectlyInTheTable')} <strong>{t('admin.add4')}</strong> {t('admin.pickerTheyReChosenFrom')}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowAltRowModal(false)}>{t('admin.cancel')}</CancelButton>
              <button
                onClick={saveAltRow}
                style={{
                  padding: '0.5rem 1rem', background: '#6366f1', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  fontWeight: 500, fontSize: '0.875rem',
                }}
              >
                {altRowEditing ? t('admin.saveChanges') : t('admin.createRow')}
              </button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Employee Modal */}
      {showDeleteEmployeeModal && selectedEmployee && (
        <ModalOverlay onClick={() => setShowDeleteEmployeeModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={24} />
                {t('admin.removeEmployee')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#991b1b' }}
                onClick={() => setShowDeleteEmployeeModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <ModalMessage>
                {t('admin.areYouSureYouWant2')} <strong>{selectedEmployee.firstName} {selectedEmployee.lastName}</strong> ({selectedEmployee.email})?
              </ModalMessage>
              <WarningBox>
                <WarningText>
                  <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    {t('admin.thisActionCannotBeUndone3')}
                  </span>
                </WarningText>
              </WarningBox>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowDeleteEmployeeModal(false)}>
                {t('admin.cancel')}
              </CancelButton>
              <ConfirmDeleteButton
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`/api/users/${selectedEmployee._id || selectedEmployee.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Employee removed');
                    setShowDeleteEmployeeModal(false);
                    setSelectedEmployee(null);
                    fetchEmployees();
                  } catch (error) {
                    console.error('Error removing employee:', error);
                    toast.error(error.response?.data?.message || 'Error removing employee. Please try again.');
                  }
                }}
              >
                <Trash2 size={18} />
                {t('admin.removeEmployee')}
              </ConfirmDeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Store Modal */}
      {showDeleteStoreModal && selectedStore && (
        <ModalOverlay onClick={() => setShowDeleteStoreModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={24} />
                {t('admin.deleteStore')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#991b1b' }}
                onClick={() => setShowDeleteStoreModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <ModalMessage>
                {t('admin.areYouSureYouWant')} <strong>{selectedStore.name}</strong> ({selectedStore.storeCode})?
              </ModalMessage>
              <WarningBox>
                <WarningText>
                  <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    {t('admin.thisActionCannotBeUndone4')}
                  </span>
                </WarningText>
              </WarningBox>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowDeleteStoreModal(false)}>
                {t('admin.cancel')}
              </CancelButton>
              <ConfirmDeleteButton
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`/api/stores/${selectedStore._id || selectedStore.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Store deleted');
                    setShowDeleteStoreModal(false);
                    setSelectedStore(null);
                    fetchStores();
                  } catch (error) {
                    console.error('Error deleting store:', error);
                    toast.error(error.response?.data?.message || 'Error deleting store. Please try again.');
                  }
                }}
              >
                <Trash2 size={18} />
                {t('admin.deleteStore')}
              </ConfirmDeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Bulk Delete Employees Modal */}
      {showBulkDeleteEmployeeModal && (
        <ModalOverlay onClick={() => !deletingEmployees && setShowBulkDeleteEmployeeModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={24} />
                {t('admin.deleteSelectedEmployees')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#991b1b' }}
                onClick={() => !deletingEmployees && setShowBulkDeleteEmployeeModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <ModalMessage>
                {t('admin.areYouSureYouWant')} <strong>{selectedEmployees.length}</strong> {t('admin.employeeS')}
              </ModalMessage>
              <WarningBox>
                <WarningText>
                  <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    {t('admin.thisActionCannotBeUndone5')}
                  </span>
                </WarningText>
              </WarningBox>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowBulkDeleteEmployeeModal(false)} disabled={deletingEmployees}>
                {t('admin.cancel')}
              </CancelButton>
              <ConfirmDeleteButton
                onClick={async () => {
                  setDeletingEmployees(true);
                  try {
                    const token = localStorage.getItem('token');
                    const deletePromises = selectedEmployees.map(id =>
                      axios.delete(`/api/users/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                    );
                    await Promise.all(deletePromises);
                    toast.success(`${selectedEmployees.length} employee(s) deleted`);
                    setShowBulkDeleteEmployeeModal(false);
                    setSelectedEmployees([]);
                    fetchEmployees();
                  } catch (error) {
                    console.error('Error deleting employees:', error);
                    toast.error(error.response?.data?.message || 'Error deleting employees. Please try again.');
                  } finally {
                    setDeletingEmployees(false);
                  }
                }}
                disabled={deletingEmployees}
              >
                {deletingEmployees ? (
                  <>
                    <SpinningIcon size={18} />
                    {t('admin.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    {t('admin.delete')} {selectedEmployees.length} {t('admin.employeeS2')}
                  </>
                )}
              </ConfirmDeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Bulk Delete Stores Modal */}
      {showBulkDeleteStoreModal && (
        <ModalOverlay onClick={() => !deletingStores && setShowBulkDeleteStoreModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={24} />
                {t('admin.deleteSelectedStores')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#991b1b' }}
                onClick={() => !deletingStores && setShowBulkDeleteStoreModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <ModalMessage>
                {t('admin.areYouSureYouWant')} <strong>{selectedStores.length}</strong> {t('admin.storeS')}
              </ModalMessage>
              <WarningBox>
                <WarningText>
                  <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    {t('admin.thisActionCannotBeUndone6')}
                  </span>
                </WarningText>
              </WarningBox>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowBulkDeleteStoreModal(false)} disabled={deletingStores}>
                {t('admin.cancel')}
              </CancelButton>
              <ConfirmDeleteButton
                onClick={async () => {
                  setDeletingStores(true);
                  try {
                    const token = localStorage.getItem('token');
                    const deletePromises = selectedStores.map(id =>
                      axios.delete(`/api/stores/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                    );
                    await Promise.all(deletePromises);
                    toast.success(`${selectedStores.length} store(s) deleted`);
                    setShowBulkDeleteStoreModal(false);
                    setSelectedStores([]);
                    fetchStores();
                  } catch (error) {
                    console.error('Error deleting stores:', error);
                    toast.error(error.response?.data?.message || 'Error deleting stores. Please try again.');
                  } finally {
                    setDeletingStores(false);
                  }
                }}
                disabled={deletingStores}
              >
                {deletingStores ? (
                  <>
                    <SpinningIcon size={18} />
                    {t('admin.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    {t('admin.delete')} {selectedStores.length} {t('admin.storeS2')}
                  </>
                )}
              </ConfirmDeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {showDeleteModal && (
        <ModalOverlay onClick={() => !deleting && setShowDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={24} />
                {t('admin.deleteAllData')}
              </ModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#991b1b' }}
                onClick={() => !deleting && setShowDeleteModal(false)}
              />
            </ModalHeader>
            <ModalBody>
              <ModalMessage>
                {t('admin.youAreAboutToPermanently')} <strong>{t('admin.allData')}</strong> {t('admin.fromTheSystemThisIncludes')}
              </ModalMessage>
              <ul style={{ margin: '1rem 0', paddingLeft: '1.5rem', color: '#1e293b' }}>
                <li>{t('admin.allVisitorEntries')}</li>
                <li>{t('admin.allPrescriptions')}</li>
                <li>{t('admin.allOrders')}</li>
                <li>{t('admin.allAnalyticsData')}</li>
              </ul>
              <WarningBox>
                <WarningText>
                  <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    <strong>{t('admin.thisActionCannotBeUndone7')}</strong> {t('admin.allDataWillBePermanently')}
                  </span>
                </WarningText>
              </WarningBox>
              <ModalMessage style={{ marginTop: '1.5rem', fontWeight: '600' }}>
                {t('admin.toConfirmPleaseType')} <strong style={{ color: '#dc2626' }}>{t('admin.deleteAllData2')}</strong> {t('admin.inTheBoxBelow')}
              </ModalMessage>
              <ConfirmInput
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('admin.typeDeleteAllDataTo')}
                disabled={deleting}
              />
            </ModalBody>
            <ModalFooter>
              <CancelButton
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                disabled={deleting}
              >
                {t('admin.cancel')}
              </CancelButton>
              <ConfirmDeleteButton
                onClick={handleDeleteAllData}
                disabled={deleteConfirmText !== 'DELETE ALL DATA' || deleting}
              >
                {deleting ? (
                  <>
                    <SpinningIcon size={18} />
                    {t('admin.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    {t('admin.deleteAllData')}
                  </>
                )}
              </ConfirmDeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* ── Global UX layers (rendered last so they paint on top of everything). ── */}
      <ShellThemeStyles />
      <Toaster />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={paletteCommands}
      />
    </Container>
  );
};

export default AdminDashboard;
